﻿import { computed, defineComponent, h, onMounted, reactive, ref, watch } from "vue";
import {
  COUNTRY_CODES,
  LOCALES,
  PLAN_LIST,
  type ComplianceFinding,
  countryPacks,
  evaluateInvoice,
  getSource,
  getCountryName,
  t,
  type ComplianceEvaluation,
  type CountryCode,
  type InvoiceInput,
  type LocaleCode,
  type PlanCode,
  type SourceReference
} from "../../shared/src/index";
import { ApiError, api } from "./lib/api";
import { trackPageView } from "./lib/analytics";
import {
  getCountryBusinessActivityOptions,
  getCountryBuyerTypeOptions,
  getCountryDefaultRate,
  getCountrySupplyNatureOptions,
  makeDraft,
  mergeExtractedDraft,
  recalculateDraft
} from "./lib/defaults";
import { getLocaleLabel, ui } from "./lib/ui-copy";

type RunRecord = { id: string; country: string; invoiceNumber?: string; status: string; createdAt: string; overallRiskLevel?: string; rulebookVersion?: string; trigger?: string; invoiceRecordId?: string; previewAvailable?: boolean };
type InvoiceRecord = { id: string; country: string; invoiceNumber: string; sellerName: string; buyerName?: string; status: string; overallRiskLevel: string; rulebookVersion: string; revisionNumber: number; generatedHtml: boolean; createdAt: string; updatedAt: string };
type InvoiceRecordDetail = { id: string; country: string; invoiceNumber: string; data: InvoiceInput; generatedHtml?: string | null; revisionNumber: number; rulebookVersion: string; overallRiskLevel: string; status: string };
type BillingOverview = {
  organization: {
    id: string;
    name: string;
    country: string;
    planCode: string;
    billingStatus: string;
    billingCycle: string;
    billingCurrentPeriodStart: string | null;
    billingCurrentPeriodEnd: string | null;
    billingNextBilledAt: string | null;
  };
  usage: { monthStart: string; checks: number; extractions: number; generatedInvoices: number; members: number };
  limits: { monthlyPriceUsd: number; checksPerMonth: number; extractionsPerMonth: number; generatedInvoicesPerMonth: number; members: number; historyDays: number };
  availablePlans: Array<{ code: string; monthlyPriceUsd: number; checksPerMonth: number; extractionsPerMonth: number; generatedInvoicesPerMonth: number; members: number; historyDays: number }>;
  subscription: {
    id: string;
    providerSubscriptionId: string;
    planCode: string;
    status: string;
    currency?: string;
    currentPeriodStart?: string;
    currentPeriodEnd?: string;
    nextBilledAt?: string;
    managementUrls?: Record<string, string | null>;
  } | null;
  orders: Array<{
    id: string;
    providerTransactionId: string;
    planCode: string;
    status: string;
    currency?: string;
    totalAmount?: number;
    refundedAmount: number;
    createdAt: string;
    completedAt?: string;
  }>;
  provider: {
    enabled: boolean;
    environment: "sandbox" | "production";
    clientToken: string;
  };
};
type OrganizationRecord = { id: string; name: string; country: string; role: string; planCode: string; billingStatus: string };
type UserRecord = { id: string; email: string; displayName: string; locale: string; emailVerifiedAt?: string | null };
type AuthMode = "login" | "register" | "reset";
type AppPage = "home" | "product" | "pricing" | "privacy" | "terms" | "refunds" | "contact" | "workspace" | "history" | "rules" | "billing" | "admin";
type EditorTab = "basics" | "parties" | "items" | "intake";
type SidebarTab = "result" | "activity";
type ResultPanelTab = "issues" | "watchlist";
type HistoryTab = "runs" | "invoices";
type PlanPreset = { code: string; monthlyPriceUsd: number; checksPerMonth: number; extractionsPerMonth: number; generatedInvoicesPerMonth: number; members: number; historyDays: number };
type CaptchaConfig = { enabled: boolean; region: "cn" | "sgp"; prefix: string; sceneId: string };
type WorkflowStep = { index: string; title: string; body: string };
type GuideCard = { title: string; body: string };
type LandingFeature = { icon: "draft" | "defaults" | "tasks"; title: string; body: string };
type LandingMarketCard = { code: CountryCode; icon: "sg" | "my" | "id" | "th"; title: string; body: string; meta: string };
type DraftProgressCard = { tab: EditorTab; fieldId: string; title: string; body: string; missingCount: number; complete: boolean };
type FieldTone = "missing" | "ready";
type FieldOptions = { tone?: FieldTone };
type StaticSection = { title: string; body: string; bullets?: string[] };
type StaticStat = { label: string; value: string; body: string };
type BillingAdminOverview = {
  plans: Array<{ code: string; monthlyPriceUsd: number }>;
  counts: {
    orders: number;
    completedOrders: number;
    activeSubscriptions: number;
    refundPending: number;
    refundedOrders: number;
  };
  recentOrders: Array<{
    id: string;
    providerTransactionId: string;
    organizationId: string;
    organizationName: string;
    planCode: string;
    status: string;
    currency?: string;
    totalAmount?: number;
    refundedAmount: number;
    createdAt: string;
    completedAt?: string;
  }>;
};

declare global {
  interface Window {
    AliyunCaptchaConfig?: { region: "cn" | "sgp"; prefix: string };
    initAliyunCaptcha?: (config: Record<string, unknown>) => void;
    Paddle?: {
      Environment?: { set: (environment: "sandbox" | "production") => void };
      Initialize?: (options: { token: string; eventCallback?: (event: { name?: string; data?: Record<string, unknown> }) => void }) => void;
      Checkout?: {
        open: (options: Record<string, unknown>) => void;
      };
    };
  }
}

const MAX_EXTRACT_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_EXTRACT_PDF_BYTES = 10 * 1024 * 1024;
const DISPLAY_NAME_MAX_LENGTH = 40;
const ORGANIZATION_NAME_MAX_LENGTH = 80;
const ALLOWED_EXTRACT_FILE_TYPES = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp", "application/pdf"]);
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const VERIFY_CODE_PATTERN = /^\d{6}$/;
const STORAGE_LOCALE_KEY = "compliance.locale";
const STORAGE_COUNTRY_KEY = "compliance.country";
const CAPTCHA_SCRIPT_ID = "aliyun-captcha-script";
const CAPTCHA_ELEMENT_ID = "email-captcha-element";
const CAPTCHA_TRIGGER_ID = "email-captcha-trigger";
const PADDLE_SCRIPT_ID = "paddle-js-script";
const DEFAULT_PLAN_CATALOG: PlanPreset[] = PLAN_LIST;
const SUPPORT_EMAIL = "support@ivela.cn";
const BILLING_EMAIL = SUPPORT_EMAIL;
const PRIVACY_EMAIL = SUPPORT_EMAIL;
const LEGAL_LAST_UPDATED = "2026-04-10";

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const formatTimestamp = (value: string | undefined, locale: LocaleCode): string => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString(locale);
};

export const AppRoot = defineComponent({
  name: "ComplianceAppRoot",
  setup() {
    const locale = ref<LocaleCode>("en");
    const country = ref<CountryCode>("SG");
    const authMode = ref<AuthMode>("login");
    const authDialogOpen = ref(false);
    const previewDialogOpen = ref(false);
    const currentPage = ref<AppPage>("home");
    const editorTab = ref<EditorTab>("basics");
    const editorGuideOpen = ref(false);
    const sidebarTab = ref<SidebarTab>("result");
    const historyTab = ref<HistoryTab>("runs");
    const historyExpanded = ref(false);
    const suppressDraftReset = ref(false);
    const resultPanelTab = ref<ResultPanelTab>("issues");
    const findingsExpanded = ref(false);
    const thresholdsExpanded = ref(false);
    const verifyCode = ref("");
    const verifyStatus = ref("");
    const verifyCooldownRemaining = ref(0);
    const passwordResetCooldownRemaining = ref(0);
    const draft = reactive<InvoiceInput>(makeDraft(country.value, locale.value));
    const notesInput = ref("");
    const extractText = ref("");
    const uploadedFileData = ref("");
    const uploadedFileName = ref("");
    const uploadedMime = ref("");
    const extractStatus = ref("");
    const apiStatus = ref("");
    const previewHtml = ref("");
    const latestServerResult = ref<ComplianceEvaluation | null>(null);
    const latestRuns = ref<RunRecord[]>([]);
    const latestInvoices = ref<InvoiceRecord[]>([]);
    const billingOverview = ref<BillingOverview | null>(null);
    const adminOverview = ref<BillingAdminOverview | null>(null);
    const adminOrders = ref<BillingAdminOverview["recentOrders"]>([]);
    const authStatus = ref("");
    const billingStatus = ref("");
    const adminStatus = ref("");
    const currentOrganizationId = ref("");
    const checkoutPending = ref(false);
    const subscriptionManagePending = ref(false);
    const adminRefundPendingOrderId = ref("");
    const auth = reactive({
      ready: false,
      loading: false,
      verifying: false,
      email: "",
      password: "",
      displayName: "",
      organizationName: "",
      organizationCountry: "SG",
      isBillingAdmin: false,
      user: null as UserRecord | null,
      organizations: [] as OrganizationRecord[]
    });
    const passwordReset = reactive({
      email: "",
      code: "",
      password: "",
      confirmPassword: "",
      requested: false
    });
    const passwordChange = reactive({
      currentPassword: "",
      password: "",
      confirmPassword: ""
    });
    const bootstrap = reactive({
      aiAvailable: false,
      mailConfigured: false,
      emailVerificationEnabled: true,
      captcha: { enabled: false, region: "cn", prefix: "", sceneId: "" } as CaptchaConfig,
      billing: { enabled: false, environment: "sandbox", clientToken: "" } as { enabled: boolean; environment: "sandbox" | "production"; clientToken: string },
      persistenceMode: "database",
      multiRegionReady: true,
      launchCountries: [] as string[],
      expansionReadyCountries: [] as string[],
      deploymentRequirements: [] as Array<{ key: string; required: boolean; category: string; description: string }>
    });

    const localResult = computed(() => evaluateInvoice(recalculateDraft({ ...draft, locale: locale.value }), { locale: locale.value }));
    const activeResult = computed(() => latestServerResult.value ?? localResult.value);
    const actionableFindings = computed(() => {
      const severityRank: Record<string, number> = { error: 0, warning: 1, info: 2 };
      return [...activeResult.value.findings]
        .filter((finding) => finding.category !== "threshold")
        .sort((left, right) => (severityRank[left.severity] ?? 9) - (severityRank[right.severity] ?? 9));
    });
    const watchlistThresholds = computed(() =>
      activeResult.value.thresholds.filter((item) => item.status !== "not_applicable")
    );
    const visibleThresholds = computed(() =>
      thresholdsExpanded.value ? watchlistThresholds.value : watchlistThresholds.value.slice(0, 2)
    );
    const sidebarRuns = computed(() => latestRuns.value.slice(0, 2));
    const sidebarInvoices = computed(() => latestInvoices.value.slice(0, 2));
    const currentSources = computed<SourceReference[]>(() =>
      pack.value.sourceIds.map((id) => getSource(id)).filter(Boolean) as SourceReference[]
    );
    const pack = computed(() => countryPacks[country.value]);
    const signedIn = computed(() => Boolean(auth.user && currentOrganizationId.value));
    const activeOrganization = computed(() => auth.organizations.find((item) => item.id === currentOrganizationId.value) ?? null);
    const hasExtractInput = computed(() => Boolean(extractText.value.trim() || uploadedFileData.value));
    const emailVerified = computed(() => Boolean(auth.user?.emailVerifiedAt));
    const canUseServerActions = computed(() => signedIn.value && emailVerified.value);
    const localeOptions = computed(() => LOCALES.map((item) => ({ value: item, label: getLocaleLabel(item) })));
    const availablePlans = computed(() => billingOverview.value?.availablePlans ?? DEFAULT_PLAN_CATALOG);
    const isBillingAdmin = computed(() => auth.isBillingAdmin);
    const currentSubscription = computed(() => billingOverview.value?.subscription ?? null);
    const canPurchasePaidPlan = computed(() => Boolean(signedIn.value && emailVerified.value && billingOverview.value?.provider.enabled));
    const requiresEmailCaptcha = computed(() => bootstrap.mailConfigured && bootstrap.emailVerificationEnabled && bootstrap.captcha.enabled);
    const draftEntryMode = computed<"manual" | "import">(() => (editorTab.value === "intake" ? "import" : "manual"));
    const copy = (key: string, params: Record<string, string | number> = {}) => ui(locale.value, key, params);
    const localeText = (messages: Partial<Record<LocaleCode, string>> & { en: string }): string => messages[locale.value] ?? messages.en;
    const buyerTypeOptions = computed(() =>
      getCountryBuyerTypeOptions(country.value).map((value) => ({ value, label: copy(`buyerType_${value}`) }))
    );
    const supplyNatureOptions = computed(() =>
      getCountrySupplyNatureOptions(country.value).map((value) => ({ value, label: copy(`supplyNature_${value}`) }))
    );
    const businessActivityOptions = computed(() =>
      getCountryBusinessActivityOptions(country.value).map((value) => ({ value, label: copy(`businessActivity_${value}`) }))
    );
    const hasTextValue = (value: string | null | undefined) => Boolean(value?.trim());
    const hasPositiveValue = (value: number | string | null | undefined) => Number(value ?? 0) > 0;
    const currentThaiInvoiceMode = computed(() => draft.invoiceType ?? "tax_invoice");
    const buyerNeedsNamedProfile = computed(() => {
      if (draft.buyerType !== "final_consumer") {
        return true;
      }
      if (country.value === "MY") {
        return true;
      }
      return country.value === "TH" && currentThaiInvoiceMode.value !== "abbreviated_tax_invoice";
    });
    const sellerNeedsTaxProfile = computed(() => {
      if (draft.taxRegistered) {
        return true;
      }
      if (country.value === "ID") {
        return true;
      }
      return country.value === "TH" && currentThaiInvoiceMode.value !== "commercial_invoice";
    });
    const sellerNeedsRegistrationNumber = computed(() => country.value === "SG" || country.value === "MY");
    const keyFieldState = computed(() => {
      const firstItem = draft.items[0];
      return {
        invoiceNumber: hasTextValue(draft.invoiceNumber),
        issueDate: hasTextValue(draft.issueDate),
        issueTime: country.value !== "MY" || hasTextValue(draft.issueTime),
        sellerLegalName: hasTextValue(draft.seller.legalName),
        sellerRegistrationNumber: !sellerNeedsRegistrationNumber.value || hasTextValue(draft.seller.registrationNumber),
        sellerTaxNumber: !sellerNeedsTaxProfile.value || hasTextValue(draft.seller.taxNumber),
        sellerAddress: hasTextValue(draft.seller.address?.line1),
        buyerLegalName: !buyerNeedsNamedProfile.value || hasTextValue(draft.buyer.legalName),
        buyerAddress: !buyerNeedsNamedProfile.value || hasTextValue(draft.buyer.address?.line1),
        firstItemDescription: Boolean(firstItem) && hasTextValue(firstItem.description),
        firstItemUnitPrice: Boolean(firstItem) && hasPositiveValue(firstItem.unitPrice),
        firstItemTaxRate: Boolean(firstItem) && String(firstItem.taxRate ?? "").trim() !== "",
        firstItemClassification:
          country.value !== "MY" && country.value !== "ID"
            ? true
            : Boolean(firstItem) && hasTextValue(firstItem.classificationCode)
      };
    });
    const draftProgressCards = computed<DraftProgressCard[]>(() => {
      const basicsMissing = [
        !keyFieldState.value.invoiceNumber,
        !keyFieldState.value.issueDate,
        !keyFieldState.value.issueTime
      ].filter(Boolean).length;
      const partiesMissing = [
        !keyFieldState.value.sellerLegalName,
        !keyFieldState.value.sellerRegistrationNumber,
        !keyFieldState.value.sellerTaxNumber,
        !keyFieldState.value.sellerAddress,
        !keyFieldState.value.buyerLegalName,
        !keyFieldState.value.buyerAddress
      ].filter(Boolean).length;
      const itemsMissing = [
        !keyFieldState.value.firstItemDescription,
        !keyFieldState.value.firstItemUnitPrice,
        !keyFieldState.value.firstItemTaxRate,
        !keyFieldState.value.firstItemClassification
      ].filter(Boolean).length;
      return [
        {
          tab: "basics",
          fieldId: !keyFieldState.value.invoiceNumber ? "invoice-number" : !keyFieldState.value.issueDate ? "issue-date" : !keyFieldState.value.issueTime ? "issue-time" : "invoice-number",
          title: localeText({
            en: "Start with the commercial core",
            "zh-CN": "先完成核心单据信息",
            "ms-MY": "Mulakan dengan teras komersial",
            "id-ID": "Mulai dari inti komersial",
            "th-TH": "เริ่มจากข้อมูลหลักของเอกสาร"
          }),
          body: localeText({
            en: "Set the invoice number and issue timing first, then review the default buyer, supply, and activity assumptions.",
            "zh-CN": "先确认发票编号和开票时间，再复核买方类型、供给类型和业务活动这些默认假设。",
            "ms-MY": "Tetapkan nombor invois dan masa terbit dahulu, kemudian semak andaian lalai untuk pembeli, jenis bekalan, dan aktiviti.",
            "id-ID": "Tetapkan nomor invoice dan waktu terbit terlebih dahulu, lalu tinjau asumsi default pembeli, jenis penyerahan, dan aktivitas.",
            "th-TH": "ตั้งเลขเอกสารและเวลาออกเอกสารก่อน จากนั้นค่อยตรวจทานค่าตั้งต้นของผู้ซื้อ ลักษณะการขาย และประเภทธุรกิจ"
          }),
          missingCount: basicsMissing,
          complete: basicsMissing === 0
        },
        {
          tab: "parties",
          fieldId: !keyFieldState.value.sellerLegalName
            ? "seller-legal-name"
            : !keyFieldState.value.sellerRegistrationNumber
              ? "seller-registration-number"
              : !keyFieldState.value.sellerTaxNumber
                ? "seller-tax-number"
                : !keyFieldState.value.sellerAddress
                  ? "seller-address-line1"
                  : !keyFieldState.value.buyerLegalName
                    ? "buyer-legal-name"
                    : !keyFieldState.value.buyerAddress
                      ? "buyer-address-line1"
                      : "seller-legal-name",
          title: localeText({
            en: "Then lock the issuing parties",
            "zh-CN": "然后确认开票主体与买方",
            "ms-MY": "Kemudian sahkan pihak invois",
            "id-ID": "Lalu kunci pihak pada invoice",
            "th-TH": "จากนั้นยืนยันข้อมูลคู่สัญญา"
          }),
          body: localeText({
            en: "Make sure the seller reflects the issuing entity, then add the buyer profile to the level the current market expects.",
            "zh-CN": "先让卖方信息与实际开票主体一致，再把买方资料补到当前市场所需的程度。",
            "ms-MY": "Pastikan penjual mewakili entiti yang mengeluarkan dokumen, kemudian lengkapkan profil pembeli mengikut keperluan pasaran semasa.",
            "id-ID": "Pastikan penjual mewakili entitas penerbit dokumen, lalu lengkapi profil pembeli sesuai kebutuhan pasar saat ini.",
            "th-TH": "ทำให้ข้อมูลผู้ขายตรงกับผู้ออกเอกสารจริง แล้วเติมข้อมูลผู้ซื้อให้ครบตามที่ตลาดปัจจุบันต้องการ"
          }),
          missingCount: partiesMissing,
          complete: partiesMissing === 0
        },
        {
          tab: "items",
          fieldId: !keyFieldState.value.firstItemDescription
            ? "invoice-item-0-description"
            : !keyFieldState.value.firstItemUnitPrice
              ? "invoice-item-0-unitPrice"
              : !keyFieldState.value.firstItemTaxRate
                ? "invoice-item-0-taxRate"
                : !keyFieldState.value.firstItemClassification
                  ? "invoice-item-0-classificationCode"
                  : "invoice-item-0-description",
          title: localeText({
            en: "Finish the first revenue line",
            "zh-CN": "再完成首条行项目",
            "ms-MY": "Lengkapkan item hasil pertama",
            "id-ID": "Lengkapi item pendapatan pertama",
            "th-TH": "ปิดงานที่บรรทัดรายได้แรก"
          }),
          body: localeText({
            en: "A clear description, commercial price, tax rate, and any required line code are enough to produce a reliable first check.",
            "zh-CN": "只要首条行项目的描述、商业金额、税率和必要编码填清楚，就足够跑出一轮可靠检查。",
            "ms-MY": "Deskripsi jelas, harga komersial, kadar cukai, dan kod item yang diperlukan sudah memadai untuk semakan pertama yang boleh dipercayai.",
            "id-ID": "Deskripsi yang jelas, harga komersial, tarif pajak, dan kode item yang diperlukan sudah cukup untuk pemeriksaan pertama yang andal.",
            "th-TH": "เมื่อบรรทัดแรกมีคำอธิบาย ราคาเชิงพาณิชย์ อัตราภาษี และรหัสที่จำเป็นครบ ก็เพียงพอสำหรับการตรวจรอบแรกที่เชื่อถือได้"
          }),
          missingCount: itemsMissing,
          complete: itemsMissing === 0
        }
      ];
    });
    const draftProgressCompleteCount = computed(() => draftProgressCards.value.filter((card) => card.complete).length);
    const draftProgressMissingCount = computed(() =>
      draftProgressCards.value.reduce((sum, card) => sum + (card.complete ? 0 : card.missingCount), 0)
    );
    const landingFeatures = computed<LandingFeature[]>(() => [
      {
        icon: "draft",
        title: localeText({ en: "One draft, shared by every step", "zh-CN": "一份草稿，贯穿全流程", "ms-MY": "Satu draf untuk semua langkah", "id-ID": "Satu draft untuk seluruh alur", "th-TH": "ร่างเดียว ใช้ได้ทุกขั้นตอน" }),
        body: localeText({
          en: "Manual entry and OCR/PDF import both update the same invoice draft.",
          "zh-CN": "手工录入和 OCR/PDF 导入都会落到同一份发票草稿里。",
          "ms-MY": "Input manual dan import OCR/PDF sama-sama mengemas kini draf invois yang sama.",
          "id-ID": "Input manual dan impor OCR/PDF sama-sama memperbarui draft invoice yang sama.",
          "th-TH": "การกรอกเองและการนำเข้า OCR/PDF จะอัปเดตร่างเอกสารชุดเดียวกัน"
        })
      },
      {
        icon: "defaults",
        title: localeText({ en: "Local defaults built in", "zh-CN": "内置本地化默认项", "ms-MY": "Tetapan tempatan terbina dalam", "id-ID": "Default lokal sudah siap", "th-TH": "มีค่าตั้งต้นตามประเทศในตัว" }),
        body: localeText({
          en: "Tax hints, buyer assumptions, and field guidance switch with the market.",
          "zh-CN": "税率提示、买方假设和字段引导会随着市场切换而变化。",
          "ms-MY": "Petunjuk cukai, andaian pembeli, dan panduan medan berubah mengikut pasaran.",
          "id-ID": "Petunjuk pajak, asumsi pembeli, dan panduan bidang berubah mengikuti pasar.",
          "th-TH": "คำใบ้ภาษี สมมติฐานผู้ซื้อ และคำแนะนำฟิลด์จะเปลี่ยนตามตลาด"
        })
      },
      {
        icon: "tasks",
        title: localeText({ en: "Clear next actions", "zh-CN": "下一步很清楚", "ms-MY": "Langkah seterusnya jelas", "id-ID": "Tindak lanjutnya jelas", "th-TH": "ขั้นตอนถัดไปชัดเจน" }),
        body: localeText({
          en: "Checks explain what to fix and point the team back to the right field.",
          "zh-CN": "检查结果会告诉你该修什么，并直接指回对应字段。",
          "ms-MY": "Semakan menerangkan apa yang perlu dibaiki dan membawa pasukan kembali ke medan yang betul.",
          "id-ID": "Pemeriksaan menjelaskan apa yang perlu diperbaiki dan mengarahkan tim ke bidang yang tepat.",
          "th-TH": "ผลการตรวจจะบอกว่าต้องแก้อะไร และพากลับไปยังฟิลด์ที่ถูกต้อง"
        })
      }
    ]);
    const landingMarketCards = computed<LandingMarketCard[]>(() =>
      COUNTRY_CODES.map((code) => {
        const marketName = getCountryName(locale.value, code);
        const meta = `${countryPacks[code].localCurrency} · ${countryPacks[code].rulebookVersion}`;
        const body =
          code === "SG"
            ? localeText({
                en: "GST invoice content, simplified invoices, InvoiceNow rollout, and cross-border evidence.",
                "zh-CN": "覆盖 GST 发票内容、简化发票、InvoiceNow 路线和跨境留档要求。",
                "ms-MY": "Meliputi kandungan invois GST, invois ringkas, pelaksanaan InvoiceNow, dan bukti rentas sempadan.",
                "id-ID": "Mencakup isi invoice GST, invoice sederhana, rollout InvoiceNow, dan bukti lintas batas.",
                "th-TH": "ครอบคลุมเนื้อหาใบกำกับ GST ใบกำกับแบบย่อ เส้นทาง InvoiceNow และหลักฐานข้ามพรมแดน"
              })
            : code === "MY"
              ? localeText({
                  en: "MyInvois rollout, SST thresholds, supplier identity, and line classification support.",
                  "zh-CN": "覆盖 MyInvois 推进、SST 门槛、卖方身份和行分类支持。",
                  "ms-MY": "Meliputi pelaksanaan MyInvois, ambang SST, identiti pembekal, dan sokongan klasifikasi item.",
                  "id-ID": "Mencakup rollout MyInvois, ambang SST, identitas pemasok, dan dukungan klasifikasi item.",
                  "th-TH": "ครอบคลุมเส้นทาง MyInvois เกณฑ์ SST ข้อมูลผู้ออก และการจัดประเภทรายการ"
                })
              : code === "ID"
                ? localeText({
                    en: "PKP registration logic, VAT paths, transaction-code handling, and tax-invoice structure.",
                    "zh-CN": "覆盖 PKP 注册判断、VAT 路径、交易代码处理和税务发票结构。",
                    "ms-MY": "Meliputi logik pendaftaran PKP, laluan VAT, pengendalian kod transaksi, dan struktur invois cukai.",
                    "id-ID": "Mencakup logika registrasi PKP, jalur VAT, penanganan kode transaksi, dan struktur faktur pajak.",
                    "th-TH": "ครอบคลุมตรรกะการจดทะเบียน PKP เส้นทาง VAT การจัดการรหัสธุรกรรม และโครงสร้างเอกสารภาษี"
                  })
                : localeText({
                    en: "Thai tax-invoice modes, branch handling, e-Tax readiness, and VAT threshold guidance.",
                    "zh-CN": "覆盖泰国发票模式、分支处理、e-Tax 准备度和 VAT 门槛提示。",
                    "ms-MY": "Meliputi mod invois Thailand, pengendalian cawangan, kesiapsiagaan e-Tax, dan panduan ambang VAT.",
                    "id-ID": "Mencakup mode invoice Thailand, penanganan cabang, kesiapan e-Tax, dan panduan ambang VAT.",
                    "th-TH": "ครอบคลุมรูปแบบเอกสารของไทย การจัดการสาขา ความพร้อมด้าน e-Tax และคำแนะนำเรื่องเกณฑ์ VAT"
                  });
        return { code, icon: code.toLowerCase() as "sg" | "my" | "id" | "th", title: marketName, body, meta };
      })
    );
    let verifyCooldownTimer: ReturnType<typeof setInterval> | null = null;
    let passwordResetCooldownTimer: ReturnType<typeof setInterval> | null = null;
    let captchaScriptPromise: Promise<void> | null = null;
    let captchaScriptLoadedAt = 0;
    let captchaInitPromise: Promise<void> | null = null;
    let captchaInitKey = "";
    let captchaReady = false;
    let captchaInstance: { show?: () => void } | null = null;
    let pendingCaptchaRunner: ((captchaVerifyParam: string) => Promise<{ captchaResult: boolean; bizResult?: boolean }>) | null = null;
    let paddleInitPromise: Promise<void> | null = null;
    const labels = computed(() => {
      switch (locale.value) {
        case "zh-CN":
          return {
            home: "首页",
            workbench: "智能开票",
            history: "使用记录",
            sources: "规则中心",
            billing: "账单与套餐",
            admin: "订单后台",
            editorBasics: "基础信息",
            editorParties: "买卖双方",
            editorItems: "行项目",
            editorIntake: "导入识别",
            sidebarResult: "合规结论",
            sidebarPreview: "发票预览",
            sidebarActivity: "最近记录",
            quickActions: "检查与操作",
            draftTitle: "发票编辑器",
            draftSubtitle: "先完成发票草稿，再检查规则并打开客户预览页。",
            partiesSubtitle: "集中维护卖方与买方主体信息，便于检查与客户预览页展示。",
            activityEmpty: "登录后可查看当前工作空间的检查快照和已保存预览。",
            sourceLead: "按当前国家和规则版本展示官方来源，便于核对、留档与对外说明。",
            homeTitle: "从发票草稿到规则校验与客户预览，一站完成。",
            homeSubtitle: "适用于 SG / MY / ID / TH 的智能开票与合规平台，支持手工录入、OCR / PDF 导入、规则校验、客户预览页与历史留痕。",
            homePrimary: "开始开票",
            homeSecondary: "查看规则中心",
            homeFeatureTitle: "为什么这样设计",
            homeFeatureA: "手工录入和 OCR / AI 导入都围绕同一份发票草稿，不会分裂成两套流程。",
            homeFeatureB: "规则引擎负责合规判断，AI 只负责识别与整理原始票据。",
            homeFeatureC: "规则来源、检查结果、发票预览和使用记录全部留在同一产品里。",
            pricingTitle: "套餐方案",
            pricingSubtitle: "按团队规模和每月处理量选择方案，适合从试用到正式商用。",
            historyTitle: "使用记录",
            historySubtitle: "按企业空间查看已保存的检查结果和预览版本，并可重新打开继续处理。",
            historyInvoices: "已保存预览",
            historyAudit: "内部日志",
            historyNoInvoices: "还没有保存的预览版本。",
            historyNoAudit: "还没有内部日志。",
            verifyTitle: "邮箱验证",
            verifySubtitle: "为保障账号安全、发信可信度与云端能力使用，请先完成邮箱验证。",
            verifyInput: "6 位验证码",
            verifyAction: "完成验证",
            resendAction: "重新发送验证码",
            verifyHint: "验证码会发送到你的注册邮箱。",
            verifyMailMissing: "当前未配置邮件发送服务，暂时无法发送验证码。",
            verifyDone: "邮箱已验证",
            goHistory: "查看使用记录",
            goPricing: "查看套餐",
            landingSectionRules: "规则中心",
            landingSectionRulesBody: "官方规则来源、版本与阈值统一展示，便于对外解释与内部留档。",
            landingSectionHistory: "记录与留痕",
            landingSectionHistoryBody: "检查结果与预览版本都可以回看，也可以重新打开继续处理。",
            landingSectionWorkspace: "团队与权限",
            landingSectionWorkspaceBody: "按企业空间隔离数据，支持成员、套餐与配额管理。"
          };
        case "ms-MY":
          return {
            home: "Laman utama",
            workbench: "Invois pintar",
            history: "Aktiviti",
            sources: "Pusat peraturan",
            billing: "Bil dan pelan",
            admin: "Pentadbir bil",
            editorBasics: "Maklumat asas",
            editorParties: "Pihak invois",
            editorItems: "Item invois",
            editorIntake: "Import dan ekstrak",
            sidebarResult: "Hasil pematuhan",
            sidebarPreview: "Pratonton invois",
            sidebarActivity: "Rekod terkini",
            quickActions: "Semakan dan tindakan",
            draftTitle: "Editor invois",
            draftSubtitle: "Lengkapkan draf invois di sini, kemudian semak peraturan atau buka halaman pratonton pelanggan.",
            partiesSubtitle: "Kekalkan maklumat penjual dan pembeli supaya semakan dan halaman pratonton pelanggan kekal jelas.",
            activityEmpty: "Log masuk untuk melihat semakan tersimpan dan pratonton yang disimpan bagi ruang kerja ini.",
            sourceLead: "Sumber rasmi dipaparkan mengikut negara dan versi peraturan semasa untuk semakan serta rujukan.",
            homeTitle: "Daripada draf invois ke semakan peraturan dan pratonton pelanggan, semuanya di satu tempat.",
            homeSubtitle: "Platform invois pintar untuk SG / MY / ID / TH dengan input manual, import OCR / PDF, semakan peraturan, halaman pratonton pelanggan, dan rekod sejarah.",
            homePrimary: "Mula invois",
            homeSecondary: "Lihat pusat peraturan",
            homeFeatureTitle: "Mengapa aliran ini jelas",
            homeFeatureA: "Input manual dan import OCR / AI berkongsi draf invois yang sama, jadi proses tidak berpecah.",
            homeFeatureB: "Enjin peraturan membuat keputusan pematuhan, manakala AI hanya mempercepatkan pengekstrakan dokumen.",
            homeFeatureC: "Sumber rasmi, hasil semakan, pratonton invois, dan aktiviti semuanya berada dalam satu produk.",
            pricingTitle: "Pelan harga",
            pricingSubtitle: "Pilih pelan mengikut saiz pasukan dan jumlah operasi bulanan anda.",
            historyTitle: "Aktiviti",
            historySubtitle: "Lihat semakan tersimpan dan versi pratonton mengikut ruang kerja, kemudian buka semula bila perlu.",
            historyInvoices: "Pratonton tersimpan",
            historyAudit: "Log dalaman",
            historyNoInvoices: "Belum ada pratonton tersimpan.",
            historyNoAudit: "Belum ada log dalaman.",
            verifyTitle: "Pengesahan e-mel",
            verifySubtitle: "Untuk keselamatan akaun, kebolehpercayaan e-mel, dan penggunaan keupayaan awan, sahkan e-mel anda dahulu.",
            verifyInput: "Kod 6 digit",
            verifyAction: "Sahkan e-mel",
            resendAction: "Hantar semula kod",
            verifyHint: "Kod akan dihantar ke e-mel pendaftaran anda.",
            verifyMailMissing: "Saluran e-mel belum dikonfigurasi, jadi kod belum boleh dihantar.",
            verifyDone: "E-mel telah disahkan",
            goHistory: "Lihat aktiviti",
            goPricing: "Lihat pelan",
            landingSectionRules: "Pusat peraturan",
            landingSectionRulesBody: "Sumber rasmi, versi dan ambang dipusatkan untuk rujukan pelanggan dan dokumentasi dalaman.",
            landingSectionHistory: "Rekod dan jejak",
            landingSectionHistoryBody: "Semakan tersimpan dan versi pratonton kekal mudah dirujuk semula.",
            landingSectionWorkspace: "Pasukan dan akses",
            landingSectionWorkspaceBody: "Data diasingkan mengikut ruang kerja, dengan sokongan ahli, pelan dan kuota."
          };
        case "id-ID":
          return {
            home: "Beranda",
            workbench: "Invoice pintar",
            history: "Aktivitas",
            sources: "Pusat aturan",
            billing: "Tagihan & paket",
            admin: "Admin tagihan",
            editorBasics: "Informasi dasar",
            editorParties: "Pihak invoice",
            editorItems: "Item invoice",
            editorIntake: "Impor dan ekstraksi",
            sidebarResult: "Hasil kepatuhan",
            sidebarPreview: "Pratinjau invoice",
            sidebarActivity: "Catatan terbaru",
            quickActions: "Pemeriksaan dan tindakan",
            draftTitle: "Editor invoice",
            draftSubtitle: "Lengkapi draft invoice di sini, lalu periksa aturan atau buka halaman pratinjau pelanggan.",
            partiesSubtitle: "Rapikan data penjual dan pembeli agar pemeriksaan dan halaman pratinjau pelanggan lebih jelas.",
            activityEmpty: "Masuk untuk melihat snapshot pemeriksaan dan pratinjau tersimpan di workspace ini.",
            sourceLead: "Sumber resmi ditampilkan menurut negara dan versi aturan saat ini untuk peninjauan serta dokumentasi.",
            homeTitle: "Dari draft invoice ke pemeriksaan aturan dan pratinjau pelanggan, semuanya dalam satu tempat.",
            homeSubtitle: "Platform invoice pintar untuk SG / MY / ID / TH dengan input manual, impor OCR / PDF, pemeriksaan aturan, halaman pratinjau pelanggan, dan riwayat lengkap.",
            homePrimary: "Mulai invoice",
            homeSecondary: "Lihat pusat aturan",
            homeFeatureTitle: "Mengapa alurnya jelas",
            homeFeatureA: "Input manual dan impor OCR / AI berbagi draft invoice yang sama sehingga alur tidak terpecah.",
            homeFeatureB: "Mesin aturan yang menentukan hasil kepatuhan, sementara AI hanya mempercepat ekstraksi dokumen.",
            homeFeatureC: "Sumber resmi, hasil pemeriksaan, pratinjau invoice, dan aktivitas tersimpan dalam satu produk.",
            pricingTitle: "Paket harga",
            pricingSubtitle: "Pilih paket berdasarkan ukuran tim dan volume operasi bulanan.",
            historyTitle: "Aktivitas",
            historySubtitle: "Tinjau pemeriksaan tersimpan dan versi pratinjau per workspace, lalu buka kembali saat dibutuhkan.",
            historyInvoices: "Pratinjau tersimpan",
            historyAudit: "Log internal",
            historyNoInvoices: "Belum ada pratinjau yang disimpan.",
            historyNoAudit: "Belum ada log internal.",
            verifyTitle: "Verifikasi email",
            verifySubtitle: "Untuk keamanan akun, keandalan email, dan penggunaan layanan cloud, verifikasi email Anda terlebih dahulu.",
            verifyInput: "Kode 6 digit",
            verifyAction: "Selesaikan verifikasi",
            resendAction: "Kirim ulang kode",
            verifyHint: "Kode akan dikirim ke email pendaftaran Anda.",
            verifyMailMissing: "Saluran email belum dikonfigurasi, jadi kode belum bisa dikirim.",
            verifyDone: "Email sudah terverifikasi",
            goHistory: "Lihat aktivitas",
            goPricing: "Lihat paket",
            landingSectionRules: "Pusat aturan",
            landingSectionRulesBody: "Sumber resmi, versi, dan ambang dipusatkan untuk penjelasan ke pelanggan dan dokumentasi internal.",
            landingSectionHistory: "Catatan dan jejak",
            landingSectionHistoryBody: "Pemeriksaan tersimpan dan versi pratinjau tetap mudah ditinjau ulang.",
            landingSectionWorkspace: "Tim dan akses",
            landingSectionWorkspaceBody: "Data dipisahkan per workspace dengan dukungan anggota, paket, dan kuota."
          };
        case "th-TH":
          return {
            home: "หน้าแรก",
            workbench: "สตูดิโอใบแจ้งหนี้",
            history: "การใช้งาน",
            sources: "ศูนย์กฎ",
            billing: "การเรียกเก็บเงิน",
            admin: "หลังบ้านคำสั่งซื้อ",
            editorBasics: "ข้อมูลพื้นฐาน",
            editorParties: "ผู้ซื้อและผู้ขาย",
            editorItems: "รายการ",
            editorIntake: "นำเข้าและแยกข้อมูล",
            sidebarResult: "ผลการตรวจสอบ",
            sidebarPreview: "ตัวอย่างเอกสาร",
            sidebarActivity: "ล่าสุด",
            quickActions: "ตรวจและดำเนินการ",
            draftTitle: "ตัวแก้ไขใบแจ้งหนี้",
            draftSubtitle: "จัดทำร่างเอกสารที่นี่ แล้วค่อยตรวจตามกฎหรือเปิดหน้าพรีวิวสำหรับลูกค้า",
            partiesSubtitle: "ดูแลข้อมูลผู้ขายและผู้ซื้อให้ครบ เพื่อให้การตรวจและหน้าพรีวิวสำหรับลูกค้าชัดเจนยิ่งขึ้น",
            activityEmpty: "เข้าสู่ระบบเพื่อดูผลการตรวจที่บันทึกไว้และรายการพรีวิวของเวิร์กสเปซนี้",
            sourceLead: "จัดกลุ่มแหล่งอ้างอิงทางการตามประเทศและเวอร์ชันกฎปัจจุบัน เพื่อใช้ตรวจทานและเก็บอ้างอิง",
            homeTitle: "ตั้งแต่ร่างเอกสารจนถึงการตรวจตามกฎและหน้าพรีวิวสำหรับลูกค้า จบได้ในที่เดียว",
            homeSubtitle: "แพลตฟอร์มออกเอกสารและตรวจสอบสำหรับ SG / MY / ID / TH รองรับการกรอกเอง การนำเข้า OCR / PDF การตรวจตามกฎ หน้าพรีวิวสำหรับลูกค้า และประวัติย้อนหลัง",
            homePrimary: "เริ่มทำเอกสาร",
            homeSecondary: "ดูศูนย์กฎ",
            homeFeatureTitle: "เหตุผลที่เวิร์กโฟลว์นี้ชัดเจน",
            homeFeatureA: "การกรอกเองและการนำเข้า OCR / AI ใช้ร่างเอกสารชุดเดียวกัน จึงไม่แยกเป็นสองกระบวนการ",
            homeFeatureB: "ระบบกฎเป็นผู้ตัดสินผลการปฏิบัติตาม ส่วน AI ช่วยดึงข้อมูลเอกสารให้เร็วขึ้น",
            homeFeatureC: "แหล่งอ้างอิงทางการ ผลการตรวจสอบ หน้า preview และประวัติการใช้งานถูกรวมไว้ในผลิตภัณฑ์เดียว",
            pricingTitle: "แพ็กเกจ",
            pricingSubtitle: "เลือกแพ็กเกจตามขนาดทีมและปริมาณงานรายเดือน",
            historyTitle: "การใช้งาน",
            historySubtitle: "ดูผลการตรวจที่บันทึกไว้และเวอร์ชันพรีวิวของแต่ละเวิร์กสเปซ แล้วกลับมาเปิดต่อได้ทันที",
            historyInvoices: "พรีวิวที่บันทึกไว้",
            historyAudit: "บันทึกภายใน",
            historyNoInvoices: "ยังไม่มีพรีวิวที่บันทึกไว้",
            historyNoAudit: "ยังไม่มีบันทึกภายใน",
            verifyTitle: "ยืนยันอีเมล",
            verifySubtitle: "เพื่อความปลอดภัยของบัญชี ความน่าเชื่อถือของอีเมล และการใช้บริการคลาวด์ กรุณายืนยันอีเมลก่อน",
            verifyInput: "รหัส 6 หลัก",
            verifyAction: "ยืนยันให้เสร็จ",
            resendAction: "ส่งรหัสอีกครั้ง",
            verifyHint: "ระบบจะส่งรหัสไปยังอีเมลที่ใช้สมัคร",
            verifyMailMissing: "ยังไม่ได้ตั้งค่าช่องทางอีเมล จึงยังส่งรหัสไม่ได้",
            verifyDone: "ยืนยันอีเมลแล้ว",
            goHistory: "ดูประวัติการใช้งาน",
            goPricing: "ดูแพ็กเกจ",
            landingSectionRules: "ศูนย์กฎ",
            landingSectionRulesBody: "รวมแหล่งอ้างอิง เวอร์ชัน และเกณฑ์ทางการไว้ในที่เดียวเพื่อใช้อธิบายกับลูกค้าและเก็บหลักฐานภายใน",
            landingSectionHistory: "ประวัติและร่องรอย",
            landingSectionHistoryBody: "ผลการตรวจและเวอร์ชันพรีวิวที่บันทึกไว้ยังสามารถย้อนกลับมาเปิดต่อได้ง่าย",
            landingSectionWorkspace: "ทีมและสิทธิ์เข้าถึง",
            landingSectionWorkspaceBody: "ข้อมูลแยกตาม workspace พร้อมรองรับสมาชิก แพ็กเกจ และโควตา"
          };
        default:
          return {
            home: "Home",
            workbench: "Invoice Studio",
            history: "Activity",
            sources: "Rule Center",
            billing: "Billing",
            admin: "Admin",
            editorBasics: "Basics",
            editorParties: "Parties",
            editorItems: "Items",
            editorIntake: "Import and extract",
            sidebarResult: "Compliance",
            sidebarPreview: "Invoice preview",
            sidebarActivity: "Recent activity",
            quickActions: "Checks and actions",
            draftTitle: "Invoice editor",
            draftSubtitle: "Complete the invoice draft here, then run rule checks or open the customer preview page.",
            partiesSubtitle: "Keep seller and buyer details aligned so validation and the customer preview stay clear.",
            activityEmpty: "Sign in to review saved check snapshots and preview records for this workspace.",
            sourceLead: "Official sources are grouped by the current country and rulebook version for review and documentation.",
            homeTitle: "From invoice draft to rule check and customer preview, all in one place.",
            homeSubtitle: "A commercial invoicing and compliance platform for SG / MY / ID / TH with manual entry, OCR / PDF import, rule validation, customer preview pages, and full history.",
            homePrimary: "Start invoicing",
            homeSecondary: "Open rule center",
            homeFeatureTitle: "Why the workflow stays clear",
            homeFeatureA: "Manual entry and OCR / AI import both feed the same invoice draft, so your team does not switch between disconnected flows.",
            homeFeatureB: "The rule engine decides compliance. AI only accelerates document extraction and structuring.",
            homeFeatureC: "Official sources, check results, invoice previews, and activity history all live in one product.",
            pricingTitle: "Pricing plans",
            pricingSubtitle: "Choose a plan based on team size and monthly operational volume.",
            historyTitle: "Activity",
            historySubtitle: "Review saved checks and preview versions by workspace, then reopen what you need.",
            historyInvoices: "Saved previews",
            historyAudit: "Internal log",
            historyNoInvoices: "No saved preview versions yet.",
            historyNoAudit: "No internal log entries.",
            verifyTitle: "Email verification",
            verifySubtitle: "To protect account security, email reliability, and cloud usage, please verify your email before using server-side features.",
            verifyInput: "6-digit code",
            verifyAction: "Complete verification",
            resendAction: "Resend code",
            verifyHint: "The code will be sent to your registration email.",
            verifyMailMissing: "The email channel is not configured yet, so no code can be delivered right now.",
            verifyDone: "Email verified",
            goHistory: "View activity",
            goPricing: "View plans",
            landingSectionRules: "Rule center",
            landingSectionRulesBody: "Official sources, versions, and thresholds are centralized for customer explanation and internal documentation.",
            landingSectionHistory: "Records and traceability",
            landingSectionHistoryBody: "Saved checks and preview versions stay traceable and easy to reopen.",
            landingSectionWorkspace: "Teams and access",
            landingSectionWorkspaceBody: "Data stays isolated by workspace, with support for members, plans, and usage quotas."
          };
      }
    });

    const buyerTypeHint = computed(() => {
      if (country.value === "SG") {
        if (draft.buyerType === "final_consumer") {
          return localeText({
            en: "Retail or end-customer mode. If the total stays within SGD 1,000, the simplified tax-invoice checks will apply.",
            "zh-CN": "用于零售或终端消费者场景。若总额不超过 SGD 1,000，会按简化税务发票规则检查。",
            "ms-MY": "Untuk jualan runcit atau pengguna akhir. Jika jumlah tidak melebihi SGD 1,000, semakan invois cukai ringkas akan digunakan.",
            "id-ID": "Untuk penjualan ritel atau konsumen akhir. Jika total tidak melebihi SGD 1.000, pemeriksaan faktur pajak sederhana akan diterapkan."
          });
        }
        if (draft.buyerType === "individual") {
          return localeText({
            en: "Use this for a named personal customer when it is not a retail simplified-invoice case.",
            "zh-CN": "适用于具名个人客户，但不属于零售简化发票场景时使用。",
            "ms-MY": "Gunakan untuk pelanggan individu bernama apabila ia bukan senario invois ringkas runcit.",
            "id-ID": "Gunakan untuk pelanggan pribadi bernama jika bukan skenario faktur sederhana ritel."
          });
        }
        if (draft.buyerType === "government") {
          return localeText({
            en: "Use this for ministries, agencies, and other public institutions.",
            "zh-CN": "用于政府部门、机构或其他公共单位采购。",
            "ms-MY": "Gunakan untuk kementerian, agensi, dan institusi awam.",
            "id-ID": "Gunakan untuk kementerian, lembaga, dan institusi publik."
          });
        }
        return localeText({
          en: "Default for Singapore. Business buyers fit the standard GST tax-invoice flow best.",
          "zh-CN": "新加坡默认选企业买方，最符合标准 GST 税务发票流程。",
          "ms-MY": "Lalai untuk Singapura. Pembeli perniagaan paling sesuai dengan aliran invois cukai GST standard.",
          "id-ID": "Default untuk Singapura. Pembeli bisnis paling sesuai dengan alur faktur pajak GST standar."
        });
      }

      if (country.value === "MY") {
        if (draft.buyerType === "individual") {
          return localeText({
            en: "Use this for named personal buyers. Keep contact and identity details as complete as possible for e-Invoice review.",
            "zh-CN": "用于具名个人买方，建议尽量补全联系方式与身份信息，便于 e-Invoice 复核。",
            "ms-MY": "Gunakan untuk pembeli individu bernama. Lengkapkan maklumat hubungan dan identiti sejauh mungkin untuk semakan e-Invois.",
            "id-ID": "Gunakan untuk pembeli pribadi bernama. Lengkapi detail kontak dan identitas sebanyak mungkin untuk peninjauan e-Invoice."
          });
        }
        if (draft.buyerType === "government") {
          return localeText({
            en: "Use this for ministries, agencies, and statutory bodies buying on behalf of the public sector.",
            "zh-CN": "用于政府部门、机构或法定机构采购场景。",
            "ms-MY": "Gunakan untuk kementerian, agensi, dan badan berkanun sektor awam.",
            "id-ID": "Gunakan untuk kementerian, lembaga, dan badan hukum sektor publik."
          });
        }
        if (draft.buyerType === "final_consumer") {
          return localeText({
            en: "Use only when you truly do not need a named buyer profile. Business or Individual is usually safer for Malaysia.",
            "zh-CN": "仅在确实不需要具名买方资料时使用；对马来西亚场景来说，企业或个人通常更稳妥。",
            "ms-MY": "Gunakan hanya apabila anda benar-benar tidak memerlukan profil pembeli bernama. Perniagaan atau Individu biasanya lebih selamat untuk Malaysia.",
            "id-ID": "Gunakan hanya jika Anda benar-benar tidak membutuhkan profil pembeli bernama. Bisnis atau Individu biasanya lebih aman untuk Malaysia."
          });
        }
        return localeText({
          en: "Default for Malaysia. Business buyers fit the common e-Invoice and SST identity structure best.",
          "zh-CN": "马来西亚默认选企业买方，更贴合常见 e-Invoice 与 SST 资料结构。",
          "ms-MY": "Lalai untuk Malaysia. Pembeli perniagaan paling sesuai dengan struktur identiti e-Invois dan SST yang lazim.",
          "id-ID": "Default untuk Malaysia. Pembeli bisnis paling sesuai dengan struktur identitas e-Invoice dan SST yang umum."
        });
      }

      if (country.value === "TH") {
        if (draft.buyerType === "final_consumer") {
          return localeText({
            en: "Use this for retail customers when an abbreviated Thai tax invoice or consumer-facing receipt flow is appropriate.",
            "zh-CN": "适用于零售终端消费者，尤其是简式税务发票或面向消费者收据场景。",
            "ms-MY": "Gunakan untuk pelanggan runcit apabila invois cukai ringkas Thailand atau resit pengguna lebih sesuai.",
            "id-ID": "Gunakan untuk pelanggan ritel saat invoice pajak ringkas Thailand atau alur struk konsumen lebih tepat.",
            "th-TH": "ใช้สำหรับลูกค้าปลีกหรือผู้บริโภคปลายทาง โดยเฉพาะกรณีใบกำกับภาษีอย่างย่อหรือเอกสารหน้าร้าน"
          });
        }
        if (draft.buyerType === "government") {
          return localeText({
            en: "Use this for ministries, public agencies, universities, and other state bodies that need a fuller buyer profile.",
            "zh-CN": "适用于政府部门、公共机构、高校或其他国家机构采购，通常需要更完整的买方资料。",
            "ms-MY": "Gunakan untuk kementerian, agensi awam, universiti, dan badan kerajaan lain yang memerlukan profil pembeli lebih lengkap.",
            "id-ID": "Gunakan untuk kementerian, lembaga publik, universitas, dan badan negara lain yang membutuhkan profil pembeli lebih lengkap.",
            "th-TH": "ใช้สำหรับหน่วยงานรัฐ มหาวิทยาลัย หรือองค์กรภาครัฐที่ต้องมีข้อมูลผู้ซื้อครบถ้วนมากขึ้น"
          });
        }
        if (draft.buyerType === "individual") {
          return localeText({
            en: "Use this for a named personal buyer when the document still needs a fuller Thai buyer profile.",
            "zh-CN": "适用于具名个人买方，但仍需要较完整的泰国买方资料时使用。",
            "ms-MY": "Gunakan untuk pembeli individu bernama apabila dokumen masih memerlukan profil pembeli Thailand yang lebih lengkap.",
            "id-ID": "Gunakan untuk pembeli pribadi bernama saat dokumen masih memerlukan profil pembeli Thailand yang lebih lengkap.",
            "th-TH": "ใช้สำหรับผู้ซื้อบุคคลธรรมดาที่ระบุชื่อชัดเจน และเอกสารยังต้องแสดงข้อมูลผู้ซื้อค่อนข้างครบ"
          });
        }
        return localeText({
          en: "Default for Thailand. Business buyers best fit the standard VAT tax-invoice structure.",
          "zh-CN": "泰国默认选企业买方，最贴合标准 VAT 税务发票结构。",
          "ms-MY": "Lalai untuk Thailand. Pembeli perniagaan paling sesuai dengan struktur invois cukai VAT standard.",
          "id-ID": "Default untuk Thailand. Pembeli bisnis paling sesuai dengan struktur invoice pajak VAT standar.",
          "th-TH": "ค่าเริ่มต้นของไทยเหมาะกับผู้ซื้อแบบนิติบุคคล ซึ่งสอดคล้องกับโครงสร้างใบกำกับภาษี VAT มาตรฐานที่สุด"
        });
      }

      if (draft.buyerType === "final_consumer") {
        return localeText({
          en: "Retail end-customer mode. This relaxes buyer identity expectations for Indonesia retail tax invoices.",
          "zh-CN": "用于零售终端消费者场景，系统会按印尼零售税务发票逻辑放宽买方身份字段。",
          "ms-MY": "Mod pengguna akhir runcit. Ini melonggarkan keperluan identiti pembeli untuk faktur cukai runcit Indonesia.",
          "id-ID": "Mode konsumen akhir ritel. Ini melonggarkan kebutuhan identitas pembeli untuk faktur pajak ritel Indonesia."
        });
      }
      if (draft.buyerType === "government") {
        return localeText({
          en: "Use this for public-sector procurement while keeping the fuller buyer identity structure.",
          "zh-CN": "用于政府采购场景，同时保留较完整的买方身份结构。",
          "ms-MY": "Gunakan untuk perolehan sektor awam sambil mengekalkan struktur identiti pembeli yang lebih lengkap.",
          "id-ID": "Gunakan untuk pengadaan sektor publik sambil mempertahankan struktur identitas pembeli yang lebih lengkap."
        });
      }
      if (draft.buyerType === "individual") {
        return localeText({
          en: "Use this for a named personal buyer when it is not a retail final-consumer invoice.",
          "zh-CN": "用于具名个人买方，但不属于零售终端消费者发票时使用。",
          "ms-MY": "Gunakan untuk pembeli individu bernama apabila ia bukan invois pengguna akhir runcit.",
          "id-ID": "Gunakan untuk pembeli pribadi bernama jika ini bukan invoice konsumen akhir ritel."
        });
      }
      return localeText({
        en: "Default for Indonesia. Business buyers keep the fuller PKP buyer identity flow.",
        "zh-CN": "印尼默认选企业买方，会保留更完整的 PKP 买方身份字段流程。",
        "ms-MY": "Lalai untuk Indonesia. Pembeli perniagaan mengekalkan aliran identiti pembeli PKP yang lebih lengkap.",
        "id-ID": "Default untuk Indonesia. Pembeli bisnis mempertahankan alur identitas pembeli PKP yang lebih lengkap."
      });
    });

    const supplyNatureHint = computed(() => {
      if (country.value === "SG") {
        if (draft.supplyNature === "export_services") {
          return localeText({
            en: "Use only for overseas service customers when you can retain zero-rate support on file.",
            "zh-CN": "仅在面向境外服务客户且能够留存零税率支持材料时使用。",
            "ms-MY": "Gunakan hanya untuk pelanggan perkhidmatan luar negara apabila anda boleh menyimpan bukti kadar sifar.",
            "id-ID": "Gunakan hanya untuk pelanggan jasa luar negeri jika Anda dapat menyimpan bukti pendukung tarif 0%."
          });
        }
        if (draft.supplyNature === "export_goods") {
          return localeText({
            en: "Use for exported goods and keep export documents together with the invoice file.",
            "zh-CN": "用于货物出口场景，并建议与发票一同留存出口单证。",
            "ms-MY": "Gunakan untuk eksport barangan dan simpan dokumen eksport bersama fail invois.",
            "id-ID": "Gunakan untuk ekspor barang dan simpan dokumen ekspor bersama file invoice."
          });
        }
        if (draft.supplyNature === "exempt") {
          return localeText({
            en: "Use only when the supply is genuinely exempt or zero-rated under the rulebook.",
            "zh-CN": "仅在该交易确实属于免税或零税率范围时使用。",
            "ms-MY": "Gunakan hanya apabila bekalan benar-benar dikecualikan atau berkadar sifar di bawah peraturan.",
            "id-ID": "Gunakan hanya jika transaksi benar-benar termasuk dikecualikan atau tarif 0% menurut aturan."
          });
        }
        if (draft.supplyNature === "reverse_charge") {
          return localeText({
            en: "Use only for imported-service self-accounting cases, not for a normal customer invoice.",
            "zh-CN": "仅用于进口服务自缴税场景，不适用于常规对客发票。",
            "ms-MY": "Gunakan hanya untuk kes perkhidmatan import yang diakaunkan sendiri, bukan invois pelanggan biasa.",
            "id-ID": "Gunakan hanya untuk kasus self-accounting jasa impor, bukan invoice pelanggan biasa."
          });
        }
        return localeText({
          en: "Default for Singapore. Domestic taxable supplies are usually checked against the 9% GST profile.",
          "zh-CN": "新加坡默认是境内交易，通常会按 9% GST 口径检查。",
          "ms-MY": "Lalai untuk Singapura. Bekalan domestik bercukai biasanya disemak terhadap profil GST 9%.",
          "id-ID": "Default untuk Singapura. Penyerahan domestik kena pajak biasanya diperiksa dengan profil GST 9%."
        });
      }

      if (country.value === "MY") {
        if (draft.supplyNature === "reverse_charge") {
          return localeText({
            en: "Use for imported taxable service or self-accounting cases, not for a standard local customer invoice.",
            "zh-CN": "用于进口应税服务或自缴税场景，不适用于普通本地客户发票。",
            "ms-MY": "Gunakan untuk perkhidmatan bercukai import atau kes pengiraan sendiri, bukan invois pelanggan tempatan biasa.",
            "id-ID": "Gunakan untuk jasa kena pajak impor atau kasus self-accounting, bukan untuk invoice pelanggan lokal biasa."
          });
        }
        if (draft.supplyNature === "export_services" || draft.supplyNature === "export_goods") {
          return localeText({
            en: "Use for cross-border billing and keep export or overseas-customer evidence together with the draft.",
            "zh-CN": "用于跨境服务或货物出口，并建议随草稿留存出口或境外客户证明。",
            "ms-MY": "Gunakan untuk pengebilan rentas sempadan dan simpan bukti eksport atau pelanggan luar negara bersama draf.",
            "id-ID": "Gunakan untuk penagihan lintas batas dan simpan bukti ekspor atau pelanggan luar negeri bersama draft."
          });
        }
        if (draft.supplyNature === "exempt") {
          return localeText({
            en: "Use only when the supply is specifically exempt or outside the SST charge.",
            "zh-CN": "仅在该交易明确属于免税或不在 SST 计税范围时使用。",
            "ms-MY": "Gunakan hanya apabila bekalan dikecualikan secara khusus atau di luar caj SST.",
            "id-ID": "Gunakan hanya jika transaksi secara khusus dikecualikan atau di luar pungutan SST."
          });
        }
        return localeText({
          en: "Default for Malaysia. Domestic is the safest starting point for local SST and local e-Invoice billing.",
          "zh-CN": "马来西亚默认是境内交易，最适合作为本地 SST 或本地 e-Invoice 的起点。",
          "ms-MY": "Lalai untuk Malaysia. Domestik ialah titik permulaan paling selamat untuk SST tempatan dan e-Invois tempatan.",
          "id-ID": "Default untuk Malaysia. Domestik adalah titik awal paling aman untuk SST lokal dan e-Invoice lokal."
        });
      }

      if (country.value === "TH") {
        if (draft.supplyNature === "export_services") {
          return localeText({
            en: "Use this for exported services. Keep the contract and overseas-customer support ready for Thai VAT review.",
            "zh-CN": "用于服务出口，并建议准备合同与境外客户证明，以备泰国 VAT 复核。",
            "ms-MY": "Gunakan untuk eksport perkhidmatan dan sediakan kontrak serta bukti pelanggan luar negara untuk semakan VAT Thailand.",
            "id-ID": "Gunakan untuk ekspor jasa dan siapkan kontrak serta bukti pelanggan luar negeri untuk peninjauan VAT Thailand.",
            "th-TH": "ใช้สำหรับการให้บริการแก่ต่างประเทศ และควรเตรียมสัญญากับหลักฐานลูกค้าต่างประเทศไว้สำหรับการทบทวน VAT"
          });
        }
        if (draft.supplyNature === "export_goods") {
          return localeText({
            en: "Use this for exported goods and keep customs or shipment evidence with the invoice record.",
            "zh-CN": "用于货物出口，并建议将报关或运输证明与发票记录一并保存。",
            "ms-MY": "Gunakan untuk eksport barangan dan simpan dokumen kastam atau penghantaran bersama rekod invois.",
            "id-ID": "Gunakan untuk ekspor barang dan simpan bukti bea cukai atau pengiriman bersama catatan invoice.",
            "th-TH": "ใช้สำหรับการส่งออกสินค้า และควรเก็บเอกสารศุลกากรหรือหลักฐานการขนส่งไว้พร้อมเอกสาร"
          });
        }
        if (draft.supplyNature === "exempt") {
          return localeText({
            en: "Use only when the supply is genuinely exempt or outside the Thai VAT charging scope.",
            "zh-CN": "仅在交易确属免税或不在泰国 VAT 征税范围时使用。",
            "ms-MY": "Gunakan hanya apabila bekalan benar-benar dikecualikan atau di luar skop VAT Thailand.",
            "id-ID": "Gunakan hanya jika transaksi benar-benar dikecualikan atau di luar lingkup pemungutan VAT Thailand.",
            "th-TH": "ใช้เฉพาะเมื่อรายการนั้นได้รับยกเว้นจริง หรืออยู่นอกขอบเขตการเรียกเก็บ VAT ของไทย"
          });
        }
        if (draft.supplyNature === "reverse_charge") {
          return localeText({
            en: "Use only for special self-accounting or imported-service situations, not for an ordinary Thai customer invoice.",
            "zh-CN": "仅用于特殊自缴税或进口服务情形，不适用于普通泰国客户发票。",
            "ms-MY": "Gunakan hanya untuk situasi pengiraan sendiri khas atau perkhidmatan import, bukan invois pelanggan Thailand biasa.",
            "id-ID": "Gunakan hanya untuk situasi self-accounting khusus atau jasa impor, bukan invoice pelanggan Thailand biasa.",
            "th-TH": "ใช้เฉพาะกรณีพิเศษ เช่น ภาระภาษีแบบคำนวณเองหรือบริการนำเข้า ไม่ใช่ใบกำกับปกติสำหรับลูกค้าในไทย"
          });
        }
        return localeText({
          en: "Default for Thailand. Domestic taxable supplies normally follow the 7% VAT path.",
          "zh-CN": "泰国默认是境内应税交易，通常按 7% VAT 路径处理。",
          "ms-MY": "Lalai untuk Thailand. Bekalan domestik bercukai biasanya mengikut laluan VAT 7%.",
          "id-ID": "Default untuk Thailand. Penyerahan domestik kena pajak biasanya mengikuti jalur VAT 7%.",
          "th-TH": "ค่าเริ่มต้นของไทยคือธุรกรรมในประเทศ ซึ่งโดยทั่วไปจะใช้แนวทาง VAT 7%"
        });
      }

      if (draft.supplyNature === "export_services") {
        return localeText({
          en: "Use for exported services and keep the contract or overseas-customer support ready for review.",
          "zh-CN": "用于服务出口，并建议提前准备合同或境外客户证明材料。",
          "ms-MY": "Gunakan untuk eksport perkhidmatan dan sediakan kontrak atau bukti pelanggan luar negara untuk semakan.",
          "id-ID": "Gunakan untuk ekspor jasa dan siapkan kontrak atau bukti pelanggan luar negeri untuk peninjauan."
        });
      }
      if (draft.supplyNature === "export_goods") {
        return localeText({
          en: "Use for exported goods and keep customs or export support together with the invoice record.",
          "zh-CN": "用于货物出口，并建议与发票记录一并留存报关或出口支持材料。",
          "ms-MY": "Gunakan untuk eksport barangan dan simpan sokongan kastam atau eksport bersama rekod invois.",
          "id-ID": "Gunakan untuk ekspor barang dan simpan dukungan bea cukai atau ekspor bersama catatan invoice."
        });
      }
      if (draft.supplyNature === "exempt") {
        return localeText({
          en: "Use only if the transaction is genuinely exempt or outside the normal VAT scope.",
          "zh-CN": "仅在该交易确实属于免税或不在正常增值税范围时使用。",
          "ms-MY": "Gunakan hanya jika transaksi benar-benar dikecualikan atau di luar skop VAT biasa.",
          "id-ID": "Gunakan hanya jika transaksi benar-benar dikecualikan atau di luar lingkup PPN normal."
        });
      }
      if (draft.supplyNature === "reverse_charge") {
        return localeText({
          en: "Use only for special self-accounting scenarios, not for standard PKP sales invoices.",
          "zh-CN": "仅用于特殊自缴税场景，不适用于标准 PKP 销售发票。",
          "ms-MY": "Gunakan hanya untuk senario pengiraan sendiri khas, bukan invois jualan PKP standard.",
          "id-ID": "Gunakan hanya untuk skenario self-accounting khusus, bukan invoice penjualan PKP standar."
        });
      }
      return localeText({
        en: "Default for Indonesia. Domestic keeps the usual PKP invoice checks and local tax-rate guidance.",
        "zh-CN": "印尼默认是境内交易，会保留常规 PKP 发票检查与本地税率提示。",
        "ms-MY": "Lalai untuk Indonesia. Domestik mengekalkan semakan invois PKP biasa dan panduan kadar cukai tempatan.",
        "id-ID": "Default untuk Indonesia. Domestik mempertahankan pemeriksaan invoice PKP biasa dan panduan tarif pajak lokal."
      });
    });

    const businessActivityHint = computed(() => {
      if (country.value === "SG") {
        if (draft.businessActivity === "it_services") {
          return localeText({
            en: "Use this for software, SaaS, development, cloud, or managed technology work.",
            "zh-CN": "适用于软件、SaaS、开发、云服务或托管技术服务。",
            "ms-MY": "Gunakan untuk perisian, SaaS, pembangunan, awan, atau kerja teknologi terurus.",
            "id-ID": "Gunakan untuk perangkat lunak, SaaS, pengembangan, cloud, atau layanan teknologi terkelola."
          });
        }
        if (draft.businessActivity === "goods" || draft.businessActivity === "retail") {
          return localeText({
            en: "Use this when the invoice mainly covers physical goods or consumer checkout flows.",
            "zh-CN": "适用于以实物商品销售或零售收银为主的发票场景。",
            "ms-MY": "Gunakan apabila invois terutamanya melibatkan barangan fizikal atau aliran pembayaran runcit.",
            "id-ID": "Gunakan saat invoice terutama mencakup penjualan barang fisik atau alur checkout ritel."
          });
        }
        return localeText({
          en: "Default for Singapore. Professional services is a safe starter profile for consulting, agency, and service-led invoices.",
          "zh-CN": "新加坡默认选专业服务，适合作为咨询、代理或服务型发票的起点。",
          "ms-MY": "Lalai untuk Singapura. Perkhidmatan profesional ialah profil permulaan yang selamat untuk invois konsultasi, agensi dan berasaskan perkhidmatan.",
          "id-ID": "Default untuk Singapura. Jasa profesional adalah profil awal yang aman untuk invoice konsultasi, agensi, dan berbasis layanan."
        });
      }

      if (country.value === "MY") {
        if (draft.businessActivity === "food_and_beverage" || draft.businessActivity === "logistics") {
          return localeText({
            en: "In the current Malaysia rule pack, these activities usually point to the 6% SST service rate.",
            "zh-CN": "在当前马来西亚规则包中，这两类业务通常对应 6% 的 SST 服务税率提示。",
            "ms-MY": "Dalam pakej peraturan Malaysia semasa, aktiviti ini biasanya menunjuk kepada kadar SST perkhidmatan 6%.",
            "id-ID": "Dalam paket aturan Malaysia saat ini, aktivitas ini biasanya mengarah ke tarif SST jasa 6%."
          });
        }
        if (draft.businessActivity === "it_services") {
          return localeText({
            en: "Use this for software, cloud, development, and managed tech services when reviewing SST and e-Invoice details.",
            "zh-CN": "适用于软件、云、开发和托管技术服务场景，便于联动 SST 与 e-Invoice 提示。",
            "ms-MY": "Gunakan untuk perisian, awan, pembangunan dan perkhidmatan teknologi terurus apabila menyemak butiran SST dan e-Invois.",
            "id-ID": "Gunakan untuk perangkat lunak, cloud, pengembangan, dan layanan teknologi terkelola saat meninjau detail SST dan e-Invoice."
          });
        }
        return localeText({
          en: "Default for Malaysia. Professional services is the safest starting profile for common SST and e-Invoice service workflows.",
          "zh-CN": "马来西亚默认选专业服务，最适合作为常见 SST 与 e-Invoice 服务流程的起点。",
          "ms-MY": "Lalai untuk Malaysia. Perkhidmatan profesional ialah profil permulaan paling selamat untuk aliran SST dan e-Invois yang lazim.",
          "id-ID": "Default untuk Malaysia. Jasa profesional adalah profil awal paling aman untuk alur SST dan e-Invoice yang umum."
        });
      }

      if (country.value === "TH") {
        if (draft.businessActivity === "goods" || draft.businessActivity === "retail") {
          return localeText({
            en: "Use this for physical-goods sales, storefront checkout, and most abbreviated tax-invoice scenarios in Thailand.",
            "zh-CN": "适用于实物商品销售、门店收银以及大多数泰国简式税务发票场景。",
            "ms-MY": "Gunakan untuk jualan barangan fizikal, kaunter kedai, dan kebanyakan senario invois cukai ringkas Thailand.",
            "id-ID": "Gunakan untuk penjualan barang fisik, checkout toko, dan sebagian besar skenario invoice pajak ringkas Thailand.",
            "th-TH": "ใช้สำหรับการขายสินค้า การชำระเงินหน้าร้าน และกรณีใบกำกับภาษีอย่างย่อของไทยส่วนใหญ่"
          });
        }
        if (draft.businessActivity === "food_and_beverage") {
          return localeText({
            en: "Use this for restaurants, cafes, catering, and similar hospitality invoices.",
            "zh-CN": "适用于餐厅、咖啡店、餐饮服务及类似服务业发票。",
            "ms-MY": "Gunakan untuk restoran, kafe, katering, dan invois hospitaliti yang serupa.",
            "id-ID": "Gunakan untuk restoran, kafe, katering, dan invoice hospitality serupa.",
            "th-TH": "ใช้สำหรับร้านอาหาร คาเฟ่ งานจัดเลี้ยง และเอกสารในธุรกิจบริการลักษณะใกล้เคียงกัน"
          });
        }
        if (draft.businessActivity === "it_services") {
          return localeText({
            en: "Use this for software, SaaS, development, cloud, and managed technology services billed from Thailand.",
            "zh-CN": "适用于从泰国开出的软件、SaaS、开发、云服务和托管技术服务。",
            "ms-MY": "Gunakan untuk perisian, SaaS, pembangunan, awan, dan perkhidmatan teknologi terurus yang dibilkan dari Thailand.",
            "id-ID": "Gunakan untuk perangkat lunak, SaaS, pengembangan, cloud, dan layanan teknologi terkelola yang ditagihkan dari Thailand.",
            "th-TH": "ใช้สำหรับซอฟต์แวร์ SaaS งานพัฒนา คลาวด์ และบริการเทคโนโลยีที่ออกบิลจากประเทศไทย"
          });
        }
        return localeText({
          en: "Default for Thailand. Professional services is a safe starting profile for standard VAT invoicing.",
          "zh-CN": "泰国默认选专业服务，适合作为标准 VAT 发票的起始档案。",
          "ms-MY": "Lalai untuk Thailand. Perkhidmatan profesional ialah profil permulaan yang selamat untuk invois VAT standard.",
          "id-ID": "Default untuk Thailand. Jasa profesional adalah profil awal yang aman untuk invoice VAT standar.",
          "th-TH": "ค่าเริ่มต้นของไทยใช้หมวดบริการวิชาชีพ ซึ่งเหมาะเป็นจุดเริ่มต้นสำหรับใบกำกับภาษี VAT มาตรฐาน"
        });
      }

      if (draft.businessActivity === "luxury_goods") {
        return localeText({
          en: "Use this when the sale falls into luxury-goods treatment. The current Indonesia rule pack will prompt the 12% rate.",
          "zh-CN": "适用于高端商品场景，当前印尼规则包会对应提示 12% 税率。",
          "ms-MY": "Gunakan apabila jualan tergolong dalam layanan barangan mewah. Pakej peraturan Indonesia semasa akan mencadangkan kadar 12%.",
          "id-ID": "Gunakan saat penjualan termasuk perlakuan barang mewah. Paket aturan Indonesia saat ini akan memunculkan tarif 12%."
        });
      }
      if (draft.businessActivity === "professional_services") {
        return localeText({
          en: "Use this for consulting or service-led invoices while keeping the standard PKP invoice structure.",
          "zh-CN": "适用于咨询或服务型发票，同时保留标准 PKP 发票结构。",
          "ms-MY": "Gunakan untuk invois konsultasi atau berasaskan perkhidmatan sambil mengekalkan struktur invois PKP standard.",
          "id-ID": "Gunakan untuk invoice konsultasi atau berbasis layanan sambil mempertahankan struktur invoice PKP standar."
        });
      }
      return localeText({
        en: "Default for Indonesia. Goods is the clearest starting point for the common PKP invoice flow at 11%.",
        "zh-CN": "印尼默认选货物销售，最适合作为常见 11% PKP 发票流程的起点。",
        "ms-MY": "Lalai untuk Indonesia. Barangan ialah titik permulaan paling jelas untuk aliran invois PKP biasa pada 11%.",
        "id-ID": "Default untuk Indonesia. Barang adalah titik awal paling jelas untuk alur invoice PKP umum pada 11%."
      });
    });

    const setCountryExtension = (key: string, value: string | number | boolean | null | undefined) => {
      draft.countryExtensions = {
        ...(draft.countryExtensions ?? {}),
        [key]: value
      };
    };

    const getCountryExtensionText = (key: string): string => {
      const value = draft.countryExtensions?.[key];
      return typeof value === "string" || typeof value === "number" ? String(value) : "";
    };

    const getCountryExtensionFlag = (key: string): boolean => Boolean(draft.countryExtensions?.[key]);

    const thInvoiceModeOptions = computed(() => [
      {
        value: "tax_invoice",
        label: localeText({
          en: "VAT tax invoice",
          "zh-CN": "VAT 税务发票",
          "ms-MY": "Invois cukai VAT",
          "id-ID": "Invoice pajak VAT",
          "th-TH": "ใบกำกับภาษี VAT"
        })
      },
      {
        value: "abbreviated_tax_invoice",
        label: localeText({
          en: "Abbreviated tax invoice",
          "zh-CN": "简式税务发票",
          "ms-MY": "Invois cukai ringkas",
          "id-ID": "Invoice pajak ringkas",
          "th-TH": "ใบกำกับภาษีอย่างย่อ"
        })
      },
      {
        value: "etax_invoice",
        label: localeText({
          en: "e-Tax invoice / e-Receipt",
          "zh-CN": "电子税务发票 / 电子收据",
          "ms-MY": "e-Tax invoice / e-Receipt",
          "id-ID": "e-Tax invoice / e-Receipt",
          "th-TH": "e-Tax Invoice / e-Receipt"
        })
      },
      {
        value: "commercial_invoice",
        label: localeText({
          en: "Commercial invoice",
          "zh-CN": "普通商业发票",
          "ms-MY": "Invois komersial",
          "id-ID": "Invoice komersial",
          "th-TH": "ใบแจ้งหนี้เชิงพาณิชย์"
        })
      }
    ]);

    const taxRegistrationLabel = computed(() =>
      country.value === "TH"
        ? localeText({
            en: "VAT registered",
            "zh-CN": "已完成 VAT 注册",
            "ms-MY": "Berdaftar VAT",
            "id-ID": "Sudah terdaftar VAT",
            "th-TH": "จดทะเบียน VAT แล้ว"
          })
        : copy("taxRegistered")
    );

    const thaiInvoiceModeHint = computed(() => {
      const mode = draft.invoiceType ?? "tax_invoice";
      if (mode === "etax_invoice") {
        return localeText({
          en: "Use this when the document will be issued through Thailand's approved e-Tax / e-Receipt route.",
          "zh-CN": "用于通过泰国认可的 e-Tax / e-Receipt 路径出具电子文件的场景。",
          "ms-MY": "Gunakan ini apabila dokumen akan dikeluarkan melalui laluan e-Tax / e-Receipt Thailand yang diluluskan.",
          "id-ID": "Gunakan ini bila dokumen akan diterbitkan melalui jalur e-Tax / e-Receipt Thailand yang disetujui.",
          "th-TH": "ใช้เมื่อเอกสารจะออกผ่านช่องทาง e-Tax / e-Receipt ที่ได้รับอนุมัติของไทย"
        });
      }
      if (mode === "abbreviated_tax_invoice") {
        return localeText({
          en: "Best suited for retail or direct-to-many-customer scenarios where an abbreviated tax invoice is appropriate.",
          "zh-CN": "更适合零售或面对大量终端消费者的场景。",
          "ms-MY": "Paling sesuai untuk runcit atau senario jualan terus kepada ramai pelanggan.",
          "id-ID": "Paling cocok untuk ritel atau skenario penjualan langsung ke banyak pelanggan.",
          "th-TH": "เหมาะกับธุรกิจค้าปลีกหรือการขายตรงให้ลูกค้าจำนวนมาก"
        });
      }
      if (mode === "commercial_invoice") {
        return localeText({
          en: "Use this when the seller is not issuing a Thai VAT tax invoice.",
          "zh-CN": "用于卖方当前并不出具泰国 VAT 税务发票的场景。",
          "ms-MY": "Gunakan ini apabila penjual tidak mengeluarkan invois cukai VAT Thailand.",
          "id-ID": "Gunakan ini bila penjual tidak menerbitkan invoice pajak VAT Thailand.",
          "th-TH": "ใช้เมื่อผู้ขายไม่ได้ออกใบกำกับภาษี VAT ของไทย"
        });
      }
      return localeText({
        en: "Default Thailand profile for standard VAT invoices.",
        "zh-CN": "泰国标准 VAT 税务发票的默认模式。",
        "ms-MY": "Profil lalai Thailand untuk invois cukai VAT standard.",
        "id-ID": "Profil default Thailand untuk invoice pajak VAT standar.",
        "th-TH": "โหมดเริ่มต้นสำหรับใบกำกับภาษี VAT มาตรฐานของไทย"
      });
    });

    const sellerRegistrationLabel = () =>
      localeText({
        en:
          country.value === "SG"
            ? "UEN / registration number"
            : country.value === "MY"
              ? "Business registration number"
              : country.value === "ID"
                ? "Business registration number"
                : "Business registration number",
        "zh-CN":
          country.value === "SG"
            ? "UEN / 注册号"
            : country.value === "MY"
              ? "公司注册号"
              : country.value === "ID"
                ? "企业注册号"
                : "商业注册号",
        "ms-MY":
          country.value === "SG"
            ? "UEN / nombor pendaftaran"
            : country.value === "MY"
              ? "Nombor pendaftaran perniagaan"
              : country.value === "ID"
                ? "Nombor pendaftaran perniagaan"
                : "Nombor pendaftaran perniagaan",
        "id-ID":
          country.value === "SG"
            ? "UEN / nomor registrasi"
            : country.value === "MY"
              ? "Nomor registrasi bisnis"
              : country.value === "ID"
                ? "Nomor registrasi bisnis"
                : "Nomor registrasi usaha",
        "th-TH":
          country.value === "SG"
            ? "UEN / เลขทะเบียน"
            : country.value === "MY"
              ? "เลขจดทะเบียนธุรกิจ"
              : country.value === "ID"
                ? "เลขทะเบียนธุรกิจ"
                : "เลขทะเบียนธุรกิจ"
      });

    const sellerTaxLabel = () =>
      localeText({
        en:
          country.value === "SG"
            ? "GST registration number"
            : country.value === "MY"
              ? "TIN / SST number"
              : country.value === "ID"
                ? "NPWP / tax number"
                : "Tax ID",
        "zh-CN":
          country.value === "SG"
            ? "GST 注册号"
            : country.value === "MY"
              ? "TIN / SST 号"
              : country.value === "ID"
                ? "NPWP / 税号"
                : "税号",
        "ms-MY":
          country.value === "SG"
            ? "Nombor pendaftaran GST"
            : country.value === "MY"
              ? "TIN / nombor SST"
              : country.value === "ID"
                ? "NPWP / nombor cukai"
                : "Nombor cukai",
        "id-ID":
          country.value === "SG"
            ? "Nomor registrasi GST"
            : country.value === "MY"
              ? "TIN / nomor SST"
              : country.value === "ID"
                ? "NPWP / nomor pajak"
                : "Nomor pajak",
        "th-TH":
          country.value === "SG"
            ? "เลขจดทะเบียน GST"
            : country.value === "MY"
              ? "TIN / เลข SST"
              : country.value === "ID"
                ? "NPWP / เลขภาษี"
                : "เลขประจำตัวผู้เสียภาษี"
      });

    const buyerTaxLabel = () =>
      localeText({
        en:
          country.value === "SG"
            ? "Buyer tax number"
            : country.value === "MY"
              ? "Buyer TIN"
              : country.value === "ID"
                ? "Buyer NPWP / tax number"
                : "Buyer tax ID",
        "zh-CN":
          country.value === "SG"
            ? "买方税号"
            : country.value === "MY"
              ? "买方 TIN"
              : country.value === "ID"
                ? "买方 NPWP / 税号"
                : "买方税号",
        "ms-MY":
          country.value === "SG"
            ? "Nombor cukai pembeli"
            : country.value === "MY"
              ? "TIN pembeli"
              : country.value === "ID"
                ? "NPWP / nombor cukai pembeli"
                : "Nombor cukai pembeli",
        "id-ID":
          country.value === "SG"
            ? "Nomor pajak pembeli"
            : country.value === "MY"
              ? "TIN pembeli"
              : country.value === "ID"
                ? "NPWP / nomor pajak pembeli"
                : "Nomor pajak pembeli",
        "th-TH":
          country.value === "SG"
            ? "เลขภาษีผู้ซื้อ"
            : country.value === "MY"
              ? "TIN ของผู้ซื้อ"
              : country.value === "ID"
                ? "NPWP / เลขภาษีผู้ซื้อ"
                : "เลขผู้เสียภาษีของผู้ซื้อ"
      });

    const classificationCodeLabel = () =>
      localeText({
        en:
          country.value === "MY"
            ? "MSIC / classification code"
            : country.value === "ID"
              ? "Transaction / item code"
              : "Classification code (optional)",
        "zh-CN":
          country.value === "MY"
            ? "MSIC / 分类编码"
            : country.value === "ID"
              ? "交易 / 项目编码"
              : "分类编码（可选）",
        "ms-MY":
          country.value === "MY"
            ? "Kod MSIC / klasifikasi"
            : country.value === "ID"
              ? "Kod transaksi / item"
              : "Kod klasifikasi (pilihan)",
        "id-ID":
          country.value === "MY"
            ? "Kode MSIC / klasifikasi"
            : country.value === "ID"
              ? "Kode transaksi / item"
              : "Kode klasifikasi (opsional)",
        "th-TH":
          country.value === "MY"
            ? "รหัส MSIC / ประเภทรายการ"
            : country.value === "ID"
              ? "รหัสธุรกรรม / รายการ"
              : "รหัสจัดประเภท (ไม่บังคับ)"
      });

    const renderGuideGrid = (cards: GuideCard[]) =>
      h(
        "div",
        { class: "guide-grid" },
        cards.map((card) =>
          h("article", { class: "guide-card" }, [
            h("span", { class: "guide-kicker" }, localeText({ en: "Guidance", "zh-CN": "填写引导", "ms-MY": "Panduan", "id-ID": "Panduan", "th-TH": "คำแนะนำ" })),
            h("strong", card.title),
            h("p", card.body)
          ])
        )
      );

    const basicsGuideCards = computed<GuideCard[]>(() => {
      if (country.value === "SG") {
        return [
          {
            title: localeText({ en: "Start with GST status", "zh-CN": "先确认 GST 注册状态", "ms-MY": "Mula dengan status GST", "id-ID": "Mulai dari status GST", "th-TH": "เริ่มจากสถานะ GST" }),
            body: localeText({ en: "Singapore checks are driven mainly by GST registration, domestic vs export treatment, and whether the invoice can fall into the simplified SGD 1,000 path.", "zh-CN": "新加坡场景主要围绕 GST 注册状态、境内或跨境处理，以及是否落入 SGD 1,000 的简化发票路径。", "ms-MY": "Semakan Singapura banyak bergantung pada status GST, rawatan domestik atau eksport, dan sama ada invois boleh masuk ke laluan ringkas SGD 1,000.", "id-ID": "Pemeriksaan Singapura terutama ditentukan oleh status GST, perlakuan domestik atau ekspor, dan apakah invoice termasuk jalur sederhana SGD 1.000.", "th-TH": "กฎของสิงคโปร์จะดูสถานะ GST การขายในประเทศหรือข้ามพรมแดน และกรณีใบกำกับแบบย่อไม่เกิน SGD 1,000 เป็นหลัก" })
          },
          {
            title: localeText({ en: "Keep the invoice number stable", "zh-CN": "发票编号保持稳定", "ms-MY": "Pastikan nombor invois stabil", "id-ID": "Jaga nomor invoice tetap stabil", "th-TH": "คงเลขเอกสารให้ชัดเจน" }),
            body: localeText({ en: "Use one invoice number per issued document. Reuse only when you are intentionally amending the same record.", "zh-CN": "一张正式发出的发票只应对应一个编号；只有在修订同一单据时才应延续原编号。", "ms-MY": "Gunakan satu nombor invois untuk setiap dokumen yang dikeluarkan. Gunakan semula hanya jika anda memang meminda rekod yang sama.", "id-ID": "Gunakan satu nomor invoice untuk setiap dokumen yang diterbitkan. Gunakan kembali hanya jika memang merevisi dokumen yang sama.", "th-TH": "เอกสารที่ออกจริงควรมีเลขอ้างอิงเฉพาะของตัวเอง และควรใช้เลขเดิมเฉพาะกรณีที่กำลังแก้ไขเอกสารฉบับเดิม" })
          }
        ];
      }
      if (country.value === "MY") {
        return [
          {
            title: localeText({ en: "Check whether MyInvois applies", "zh-CN": "先判断是否进入 MyInvois 路径", "ms-MY": "Semak sama ada MyInvois terpakai", "id-ID": "Periksa apakah jalur MyInvois berlaku", "th-TH": "ตรวจว่าต้องเข้ากระบวนการ MyInvois หรือไม่" }),
            body: localeText({ en: "Malaysia basics depend on whether the document is only a commercial invoice, an SST invoice, or part of the e-Invoice rollout. Turnover and buyer identity matter earlier here.", "zh-CN": "马来西亚会先区分普通商业发票、SST 发票和 e-Invoice 路径，营业额和买方身份会更早影响字段要求。", "ms-MY": "Maklumat asas Malaysia bergantung pada sama ada dokumen hanya invois komersial, invois SST, atau sebahagian daripada pelaksanaan e-Invois. Perolehan dan identiti pembeli memberi kesan lebih awal.", "id-ID": "Informasi dasar Malaysia bergantung pada apakah dokumen hanya invoice komersial, invoice SST, atau bagian dari rollout e-Invoice. Omzet dan identitas pembeli berpengaruh lebih awal.", "th-TH": "เอกสารของมาเลเซียจะต้องแยกก่อนว่าเป็นเพียงใบแจ้งหนี้เชิงพาณิชย์ ใบ SST หรืออยู่ในเส้นทาง e-Invoice ซึ่งยอดรายได้และข้อมูลผู้ซื้อมีผลตั้งแต่ต้น" })
          },
          {
            title: localeText({ en: "Issue time matters", "zh-CN": "开票时间很重要", "ms-MY": "Masa terbit penting", "id-ID": "Waktu terbit penting", "th-TH": "เวลาออกเอกสารมีความสำคัญ" }),
            body: localeText({ en: "For Malaysia e-Invoice workflows, date, time, supplier identity, buyer identity, and line classification all become part of the core structure.", "zh-CN": "在马来西亚 e-Invoice 场景下，日期、时间、卖方身份、买方身份和行分类都会成为核心结构的一部分。", "ms-MY": "Dalam aliran e-Invois Malaysia, tarikh, masa, identiti pembekal, identiti pembeli dan klasifikasi item menjadi sebahagian daripada struktur teras.", "id-ID": "Dalam alur e-Invoice Malaysia, tanggal, waktu, identitas pemasok, identitas pembeli, dan klasifikasi item menjadi bagian inti struktur.", "th-TH": "ในเส้นทาง e-Invoice ของมาเลเซีย วันที่ เวลา ข้อมูลผู้ออก ผู้ซื้อ และรหัสรายการจะกลายเป็นโครงสร้างหลักของเอกสาร" })
          }
        ];
      }
      if (country.value === "ID") {
        return [
          {
            title: localeText({ en: "Start with PKP status", "zh-CN": "先确认 PKP 身份", "ms-MY": "Mula dengan status PKP", "id-ID": "Mulai dari status PKP", "th-TH": "เริ่มจากสถานะ PKP" }),
            body: localeText({ en: "Indonesia checks first ask whether the seller is PKP. That decision drives whether VAT can appear at all and whether the tax-invoice transaction code becomes mandatory.", "zh-CN": "印尼会先判断卖方是否为 PKP，这会直接决定是否可以计税，以及税务发票交易代码是否变成必填。", "ms-MY": "Semakan Indonesia terlebih dahulu melihat sama ada penjual ialah PKP. Keputusan ini menentukan sama ada VAT boleh ditunjukkan dan sama ada kod transaksi cukai menjadi mandatori.", "id-ID": "Pemeriksaan Indonesia pertama-tama melihat apakah penjual adalah PKP. Keputusan ini menentukan apakah PPN boleh dipungut dan apakah kode transaksi menjadi wajib.", "th-TH": "กฎของอินโดนีเซียจะเริ่มจากการดูว่าผู้ขายเป็น PKP หรือไม่ ซึ่งมีผลโดยตรงต่อการคิด VAT และการบังคับใช้รหัสธุรกรรมภาษี" })
          },
          {
            title: localeText({ en: "Use the domestic rate carefully", "zh-CN": "境内税率要谨慎选择", "ms-MY": "Gunakan kadar domestik dengan teliti", "id-ID": "Gunakan tarif domestik dengan cermat", "th-TH": "ใช้ภาษีในประเทศอย่างระมัดระวัง" }),
            body: localeText({ en: "The current rule pack uses the common effective 11% path for ordinary taxable supplies and 12% only for luxury-goods treatment. Choose the business activity accordingly.", "zh-CN": "当前规则包对普通应税交易采用常见的有效 11% 路径，仅对奢侈品路径使用 12%，因此业务活动选择会直接影响税率提示。", "ms-MY": "Pakej peraturan semasa menggunakan laluan efektif 11% bagi kebanyakan bekalan biasa dan 12% hanya untuk layanan barangan mewah. Pilih aktiviti perniagaan dengan tepat.", "id-ID": "Paket aturan saat ini memakai jalur efektif 11% untuk penyerahan biasa dan 12% hanya untuk perlakuan barang mewah. Pilih aktivitas bisnis dengan tepat.", "th-TH": "ชุดกฎปัจจุบันใช้แนวทางอัตราที่มีผลจริง 11% สำหรับธุรกรรมทั่วไป และใช้ 12% เฉพาะกรณีสินค้าฟุ่มเฟือย ดังนั้นการเลือกประเภทธุรกิจจึงสำคัญ" })
          }
        ];
      }
      return [
        {
          title: localeText({ en: "Choose the Thai document mode first", "zh-CN": "先确认泰国文档模式", "ms-MY": "Pilih mod dokumen Thailand dahulu", "id-ID": "Pilih mode dokumen Thailand terlebih dahulu", "th-TH": "เลือกประเภทเอกสารของไทยก่อน" }),
          body: localeText({ en: "Thailand basics change a lot between standard tax invoice, abbreviated tax invoice, e-Tax / e-Receipt, and non-VAT commercial invoice. Pick the document mode before filling the rest.", "zh-CN": "泰国会先区分标准税务发票、简式税务发票、e-Tax / e-Receipt 和普通商业发票，不同模式会影响后续字段要求。", "ms-MY": "Maklumat asas Thailand banyak berubah antara invois cukai standard, invois cukai ringkas, e-Tax / e-Receipt, dan invois komersial bukan VAT. Pilih mod dahulu sebelum mengisi selebihnya.", "id-ID": "Informasi dasar Thailand banyak berubah antara invoice pajak standar, invoice pajak ringkas, e-Tax / e-Receipt, dan invoice komersial non-VAT. Pilih mode terlebih dahulu sebelum mengisi sisanya.", "th-TH": "ข้อมูลพื้นฐานของไทยจะเปลี่ยนมากตามประเภทเอกสาร ไม่ว่าจะเป็นใบกำกับภาษีมาตรฐาน ใบกำกับภาษีอย่างย่อ e-Tax / e-Receipt หรือเอกสารเชิงพาณิชย์ทั่วไป จึงควรเลือกโหมดก่อนกรอกข้อมูลอื่น" })
        },
        {
          title: localeText({ en: "THB and Thai language are the default assumption", "zh-CN": "默认按泰语和 THB 理解", "ms-MY": "Bahasa Thai dan THB ialah andaian lalai", "id-ID": "Bahasa Thai dan THB adalah asumsi default", "th-TH": "ระบบจะยึดภาษาไทยและเงินบาทเป็นค่าเริ่มต้น" }),
          body: localeText({ en: "If you need foreign language or foreign currency on a Thai tax invoice, confirm approval before relying on that format.", "zh-CN": "如果需要在泰国税务发票上使用外语或外币，请先确认已经取得相应批准。", "ms-MY": "Jika anda perlu menggunakan bahasa atau mata wang asing pada invois cukai Thailand, sahkan kelulusan terlebih dahulu.", "id-ID": "Jika Anda perlu memakai bahasa atau mata uang asing pada invoice pajak Thailand, pastikan persetujuan terlebih dahulu.", "th-TH": "หากต้องใช้ภาษาต่างประเทศหรือสกุลเงินต่างประเทศในใบกำกับภาษีไทย ควรยืนยันการอนุมัติก่อนเสมอ" })
        }
      ];
    });

    const partyGuideCards = computed<{ seller: GuideCard[]; buyer: GuideCard[] }>(() => {
      if (country.value === "SG") {
        return {
          seller: [
            {
              title: localeText({ en: "Seller identity should match GST status", "zh-CN": "卖方身份要与 GST 状态匹配", "ms-MY": "Identiti penjual mesti sepadan dengan status GST", "id-ID": "Identitas penjual harus sesuai dengan status GST", "th-TH": "ข้อมูลผู้ขายต้องสอดคล้องกับสถานะ GST" }),
              body: localeText({ en: "For Singapore, seller legal name, UEN, address, and GST number should line up with the registration profile you actually use to invoice customers.", "zh-CN": "在新加坡场景下，卖方法定名称、UEN、地址和 GST 注册号应与实际开票主体保持一致。", "ms-MY": "Bagi Singapura, nama sah penjual, UEN, alamat, dan nombor GST perlu sepadan dengan profil pendaftaran yang digunakan untuk mengebil pelanggan.", "id-ID": "Untuk Singapura, nama sah penjual, UEN, alamat, dan nomor GST harus sesuai dengan profil registrasi yang benar-benar dipakai saat menagih pelanggan.", "th-TH": "สำหรับสิงคโปร์ ชื่อผู้ขาย UEN ที่อยู่ และเลข GST ควรตรงกับนิติบุคคลที่ใช้ในการออกเอกสารจริง" })
            }
          ],
          buyer: [
            {
              title: localeText({ en: "Named buyer fields expand for business invoices", "zh-CN": "企业买方时建议填写完整", "ms-MY": "Medan pembeli bernama penting untuk invois perniagaan", "id-ID": "Bidang pembeli bernama penting untuk invoice bisnis", "th-TH": "เอกสารแบบผู้ซื้อเป็นธุรกิจควรกรอกข้อมูลผู้ซื้อให้ครบ" }),
              body: localeText({ en: "Business buyers usually need a clearer legal name and address. Final-consumer mode is mostly for simplified retail situations.", "zh-CN": "企业买方通常需要更完整的法定名称和地址；最终消费者模式更多用于简化零售场景。", "ms-MY": "Pembeli perniagaan biasanya memerlukan nama sah dan alamat yang lebih jelas. Mod pengguna akhir lebih sesuai untuk senario runcit ringkas.", "id-ID": "Pembeli bisnis biasanya memerlukan nama sah dan alamat yang lebih lengkap. Mode konsumen akhir lebih cocok untuk situasi ritel sederhana.", "th-TH": "กรณีผู้ซื้อเป็นธุรกิจมักต้องมีชื่อและที่อยู่ตามกฎหมายที่ชัดเจน ส่วนโหมดผู้บริโภคปลายทางเหมาะกับกรณีขายปลีกแบบย่อมากกว่า" })
            }
          ]
        };
      }
      if (country.value === "MY") {
        return {
          seller: [
            {
              title: localeText({ en: "Supplier identity is heavier in Malaysia", "zh-CN": "马来西亚对卖方身份要求更重", "ms-MY": "Identiti pembekal lebih berat di Malaysia", "id-ID": "Identitas pemasok lebih berat di Malaysia", "th-TH": "มาเลเซียให้ความสำคัญกับข้อมูลผู้ออกเอกสารมากขึ้น" }),
              body: localeText({ en: "If the document falls into e-Invoice, supplier registration, TIN, address, and contact details should be treated as core rather than optional.", "zh-CN": "如果这张单据落入 e-Invoice 路径，卖方注册号、TIN、地址和联系方式都应视为核心字段。", "ms-MY": "Jika dokumen ini berada dalam laluan e-Invois, nombor pendaftaran, TIN, alamat, dan butiran hubungan pembekal perlu dianggap sebagai medan teras.", "id-ID": "Jika dokumen ini masuk jalur e-Invoice, nomor registrasi, TIN, alamat, dan detail kontak pemasok harus dianggap sebagai bidang inti.", "th-TH": "หากเอกสารอยู่ในเส้นทาง e-Invoice ข้อมูลทะเบียน TIN ที่อยู่ และช่องทางติดต่อของผู้ขายควรถูกมองว่าเป็นข้อมูลหลัก" })
            }
          ],
          buyer: [
            {
              title: localeText({ en: "Buyer identity affects readiness", "zh-CN": "买方身份会直接影响可用性", "ms-MY": "Identiti pembeli mempengaruhi kesiapsiagaan", "id-ID": "Identitas pembeli memengaruhi kesiapan dokumen", "th-TH": "ข้อมูลผู้ซื้อมีผลโดยตรงต่อความพร้อมของเอกสาร" }),
              body: localeText({ en: "Malaysia workflows often need a better buyer profile than other markets. A named business or individual buyer is usually easier to validate than a blank generic final-consumer profile.", "zh-CN": "马来西亚通常比其他国家更依赖买方身份字段，具名企业或个人买方通常比空白的最终消费者资料更容易通过复核。", "ms-MY": "Aliran Malaysia sering memerlukan profil pembeli yang lebih lengkap berbanding pasaran lain. Pembeli perniagaan atau individu yang dinamakan biasanya lebih mudah disemak berbanding profil pengguna akhir umum yang dibiarkan kosong.", "id-ID": "Alur Malaysia sering memerlukan profil pembeli yang lebih lengkap dibanding pasar lain. Pembeli bisnis atau pribadi bernama biasanya lebih mudah ditinjau daripada profil konsumen akhir umum yang dibiarkan kosong.", "th-TH": "กระบวนการของมาเลเซียมักต้องพึ่งข้อมูลผู้ซื้อมากกว่าตลาดอื่น ผู้ซื้อที่มีชื่อชัดเจนทั้งแบบบุคคลและนิติบุคคลจึงผ่านการทบทวนได้ง่ายกว่าการปล่อยข้อมูลผู้บริโภคทั่วไปว่างไว้" })
            }
          ]
        };
      }
      if (country.value === "ID") {
        return {
          seller: [
            {
              title: localeText({ en: "Seller NPWP drives the PKP path", "zh-CN": "卖方 NPWP 会驱动 PKP 路径", "ms-MY": "NPWP penjual memacu laluan PKP", "id-ID": "NPWP penjual menentukan jalur PKP", "th-TH": "NPWP ของผู้ขายมีผลต่อเส้นทาง PKP" }),
              body: localeText({ en: "For Indonesian PKP invoices, the seller tax number is central. Keep it aligned with the transaction code and the chosen tax treatment.", "zh-CN": "在印尼 PKP 发票里，卖方税号是核心字段，最好与交易代码和税务处理路径一并核对。", "ms-MY": "Bagi invois PKP Indonesia, nombor cukai penjual ialah medan utama. Pastikan ia selaras dengan kod transaksi dan rawatan cukai yang dipilih.", "id-ID": "Untuk invoice PKP Indonesia, nomor pajak penjual adalah bidang inti. Pastikan selaras dengan kode transaksi dan perlakuan pajak yang dipilih.", "th-TH": "สำหรับเอกสาร PKP ของอินโดนีเซีย เลขภาษีของผู้ขายเป็นข้อมูลหลัก ควรตรวจให้สอดคล้องกับรหัสธุรกรรมและแนวทางภาษีที่เลือก" })
            }
          ],
          buyer: [
            {
              title: localeText({ en: "Buyer detail changes by invoice type", "zh-CN": "买方信息会随发票类型变化", "ms-MY": "Butiran pembeli berubah mengikut jenis invois", "id-ID": "Detail pembeli berubah menurut jenis faktur", "th-TH": "ข้อมูลผู้ซื้อเปลี่ยนตามประเภทเอกสาร" }),
              body: localeText({ en: "Retail consumer invoices can relax buyer identity, but a normal PKP invoice still expects a clearer buyer name, address, and tax number.", "zh-CN": "零售消费者发票可以适当放宽买方身份字段，但标准 PKP 发票仍然需要更明确的买方名称、地址和税号。", "ms-MY": "Invois runcit pengguna boleh melonggarkan identiti pembeli, tetapi invois PKP biasa masih memerlukan nama, alamat dan nombor cukai pembeli yang lebih jelas.", "id-ID": "Faktur ritel konsumen boleh melonggarkan identitas pembeli, tetapi faktur PKP biasa tetap memerlukan nama, alamat, dan nomor pajak pembeli yang lebih jelas.", "th-TH": "เอกสารขายปลีกให้ผู้บริโภคสามารถผ่อนข้อมูลผู้ซื้อได้บ้าง แต่เอกสาร PKP ปกติยังคาดหวังชื่อ ที่อยู่ และเลขภาษีของผู้ซื้อที่ชัดเจน" })
            }
          ]
        };
      }
      return {
        seller: [
          {
            title: localeText({ en: "Thai seller tax ID and branch data matter", "zh-CN": "泰国卖方税号和分支信息很关键", "ms-MY": "Nombor cukai dan data cawangan penjual Thailand penting", "id-ID": "Nomor pajak dan data cabang penjual Thailand penting", "th-TH": "เลขภาษีและข้อมูลสาขาของผู้ขายไทยสำคัญมาก" }),
            body: localeText({ en: "When using Thai VAT invoices, the seller tax ID is a core field and branch numbers should be added where the issuing entity is not the head office.", "zh-CN": "在泰国 VAT 发票场景中，卖方税号是核心字段；如果开票主体不是总机构，分支编号也应一起体现。", "ms-MY": "Apabila menggunakan invois VAT Thailand, nombor cukai penjual ialah medan teras dan nombor cawangan perlu diisi jika entiti pengeluar bukan ibu pejabat.", "id-ID": "Saat memakai invoice VAT Thailand, nomor pajak penjual adalah bidang inti dan nomor cabang perlu diisi bila penerbit bukan kantor pusat.", "th-TH": "เมื่อใช้ใบกำกับภาษี VAT ของไทย เลขผู้เสียภาษีของผู้ขายเป็นข้อมูลหลัก และหากผู้ออกไม่ใช่สำนักงานใหญ่ก็ควรระบุเลขสาขาด้วย" })
          }
        ],
        buyer: [
          {
            title: localeText({ en: "Thai buyer detail depends on document mode", "zh-CN": "泰国买方信息取决于文档模式", "ms-MY": "Butiran pembeli Thailand bergantung pada mod dokumen", "id-ID": "Detail pembeli Thailand bergantung pada mode dokumen", "th-TH": "ข้อมูลผู้ซื้อของไทยขึ้นกับประเภทเอกสาร" }),
            body: localeText({ en: "Standard tax invoices usually benefit from a fuller buyer profile. Abbreviated tax invoices are more tolerant for retail flows.", "zh-CN": "标准税务发票通常更适合填写完整的买方资料，而简式税务发票对零售场景会更宽松。", "ms-MY": "Invois cukai standard biasanya lebih sesuai dengan profil pembeli yang lebih lengkap, manakala invois cukai ringkas lebih longgar untuk aliran runcit.", "id-ID": "Invoice pajak standar biasanya lebih cocok dengan profil pembeli yang lebih lengkap, sedangkan invoice pajak ringkas lebih longgar untuk alur ritel.", "th-TH": "ใบกำกับภาษีมาตรฐานมักเหมาะกับการกรอกข้อมูลผู้ซื้อให้ครบ ขณะที่ใบกำกับภาษีอย่างย่อจะผ่อนปรนมากกว่าในกรณีขายปลีก" })
          }
        ]
      };
    });

    const itemGuideCards = computed<GuideCard[]>(() => {
      if (country.value === "MY") {
        return [
          {
            title: localeText({ en: "Classification codes are part of the e-Invoice path", "zh-CN": "分类编码会进入 e-Invoice 路径", "ms-MY": "Kod klasifikasi ialah sebahagian laluan e-Invois", "id-ID": "Kode klasifikasi masuk dalam jalur e-Invoice", "th-TH": "รหัสจัดประเภทเป็นส่วนหนึ่งของเส้นทาง e-Invoice" }),
            body: localeText({ en: "Malaysia line items often need classification codes alongside quantity, price, tax, and totals. Keep the line description commercially clear as well.", "zh-CN": "马来西亚的行项目通常不仅要有数量、单价、税额和总额，也要准备好分类编码，描述也应保持清晰。", "ms-MY": "Item Malaysia sering memerlukan kod klasifikasi bersama kuantiti, harga, cukai dan jumlah. Pastikan deskripsi item juga jelas.", "id-ID": "Item Malaysia sering memerlukan kode klasifikasi bersama kuantitas, harga, pajak, dan total. Pastikan deskripsi item juga jelas.", "th-TH": "รายการของมาเลเซียมักต้องมีทั้งปริมาณ ราคา ภาษี ยอดรวม และรหัสจัดประเภท พร้อมคำอธิบายรายการที่ชัดเจน" })
          }
        ];
      }
      if (country.value === "ID") {
        return [
          {
            title: localeText({ en: "Line tax treatment should match the transaction code", "zh-CN": "行税务处理要与交易代码一致", "ms-MY": "Rawatan cukai baris perlu sepadan dengan kod transaksi", "id-ID": "Perlakuan pajak per baris harus sesuai dengan kode transaksi", "th-TH": "การคิดภาษีต่อรายการต้องสอดคล้องกับรหัสธุรกรรม" }),
            body: localeText({ en: "Indonesia line items should make the taxable base, VAT, and transaction code look coherent. Use business activity and supply type to keep the rate on the right path.", "zh-CN": "在印尼场景下，行项目应让计税基础、VAT 和交易代码彼此一致，业务活动和供给类型会共同影响税率路径。", "ms-MY": "Bagi Indonesia, item invois perlu menjadikan asas cukai, VAT, dan kod transaksi kelihatan konsisten. Aktiviti perniagaan dan jenis bekalan membantu mengekalkan kadar yang betul.", "id-ID": "Untuk Indonesia, item invoice harus membuat dasar pengenaan pajak, VAT, dan kode transaksi tetap konsisten. Aktivitas bisnis dan jenis penyerahan membantu menjaga tarif tetap pada jalur yang benar.", "th-TH": "สำหรับอินโดนีเซีย รายการแต่ละบรรทัดควรทำให้ฐานภาษี VAT และรหัสธุรกรรมสอดคล้องกัน โดยประเภทธุรกิจและลักษณะการขายจะช่วยกำหนดอัตราที่ถูกต้อง" })
          }
        ];
      }
      if (country.value === "TH") {
        return [
          {
            title: localeText({ en: "Retail and standard Thai invoices differ", "zh-CN": "泰国零售与标准发票的明细要求不同", "ms-MY": "Invois runcit dan standard Thailand berbeza", "id-ID": "Invoice ritel dan standar Thailand berbeda", "th-TH": "เอกสารไทยแบบค้าปลีกและแบบมาตรฐานใช้รายละเอียดต่างกัน" }),
            body: localeText({ en: "Standard Thai VAT invoices usually benefit from full quantity, unit price, tax, and total fields. Abbreviated tax invoices can stay lighter, but still need a clear item trail.", "zh-CN": "泰国标准 VAT 发票通常更适合填写完整的数量、单价、税额和总额；简式税务发票可以更轻，但仍应保留清晰的项目轨迹。", "ms-MY": "Invois VAT Thailand standard lazimnya lebih sesuai dengan kuantiti, harga seunit, cukai dan jumlah yang lengkap. Invois ringkas boleh lebih ringan tetapi masih perlu jejak item yang jelas.", "id-ID": "Invoice VAT Thailand standar biasanya lebih cocok dengan kuantitas, harga satuan, pajak, dan total yang lengkap. Invoice ringkas boleh lebih ringan tetapi tetap memerlukan jejak item yang jelas.", "th-TH": "ใบกำกับภาษี VAT ของไทยแบบมาตรฐานมักควรมีจำนวน ราคา ภาษี และยอดรวมครบถ้วน ส่วนใบกำกับอย่างย่ออาจกรอกน้อยกว่าได้ แต่ยังควรมีร่องรอยของรายการอย่างชัดเจน" })
          }
        ];
      }
      return [
        {
          title: localeText({ en: "Keep line items readable for both tax and customer review", "zh-CN": "行项目要同时兼顾税务与客户可读性", "ms-MY": "Pastikan item mudah difahami untuk cukai dan pelanggan", "id-ID": "Pastikan item mudah dibaca untuk pajak dan pelanggan", "th-TH": "ทำให้รายการอ่านง่ายทั้งสำหรับภาษีและลูกค้า" }),
          body: localeText({ en: "A clear description, commercially reasonable quantity, tax rate, and line total make the draft easier to validate and easier for customers to understand.", "zh-CN": "清晰的描述、合理的数量、正确的税率和行总额，会让草稿更容易校验，也更容易被客户理解。", "ms-MY": "Deskripsi yang jelas, kuantiti yang munasabah, kadar cukai dan jumlah baris yang betul menjadikan draf lebih mudah disemak dan difahami pelanggan.", "id-ID": "Deskripsi yang jelas, kuantitas yang masuk akal, tarif pajak, dan total baris yang tepat membuat draft lebih mudah ditinjau dan dipahami pelanggan.", "th-TH": "คำอธิบายที่ชัดเจน จำนวนที่สมเหตุสมผล อัตราภาษีที่ถูกต้อง และยอดรวมต่อบรรทัดที่ครบถ้วน จะช่วยให้ตรวจง่ายและลูกค้าเข้าใจได้ง่ายขึ้น" })
        }
      ];
    });

    const intakeGuideCards = computed<GuideCard[]>(() => [
      {
        title: localeText({
          en: "Import feeds the same draft",
          "zh-CN": "导入仍然回到同一份草稿",
          "ms-MY": "Import tetap kembali ke draf yang sama",
          "id-ID": "Impor tetap masuk ke draft yang sama",
          "th-TH": "การนำเข้ายังกลับมาที่ร่างฉบับเดียวกัน"
        }),
        body: localeText({
          en: "OCR or file import only helps populate fields faster. Your team still reviews and edits the same draft before running checks.",
          "zh-CN": "OCR 或文件导入只是帮助更快回填字段，团队仍然是在同一份草稿上继续复核和编辑。",
          "ms-MY": "OCR atau import fail hanya membantu mengisi medan dengan lebih cepat. Pasukan anda tetap menyemak dan menyunting draf yang sama.",
          "id-ID": "OCR atau impor file hanya membantu mengisi field lebih cepat. Tim Anda tetap meninjau dan mengedit draft yang sama.",
          "th-TH": "OCR หรือการนำเข้าไฟล์มีไว้เพื่อเติมข้อมูลให้เร็วขึ้น แต่ทีมของคุณยังคงทบทวนและแก้ไขในร่างฉบับเดียวกัน"
        })
      },
      {
        title: localeText({
          en: "Bring in only the clearest source",
          "zh-CN": "只导入最清晰的来源",
          "ms-MY": "Import sumber yang paling jelas sahaja",
          "id-ID": "Impor hanya sumber yang paling jelas",
          "th-TH": "นำเข้าเฉพาะต้นทางที่ชัดที่สุด"
        }),
        body: localeText({
          en: "Use one clean image, PDF, or OCR text block at a time. Cleaner inputs produce cleaner drafts and fewer corrections later.",
          "zh-CN": "一次只使用一份清晰的图片、PDF 或 OCR 文本，来源越干净，后续需要修正的地方越少。",
          "ms-MY": "Gunakan satu imej, PDF, atau teks OCR yang jelas pada satu masa. Sumber yang lebih bersih menghasilkan draf yang lebih kemas.",
          "id-ID": "Gunakan satu gambar, PDF, atau teks OCR yang jelas setiap kali. Sumber yang lebih bersih menghasilkan draft yang lebih rapi.",
          "th-TH": "ใช้รูปภาพ PDF หรือข้อความ OCR ที่ชัดเจนทีละชุด ยิ่งต้นทางสะอาด ร่างที่ได้ก็ยิ่งแก้งานน้อย"
        })
      }
    ]);

    const sellerRegistrationHelp = computed(() =>
      localeText({
        en:
          country.value === "SG"
            ? "Use the UEN or the business registration number that the seller uses for invoicing."
            : country.value === "MY"
              ? "Use the supplier's official business registration number. Keep it aligned with the e-Invoice or SST profile."
              : country.value === "ID"
                ? "Use the seller registration number if your operation keeps one alongside the tax profile."
                : "Use the business registration number shown by the Thai seller entity, if applicable.",
        "zh-CN":
          country.value === "SG"
            ? "填写卖方实际开票主体使用的 UEN 或注册号。"
            : country.value === "MY"
              ? "填写卖方官方公司注册号，并与 e-Invoice 或 SST 档案保持一致。"
              : country.value === "ID"
                ? "如果你的经营主体会同时保留企业注册号与税务身份，可在这里填写注册号。"
                : "如果泰国开票主体有对应的商业注册号，可在这里填写。",
        "ms-MY":
          country.value === "SG"
            ? "Gunakan UEN atau nombor pendaftaran perniagaan yang benar-benar digunakan untuk mengebil."
            : country.value === "MY"
              ? "Gunakan nombor pendaftaran rasmi pembekal dan pastikan ia selaras dengan profil e-Invois atau SST."
              : country.value === "ID"
                ? "Gunakan nombor pendaftaran penjual jika operasi anda menyimpannya bersama profil cukai."
                : "Gunakan nombor pendaftaran perniagaan penjual Thailand jika berkenaan.",
        "id-ID":
          country.value === "SG"
            ? "Gunakan UEN atau nomor registrasi bisnis yang benar-benar dipakai untuk menagih."
            : country.value === "MY"
              ? "Gunakan nomor registrasi resmi pemasok dan pastikan selaras dengan profil e-Invoice atau SST."
              : country.value === "ID"
                ? "Gunakan nomor registrasi penjual jika operasi Anda menyimpannya bersama profil pajak."
                : "Gunakan nomor registrasi usaha entitas penjual Thailand jika relevan.",
        "th-TH":
          country.value === "SG"
            ? "ใช้ UEN หรือเลขทะเบียนที่ผู้ขายใช้ออกเอกสารจริง"
            : country.value === "MY"
              ? "ใช้เลขทะเบียนธุรกิจอย่างเป็นทางการของผู้ขาย และให้สอดคล้องกับโปรไฟล์ e-Invoice หรือ SST"
              : country.value === "ID"
                ? "ใช้เลขทะเบียนของผู้ขายหากกิจการของคุณเก็บข้อมูลนี้ควบคู่กับโปรไฟล์ภาษี"
                : "ใช้เลขทะเบียนธุรกิจของนิติบุคคลไทยหากมี"
      })
    );

    const sellerTaxHelp = computed(() =>
      localeText({
        en:
          country.value === "SG"
            ? "Required when GST is charged."
            : country.value === "MY"
              ? "Use the supplier TIN. If SST is charged, keep the SST registration aligned as well."
              : country.value === "ID"
                ? "For PKP invoices, this should normally be the seller NPWP or the tax identifier actually used."
                : "For Thai VAT invoices, keep the 13-digit tax ID consistent with the issuing entity.",
        "zh-CN":
          country.value === "SG"
            ? "当发票计收 GST 时，这个字段应完整显示。"
            : country.value === "MY"
              ? "优先填写卖方 TIN；如果计收 SST，也要确保与对应注册身份一致。"
              : country.value === "ID"
                ? "在 PKP 场景下，这里通常应填写卖方 NPWP 或实际开票使用的税号。"
                : "泰国 VAT 发票通常应填写与开票主体一致的 13 位税号。",
        "ms-MY":
          country.value === "SG"
            ? "Diperlukan apabila GST dikenakan."
            : country.value === "MY"
              ? "Gunakan TIN pembekal. Jika SST dikenakan, pastikan status pendaftaran SST juga selaras."
              : country.value === "ID"
                ? "Bagi invois PKP, ini biasanya ialah NPWP penjual atau pengenal cukai yang benar-benar digunakan."
                : "Bagi invois VAT Thailand, gunakan nombor cukai 13 digit yang sepadan dengan entiti pengeluar.",
        "id-ID":
          country.value === "SG"
            ? "Wajib ditampilkan bila GST dipungut."
            : country.value === "MY"
              ? "Gunakan TIN pemasok. Jika SST dipungut, pastikan status pendaftaran SST juga selaras."
              : country.value === "ID"
                ? "Untuk faktur PKP, ini biasanya NPWP penjual atau identitas pajak yang benar-benar dipakai."
                : "Untuk invoice VAT Thailand, gunakan Tax ID 13 digit yang konsisten dengan entitas penerbit.",
        "th-TH":
          country.value === "SG"
            ? "ต้องแสดงเมื่อมีการเรียกเก็บ GST"
            : country.value === "MY"
              ? "ใช้ TIN ของผู้ขาย และหากมีการคิด SST ก็ควรสอดคล้องกับสถานะการจดทะเบียนด้วย"
              : country.value === "ID"
                ? "สำหรับเอกสาร PKP ช่องนี้มักควรใช้ NPWP ของผู้ขายหรือเลขภาษีที่ใช้จริง"
                : "สำหรับใบกำกับ VAT ของไทย ควรใช้เลขผู้เสียภาษี 13 หลักที่ตรงกับผู้ออกเอกสาร"
      })
    );

    const buyerTaxHelp = computed(() =>
      localeText({
        en:
          country.value === "MY"
            ? "Malaysia buyer identity is often needed earlier, especially in e-Invoice scenarios."
            : country.value === "ID"
              ? "Required for a standard PKP invoice, but often relaxed for retail final-consumer flows."
              : country.value === "TH"
                ? "Use this when the Thai buyer profile needs to be explicit on the document."
                : "Add this when the buyer is a named business or otherwise needs a fuller tax profile.",
        "zh-CN":
          country.value === "MY"
            ? "马来西亚对买方身份依赖更高，尤其在 e-Invoice 场景下更应尽早补齐。"
            : country.value === "ID"
              ? "标准 PKP 发票通常需要买方税号；零售最终消费者路径则可能放宽。"
              : country.value === "TH"
                ? "当泰国买方身份需要明确展示时，应在这里填写。"
                : "当买方是具名企业，或需要完整税务身份时，建议填写此字段。",
        "ms-MY":
          country.value === "MY"
            ? "Identiti pembeli Malaysia sering diperlukan lebih awal, khususnya dalam senario e-Invois."
            : country.value === "ID"
              ? "Mandatori bagi invois PKP standard tetapi sering dilonggarkan untuk aliran pengguna akhir runcit."
              : country.value === "TH"
                ? "Gunakan apabila profil pembeli Thailand perlu dipaparkan dengan jelas pada dokumen."
                : "Tambahkan ini apabila pembeli ialah perniagaan bernama atau memerlukan profil cukai lebih lengkap.",
        "id-ID":
          country.value === "MY"
            ? "Identitas pembeli Malaysia sering dibutuhkan lebih awal, terutama pada skenario e-Invoice."
            : country.value === "ID"
              ? "Wajib untuk faktur PKP standar tetapi sering dilonggarkan untuk alur konsumen akhir ritel."
              : country.value === "TH"
                ? "Gunakan ini bila profil pembeli Thailand perlu ditampilkan dengan jelas di dokumen."
                : "Tambahkan ini ketika pembeli adalah bisnis bernama atau memerlukan profil pajak yang lebih lengkap.",
        "th-TH":
          country.value === "MY"
            ? "ข้อมูลผู้ซื้อของมาเลเซียมักจำเป็นตั้งแต่ต้น โดยเฉพาะในเส้นทาง e-Invoice"
            : country.value === "ID"
              ? "มักบังคับสำหรับเอกสาร PKP มาตรฐาน แต่จะผ่อนลงได้ในกรณีขายปลีกให้ผู้บริโภค"
              : country.value === "TH"
                ? "ใช้เมื่อต้องแสดงข้อมูลผู้ซื้อของไทยให้ชัดเจนบนเอกสาร"
                : "ควรเพิ่มข้อมูลนี้เมื่อผู้ซื้อเป็นนิติบุคคลที่ระบุชื่อ หรือเมื่อจำเป็นต้องแสดงสถานะภาษีให้ครบ"
      })
    );

    const classificationCodeHelp = computed(() =>
      localeText({
        en:
          country.value === "MY"
            ? "Malaysia e-Invoice lines usually need a valid classification code."
            : country.value === "ID"
              ? "Use an internal or operational code that helps align the line with your Indonesian tax treatment."
              : "Use this only if your team needs an internal category or commodity code.",
        "zh-CN":
          country.value === "MY"
            ? "马来西亚 e-Invoice 行项目通常需要准备有效的分类编码。"
            : country.value === "ID"
              ? "可填写能帮助你对齐印尼税务处理的内部或业务编码。"
              : "只有当团队需要保留内部分类或商品编码时再填写。",
        "ms-MY":
          country.value === "MY"
            ? "Baris e-Invois Malaysia biasanya memerlukan kod klasifikasi yang sah."
            : country.value === "ID"
              ? "Gunakan kod dalaman atau operasi yang membantu menyelaraskan item dengan rawatan cukai Indonesia anda."
              : "Gunakan ini hanya jika pasukan anda memerlukan kategori dalaman atau kod komoditi.",
        "id-ID":
          country.value === "MY"
            ? "Baris e-Invoice Malaysia biasanya memerlukan kode klasifikasi yang valid."
            : country.value === "ID"
              ? "Gunakan kode internal atau operasional yang membantu menyelaraskan item dengan perlakuan pajak Indonesia Anda."
              : "Gunakan ini hanya bila tim Anda memerlukan kategori internal atau kode komoditas.",
        "th-TH":
          country.value === "MY"
            ? "รายการใน e-Invoice ของมาเลเซียมักต้องใช้รหัสจัดประเภทที่ถูกต้อง"
            : country.value === "ID"
              ? "ใช้รหัสภายในหรือรหัสปฏิบัติการที่ช่วยให้รายการสอดคล้องกับการคิดภาษีของอินโดนีเซีย"
              : "ใช้เฉพาะเมื่อทีมของคุณต้องเก็บรหัสหมวดหมู่หรือรหัสสินค้าไว้ภายใน"
      })
    );

    const isCaptchaFailure = (error: unknown) =>
      error instanceof ApiError &&
      (error.code === "CAPTCHA_REQUIRED" || error.code === "CAPTCHA_FAILED" || error.code === "CAPTCHA_SERVICE_ERROR");

    const captchaLanguage = (value: LocaleCode): "cn" | "en" => (value === "zh-CN" ? "cn" : "en");

    const ensureCaptchaScript = async () => {
      if (!requiresEmailCaptcha.value) {
        return;
      }
      if (typeof window === "undefined") {
        throw new Error("Captcha is only available in the browser.");
      }
      if (window.initAliyunCaptcha) {
        if (!captchaScriptLoadedAt) {
          captchaScriptLoadedAt = Date.now();
        }
        return;
      }
      if (captchaScriptPromise) {
        return captchaScriptPromise;
      }

      window.AliyunCaptchaConfig = {
        region: bootstrap.captcha.region,
        prefix: bootstrap.captcha.prefix
      };

      captchaScriptPromise = new Promise<void>((resolve, reject) => {
        const existing = document.getElementById(CAPTCHA_SCRIPT_ID) as HTMLScriptElement | null;
        if (existing) {
          existing.addEventListener("load", () => resolve(), { once: true });
          existing.addEventListener("error", () => reject(new Error("Failed to load captcha script.")), { once: true });
          return;
        }
        const script = document.createElement("script");
        script.id = CAPTCHA_SCRIPT_ID;
        script.src = "https://o.alicdn.com/captcha-frontend/aliyunCaptcha/AliyunCaptcha.js";
        script.async = true;
        script.onload = () => {
          captchaScriptLoadedAt = Date.now();
          resolve();
        };
        script.onerror = () => reject(new Error("Failed to load captcha script."));
        document.head.appendChild(script);
      });

      return captchaScriptPromise;
    };

    const ensureCaptchaReady = async () => {
      if (!requiresEmailCaptcha.value) {
        return;
      }

      await ensureCaptchaScript();
      const warmupDelay = 2100 - (Date.now() - captchaScriptLoadedAt);
      if (warmupDelay > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, warmupDelay));
      }
      await new Promise((resolve) => window.setTimeout(resolve, 0));

      const nextKey = `${bootstrap.captcha.region}:${bootstrap.captcha.prefix}:${bootstrap.captcha.sceneId}:${captchaLanguage(locale.value)}`;
      if (captchaReady && captchaInitKey === nextKey) {
        return;
      }
      if (captchaInitPromise && captchaInitKey === nextKey) {
        return captchaInitPromise;
      }

      window.AliyunCaptchaConfig = {
        region: bootstrap.captcha.region,
        prefix: bootstrap.captcha.prefix
      };

      captchaInitKey = nextKey;
      captchaInitPromise = new Promise<void>((resolve, reject) => {
        if (typeof window.initAliyunCaptcha !== "function") {
          reject(new Error("Captcha initialization function is unavailable."));
          return;
        }
        let settled = false;
        const failInit = (message: string) => {
          if (settled) {
            return;
          }
          settled = true;
          captchaReady = false;
          captchaInstance = null;
          captchaInitPromise = null;
          reject(new Error(message));
        };
        try {
          window.initAliyunCaptcha({
            SceneId: bootstrap.captcha.sceneId,
            mode: "popup",
            element: `#${CAPTCHA_ELEMENT_ID}`,
            button: `#${CAPTCHA_TRIGGER_ID}`,
            captchaVerifyCallback: async (captchaVerifyParam: string) => {
              if (!pendingCaptchaRunner) {
                return { captchaResult: false, bizResult: false };
              }
              const runner = pendingCaptchaRunner;
              pendingCaptchaRunner = null;
              return runner(captchaVerifyParam);
            },
            onBizResultCallback: () => undefined,
            getInstance: (instance: { show?: () => void }) => {
              if (settled) {
                return;
              }
              settled = true;
              captchaInstance = instance;
              captchaReady = true;
              resolve();
            },
            slideStyle: {
              width: 360,
              height: 40
            },
            language: captchaLanguage(locale.value),
            onError: () => {
              failInit(
                localeText({
                  en: "Security verification could not be initialized. Please refresh and try again.",
                  "zh-CN": "安全验证码初始化失败，请刷新页面后重试。",
                  "ms-MY": "Captcha keselamatan gagal dimulakan. Muat semula halaman dan cuba lagi.",
                  "id-ID": "Captcha keamanan gagal dimuat. Segarkan halaman lalu coba lagi.",
                  "th-TH": "ไม่สามารถเริ่มต้นการยืนยันความปลอดภัยได้ กรุณารีเฟรชหน้าแล้วลองใหม่"
                })
              );
            }
          });
        } catch (error) {
          failInit(error instanceof Error ? error.message : "Captcha initialization failed.");
        }
      });

      return captchaInitPromise;
    };

    const runWithEmailCaptcha = async (
      onFailure: (message: string) => void,
      runner: (captchaVerifyParam?: string) => Promise<void>
    ) => {
      if (!requiresEmailCaptcha.value) {
        try {
          await runner();
        } catch (error) {
          onFailure(error instanceof Error ? error.message : copy("serviceUnavailable"));
        }
        return;
      }

      try {
        await ensureCaptchaReady();
      } catch (error) {
        onFailure(
          error instanceof Error
            ? error.message
            : localeText({
                en: "Security verification is temporarily unavailable.",
                "zh-CN": "安全验证码暂时不可用。",
                "ms-MY": "Captcha keselamatan tidak tersedia buat sementara waktu.",
                "id-ID": "Captcha keamanan sedang tidak tersedia.",
                "th-TH": "การยืนยันความปลอดภัยไม่พร้อมใช้งานชั่วคราว"
              })
        );
        return;
      }

      try {
        await new Promise<void>((resolve, reject) => {
          pendingCaptchaRunner = async (captchaVerifyParam: string) => {
            try {
              await runner(captchaVerifyParam);
              resolve();
              return { captchaResult: true, bizResult: true };
            } catch (error) {
              const message = error instanceof Error ? error.message : copy("serviceUnavailable");
              onFailure(message);
              resolve();
              return {
                captchaResult: !isCaptchaFailure(error),
                bizResult: false
              };
            }
          };

          const trigger = document.getElementById(CAPTCHA_TRIGGER_ID) as HTMLButtonElement | null;
          if (!trigger) {
            pendingCaptchaRunner = null;
            reject(new Error("Captcha trigger element was not found."));
            return;
          }
          if (typeof captchaInstance?.show === "function") {
            captchaInstance.show();
          } else {
            trigger.click();
          }
        });
      } catch (error) {
        onFailure(error instanceof Error ? error.message : copy("serviceUnavailable"));
      }
    };

    const clearVerificationCooldown = () => {
      if (verifyCooldownTimer) {
        clearInterval(verifyCooldownTimer);
        verifyCooldownTimer = null;
      }
    };

    const clearPasswordResetCooldown = () => {
      if (passwordResetCooldownTimer) {
        clearInterval(passwordResetCooldownTimer);
        passwordResetCooldownTimer = null;
      }
    };

    const applyVerificationCooldown = (seconds?: number) => {
      if (!seconds || seconds <= 0) {
        return;
      }
      clearVerificationCooldown();
      verifyCooldownRemaining.value = seconds;
      verifyCooldownTimer = setInterval(() => {
        verifyCooldownRemaining.value = Math.max(0, verifyCooldownRemaining.value - 1);
        if (verifyCooldownRemaining.value <= 0) {
          clearVerificationCooldown();
        }
      }, 1000);
    };

    const applyPasswordResetCooldown = (seconds?: number) => {
      if (!seconds || seconds <= 0) {
        return;
      }
      clearPasswordResetCooldown();
      passwordResetCooldownRemaining.value = seconds;
      passwordResetCooldownTimer = setInterval(() => {
        passwordResetCooldownRemaining.value = Math.max(0, passwordResetCooldownRemaining.value - 1);
        if (passwordResetCooldownRemaining.value <= 0) {
          clearPasswordResetCooldown();
        }
      }, 1000);
    };

    const describeVerificationDelivery = (delivery?: { delivered: boolean; reason?: string; cooldownSeconds?: number }) => {
      if (!delivery) {
        return "";
      }
      if (delivery.delivered) {
        applyVerificationCooldown(delivery.cooldownSeconds);
        return localeText({
          en: delivery.cooldownSeconds ? `Verification email sent. Please wait ${delivery.cooldownSeconds}s before requesting another code.` : labels.value.verifyHint,
          "zh-CN": delivery.cooldownSeconds ? `验证码邮件已发送。请等待 ${delivery.cooldownSeconds} 秒后再申请新的验证码。` : labels.value.verifyHint,
          "ms-MY": delivery.cooldownSeconds ? `E-mel pengesahan telah dihantar. Tunggu ${delivery.cooldownSeconds}s sebelum meminta kod baharu.` : labels.value.verifyHint,
          "id-ID": delivery.cooldownSeconds ? `Email verifikasi sudah dikirim. Tunggu ${delivery.cooldownSeconds} dtk sebelum meminta kode baru.` : labels.value.verifyHint,
          "th-TH": delivery.cooldownSeconds ? `ส่งอีเมลยืนยันแล้ว โปรดรอ ${delivery.cooldownSeconds} วินาทีก่อนขอรหัสใหม่` : labels.value.verifyHint
        });
      }
      if (delivery.reason === "cooldown") {
        applyVerificationCooldown(delivery.cooldownSeconds);
        const seconds = delivery.cooldownSeconds ?? 60;
        return localeText({
          en: `Please wait ${seconds}s before requesting another verification email.`,
          "zh-CN": `请等待 ${seconds} 秒后再重新发送验证码邮件。`,
          "ms-MY": `Sila tunggu ${seconds}s sebelum meminta e-mel pengesahan baharu.`,
          "id-ID": `Tunggu ${seconds} dtk sebelum meminta email verifikasi baru.`,
          "th-TH": `โปรดรอ ${seconds} วินาทีก่อนขออีเมลยืนยันอีกครั้ง`
        });
      }
      if (delivery.reason === "not_configured") {
        return labels.value.verifyMailMissing;
      }
      return labels.value.verifyHint;
    };

    const refreshAuthStatus = () => {
      authStatus.value = "";
    };

    const resetPasswordChangeForm = () => {
      passwordChange.currentPassword = "";
      passwordChange.password = "";
      passwordChange.confirmPassword = "";
    };

    const resetPasswordFlow = (options: { email?: string; requested?: boolean } = {}) => {
      passwordReset.email = options.email ?? auth.email.trim().toLowerCase();
      passwordReset.code = "";
      passwordReset.password = "";
      passwordReset.confirmPassword = "";
      passwordReset.requested = Boolean(options.requested);
      passwordResetCooldownRemaining.value = 0;
      clearPasswordResetCooldown();
    };

    const resolvePageFromPath = (pathname: string): AppPage => {
      if (pathname.startsWith("/admin")) {
        return "admin";
      }
      if (pathname.startsWith("/billing")) {
        return "billing";
      }
      if (pathname.startsWith("/product")) {
        return "product";
      }
      if (pathname.startsWith("/pricing")) {
        return "pricing";
      }
      if (pathname.startsWith("/privacy")) {
        return "privacy";
      }
      if (pathname.startsWith("/terms")) {
        return "terms";
      }
      if (pathname.startsWith("/refunds")) {
        return "refunds";
      }
      if (pathname.startsWith("/contact")) {
        return "contact";
      }
      if (pathname.startsWith("/workspace/history")) {
        return "history";
      }
      if (pathname.startsWith("/workspace")) {
        return "workspace";
      }
      if (pathname.startsWith("/rules")) {
        return "rules";
      }
      return "home";
    };

    const pagePath = (page: AppPage): string => {
      switch (page) {
        case "product":
          return "/product";
        case "pricing":
          return "/pricing";
        case "privacy":
          return "/privacy";
        case "terms":
          return "/terms";
        case "refunds":
          return "/refunds";
        case "contact":
          return "/contact";
        case "billing":
          return "/billing";
        case "admin":
          return "/admin";
        case "workspace":
          return "/workspace";
        case "history":
          return "/workspace/history";
        case "rules":
          return "/rules";
        default:
          return "/";
      }
    };

    const navigateTo = (page: AppPage) => {
      editorGuideOpen.value = false;
      currentPage.value = page;
      const nextPath = pagePath(page);
      if (window.location.pathname !== nextPath) {
        window.history.pushState({}, "", nextPath);
      }
      trackPageView(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const updateAuthSearchParams = (nextAuthMode?: "reset", email?: string) => {
      const url = new URL(window.location.href);
      if (nextAuthMode === "reset") {
        url.searchParams.set("auth", "reset");
        if (email?.trim()) {
          url.searchParams.set("email", email.trim().toLowerCase());
        } else {
          url.searchParams.delete("email");
        }
      } else {
        url.searchParams.delete("auth");
        url.searchParams.delete("email");
      }
      const nextUrl = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState({}, "", nextUrl);
    };

    const openAuthModal = (mode: AuthMode) => {
      authMode.value = mode;
      if (mode !== "reset") {
        resetPasswordFlow({ email: auth.email.trim().toLowerCase(), requested: false });
        updateAuthSearchParams();
      }
      if (auth.user) {
        resetPasswordChangeForm();
        authStatus.value = "";
      }
      authDialogOpen.value = true;
      if (requiresEmailCaptcha.value) {
        void ensureCaptchaReady().catch((error) => {
          authStatus.value = error instanceof Error ? error.message : copy("serviceUnavailable");
        });
      }
    };

    const openPasswordReset = (email?: string, requested = false) => {
      authMode.value = "reset";
      authStatus.value = "";
      resetPasswordFlow({ email, requested });
      authDialogOpen.value = true;
      updateAuthSearchParams("reset", passwordReset.email);
      if (requiresEmailCaptcha.value) {
        void ensureCaptchaReady().catch((error) => {
          authStatus.value = error instanceof Error ? error.message : copy("serviceUnavailable");
        });
      }
    };

    const closeAuthModal = () => {
      authDialogOpen.value = false;
      if (authMode.value === "reset") {
        updateAuthSearchParams();
      }
    };

    const syncAuthModalFromLocation = () => {
      const params = new URLSearchParams(window.location.search);
      if (params.get("auth") === "reset") {
        openPasswordReset(params.get("email") ?? auth.email, true);
      }
    };

    const localizeOpsStatus = (value?: string) => {
      switch ((value ?? "").toLowerCase()) {
        case "queued":
          return copy("statusQueued");
        case "processing":
        case "running":
          return copy("statusProcessing");
        case "completed":
          return copy("statusCompleted");
        case "failed":
          return copy("statusFailed");
        case "sync":
          return copy("triggerSync");
        case "pass":
        case "review":
        case "fail":
          return t(locale.value, `status_${value.toLowerCase()}`);
        default:
          return value ?? copy("notAvailable");
      }
    };

    const localizeRole = (value?: string) => {
      switch ((value ?? "").toLowerCase()) {
        case "owner":
          return locale.value === "zh-CN" ? "所有者" : locale.value === "ms-MY" ? "pemilik" : locale.value === "id-ID" ? "pemilik" : "owner";
        default:
          return value ?? copy("notAvailable");
      }
    };

    const localizePlan = (value?: string) => {
      switch ((value ?? "").toLowerCase()) {
        case "starter":
          return locale.value === "zh-CN" ? "免费版" : locale.value === "ms-MY" ? "starter" : locale.value === "id-ID" ? "starter" : "starter";
        case "growth":
          return locale.value === "zh-CN" ? "成长版" : locale.value === "ms-MY" ? "growth" : locale.value === "id-ID" ? "growth" : "growth";
        case "scale":
          return locale.value === "zh-CN" ? "团队版" : locale.value === "ms-MY" ? "scale" : locale.value === "id-ID" ? "scale" : "scale";
        default:
          return value ?? copy("notAvailable");
      }
    };

    const localizePlanPrice = (plan: Pick<PlanPreset, "monthlyPriceUsd">) => {
      if (plan.monthlyPriceUsd <= 0) {
        return localeText({
          en: "Free",
          "zh-CN": "免费",
          "ms-MY": "Percuma",
          "id-ID": "Gratis",
          "th-TH": "ฟรี"
        });
      }
      return localeText({
        en: `$${plan.monthlyPriceUsd}/mo`,
        "zh-CN": `$${plan.monthlyPriceUsd}/月`,
        "ms-MY": `$${plan.monthlyPriceUsd}/bln`,
        "id-ID": `$${plan.monthlyPriceUsd}/bln`,
        "th-TH": `$${plan.monthlyPriceUsd}/เดือน`
      });
    };

    const totalAmountLabel = () =>
      localeText({
        en: "Total due (tax included)",
        "zh-CN": "含税总额",
        "ms-MY": "Jumlah perlu dibayar (termasuk cukai)",
        "id-ID": "Total tagihan (termasuk pajak)",
        "th-TH": "ยอดรวมสุทธิรวมภาษี"
      });

    const workflowTitle = () =>
      localeText({
        en: "One workspace for drafting, rule checks, and customer previews.",
        "zh-CN": "一个工作空间，完成起草、规则校验与客户预览。",
        "ms-MY": "Satu ruang kerja untuk draf, semakan peraturan, dan pratonton pelanggan.",
        "id-ID": "Satu workspace untuk draft, pemeriksaan aturan, dan pratinjau pelanggan.",
        "th-TH": "เวิร์กสเปซเดียวสำหรับร่างเอกสาร ตรวจตามกฎ และพรีวิวสำหรับลูกค้า"
      });

    const workflowSubtitle = () =>
      localeText({
        en: "Manual entry and OCR / PDF import feed the same invoice draft, so your team can review one clean record instead of switching between disconnected flows.",
        "zh-CN": "手工录入与 OCR / PDF 导入都会回到同一份发票草稿，团队只需要围绕一条清晰流程协作。",
        "ms-MY": "Input manual dan import OCR / PDF memasuki draf invois yang sama, jadi pasukan anda hanya perlu menyemak satu rekod yang jelas.",
        "id-ID": "Input manual dan impor OCR / PDF akan masuk ke draft invoice yang sama, jadi tim Anda cukup meninjau satu alur yang rapi.",
        "th-TH": "การกรอกเองและการนำเข้า OCR / PDF จะกลับมาที่ร่างฉบับเดียวกัน ทำให้ทีมของคุณตรวจทานผ่านกระบวนการที่ชัดเจนเพียงชุดเดียว"
      });

    const homeWorkflowSteps = computed<WorkflowStep[]>(() => [
      {
        index: "01",
        title: localeText({ en: "Create the invoice draft", "zh-CN": "创建发票草稿", "ms-MY": "Cipta draf invois", "id-ID": "Buat draft invoice", "th-TH": "สร้างร่างเอกสาร" }),
        body: localeText({
          en: "Choose the target market, then complete invoice basics, seller details, buyer details, and line items.",
          "zh-CN": "先选择目标市场，再完善发票基础信息、买卖双方主体和行项目。",
          "ms-MY": "Pilih pasaran sasaran, kemudian lengkapkan maklumat asas invois, butiran penjual/pembeli, dan item invois.",
          "id-ID": "Pilih pasar tujuan, lalu lengkapi informasi dasar invoice, data penjual/pembeli, dan item invoice.",
          "th-TH": "เลือกตลาดเป้าหมาย แล้วกรอกข้อมูลหลักของเอกสาร ข้อมูลผู้ขาย ผู้ซื้อ และรายการสินค้า/บริการ"
        })
      },
      {
        index: "02",
        title: localeText({ en: "Accelerate with OCR / AI", "zh-CN": "用 OCR / AI 加速录入", "ms-MY": "Percepat dengan OCR / AI", "id-ID": "Percepat dengan OCR / AI", "th-TH": "เร่งงานด้วย OCR / AI" }),
        body: localeText({
          en: "If you already have source documents, upload an image or PDF, or paste OCR text to turn them into an editable draft.",
          "zh-CN": "如果你已经有原始票据，可上传图片或 PDF，或粘贴 OCR 文本，将其转换为可编辑草稿。",
          "ms-MY": "Jika anda sudah ada dokumen asal, muat naik imej atau PDF, atau tampal teks OCR untuk menukarnya kepada draf boleh sunting.",
          "id-ID": "Jika Anda sudah punya dokumen sumber, unggah gambar atau PDF, atau tempel teks OCR untuk mengubahnya menjadi draft yang bisa diedit.",
          "th-TH": "หากมีเอกสารต้นทางอยู่แล้ว คุณสามารถอัปโหลดรูปหรือ PDF หรือวางข้อความ OCR เพื่อแปลงเป็นร่างที่แก้ไขต่อได้"
        })
      },
      {
        index: "03",
        title: localeText({ en: "Validate and preview", "zh-CN": "校验并预览", "ms-MY": "Sahkan dan pratonton", "id-ID": "Validasi dan pratinjau", "th-TH": "ตรวจสอบและพรีวิว" }),
        body: localeText({
          en: "Run compliance checks, open the customer preview page, and keep traceable records for your team.",
          "zh-CN": "执行合规检查、打开客户预览页，并为团队保留可追踪的使用记录。",
          "ms-MY": "Jalankan semakan pematuhan, buka halaman pratonton pelanggan, dan simpan rekod yang boleh dijejaki untuk pasukan anda.",
          "id-ID": "Jalankan pemeriksaan kepatuhan, buka halaman pratinjau pelanggan, dan simpan catatan yang dapat ditelusuri untuk tim Anda.",
          "th-TH": "ตรวจสอบตามกฎ สร้างหน้าพรีวิวสำหรับลูกค้า และเก็บประวัติการใช้งานที่ตรวจสอบย้อนหลังได้"
        })
      }
    ]);

    const editorTabDefaultFieldId = (tab: EditorTab) => {
      switch (tab) {
        case "parties":
          return "seller-legal-name";
        case "items":
          return "invoice-item-0-description";
        case "intake":
          return "draft-notes";
        default:
          return "invoice-number";
      }
    };

    const openEditorLocation = (tab: EditorTab, fieldId?: string) => {
      editorTab.value = tab;
      sidebarTab.value = "result";
      navigateTo("workspace");
      const targetId = fieldId ?? editorTabDefaultFieldId(tab);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const target = document.getElementById(targetId) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
          if (!target) {
            return;
          }
          target.scrollIntoView({ behavior: "smooth", block: "center" });
          try {
            target.focus({ preventScroll: true });
          } catch {
            target.focus();
          }
        });
      });
    };

    const openPreviewDialog = () => {
      if (!previewHtml.value) {
        return;
      }
      previewDialogOpen.value = true;
    };

    const showPreviewHtml = (html: string) => {
      previewHtml.value = html;
      openPreviewDialog();
    };

    const closePreviewDialog = () => {
      previewDialogOpen.value = false;
    };

    const closeEditorGuide = () => {
      editorGuideOpen.value = false;
    };

    const sectionLabelForTab = (tab: EditorTab) => {
      switch (tab) {
        case "parties":
          return labels.value.editorParties;
        case "items":
          return labels.value.editorItems;
        case "intake":
          return labels.value.editorIntake;
        default:
          return labels.value.editorBasics;
      }
    };

    const renderGuideSection = (title: string, cards: GuideCard[]) =>
      h("section", { class: "guide-drawer-section" }, [
        h("div", { class: "section-copy section-copy-compact" }, [h("h3", title)]),
        renderGuideGrid(cards)
      ]);

    const renderEditorGuideContent = () => {
      switch (editorTab.value) {
        case "parties":
          return h("div", { class: "guide-drawer-content" }, [
            renderGuideSection(copy("seller"), partyGuideCards.value.seller),
            renderGuideSection(copy("buyer"), partyGuideCards.value.buyer)
          ]);
        case "items":
          return h("div", { class: "guide-drawer-content" }, [renderGuideSection(copy("invoiceItems"), itemGuideCards.value)]);
        case "intake":
          return h("div", { class: "guide-drawer-content" }, [renderGuideSection(copy("uploadImageLabel"), intakeGuideCards.value)]);
        default:
          return h("div", { class: "guide-drawer-content" }, [renderGuideSection(copy("invoiceCore"), basicsGuideCards.value)]);
      }
    };

    const renderEditorGuideDrawer = () =>
      !editorGuideOpen.value
        ? null
        : h(
            "div",
            {
              class: "modal-backdrop guide-drawer-backdrop",
              onClick: (event: Event) => {
                if (event.target === event.currentTarget) {
                  closeEditorGuide();
                }
              }
            },
            [
              h("aside", { class: "guide-drawer-card" }, [
                h("div", { class: "guide-drawer-shell" }, [
                  h("div", { class: "panel-head guide-drawer-head" }, [
                    h("div", { class: "section-copy section-copy-compact" }, [
                      h("p", { class: "eyebrow" }, localeText({
                        en: "Section guidance",
                        "zh-CN": "当前区块说明",
                        "ms-MY": "Panduan bahagian",
                        "id-ID": "Panduan bagian",
                        "th-TH": "คำแนะนำของส่วนนี้"
                      })),
                      h("h2", sectionLabelForTab(editorTab.value)),
                      h("p", { class: "muted-text" }, localeText({
                        en: "Keep the editing canvas clean. Open the guidance only when you need market-specific reminders.",
                        "zh-CN": "让编辑画布保持干净，需要国家提醒时再打开这一层说明。",
                        "ms-MY": "Kekalkan kanvas edit supaya bersih. Buka panduan hanya apabila anda perlukan peringatan khusus pasaran.",
                        "id-ID": "Jaga kanvas edit tetap bersih. Buka panduan hanya saat Anda perlu pengingat khusus pasar.",
                        "th-TH": "ทำให้พื้นที่แก้ไขสะอาดไว้ก่อน แล้วค่อยเปิดคำแนะนำเมื่อคุณต้องการข้อเตือนตามประเทศ"
                      }))
                    ]),
                    h("button", { class: "icon-button", type: "button", onClick: closeEditorGuide }, copy("close"))
                  ]),
                  h("div", { class: "chip-row guide-drawer-meta" }, [
                    h("span", { class: "status-badge" }, getCountryName(locale.value, country.value)),
                    h("span", { class: "status-badge" }, sectionLabelForTab(editorTab.value)),
                    h("span", { class: "status-badge" }, pack.value.rulebookVersion)
                  ]),
                  h("div", { class: "guide-drawer-scroll" }, [renderEditorGuideContent()])
                ])
              ])
            ]
          );

    const itemFieldLabel = (field: string) => {
      switch (field) {
        case "description":
          return copy("description");
        case "classificationCode":
          return copy("classCode");
        case "quantity":
          return copy("qty");
        case "unitPrice":
          return copy("unitPrice");
        case "taxRate":
          return copy("taxRate");
        case "amountExcludingTax":
          return copy("netAmount");
        case "taxAmount":
          return copy("taxAmount");
        case "totalAmount":
          return copy("lineTotal");
        default:
          return field;
      }
    };

    const resolveFindingTask = (finding: ComplianceFinding) => {
      const fieldPath = finding.fieldPath ?? "";
      const directFieldMap: Record<string, { tab: EditorTab; fieldId: string; fieldLabel: string }> = {
        invoiceNumber: { tab: "basics", fieldId: "invoice-number", fieldLabel: copy("invoiceNumber") },
        issueDate: { tab: "basics", fieldId: "issue-date", fieldLabel: copy("issueDate") },
        issueTime: { tab: "basics", fieldId: "issue-time", fieldLabel: copy("issueTime") },
        "totals.currency": { tab: "basics", fieldId: "invoice-currency", fieldLabel: copy("currency") },
        "seller.legalName": { tab: "parties", fieldId: "seller-legal-name", fieldLabel: `${copy("seller")} · ${copy("legalName")}` },
        "seller.registrationNumber": { tab: "parties", fieldId: "seller-registration-number", fieldLabel: `${copy("seller")} · ${sellerRegistrationLabel()}` },
        "seller.taxNumber": { tab: "parties", fieldId: "seller-tax-number", fieldLabel: `${copy("seller")} · ${sellerTaxLabel()}` },
        "seller.address": { tab: "parties", fieldId: "seller-address-line1", fieldLabel: `${copy("seller")} · ${copy("addressLine1")}` },
        "buyer.legalName": { tab: "parties", fieldId: "buyer-legal-name", fieldLabel: `${copy("buyer")} · ${copy("legalName")}` },
        "buyer.address": { tab: "parties", fieldId: "buyer-address-line1", fieldLabel: `${copy("buyer")} · ${copy("addressLine1")}` },
        "buyer.taxNumber": { tab: "parties", fieldId: "buyer-tax-number", fieldLabel: `${copy("buyer")} · ${buyerTaxLabel()}` },
        "buyer.phone": { tab: "parties", fieldId: "buyer-phone", fieldLabel: `${copy("buyer")} · ${copy("phone")}` },
        "countryExtensions.transactionCode": {
          tab: "basics",
          fieldId: "id-transaction-code",
          fieldLabel: localeText({
            en: "Tax invoice transaction code",
            "zh-CN": "税务发票交易代码",
            "ms-MY": "Kod transaksi invois cukai",
            "id-ID": "Kode transaksi faktur pajak",
            "th-TH": "รหัสประเภทธุรกรรมภาษี"
          })
        },
        "countryExtensions.etaxRegistered": {
          tab: "basics",
          fieldId: "th-etax-approved",
          fieldLabel: localeText({
            en: "Approved e-Tax / e-Receipt issuer",
            "zh-CN": "已获批准的 e-Tax / e-Receipt 发行资格",
            "ms-MY": "Pengeluar e-Tax / e-Receipt yang diluluskan",
            "id-ID": "Penerbit e-Tax / e-Receipt yang disetujui",
            "th-TH": "ผู้มีสิทธิออก e-Tax / e-Receipt"
          })
        },
        "countryExtensions.foreignCurrencyApproval": {
          tab: "basics",
          fieldId: "th-foreign-approval",
          fieldLabel: localeText({
            en: "Foreign language / currency approval",
            "zh-CN": "外文 / 外币批准确认",
            "ms-MY": "Kelulusan bahasa / mata wang asing",
            "id-ID": "Persetujuan bahasa / mata uang asing",
            "th-TH": "การอนุมัติให้ใช้ภาษาหรือสกุลเงินต่างประเทศ"
          })
        },
        notes: { tab: "intake", fieldId: "draft-notes", fieldLabel: copy("notesLabel") }
      };
      const directMatch = directFieldMap[fieldPath];
      if (directMatch) {
        return {
          ...directMatch,
          sectionLabel: sectionLabelForTab(directMatch.tab)
        };
      }

      const itemMatch = fieldPath.match(/^items\[(\d+)\]\.(.+)$/);
      if (itemMatch) {
        const index = Number(itemMatch[1]);
        const field = itemMatch[2];
        return {
          tab: "items" as EditorTab,
          fieldId: `invoice-item-${index}-${field}`,
          sectionLabel: sectionLabelForTab("items"),
          fieldLabel: `${copy("invoiceItems")} ${index + 1} · ${itemFieldLabel(field)}`
        };
      }

      if (finding.ruleId === "duplicate-invoice") {
        return {
          tab: "basics" as EditorTab,
          fieldId: "invoice-number",
          sectionLabel: sectionLabelForTab("basics"),
          fieldLabel: copy("invoiceNumber")
        };
      }

      if (finding.ruleId === "total-mismatch") {
        return {
          tab: "items" as EditorTab,
          fieldId: "invoice-item-0-totalAmount",
          sectionLabel: sectionLabelForTab("items"),
          fieldLabel: `${copy("invoiceItems")} 1 · ${copy("lineTotal")}`
        };
      }

      if (finding.ruleId === "tax-rate-mismatch" || finding.ruleId === "unregistered-tax-charged") {
        return {
          tab: "items" as EditorTab,
          fieldId: "invoice-item-0-taxRate",
          sectionLabel: sectionLabelForTab("items"),
          fieldLabel: `${copy("invoiceItems")} 1 · ${copy("taxRate")}`
        };
      }

      if (finding.ruleId === "tax-id-required") {
        return {
          tab: "parties" as EditorTab,
          fieldId: "seller-tax-number",
          sectionLabel: sectionLabelForTab("parties"),
          fieldLabel: `${copy("seller")} · ${copy("taxNumber")}`
        };
      }

      return null;
    };

    const findingFingerprint = (finding: ComplianceFinding) => `${finding.ruleId}::${finding.fieldPath ?? ""}`;
    const localFindingFingerprints = computed(() =>
      new Set(localResult.value.findings.filter((finding) => finding.category !== "threshold").map(findingFingerprint))
    );
    const findingTasks = computed(() =>
      actionableFindings.value
        .map((finding) => ({
          finding,
          task: resolveFindingTask(finding),
          resolved: activeResult.value !== localResult.value && !localFindingFingerprints.value.has(findingFingerprint(finding))
        }))
        .sort((left, right) => Number(left.resolved) - Number(right.resolved))
    );
    const visibleFindingTasks = computed(() => (findingsExpanded.value ? findingTasks.value : findingTasks.value.slice(0, 3)));
    const resolvedFindingCount = computed(() => findingTasks.value.filter((item) => item.resolved).length);
    const openFindingCount = computed(() => findingTasks.value.length - resolvedFindingCount.value);

    const sourceOverviewCards = computed(() => {
      const latestAccessed = currentSources.value[0]?.accessedOn ?? copy("notAvailable");
      const marketSummary =
        country.value === "SG"
          ? localeText({
              en: "Focuses on GST invoice content, registration triggers, InvoiceNow rollout, and zero-rating evidence.",
              "zh-CN": "重点覆盖 GST 发票内容、注册触发、InvoiceNow 时间线和零税率留档要求。",
              "ms-MY": "Memfokuskan kandungan invois GST, pencetus pendaftaran, garis masa InvoiceNow, dan bukti kadar sifar.",
              "id-ID": "Berfokus pada isi invoice GST, pemicu registrasi, rollout InvoiceNow, dan bukti tarif 0%.",
              "th-TH": "ครอบคลุมเนื้อหาใบกำกับ GST จุดที่ต้องจดทะเบียน เส้นเวลา InvoiceNow และหลักฐานสำหรับอัตรา 0%"
            })
          : country.value === "MY"
            ? localeText({
                en: "Combines Malaysia e-Invoice rollout, MyInvois schema rules, and SST thresholds in one library.",
                "zh-CN": "把马来西亚 e-Invoice 实施时间线、MyInvois 字段规则和 SST 阈值放在同一知识库里。",
                "ms-MY": "Menggabungkan garis masa e-Invois Malaysia, peraturan skema MyInvois, dan ambang SST dalam satu pustaka.",
                "id-ID": "Menggabungkan rollout e-Invoice Malaysia, aturan skema MyInvois, dan ambang SST dalam satu pustaka.",
                "th-TH": "รวมเส้นเวลา e-Invoice ของมาเลเซีย กฎ schema ของ MyInvois และเกณฑ์ SST ไว้ในคลังเดียว"
              })
            : country.value === "ID"
              ? localeText({
                  en: "Covers PKP registration, effective VAT rates, e-Faktur content, issuance channels, and export support.",
                  "zh-CN": "覆盖 PKP 注册门槛、有效 VAT 税率、e-Faktur 内容、出具渠道和出口支持材料。",
                  "ms-MY": "Meliputi pendaftaran PKP, kadar VAT berkesan, kandungan e-Faktur, saluran terbitan, dan bukti eksport.",
                  "id-ID": "Mencakup registrasi PKP, tarif PPN efektif, isi e-Faktur, kanal penerbitan, dan bukti ekspor.",
                  "th-TH": "ครอบคลุมการจดทะเบียน PKP อัตรา VAT ที่ใช้จริง เนื้อหา e-Faktur ช่องทางออกเอกสาร และหลักฐานส่งออก"
                })
              : localeText({
                  en: "Covers VAT threshold, invoice modes, Thai-language and THB requirements, and e-Tax eligibility.",
                  "zh-CN": "覆盖 VAT 门槛、泰国发票模式、泰文与 THB 要求，以及 e-Tax 发行资格。",
                  "ms-MY": "Meliputi ambang VAT, mod invois Thailand, keperluan bahasa Thai / THB, dan kelayakan e-Tax.",
                  "id-ID": "Mencakup ambang VAT, mode invoice Thailand, syarat bahasa Thai / THB, dan kelayakan e-Tax.",
                  "th-TH": "ครอบคลุมเกณฑ์ VAT รูปแบบเอกสารของไทย ข้อกำหนดภาษาไทย / THB และคุณสมบัติสำหรับ e-Tax"
                });

      return [
        {
          label: localeText({ en: "Current market", "zh-CN": "当前市场", "ms-MY": "Pasaran semasa", "id-ID": "Pasar saat ini", "th-TH": "ตลาดปัจจุบัน" }),
          value: getCountryName(locale.value, country.value),
          body: marketSummary
        },
        {
          label: localeText({ en: "Rulebook snapshot", "zh-CN": "规则快照", "ms-MY": "Snapshot peraturan", "id-ID": "Snapshot aturan", "th-TH": "สแนปชอตกฎ" }),
          value: pack.value.rulebookVersion,
          body: localeText({
            en: `This market library was rechecked against official sources on ${latestAccessed}.`,
            "zh-CN": `当前市场知识库已按官方来源重新核验，核验日期为 ${latestAccessed}。`,
            "ms-MY": `Pustaka pasaran ini telah disemak semula dengan sumber rasmi pada ${latestAccessed}.`,
            "id-ID": `Pustaka pasar ini telah dicek ulang terhadap sumber resmi pada ${latestAccessed}.`,
            "th-TH": `คลังความรู้ของตลาดนี้ถูกตรวจทานกับแหล่งทางการอีกครั้งเมื่อ ${latestAccessed}`
          })
        },
        {
          label: localeText({ en: "Official references", "zh-CN": "官方来源数", "ms-MY": "Rujukan rasmi", "id-ID": "Referensi resmi", "th-TH": "จำนวนแหล่งทางการ" }),
          value: String(currentSources.value.length),
          body: localeText({
            en: "Includes document content rules, registration thresholds, tax identity guidance, and electronic issuance references.",
            "zh-CN": "包含单据内容要求、注册门槛、税号身份规则以及电子开票参考。",
            "ms-MY": "Merangkumi keperluan kandungan dokumen, ambang pendaftaran, panduan identiti cukai, dan rujukan e-terbitan.",
            "id-ID": "Mencakup aturan isi dokumen, ambang registrasi, panduan identitas pajak, dan referensi penerbitan elektronik.",
            "th-TH": "รวมข้อกำหนดเนื้อหาเอกสาร เกณฑ์การจดทะเบียน แนวทางด้านเลขประจำตัวภาษี และแหล่งอ้างอิงการออกเอกสารอิเล็กทรอนิกส์"
          })
        }
      ];
    });

    const sourceLibrarySections = computed<{ title: string; body: string; sourceIds: string[] }[]>(() => {
      switch (country.value) {
        case "SG":
          return [
            {
              title: localeText({ en: "Core GST invoicing", "zh-CN": "GST 开票核心要求", "ms-MY": "Asas invois GST", "id-ID": "Dasar invoice GST", "th-TH": "ข้อกำหนดหลักของใบกำกับ GST" }),
              body: localeText({
                en: "Mandatory invoice fields, issuance timing, simplified-invoice treatment, and day-to-day GST operating duties.",
                "zh-CN": "覆盖发票必填字段、开票时间、简化发票路径，以及日常 GST 经营责任。",
                "ms-MY": "Meliputi medan wajib invois, masa terbitan, laluan invois ringkas, dan tanggungjawab operasi GST harian.",
                "id-ID": "Mencakup kolom wajib invoice, waktu penerbitan, jalur invoice sederhana, dan tanggung jawab operasional GST sehari-hari.",
                "th-TH": "ครอบคลุมข้อมูลบังคับของเอกสาร ช่วงเวลาออกเอกสาร แนวทางใบกำกับแบบย่อ และหน้าที่การดำเนินงาน GST รายวัน"
              }),
              sourceIds: ["SG_IRAS_INVOICING_CUSTOMERS", "SG_IRAS_GST_RESPONSIBILITIES"]
            },
            {
              title: localeText({ en: "Registration and InvoiceNow rollout", "zh-CN": "注册与 InvoiceNow 时间线", "ms-MY": "Pendaftaran dan garis masa InvoiceNow", "id-ID": "Registrasi dan timeline InvoiceNow", "th-TH": "การจดทะเบียนและเส้นเวลา InvoiceNow" }),
              body: localeText({
                en: "Threshold checks, prospective registration planning, and phased InvoiceNow requirement dates.",
                "zh-CN": "覆盖注册门槛、预测注册责任与 InvoiceNow 分阶段时间线。",
                "ms-MY": "Meliputi ambang pendaftaran, perancangan prospektif, dan tarikh berperingkat InvoiceNow.",
                "id-ID": "Mencakup ambang registrasi, perencanaan prospektif, dan tanggal bertahap InvoiceNow.",
                "th-TH": "ครอบคลุมเกณฑ์การจดทะเบียน การคาดการณ์ภาระจดทะเบียน และวันที่บังคับใช้ InvoiceNow แบบเป็นช่วง"
              }),
              sourceIds: ["SG_IRAS_GST_REGISTRATION", "SG_IRAS_GST_INVOICENOW_FAQ"]
            },
            {
              title: localeText({ en: "Cross-border and identity references", "zh-CN": "跨境与身份识别参考", "ms-MY": "Rujukan rentas sempadan dan identiti", "id-ID": "Referensi lintas batas dan identitas", "th-TH": "ข้อมูลอ้างอิงด้านข้ามพรมแดนและตัวตน" }),
              body: localeText({
                en: "Zero-rating support requirements and seller registration-format guidance such as UEN structure.",
                "zh-CN": "覆盖零税率支持材料与卖方注册号格式要求，如 UEN 结构。",
                "ms-MY": "Meliputi bukti kadar sifar dan panduan format nombor pendaftaran seperti UEN.",
                "id-ID": "Mencakup bukti tarif 0% dan panduan format nomor registrasi seperti UEN.",
                "th-TH": "ครอบคลุมหลักฐานสำหรับอัตรา 0% และแนวทางรูปแบบเลขจดทะเบียน เช่น UEN"
              }),
              sourceIds: ["SG_IRAS_INTL_SERVICES", "SG_IRAS_UEN_FORMAT"]
            }
          ];
        case "MY":
          return [
            {
              title: localeText({ en: "e-Invoice rollout and schema", "zh-CN": "e-Invoice 时间线与字段结构", "ms-MY": "Garis masa e-Invois dan skema", "id-ID": "Timeline e-Invoice dan skema", "th-TH": "เส้นเวลา e-Invoice และโครงสร้างข้อมูล" }),
              body: localeText({
                en: "Rollout scope, lower-turnover edge cases, and what the MyInvois payload must contain.",
                "zh-CN": "覆盖实施范围、低营业额边界场景以及 MyInvois 必填字段。",
                "ms-MY": "Meliputi skop pelaksanaan, kes tepi perolehan rendah, dan medan wajib MyInvois.",
                "id-ID": "Mencakup ruang lingkup implementasi, kasus omzet rendah, dan field wajib MyInvois.",
                "th-TH": "ครอบคลุมขอบเขตการเริ่มใช้ กรณีขอบของรายได้ต่ำ และข้อมูลบังคับของ MyInvois"
              }),
              sourceIds: ["MY_HASIL_EINVOICE_TIMELINE", "MY_HASIL_EINVOICE_FAQ", "MY_MYINVOIS_INVOICE_V11", "MY_MYINVOIS_VALIDATE_TIN"]
            },
            {
              title: localeText({ en: "SST thresholds and invoice duties", "zh-CN": "SST 门槛与开票义务", "ms-MY": "Ambang SST dan kewajipan invois", "id-ID": "Ambang SST dan kewajiban invoice", "th-TH": "เกณฑ์ SST และหน้าที่การออกเอกสาร" }),
              body: localeText({
                en: "Service-tax thresholds, 6% vs 8% rate handling, and prescribed particulars for SST invoices.",
                "zh-CN": "覆盖服务税门槛、6% 与 8% 税率路径，以及 SST 发票规定字段。",
                "ms-MY": "Meliputi ambang cukai perkhidmatan, laluan kadar 6% dan 8%, serta butiran wajib invois SST.",
                "id-ID": "Mencakup ambang pajak layanan, jalur tarif 6% dan 8%, serta rincian wajib invoice SST.",
                "th-TH": "ครอบคลุมเกณฑ์ภาษีบริการ เส้นทางอัตรา 6% และ 8% และข้อมูลบังคับของใบกำกับ SST"
              }),
              sourceIds: ["MY_MYSST_BACKGROUND", "MY_MYSST_TAX_INVOICES", "MY_MYSST_RETURN_PAYMENT_GUIDE", "MY_MYSST_REGISTRATION_TABLE", "MY_MYSST_IT_GUIDE"]
            }
          ];
        case "ID":
          return [
            {
              title: localeText({ en: "PKP and VAT rates", "zh-CN": "PKP 与 VAT 税率", "ms-MY": "PKP dan kadar VAT", "id-ID": "PKP dan tarif VAT", "th-TH": "PKP และอัตรา VAT" }),
              body: localeText({
                en: "The PKP threshold and the current effective VAT path, including 11% ordinary and 12% luxury treatment.",
                "zh-CN": "覆盖 PKP 门槛与当前有效 VAT 路径，包括普通 11% 与奢侈品 12%。",
                "ms-MY": "Meliputi ambang PKP dan laluan kadar VAT semasa, termasuk 11% biasa dan 12% untuk barangan mewah.",
                "id-ID": "Mencakup ambang PKP dan jalur tarif VAT saat ini, termasuk 11% biasa dan 12% untuk barang mewah.",
                "th-TH": "ครอบคลุมเกณฑ์ PKP และเส้นทางอัตรา VAT ที่ใช้จริง รวมถึง 11% แบบทั่วไปและ 12% สำหรับสินค้าหรู"
              }),
              sourceIds: ["ID_DJP_PPN_COLLECTION", "ID_DJP_PPN_RATE"]
            },
            {
              title: localeText({ en: "Tax invoice content and channels", "zh-CN": "税务发票内容与出具渠道", "ms-MY": "Kandungan invois cukai dan saluran", "id-ID": "Isi faktur dan kanal penerbitan", "th-TH": "เนื้อหาเอกสารภาษีและช่องทางออกเอกสาร" }),
              body: localeText({
                en: "Mandatory faktur particulars, retail exceptions, and the supported issuance channels.",
                "zh-CN": "覆盖 Faktur 必填内容、零售例外以及当前支持的出具渠道。",
                "ms-MY": "Meliputi butiran wajib faktur, pengecualian runcit, dan saluran terbitan semasa.",
                "id-ID": "Mencakup rincian wajib faktur, pengecualian ritel, dan kanal penerbitan yang didukung.",
                "th-TH": "ครอบคลุมรายละเอียดบังคับของ faktur ข้อยกเว้นแบบค้าปลีก และช่องทางที่รองรับในปัจจุบัน"
              }),
              sourceIds: ["ID_DJP_FAKTUR_REGULATION", "ID_DJP_FAKTUR_CHANNELS"]
            },
            {
              title: localeText({ en: "Identity and cross-border support", "zh-CN": "税号身份与跨境支持材料", "ms-MY": "Identiti cukai dan sokongan rentas sempadan", "id-ID": "Identitas pajak dan dukungan lintas batas", "th-TH": "ข้อมูลตัวตนภาษีและหลักฐานข้ามพรมแดน" }),
              body: localeText({
                en: "NPWP structure guidance and support for 0% VAT on exported services.",
                "zh-CN": "覆盖 NPWP 格式要求与服务出口 0% VAT 支持材料。",
                "ms-MY": "Meliputi struktur NPWP dan sokongan VAT 0% untuk eksport perkhidmatan.",
                "id-ID": "Mencakup struktur NPWP dan dukungan PPN 0% untuk ekspor jasa.",
                "th-TH": "ครอบคลุมโครงสร้าง NPWP และหลักฐานรองรับ VAT 0% สำหรับบริการส่งออก"
              }),
              sourceIds: ["ID_DJP_NPWP_16", "ID_DJP_EXPORT_SERVICES"]
            }
          ];
        default:
          return [
            {
              title: localeText({ en: "VAT threshold and rate", "zh-CN": "VAT 门槛与税率", "ms-MY": "Ambang VAT dan kadar", "id-ID": "Ambang VAT dan tarif", "th-TH": "เกณฑ์ VAT และอัตรา" }),
              body: localeText({
                en: "The THB 1.8 million VAT threshold and the currently extended 7% VAT rate.",
                "zh-CN": "覆盖 THB 1.8 million 的 VAT 门槛和当前延长适用的 7% VAT。",
                "ms-MY": "Meliputi ambang VAT THB 1.8 juta dan kadar VAT 7% yang masih dilanjutkan.",
                "id-ID": "Mencakup ambang VAT THB 1,8 juta dan tarif VAT 7% yang saat ini masih diperpanjang.",
                "th-TH": "ครอบคลุมเกณฑ์ VAT ที่ THB 1.8 ล้าน และอัตรา VAT 7% ที่ยังขยายเวลาใช้อยู่"
              }),
              sourceIds: ["TH_RD_VAT_OVERVIEW", "TH_RD_VAT_RATE_2026"]
            },
            {
              title: localeText({ en: "Tax invoice formats and identity", "zh-CN": "发票格式与身份字段", "ms-MY": "Format invois cukai dan identiti", "id-ID": "Format invoice pajak dan identitas", "th-TH": "รูปแบบเอกสารภาษีและข้อมูลตัวตน" }),
              body: localeText({
                en: "Standard vs abbreviated Thai invoice rules, Thai-language / THB expectations, and TIN format checks.",
                "zh-CN": "覆盖标准与简式泰国发票规则、泰文 / THB 要求，以及税号格式。",
                "ms-MY": "Meliputi peraturan invois Thailand standard / ringkas, keperluan bahasa Thai / THB, dan semakan TIN.",
                "id-ID": "Mencakup aturan invoice Thailand standar / ringkas, syarat bahasa Thai / THB, dan pemeriksaan format TIN.",
                "th-TH": "ครอบคลุมกฎของใบกำกับมาตรฐานและแบบย่อของไทย ข้อกำหนดภาษาไทย / THB และรูปแบบเลขผู้เสียภาษี"
              }),
              sourceIds: ["TH_RD_TAX_INVOICE_SECTIONS", "TH_RD_TIN_SERVICE"]
            },
            {
              title: localeText({ en: "Electronic issuance", "zh-CN": "电子开票路径", "ms-MY": "Terbitan elektronik", "id-ID": "Penerbitan elektronik", "th-TH": "การออกเอกสารอิเล็กทรอนิกส์" }),
              body: localeText({
                en: "Thailand e-Tax / e-Receipt eligibility, approved routes, and electronic issuance controls.",
                "zh-CN": "覆盖泰国 e-Tax / e-Receipt 资格、认可路径和电子出具要求。",
                "ms-MY": "Meliputi kelayakan e-Tax / e-Receipt Thailand, laluan yang diluluskan, dan kawalan terbitan elektronik.",
                "id-ID": "Mencakup kelayakan e-Tax / e-Receipt Thailand, jalur yang disetujui, dan kontrol penerbitan elektronik.",
                "th-TH": "ครอบคลุมคุณสมบัติ e-Tax / e-Receipt ของไทย ช่องทางที่ได้รับอนุมัติ และข้อกำหนดการออกเอกสารอิเล็กทรอนิกส์"
              }),
              sourceIds: ["TH_RD_ETAX_OVERVIEW"]
            }
          ];
      }
    });

    const sourceLibraryEntries = computed(() => {
      const sourceMap = new Map(currentSources.value.map((source) => [source.id, source] as const));
      const consumed = new Set<string>();
      const sections = sourceLibrarySections.value
        .map((section) => {
          const sources = section.sourceIds.map((id) => sourceMap.get(id)).filter(Boolean) as SourceReference[];
          sources.forEach((source) => consumed.add(source.id));
          return { ...section, sources };
        })
        .filter((section) => section.sources.length > 0);

      const uncategorized = currentSources.value.filter((source) => !consumed.has(source.id));
      if (uncategorized.length > 0) {
        sections.push({
          title: localeText({ en: "Additional references", "zh-CN": "补充来源", "ms-MY": "Rujukan tambahan", "id-ID": "Referensi tambahan", "th-TH": "แหล่งอ้างอิงเพิ่มเติม" }),
          body: localeText({
            en: "Additional references kept for edge cases and deeper review.",
            "zh-CN": "保留给边界场景和更深入复核使用的补充来源。",
            "ms-MY": "Rujukan tambahan untuk kes pinggiran dan semakan lebih mendalam.",
            "id-ID": "Referensi tambahan untuk kasus khusus dan peninjauan lebih mendalam.",
            "th-TH": "แหล่งอ้างอิงเพิ่มเติมสำหรับกรณีพิเศษและการตรวจทานเชิงลึก"
          }),
          sourceIds: uncategorized.map((source) => source.id),
          sources: uncategorized
        });
      }

      return sections;
    });

    const localizeBilling = (value?: string) => {
      switch ((value ?? "").toLowerCase()) {
        case "trial":
          return locale.value === "zh-CN" ? "试用中" : locale.value === "ms-MY" ? "percubaan" : locale.value === "id-ID" ? "trial" : "trial";
        case "active":
          return locale.value === "zh-CN" ? "已生效" : locale.value === "ms-MY" ? "aktif" : locale.value === "id-ID" ? "aktif" : "active";
        case "checkout_open":
          return locale.value === "zh-CN" ? "待支付" : locale.value === "ms-MY" ? "menunggu bayaran" : locale.value === "id-ID" ? "menunggu pembayaran" : "awaiting payment";
        case "refunded":
          return locale.value === "zh-CN" ? "已退款" : locale.value === "ms-MY" ? "dipulangkan" : locale.value === "id-ID" ? "sudah refund" : "refunded";
        case "refund_pending":
          return locale.value === "zh-CN" ? "退款处理中" : locale.value === "ms-MY" ? "refund diproses" : locale.value === "id-ID" ? "refund diproses" : "refund pending";
        case "canceled":
          return locale.value === "zh-CN" ? "已取消" : locale.value === "ms-MY" ? "dibatalkan" : locale.value === "id-ID" ? "dibatalkan" : "canceled";
        default:
          return value ?? copy("notAvailable");
      }
    };

    const localizeRisk = (value?: string) => {
      switch ((value ?? "").toLowerCase()) {
        case "high":
          return locale.value === "zh-CN" ? "高" : locale.value === "ms-MY" ? "tinggi" : locale.value === "id-ID" ? "tinggi" : "high";
        case "medium":
          return locale.value === "zh-CN" ? "中" : locale.value === "ms-MY" ? "sederhana" : locale.value === "id-ID" ? "sedang" : "medium";
        case "low":
          return locale.value === "zh-CN" ? "低" : locale.value === "ms-MY" ? "rendah" : locale.value === "id-ID" ? "rendah" : "low";
        default:
          return value ?? copy("notAvailable");
      }
    };

    const localizeJobType = (value?: string) => {
      switch ((value ?? "").toLowerCase()) {
        case "check":
          return locale.value === "zh-CN" ? "检查" : locale.value === "ms-MY" ? "semakan" : locale.value === "id-ID" ? "pemeriksaan" : "check";
        case "generate":
          return locale.value === "zh-CN" ? "生成" : locale.value === "ms-MY" ? "jana" : locale.value === "id-ID" ? "generate" : "generate";
        case "extract":
          return locale.value === "zh-CN" ? "抽取" : locale.value === "ms-MY" ? "ekstrak" : locale.value === "id-ID" ? "ekstraksi" : "extract";
        default:
          return value ?? copy("notAvailable");
      }
    };

    const localizeCountryLabel = (value?: string) => {
      if (value && COUNTRY_CODES.includes(value as CountryCode)) {
        return getCountryName(locale.value, value as CountryCode);
      }
      return value ?? copy("notAvailable");
    };

    const applySession = (data: { authenticated: boolean; user?: UserRecord; organizations?: OrganizationRecord[]; defaultOrganizationId?: string | null; verificationRequired?: boolean; isBillingAdmin?: boolean }) => {
      if (!data.authenticated || !data.user) {
        auth.user = null;
        auth.organizations = [];
        auth.isBillingAdmin = false;
        currentOrganizationId.value = "";
        api.setOrganizationId("");
        verifyStatus.value = "";
        verifyCooldownRemaining.value = 0;
        clearVerificationCooldown();
        resetPasswordFlow({ email: auth.email.trim().toLowerCase(), requested: false });
        resetPasswordChangeForm();
        refreshAuthStatus();
        return;
      }
      auth.user = data.user;
      auth.organizations = data.organizations ?? [];
      auth.isBillingAdmin = Boolean(data.isBillingAdmin);
      currentOrganizationId.value = data.defaultOrganizationId ?? data.organizations?.[0]?.id ?? "";
      api.setOrganizationId(currentOrganizationId.value);
      verifyStatus.value = data.verificationRequired ? labels.value.verifyHint : "";
      if (!data.verificationRequired) {
        verifyCooldownRemaining.value = 0;
        clearVerificationCooldown();
      }
      resetPasswordFlow({ email: data.user.email, requested: false });
      resetPasswordChangeForm();
      refreshAuthStatus();
    };

    const loadSession = async () => {
      try {
        const session = await api.session();
        applySession(session);
      } catch (error) {
        authStatus.value = error instanceof Error ? error.message : copy("serviceUnavailable");
      } finally {
        auth.ready = true;
      }
    };

    const replaceDraft = (next: InvoiceInput) => {
      const fresh = recalculateDraft({ ...next, country: country.value, locale: locale.value });
      Object.assign(draft, fresh);
      draft.seller = fresh.seller;
      draft.buyer = fresh.buyer;
      draft.items = fresh.items;
      draft.totals = fresh.totals;
      notesInput.value = (fresh.notes ?? []).join("\n");
    };

    const restoreSavedDraft = (next: InvoiceInput) => {
      const nextCountry = next.country;
      const nextLocale = LOCALES.includes((next.locale ?? locale.value) as LocaleCode) ? (next.locale as LocaleCode) : locale.value;
      suppressDraftReset.value = true;
      country.value = nextCountry;
      locale.value = nextLocale;
      api.setCountry(nextCountry);
      api.setLocale(nextLocale);
      window.localStorage.setItem(STORAGE_COUNTRY_KEY, nextCountry);
      window.localStorage.setItem(STORAGE_LOCALE_KEY, nextLocale);
      suppressDraftReset.value = false;
      replaceDraft({ ...next, country: nextCountry, locale: nextLocale });
      latestServerResult.value = null;
      previewDialogOpen.value = false;
      navigateTo("workspace");
      editorTab.value = "basics";
      sidebarTab.value = "result";
    };

    watch(country, (value) => {
      api.setCountry(value);
      window.localStorage.setItem(STORAGE_COUNTRY_KEY, value);
      if (suppressDraftReset.value) {
        return;
      }
      if (value !== "MY") {
        draft.eInvoiceRequested = false;
      }
      replaceDraft(makeDraft(value, locale.value));
      latestServerResult.value = null;
      previewHtml.value = "";
      previewDialogOpen.value = false;
    });

    watch(currentOrganizationId, (value) => {
      api.setOrganizationId(value);
      void loadOps();
      void loadAdminBilling();
    });

    watch(locale, () => {
      draft.locale = locale.value;
      api.setLocale(locale.value);
      window.localStorage.setItem(STORAGE_LOCALE_KEY, locale.value);
      captchaReady = false;
      captchaInitPromise = null;
      captchaInitKey = "";
      captchaInstance = null;
      refreshAuthStatus();
    });

    watch(
      () => draft.supplyNature,
      (value) => {
        if (value !== "reverse_charge") {
          draft.reverseCharge = false;
        }
      }
    );

    watch(
      () => draft.taxRegistered,
      (value) => {
        if (country.value !== "TH") {
          return;
        }
        if (!value) {
          draft.invoiceType = "commercial_invoice";
          draft.eInvoiceRequested = false;
          setCountryExtension("etaxRegistered", false);
          return;
        }
        if (!draft.invoiceType || draft.invoiceType === "commercial_invoice") {
          draft.invoiceType = "tax_invoice";
        }
      }
    );

    watch(historyTab, () => {
      historyExpanded.value = false;
    });

    const applyCalculatedTotals = () => {
      replaceDraft({ ...draft, locale: locale.value, notes: notesInput.value.split("\n").map((item) => item.trim()).filter(Boolean) });
    };

    const validateLogin = (): boolean => {
      const email = auth.email.trim();
      if (!email) {
        authStatus.value = copy("authValidationEmailRequired");
        return false;
      }
      if (!EMAIL_PATTERN.test(email)) {
        authStatus.value = copy("authValidationEmailInvalid");
        return false;
      }
      if (!auth.password) {
        authStatus.value = copy("authValidationPasswordRequired");
        return false;
      }
      return true;
    };

    const validateRegister = (): boolean => {
      const email = auth.email.trim();
      const displayName = auth.displayName.trim();
      const organizationName = auth.organizationName.trim();
      if (!email) {
        authStatus.value = copy("authValidationEmailRequired");
        return false;
      }
      if (!EMAIL_PATTERN.test(email)) {
        authStatus.value = copy("authValidationEmailInvalid");
        return false;
      }
      if (!auth.password) {
        authStatus.value = copy("authValidationPasswordRequired");
        return false;
      }
      if (auth.password.length < 8) {
        authStatus.value = copy("authValidationPasswordShort");
        return false;
      }
      if (!displayName) {
        authStatus.value = copy("authValidationDisplayNameRequired");
        return false;
      }
      if (displayName.length > DISPLAY_NAME_MAX_LENGTH) {
        authStatus.value = copy("authValidationDisplayNameTooLong", { max: DISPLAY_NAME_MAX_LENGTH });
        return false;
      }
      if (!organizationName) {
        authStatus.value = copy("authValidationOrganizationRequired");
        return false;
      }
      if (organizationName.length > ORGANIZATION_NAME_MAX_LENGTH) {
        authStatus.value = copy("authValidationOrganizationTooLong", { max: ORGANIZATION_NAME_MAX_LENGTH });
        return false;
      }
      return true;
    };

    const validatePasswordResetRequest = (): boolean => {
      const email = passwordReset.email.trim();
      if (!email) {
        authStatus.value = copy("authValidationEmailRequired");
        return false;
      }
      if (!EMAIL_PATTERN.test(email)) {
        authStatus.value = copy("authValidationEmailInvalid");
        return false;
      }
      return true;
    };

    const validatePasswordResetConfirm = (): boolean => {
      if (!validatePasswordResetRequest()) {
        return false;
      }
      if (!VERIFY_CODE_PATTERN.test(passwordReset.code.trim())) {
        authStatus.value = localeText({
          en: "Please enter the 6-digit reset code.",
          "zh-CN": "请输入 6 位重置验证码。",
          "ms-MY": "Sila masukkan kod tetapan semula 6 digit.",
          "id-ID": "Silakan masukkan kode reset 6 digit.",
          "th-TH": "กรุณากรอกรหัสรีเซ็ต 6 หลัก"
        });
        return false;
      }
      if (!passwordReset.password) {
        authStatus.value = copy("authValidationPasswordRequired");
        return false;
      }
      if (passwordReset.password.length < 8) {
        authStatus.value = copy("authValidationPasswordShort");
        return false;
      }
      if (passwordReset.password !== passwordReset.confirmPassword) {
        authStatus.value = localeText({
          en: "The two password entries do not match.",
          "zh-CN": "两次输入的密码不一致。",
          "ms-MY": "Kedua-dua kata laluan tidak sepadan.",
          "id-ID": "Kedua kata sandi tidak cocok.",
          "th-TH": "รหัสผ่านทั้งสองช่องไม่ตรงกัน"
        });
        return false;
      }
      return true;
    };

    const validatePasswordChange = (): boolean => {
      if (!passwordChange.currentPassword) {
        authStatus.value = copy("authValidationCurrentPasswordRequired");
        return false;
      }
      if (!passwordChange.password) {
        authStatus.value = copy("authValidationPasswordRequired");
        return false;
      }
      if (passwordChange.password.length < 8) {
        authStatus.value = copy("authValidationPasswordShort");
        return false;
      }
      if (!passwordChange.confirmPassword) {
        authStatus.value = copy("authValidationConfirmPasswordRequired");
        return false;
      }
      if (passwordChange.password !== passwordChange.confirmPassword) {
        authStatus.value = copy("authValidationPasswordMismatch");
        return false;
      }
      return true;
    };

    const withAuthLoading = async (runner: () => Promise<void>) => {
      auth.loading = true;
      try {
        await runner();
      } finally {
        auth.loading = false;
      }
    };

    const completeRegister = async (captchaVerifyParam?: string) => {
      const result = await api.register({
        email: auth.email,
        password: auth.password,
        displayName: auth.displayName,
        locale: locale.value,
        organizationName: auth.organizationName,
        organizationCountry: auth.organizationCountry,
        captchaVerifyParam
      });
      applySession(result);
      auth.password = "";
      verifyStatus.value = describeVerificationDelivery(result.verificationDelivery);
      updateAuthSearchParams();
      authDialogOpen.value = false;
      navigateTo("workspace");
    };

    const completeLogin = async (captchaVerifyParam?: string) => {
      const result = await api.login({
        email: auth.email,
        password: auth.password,
        captchaVerifyParam
      });
      applySession(result);
      auth.password = "";
      verifyStatus.value = result.verificationRequired ? describeVerificationDelivery(result.verificationDelivery) : "";
      updateAuthSearchParams();
      authDialogOpen.value = false;
      navigateTo("workspace");
    };

    const onRegister = async () => {
      if (!validateRegister()) {
        return;
      }
      authStatus.value = "";
      await runWithEmailCaptcha(
        (message) => {
          authStatus.value = message;
        },
        async (captchaVerifyParam) => {
          await withAuthLoading(() => completeRegister(captchaVerifyParam));
        }
      );
    };

    const onLogin = async () => {
      if (!validateLogin()) {
        return;
      }
      authStatus.value = "";
      try {
        await withAuthLoading(() => completeLogin());
      } catch (error) {
        if (requiresEmailCaptcha.value && error instanceof ApiError && error.code === "CAPTCHA_REQUIRED") {
          await runWithEmailCaptcha(
            (message) => {
              authStatus.value = message;
            },
            async (captchaVerifyParam) => {
              await withAuthLoading(() => completeLogin(captchaVerifyParam));
            }
          );
          return;
        }
        authStatus.value = error instanceof Error ? error.message : copy("serviceUnavailable");
      }
    };

    const onRequestPasswordReset = async () => {
      if (!validatePasswordResetRequest()) {
        return;
      }
      authStatus.value = "";
      await runWithEmailCaptcha(
        (message) => {
          authStatus.value = message;
        },
        async (captchaVerifyParam) => {
          await withAuthLoading(async () => {
            const result = await api.forgotPassword({
              email: passwordReset.email.trim(),
              captchaVerifyParam
            });
            passwordReset.requested = true;
            auth.email = passwordReset.email.trim().toLowerCase();
            applyPasswordResetCooldown(result.cooldownSeconds);
            authStatus.value = localeText({
              en: "If an account exists for this email, a reset code has been sent.",
              "zh-CN": "如果这个邮箱对应的账户存在，重置验证码已经发送。",
              "ms-MY": "Jika akaun bagi e-mel ini wujud, kod tetapan semula telah dihantar.",
              "id-ID": "Jika akun untuk email ini ada, kode reset sudah dikirim.",
              "th-TH": "หากมีบัญชีสำหรับอีเมลนี้ ระบบได้ส่งรหัสรีเซ็ตแล้ว"
            });
          });
        }
      );
    };

    const onCompletePasswordReset = async () => {
      if (!validatePasswordResetConfirm()) {
        return;
      }
      authStatus.value = "";
      await withAuthLoading(async () => {
        await api.resetPassword({
          email: passwordReset.email.trim(),
          code: passwordReset.code.trim(),
          password: passwordReset.password
        });
        auth.email = passwordReset.email.trim().toLowerCase();
        auth.password = "";
        authMode.value = "login";
        resetPasswordFlow({ email: auth.email, requested: false });
        updateAuthSearchParams();
        authStatus.value = localeText({
          en: "Password has been reset. You can sign in with the new password now.",
          "zh-CN": "密码已经重置成功，现在可以使用新密码登录。",
          "ms-MY": "Kata laluan telah ditetapkan semula. Anda kini boleh log masuk dengan kata laluan baharu.",
          "id-ID": "Kata sandi sudah direset. Sekarang Anda bisa masuk dengan kata sandi baru.",
          "th-TH": "รีเซ็ตรหัสผ่านสำเร็จแล้ว ตอนนี้คุณสามารถเข้าสู่ระบบด้วยรหัสผ่านใหม่ได้"
        });
      });
    };

    const onChangePassword = async () => {
      if (!validatePasswordChange()) {
        return;
      }
      authStatus.value = "";
      await withAuthLoading(async () => {
        await api.changePassword({
          currentPassword: passwordChange.currentPassword,
          password: passwordChange.password
        });
        resetPasswordChangeForm();
        authStatus.value = copy("passwordChangedSuccess");
      });
    };

    const onLogout = async () => {
      auth.loading = true;
      try {
        await api.logout();
        applySession({ authenticated: false });
        latestServerResult.value = null;
        previewHtml.value = "";
        previewDialogOpen.value = false;
        billingStatus.value = "";
        adminStatus.value = "";
        adminOverview.value = null;
        adminOrders.value = [];
        authMode.value = "login";
        resetPasswordFlow({ email: auth.email.trim().toLowerCase(), requested: false });
        updateAuthSearchParams();
        authDialogOpen.value = false;
      } catch (error) {
        authStatus.value = error instanceof Error ? error.message : copy("serviceUnavailable");
      } finally {
        auth.loading = false;
      }
    };

    const onVerifyEmail = async () => {
      if (!VERIFY_CODE_PATTERN.test(verifyCode.value.trim())) {
        verifyStatus.value = localeText({
          en: "Please enter the 6-digit verification code.",
          "zh-CN": "请输入 6 位验证码。",
          "ms-MY": "Sila masukkan kod pengesahan 6 digit.",
          "id-ID": "Silakan masukkan kode verifikasi 6 digit.",
          "th-TH": "กรุณากรอกรหัสยืนยัน 6 หลัก"
        });
        return;
      }
      auth.verifying = true;
      try {
        const result = await api.verifyEmail({ code: verifyCode.value.trim() });
        applySession({
          authenticated: Boolean(result.user),
          user: result.user,
          organizations: result.organizations as OrganizationRecord[] | undefined,
          defaultOrganizationId: result.defaultOrganizationId,
          verificationRequired: result.verificationRequired
        });
        verifyStatus.value = labels.value.verifyDone;
        verifyCode.value = "";
      } catch (error) {
        verifyStatus.value = error instanceof Error ? error.message : copy("serviceUnavailable");
      } finally {
        auth.verifying = false;
      }
    };

    const onResendVerification = async () => {
      verifyStatus.value = "";
      await runWithEmailCaptcha(
        (message) => {
          verifyStatus.value = message;
        },
        async (captchaVerifyParam) => {
          auth.verifying = true;
          try {
            const delivery = await api.resendVerification({ captchaVerifyParam });
            verifyStatus.value = describeVerificationDelivery(delivery);
          } finally {
            auth.verifying = false;
          }
        }
      );
    };

    const onAddItem = () => {
      draft.items.unshift({
        description: "",
        classificationCode: draft.country === "MY" ? "004" : draft.country === "ID" ? "GEN" : undefined,
        quantity: 1,
        unitPrice: 0,
        amountExcludingTax: 0,
        taxRate: getCountryDefaultRate(draft.country, Boolean(draft.taxRegistered), draft.supplyNature ?? "domestic", draft.businessActivity ?? "general_services"),
        taxAmount: 0,
        totalAmount: 0
      });
      applyCalculatedTotals();
    };

    const onRemoveItem = (index: number) => {
      if (draft.items.length === 1) {
        return;
      }
      const confirmed = window.confirm(
        localeText({
          en: "Delete this line item? This action cannot be undone.",
          "zh-CN": "确定删除这一条行项目吗？删除后无法恢复。",
          "ms-MY": "Padam item invois ini? Tindakan ini tidak boleh dibuat asal.",
          "id-ID": "Hapus item invoice ini? Tindakan ini tidak dapat dibatalkan.",
          "th-TH": "ลบรายการนี้ใช่หรือไม่ การกระทำนี้ไม่สามารถย้อนกลับได้"
        })
      );
      if (!confirmed) {
        return;
      }
      draft.items.splice(index, 1);
      applyCalculatedTotals();
    };

    const ensureVerifiedBeforeAction = (): boolean => {
      if (emailVerified.value) {
        return true;
      }
      verifyStatus.value = bootstrap.mailConfigured ? labels.value.verifyHint : labels.value.verifyMailMissing;
      apiStatus.value = verifyStatus.value;
      navigateTo("workspace");
      return false;
    };

    const onExtract = async () => {
      if (!signedIn.value) {
        extractStatus.value = copy("authRequired");
        openAuthModal("login");
        return;
      }
      if (!ensureVerifiedBeforeAction()) {
        extractStatus.value = verifyStatus.value;
        return;
      }
      if (!bootstrap.aiAvailable) {
        extractStatus.value = copy("serviceUnavailable");
        return;
      }
      extractStatus.value = copy("extracting");
      try {
        const response = await api.extract({
          country: country.value,
          locale: locale.value,
          text: extractText.value,
          fileBase64: uploadedFileData.value || undefined,
          fileName: uploadedFileName.value || undefined,
          imageBase64: uploadedMime.value.startsWith("image/") ? uploadedFileData.value || undefined : undefined,
          mimeType: uploadedMime.value || undefined
        });
        if (response.draft) {
          replaceDraft(mergeExtractedDraft(draft, response.draft as Partial<InvoiceInput>));
          extractStatus.value = response.reason ?? localeText({
            en: "Draft imported. Please review invoice basics, parties, and line items before using the preview output.",
            "zh-CN": "草稿已导入。请继续检查基础信息、买卖双方和行项目后，再使用预览页输出。",
            "ms-MY": "Draf telah diimport. Sila semak maklumat asas, pihak invois, dan item sebelum menggunakan hasil pratonton.",
            "id-ID": "Draft sudah diimpor. Silakan periksa informasi dasar, pihak invoice, dan item sebelum menggunakan hasil pratinjau.",
            "th-TH": "นำเข้าร่างเรียบร้อยแล้ว โปรดตรวจทานข้อมูลหลัก คู่ค้า และรายการก่อนใช้หน้าพรีวิว"
          });
          editorTab.value = "basics";
        } else {
          extractStatus.value = response.reason ?? copy("extractNoDraft");
        }
        await loadOps();
      } catch (error) {
        extractStatus.value = error instanceof Error ? error.message : copy("extractFailed");
      }
    };

    const onServerCheck = async () => {
      if (!signedIn.value) {
        apiStatus.value = copy("authRequired");
        openAuthModal("login");
        return;
      }
      if (!ensureVerifiedBeforeAction()) {
        return;
      }
      applyCalculatedTotals();
      apiStatus.value = copy("checkRunning");
      try {
        const response = await api.check({ ...draft, locale: locale.value, notes: notesInput.value.split("\n").map((item) => item.trim()).filter(Boolean) });
        latestServerResult.value = response.result as ComplianceEvaluation;
        apiStatus.value = copy("checkCompleted");
        sidebarTab.value = "result";
        await loadOps();
      } catch (error) {
        apiStatus.value = error instanceof Error ? error.message : copy("checkFailed");
      }
    };

    const onGenerateInvoice = async () => {
      if (!signedIn.value) {
        apiStatus.value = copy("authRequired");
        openAuthModal("login");
        return;
      }
      if (!ensureVerifiedBeforeAction()) {
        return;
      }
      applyCalculatedTotals();
      apiStatus.value = copy("generateRunning");
      try {
        const response = await api.generate({ ...draft, locale: locale.value, notes: notesInput.value.split("\n").map((item) => item.trim()).filter(Boolean) });
        latestServerResult.value = response.result as ComplianceEvaluation;
        apiStatus.value = copy("generateCompleted");
        showPreviewHtml(response.html);
        sidebarTab.value = "result";
        await loadOps();
      } catch (error) {
        apiStatus.value = error instanceof Error ? error.message : copy("generateFailed");
      }
    };

    const openSavedInvoicePreview = async (invoiceId: string) => {
      try {
        const record = (await api.invoice(invoiceId)) as InvoiceRecordDetail;
        if (!record.generatedHtml) {
          apiStatus.value = localeText({
            en: "Preview content is not available for this record yet.",
            "zh-CN": "这条记录当前还没有可打开的预览内容。",
            "ms-MY": "Kandungan pratonton belum tersedia untuk rekod ini.",
            "id-ID": "Konten pratinjau belum tersedia untuk catatan ini.",
            "th-TH": "ระเบียนนี้ยังไม่มีเนื้อหาพรีวิวให้เปิด"
          });
          return;
        }
        showPreviewHtml(record.generatedHtml);
      } catch (error) {
        apiStatus.value = error instanceof Error ? error.message : copy("serviceUnavailable");
      }
    };

    const loadSavedInvoiceIntoEditor = async (invoiceId: string) => {
      try {
        const record = (await api.invoice(invoiceId)) as InvoiceRecordDetail;
        restoreSavedDraft(record.data);
        apiStatus.value = localeText({
          en: "Saved draft loaded into the editor.",
          "zh-CN": "已将保存记录载入编辑器。",
          "ms-MY": "Draf tersimpan telah dimuatkan ke editor.",
          "id-ID": "Draft tersimpan sudah dimuat ke editor.",
          "th-TH": "โหลดร่างที่บันทึกไว้กลับเข้าเครื่องมือแก้ไขแล้ว"
        });
      } catch (error) {
        apiStatus.value = error instanceof Error ? error.message : copy("serviceUnavailable");
      }
    };

    const ensurePaddleReady = async (): Promise<void> => {
      if (!bootstrap.billing.enabled || !bootstrap.billing.clientToken) {
        throw new Error(
          localeText({
            en: "Billing checkout is not configured yet.",
            "zh-CN": "当前还没有配置在线支付。",
            "ms-MY": "Pembayaran dalam talian belum dikonfigurasi.",
            "id-ID": "Pembayaran online belum dikonfigurasi.",
            "th-TH": "ยังไม่ได้ตั้งค่าการชำระเงินออนไลน์"
          })
        );
      }
      if (window.Paddle?.Checkout && window.Paddle?.Initialize) {
        return;
      }
      if (!paddleInitPromise) {
        paddleInitPromise = new Promise<void>((resolve, reject) => {
          const existing = document.getElementById(PADDLE_SCRIPT_ID) as HTMLScriptElement | null;
          if (existing && window.Paddle?.Initialize) {
            resolve();
            return;
          }

          const script = existing ?? document.createElement("script");
          script.id = PADDLE_SCRIPT_ID;
          script.src = "https://cdn.paddle.com/paddle/v2/paddle.js";
          script.async = true;
          script.onload = () => {
            try {
              if (!window.Paddle?.Initialize) {
                reject(new Error("Paddle.js did not initialize."));
                return;
              }
              if (bootstrap.billing.environment === "sandbox") {
                window.Paddle.Environment?.set?.("sandbox");
              }
              window.Paddle.Initialize({
                token: bootstrap.billing.clientToken
              });
              resolve();
            } catch (error) {
              reject(error instanceof Error ? error : new Error("Unable to initialize Paddle."));
            }
          };
          script.onerror = () => reject(new Error("Unable to load Paddle.js."));
          if (!existing) {
            document.head.appendChild(script);
          }
        }).catch((error) => {
          paddleInitPromise = null;
          throw error;
        });
      }
      await paddleInitPromise;
    };

    const openCheckoutForPlan = async (planCode: PlanCode) => {
      if (!signedIn.value) {
        billingStatus.value = copy("authRequired");
        openAuthModal("login");
        return;
      }
      if (!ensureVerifiedBeforeAction()) {
        billingStatus.value = verifyStatus.value;
        return;
      }
      checkoutPending.value = true;
      billingStatus.value = "";
      try {
        await ensurePaddleReady();
        const checkout = await api.createCheckout({ planCode });
        window.Paddle?.Checkout?.open({
          transactionId: checkout.transactionId,
          customer: {
            email: auth.user?.email
          },
          settings: {
            displayMode: "overlay",
            locale: "en"
          },
          eventCallback: async (event: { name?: string; data?: Record<string, unknown> }) => {
            if (event.name === "checkout.completed") {
              const transactionId = typeof event.data?.transaction_id === "string" ? event.data.transaction_id : checkout.transactionId;
              try {
                await api.confirmCheckout({ transactionId });
                billingStatus.value = localeText({
                  en: "Payment completed and the workspace plan is now active.",
                  "zh-CN": "支付完成，工作空间套餐已生效。",
                  "ms-MY": "Bayaran selesai dan pelan ruang kerja kini aktif.",
                  "id-ID": "Pembayaran selesai dan paket workspace sekarang aktif.",
                  "th-TH": "ชำระเงินสำเร็จและแพ็กเกจของเวิร์กสเปซมีผลแล้ว"
                });
                await loadOps();
              } catch (error) {
                billingStatus.value = error instanceof Error ? error.message : copy("serviceUnavailable");
              }
            }
          }
        });
      } catch (error) {
        billingStatus.value = error instanceof Error ? error.message : copy("serviceUnavailable");
      } finally {
        checkoutPending.value = false;
      }
    };

    const openSubscriptionManagement = async () => {
      if (!signedIn.value) {
        billingStatus.value = copy("authRequired");
        return;
      }
      subscriptionManagePending.value = true;
      try {
        const result = await api.subscriptionManagement();
        const url =
          result.managementUrls?.update_payment_method ??
          result.managementUrls?.cancel ??
          result.managementUrls?.customer_portal ??
          null;
        if (!url) {
          billingStatus.value = localeText({
            en: "No subscription management link is available yet.",
            "zh-CN": "当前还没有可用的订阅管理链接。",
            "ms-MY": "Tiada pautan pengurusan langganan tersedia lagi.",
            "id-ID": "Belum ada tautan pengelolaan langganan yang tersedia.",
            "th-TH": "ยังไม่มีลิงก์จัดการการสมัครใช้งาน"
          });
          return;
        }
        window.open(url, "_blank", "noopener,noreferrer");
      } catch (error) {
        billingStatus.value = error instanceof Error ? error.message : copy("serviceUnavailable");
      } finally {
        subscriptionManagePending.value = false;
      }
    };

    const refundAdminOrder = async (orderId: string) => {
      const confirmed = window.confirm(
        localeText({
          en: "Issue a full refund for this order? If it is linked to an active subscription, the subscription will be canceled.",
          "zh-CN": "确定对这笔订单执行全额退款吗？如果它绑定了有效订阅，也会一并取消订阅。",
          "ms-MY": "Pulangkan bayaran penuh untuk pesanan ini? Jika pesanan dipautkan kepada langganan aktif, langganan itu juga akan dibatalkan.",
          "id-ID": "Lakukan refund penuh untuk pesanan ini? Jika terhubung dengan langganan aktif, langganan itu juga akan dibatalkan.",
          "th-TH": "ต้องการคืนเงินเต็มจำนวนสำหรับคำสั่งซื้อนี้หรือไม่ หากเชื่อมกับการสมัครใช้งานอยู่ ระบบจะยกเลิกการสมัครด้วย"
        })
      );
      if (!confirmed) {
        return;
      }
      adminRefundPendingOrderId.value = orderId;
      adminStatus.value = "";
      try {
        await api.adminRefundOrder(orderId, {
          reason: "Refund issued by workspace billing admin"
        });
        adminStatus.value = localeText({
          en: "Refund request sent successfully.",
          "zh-CN": "退款请求已成功提交。",
          "ms-MY": "Permintaan refund berjaya dihantar.",
          "id-ID": "Permintaan refund berhasil dikirim.",
          "th-TH": "ส่งคำขอคืนเงินเรียบร้อยแล้ว"
        });
        await Promise.all([loadOps(), loadAdminBilling()]);
      } catch (error) {
        adminStatus.value = error instanceof Error ? error.message : copy("serviceUnavailable");
      } finally {
        adminRefundPendingOrderId.value = "";
      }
    };

    const loadOps = async () => {
      if (!signedIn.value) {
        latestRuns.value = [];
        latestInvoices.value = [];
        billingOverview.value = null;
        return;
      }
      try {
        latestRuns.value = (await api.runs(100)) as RunRecord[];
      } catch {
        latestRuns.value = [];
      }

      try {
        latestInvoices.value = (await api.invoices(100)) as InvoiceRecord[];
      } catch {
        latestInvoices.value = [];
      }

      try {
        billingOverview.value = (await api.billingOverview()) as BillingOverview;
      } catch {
        billingOverview.value = null;
      }
    };

    const loadAdminBilling = async () => {
      if (!isBillingAdmin.value) {
        adminOverview.value = null;
        adminOrders.value = [];
        return;
      }
      try {
        adminOverview.value = (await api.adminBillingOverview()) as BillingAdminOverview;
      } catch {
        adminOverview.value = null;
      }

      try {
        adminOrders.value = (await api.adminBillingOrders(100)) as BillingAdminOverview["recentOrders"];
      } catch {
        adminOrders.value = [];
      }
    };

    onMounted(async () => {
      const storedLocale = window.localStorage.getItem(STORAGE_LOCALE_KEY);
      if (storedLocale && LOCALES.includes(storedLocale as LocaleCode)) {
        locale.value = storedLocale as LocaleCode;
      }
      const storedCountry = window.localStorage.getItem(STORAGE_COUNTRY_KEY);
      if (storedCountry && COUNTRY_CODES.includes(storedCountry as CountryCode)) {
        country.value = storedCountry as CountryCode;
      }
      currentPage.value = resolvePageFromPath(window.location.pathname);
      window.addEventListener("popstate", () => {
        currentPage.value = resolvePageFromPath(window.location.pathname);
        syncAuthModalFromLocation();
      });
      syncAuthModalFromLocation();
      api.setCountry(country.value);
      api.setLocale(locale.value);
      try {
        const data = await api.bootstrap();
        bootstrap.aiAvailable = data.aiAvailable;
        bootstrap.mailConfigured = data.mailConfigured;
        bootstrap.emailVerificationEnabled = data.emailVerificationEnabled;
        bootstrap.captcha = data.captcha;
        bootstrap.billing = data.billing;
        captchaReady = false;
        captchaInitPromise = null;
        captchaInitKey = "";
        captchaInstance = null;
        bootstrap.persistenceMode = data.persistenceMode;
        bootstrap.multiRegionReady = data.multiRegionReady;
        bootstrap.launchCountries = data.launchCountries;
        bootstrap.expansionReadyCountries = data.expansionReadyCountries;
        bootstrap.deploymentRequirements = data.deploymentRequirements;
        if (data.captcha.enabled && data.mailConfigured) {
          void ensureCaptchaReady().catch(() => undefined);
        }
        apiStatus.value = "";
      } catch (error) {
        apiStatus.value = error instanceof Error ? error.message : copy("serviceUnavailable");
      }
      refreshAuthStatus();
      await loadSession();
      await Promise.all([loadOps(), loadAdminBilling()]);
    });

    const inputField = (
      label: string,
      value: string | number | undefined,
      onInput: (value: string) => void,
      type = "text",
      className = "",
      fieldId = "",
      maxLength?: number,
      helpText = "",
      options: FieldOptions = {}
    ) =>
      h("label", { class: ["field", className, options.tone ? `field-${options.tone}` : ""].filter(Boolean).join(" "), ...(fieldId ? { for: fieldId } : {}) }, [
        h("span", { class: "field-label" }, label),
        h("input", {
          ...(fieldId ? { id: fieldId } : {}),
          ...(typeof maxLength === "number" ? { maxLength } : {}),
          class: "field-input",
          type,
          value: value ?? "",
          onInput: (event: Event) => onInput((event.target as HTMLInputElement).value)
        }),
        helpText ? h("small", { class: "field-help" }, helpText) : null
      ]);

    const textareaField = (
      label: string,
      value: string | undefined,
      onInput: (value: string) => void,
      rows = 6,
      className = "",
      fieldId = "",
      helpText = "",
      options: FieldOptions = {}
    ) =>
      h("label", { class: ["field", className, options.tone ? `field-${options.tone}` : ""].filter(Boolean).join(" "), ...(fieldId ? { for: fieldId } : {}) }, [
        h("span", { class: "field-label" }, label),
        h("textarea", {
          ...(fieldId ? { id: fieldId } : {}),
          class: "field-textarea",
          rows,
          value: value ?? "",
          onInput: (event: Event) => onInput((event.target as HTMLTextAreaElement).value)
        }),
        helpText ? h("small", { class: "field-help" }, helpText) : null
      ]);

    const selectField = (
      label: string,
      value: string,
      options: Array<{ value: string; label: string }>,
      onChange: (value: string) => void,
      className = "",
      fieldId = "",
      helpText = "",
      fieldOptions: FieldOptions = {}
    ) =>
      h("label", { class: ["field", className, fieldOptions.tone ? `field-${fieldOptions.tone}` : ""].filter(Boolean).join(" "), ...(fieldId ? { for: fieldId } : {}) }, [
        h("span", { class: "field-label" }, label),
        h(
          "select",
          {
            ...(fieldId ? { id: fieldId } : {}),
            class: "field-input",
            value,
            onChange: (event: Event) => onChange((event.target as HTMLSelectElement).value)
          },
          options.map((option) => h("option", { value: option.value }, option.label))
        ),
        helpText ? h("small", { class: "field-help" }, helpText) : null
      ]);

    const compactSelect = (label: string, value: string, options: Array<{ value: string; label: string }>, onChange: (value: string) => void) =>
      h("label", { class: "compact-field topbar-control" }, [
        h("span", { class: "compact-label" }, label),
        h(
          "select",
          {
            class: "compact-select",
            value,
            onChange: (event: Event) => onChange((event.target as HTMLSelectElement).value)
          },
          options.map((option) => h("option", { value: option.value }, option.label))
        )
      ]);

    const checkboxField = (label: string, value: boolean | undefined, onChange: (value: boolean) => void) =>
      h("label", { class: "checkbox" }, [
        h("input", {
          type: "checkbox",
          checked: Boolean(value),
          onChange: (event: Event) => onChange((event.target as HTMLInputElement).checked)
        }),
        h("span", label)
      ]);

    const tabButton = (label: string, active: boolean, onClick: () => void) =>
      h(
        "button",
        {
          class: active ? "tab-button active" : "tab-button",
          type: "button",
          onClick
        },
        label
      );

    const setDraftEntryMode = (mode: "manual" | "import") => {
      editorTab.value = mode === "import" ? "intake" : "basics";
    };

    const navButton = (label: string, page: AppPage) =>
      h(
        "button",
        {
          class: currentPage.value === page ? "nav-link active" : "nav-link",
          type: "button",
          onClick: () => navigateTo(page)
        },
        label
      );

    const renderFindings = () =>
      findingTasks.value.length === 0
        ? h("div", { class: "empty-state" }, localeText({
            en: "No blocking issues are currently detected in this invoice draft.",
            "zh-CN": "当前这份发票草稿没有发现需要立即处理的问题。",
            "ms-MY": "Tiada isu utama ditemui dalam draf invois ini buat masa ini.",
            "id-ID": "Saat ini tidak ada masalah utama yang terdeteksi pada draft invoice ini.",
            "th-TH": "ขณะนี้ยังไม่พบประเด็นสำคัญที่ต้องแก้ไขทันทีในร่างเอกสารนี้"
          }))
        : h(
            "div",
            { class: "section-stack" },
            [
              resolvedFindingCount.value > 0
                ? h("div", { class: "notice-bar" }, localeText({
                    en: `${resolvedFindingCount.value} item(s) already look fixed in the current draft. Run the check again to confirm they are cleared.`,
                    "zh-CN": `当前草稿里已有 ${resolvedFindingCount.value} 项看起来已修复，建议重新执行检查以确认已清除。`,
                    "ms-MY": `${resolvedFindingCount.value} item kelihatan sudah diperbetulkan dalam draf semasa. Jalankan semakan semula untuk mengesahkan ia telah dibersihkan.`,
                    "id-ID": `${resolvedFindingCount.value} item tampak sudah diperbaiki di draft saat ini. Jalankan pemeriksaan lagi untuk memastikan item tersebut benar-benar bersih.`,
                    "th-TH": `${resolvedFindingCount.value} รายการดูเหมือนแก้แล้วในร่างปัจจุบัน แนะนำให้ตรวจอีกครั้งเพื่อยืนยันว่าหายจริง`
                  }))
                : null,
              h("div", { class: "finding-list" }, visibleFindingTasks.value.map(({ finding, task, resolved }) =>
                (() => {
                  const taskButtonLabel = task?.fieldId
                    ? localeText({ en: resolved ? "Review field" : "Go to field", "zh-CN": resolved ? "查看字段" : "定位字段", "ms-MY": resolved ? "Semak medan" : "Pergi ke medan", "id-ID": resolved ? "Tinjau bidang" : "Buka bidang", "th-TH": resolved ? "ดูฟิลด์" : "ไปยังฟิลด์" })
                    : localeText({ en: resolved ? "Review section" : "Open section", "zh-CN": resolved ? "查看区块" : "打开对应区块", "ms-MY": resolved ? "Semak bahagian" : "Buka bahagian", "id-ID": resolved ? "Tinjau bagian" : "Buka bagian", "th-TH": resolved ? "ดูส่วนนี้" : "เปิดส่วนที่เกี่ยวข้อง" });
                  return h("article", { class: `finding finding-${finding.severity}${resolved ? " finding-resolved" : ""}` }, [
                    h("div", { class: "finding-head" }, [
                        h("strong", finding.title),
                        h("div", { class: "chip-row" }, [
                          h("span", { class: `finding-chip finding-status-chip${resolved ? " finding-status-chip-resolved" : ""}` }, localeText({
                            en: resolved ? "Marked fixed locally" : "To fix",
                            "zh-CN": resolved ? "当前草稿已修复" : "待处理",
                            "ms-MY": resolved ? "Sudah dibaiki dalam draf" : "Perlu dibaiki",
                            "id-ID": resolved ? "Sudah diperbaiki di draft" : "Perlu diperbaiki",
                            "th-TH": resolved ? "ร่างนี้แก้แล้ว" : "ต้องจัดการ"
                          })),
                          h("span", { class: "finding-chip" }, t(locale.value, "severity_" + finding.severity)),
                          h("span", { class: "finding-chip pill-muted" }, `${copy("riskLabel")}: ${localizeRisk(finding.riskLevel)}`)
                        ])
                    ]),
                    h("p", finding.summary),
                    h("div", { class: "finding-task-footer" }, [
                      h("div", { class: "finding-task-copy" }, [
                        h("small", finding.action),
                        resolved
                          ? h(
                              "span",
                              { class: "finding-task-location" },
                              localeText({
                                en: "This item looks resolved in the current draft. Re-run the check to refresh the saved result.",
                                "zh-CN": "当前草稿里这项看起来已修复；重新执行检查后会刷新已保存结果。",
                                "ms-MY": "Item ini kelihatan telah diselesaikan dalam draf semasa. Jalankan semakan semula untuk menyegarkan hasil tersimpan.",
                                "id-ID": "Item ini tampak sudah selesai di draft saat ini. Jalankan pemeriksaan lagi untuk menyegarkan hasil tersimpan.",
                                "th-TH": "รายการนี้ดูเหมือนแก้แล้วในร่างปัจจุบัน ตรวจอีกครั้งเพื่อรีเฟรชผลที่บันทึกไว้"
                              })
                            )
                          : null,
                        task
                          ? h(
                              "span",
                              { class: "finding-task-location" },
                              localeText({
                                en: `Suggested location: ${task.fieldLabel ?? task.sectionLabel}`,
                                "zh-CN": `建议处理位置：${task.fieldLabel ?? task.sectionLabel}`,
                                "ms-MY": `Lokasi yang disyorkan: ${task.fieldLabel ?? task.sectionLabel}`,
                                "id-ID": `Lokasi yang disarankan: ${task.fieldLabel ?? task.sectionLabel}`,
                                "th-TH": `ตำแหน่งที่แนะนำ: ${task.fieldLabel ?? task.sectionLabel}`
                              })
                            )
                          : null
                      ]),
                      task
                        ? h(
                            "button",
                            {
                              class: "action-button secondary-button subtle-button finding-task-button",
                              type: "button",
                              onClick: () => openEditorLocation(task.tab, task.fieldId)
                            },
                            taskButtonLabel
                          )
                        : null
                    ])
                  ]);
                })()
              )),
              findingTasks.value.length > 3
                ? h(
                    "button",
                    {
                      class: "action-button secondary-button subtle-button result-more-button",
                      type: "button",
                      onClick: () => (findingsExpanded.value = !findingsExpanded.value)
                    },
                    findingsExpanded.value
                      ? localeText({ en: "Show fewer issues", "zh-CN": "收起问题清单", "ms-MY": "Sembunyikan sebahagian isu", "id-ID": "Tampilkan lebih sedikit", "th-TH": "แสดงประเด็นให้น้อยลง" })
                      : localeText({ en: `Show all ${findingTasks.value.length} issues`, "zh-CN": `查看全部 ${findingTasks.value.length} 条问题`, "ms-MY": `Lihat semua ${findingTasks.value.length} isu`, "id-ID": `Lihat semua ${findingTasks.value.length} masalah`, "th-TH": `ดูประเด็นทั้งหมด ${findingTasks.value.length} รายการ` })
                  )
                : null
            ]
          );

    const renderThresholds = () =>
      watchlistThresholds.value.length === 0
        ? h("div", { class: "empty-state" }, localeText({
            en: "No threshold or watchlist reminders are active for this draft.",
            "zh-CN": "当前这份草稿没有需要关注的阈值或经营提醒。",
            "ms-MY": "Tiada peringatan ambang atau pemerhatian aktif untuk draf ini.",
            "id-ID": "Tidak ada ambang atau pengingat bisnis yang aktif untuk draft ini.",
            "th-TH": "ขณะนี้ยังไม่มีเกณฑ์หรือรายการเฝ้าระวังที่ต้องติดตามสำหรับร่างฉบับนี้"
          }))
        : h(
            "div",
            { class: "section-stack" },
            [
              h("div", { class: "threshold-grid" }, visibleThresholds.value.map((item) =>
                h("article", { class: "threshold-card" }, [
                  h("strong", item.title),
                  h("p", item.message)
                ])
              )),
              watchlistThresholds.value.length > 4
                ? h(
                    "button",
                    {
                      class: "action-button secondary-button subtle-button result-more-button",
                      type: "button",
                      onClick: () => (thresholdsExpanded.value = !thresholdsExpanded.value)
                    },
                    thresholdsExpanded.value
                      ? localeText({ en: "Show fewer reminders", "zh-CN": "收起业务提醒", "ms-MY": "Sembunyikan sebahagian peringatan", "id-ID": "Sembunyikan sebagian pengingat", "th-TH": "แสดงคำเตือนให้น้อยลง" })
                      : localeText({ en: `Show all ${watchlistThresholds.value.length} reminders`, "zh-CN": `查看全部 ${watchlistThresholds.value.length} 条提醒`, "ms-MY": `Lihat semua ${watchlistThresholds.value.length} peringatan`, "id-ID": `Lihat semua ${watchlistThresholds.value.length} pengingat`, "th-TH": `ดูคำเตือนทั้งหมด ${watchlistThresholds.value.length} รายการ` })
                  )
                : null
            ]
          );

    const renderAuthModal = () =>
      !authDialogOpen.value
        ? null
        : h(
            "div",
            {
              class: "modal-backdrop",
              onClick: (event: Event) => {
                if (event.target === event.currentTarget) {
                  closeAuthModal();
                }
              }
            },
            [
              h("section", { class: "modal-card" }, [
                h("div", { class: "modal-shell" }, [
                  h("aside", { class: "modal-lead" }, [
                    h("p", { class: "eyebrow" }, copy("accountTitle")),
                    h(
                      "h2",
                      auth.user
                        ? copy("authDialogTitleSignedIn")
                        : authMode.value === "login"
                          ? copy("authDialogTitleLogin")
                          : authMode.value === "register"
                            ? copy("authDialogTitleRegister")
                            : localeText({
                                en: "Reset password",
                                "zh-CN": "重置密码",
                                "ms-MY": "Tetapkan semula kata laluan",
                                "id-ID": "Reset kata sandi",
                                "th-TH": "รีเซ็ตรหัสผ่าน"
                              })
                    ),
                    h(
                      "p",
                      { class: "muted-text muted-text-inverse" },
                      auth.user
                        ? copy("accountSubtitleSignedIn")
                        : authMode.value === "login"
                          ? copy("accountSubtitleLogin")
                          : authMode.value === "register"
                            ? copy("accountSubtitleRegister")
                            : localeText({
                                en: "Request a reset code by email, then choose a new password.",
                                "zh-CN": "先通过邮箱获取重置验证码，然后设置新密码。",
                                "ms-MY": "Minta kod tetapan semula melalui e-mel, kemudian pilih kata laluan baharu.",
                                "id-ID": "Minta kode reset melalui email, lalu pilih kata sandi baru.",
                                "th-TH": "ขอรหัสรีเซ็ตผ่านอีเมล แล้วตั้งรหัสผ่านใหม่"
                              })
                    ),
                    h("div", { class: "chip-row" }, [
                      h("span", { class: "status-badge inverted-badge" }, getCountryName(locale.value, country.value)),
                      h("span", { class: "status-badge inverted-badge" }, getLocaleLabel(locale.value))
                    ])
                  ]),
                  h("section", { class: "panel modal-panel" }, [
                    h("div", { class: "panel-head" }, [
                      h("div", { class: "panel-copy" }, [
                        h(
                          "h2",
                          auth.user
                            ? copy("authDialogTitleSignedIn")
                            : authMode.value === "login"
                              ? copy("authDialogTitleLogin")
                              : authMode.value === "register"
                                ? copy("authDialogTitleRegister")
                                : localeText({
                                    en: "Reset password",
                                    "zh-CN": "重置密码",
                                    "ms-MY": "Tetapkan semula kata laluan",
                                    "id-ID": "Reset kata sandi",
                                    "th-TH": "รีเซ็ตรหัสผ่าน"
                                  })
                        ),
                        h(
                          "p",
                          { class: "muted-text" },
                          auth.user
                            ? copy("accountSubtitleSignedIn")
                            : authMode.value === "login"
                              ? copy("accountSubtitleLogin")
                              : authMode.value === "register"
                                ? copy("accountSubtitleRegister")
                                : localeText({
                                    en: "Use your email inbox to confirm the reset, then return here to set a new password.",
                                    "zh-CN": "通过邮箱完成重置确认后，再回到这里设置新密码。",
                                    "ms-MY": "Gunakan peti masuk e-mel anda untuk mengesahkan tetapan semula, kemudian kembali ke sini untuk menetapkan kata laluan baharu.",
                                    "id-ID": "Gunakan kotak masuk email Anda untuk mengonfirmasi reset, lalu kembali ke sini untuk menetapkan kata sandi baru.",
                                    "th-TH": "ใช้กล่องจดหมายของคุณเพื่อยืนยันการรีเซ็ต แล้วกลับมาตั้งรหัสผ่านใหม่ที่นี่"
                                  })
                        )
                      ]),
                      h("button", { class: "icon-button", type: "button", onClick: closeAuthModal }, copy("close"))
                    ]),
                    authStatus.value ? h("div", { class: "notice-bar" }, authStatus.value) : null,
                    auth.user
                      ? h("div", { class: "section-stack" }, [
                          h("div", { class: "notice-bar" }, localeText({
                            en: emailVerified.value ? `Signed in as ${auth.user.displayName} · ${auth.user.email}` : `Signed in, but email verification is still pending: ${auth.user.email}`,
                            "zh-CN": emailVerified.value ? `当前登录账户：${auth.user.displayName} · ${auth.user.email}` : `当前已登录，但邮箱验证尚未完成：${auth.user.email}`,
                            "ms-MY": emailVerified.value ? `Log masuk sebagai ${auth.user.displayName} · ${auth.user.email}` : `Sudah log masuk, tetapi pengesahan e-mel masih belum selesai: ${auth.user.email}`,
                            "id-ID": emailVerified.value ? `Masuk sebagai ${auth.user.displayName} · ${auth.user.email}` : `Sudah masuk, tetapi verifikasi email masih tertunda: ${auth.user.email}`
                          })),
                          h("div", { class: "metric-grid" }, [
                            h("article", { class: "metric-card" }, [
                              h("span", { class: "field-label" }, copy("displayName")),
                              h("strong", auth.user.displayName),
                              h("small", auth.user.email)
                            ]),
                            h("article", { class: "metric-card" }, [
                              h("span", { class: "field-label" }, copy("currentOrganization")),
                              h("strong", activeOrganization.value?.name ?? copy("notAvailable")),
                              h("small", getCountryName(locale.value, activeOrganization.value?.country as CountryCode ?? country.value))
                            ]),
                            h("article", { class: "metric-card" }, [
                              h("span", { class: "field-label" }, localeText({ en: "Plan / subscription", "zh-CN": "套餐 / 订阅", "ms-MY": "Pelan / langganan", "id-ID": "Paket / langganan" })),
                              h("strong", `${localizePlan(activeOrganization.value?.planCode)} · ${localizeBilling(activeOrganization.value?.billingStatus)}`),
                              h("small", `${copy("organizationRole")}: ${localizeRole(activeOrganization.value?.role)}`)
                            ]),
                            h("article", { class: "metric-card" }, [
                              h("span", { class: "field-label" }, labels.value.verifyTitle),
                              h("strong", emailVerified.value ? labels.value.verifyDone : labels.value.verifyHint),
                              h("small", emailVerified.value ? auth.user.email : (bootstrap.mailConfigured ? auth.user.email : labels.value.verifyMailMissing))
                            ])
                          ]),
                          selectField(
                            copy("currentOrganization"),
                            currentOrganizationId.value,
                            auth.organizations.map((item) => ({
                              value: item.id,
                              label: `${item.name} · ${getCountryName(locale.value, item.country as CountryCode)} · ${localizePlan(item.planCode)}`
                            })),
                            (value) => (currentOrganizationId.value = value)
                          ),
                          h("section", { class: "subpanel section-stack account-security-panel" }, [
                            h("div", { class: "section-copy section-copy-compact" }, [
                              h("h3", copy("changePasswordTitle")),
                              h("p", { class: "muted-text" }, copy("changePasswordBody"))
                            ]),
                            h("div", { class: "form-grid form-grid-2" }, [
                              inputField(copy("currentPassword"), passwordChange.currentPassword, (value) => (passwordChange.currentPassword = value), "password"),
                              inputField(copy("newPassword"), passwordChange.password, (value) => (passwordChange.password = value), "password"),
                              inputField(copy("confirmNewPassword"), passwordChange.confirmPassword, (value) => (passwordChange.confirmPassword = value), "password", "field-span-2")
                            ]),
                            h("div", { class: "toolbar-row" }, [
                              h(
                                "button",
                                { class: "action-button secondary-button", type: "button", onClick: onChangePassword, disabled: auth.loading },
                                auth.loading ? copy("processing") : copy("changePasswordAction")
                              )
                            ])
                          ]),
                          h("div", { class: "toolbar-row" }, [
                            h("button", { class: "action-button secondary-button", type: "button", onClick: () => { authDialogOpen.value = false; navigateTo("workspace"); } }, labels.value.homePrimary),
                            h("button", { class: "action-button secondary-button", type: "button", onClick: () => { authDialogOpen.value = false; navigateTo("history"); } }, labels.value.goHistory),
                            h(
                              "button",
                              { class: "action-button secondary-button", type: "button", onClick: onLogout, disabled: auth.loading },
                              auth.loading ? copy("processing") : copy("logout")
                            )
                          ])
                        ])
                      : h("div", { class: "auth-shell" }, [
                          authMode.value === "reset"
                            ? h("div", { class: "toolbar-row auth-alt-row" }, [
                                h(
                                  "button",
                                  {
                                    class: "text-button",
                                    type: "button",
                                    onClick: () => {
                                      authMode.value = "login";
                                      authStatus.value = "";
                                      updateAuthSearchParams();
                                    }
                                  },
                                  localeText({
                                    en: "Back to sign in",
                                    "zh-CN": "返回登录",
                                    "ms-MY": "Kembali ke log masuk",
                                    "id-ID": "Kembali ke masuk",
                                    "th-TH": "กลับไปเข้าสู่ระบบ"
                                  })
                                )
                              ])
                            : h("div", { class: "segmented-control" }, [
                                h("button", { class: authMode.value === "login" ? "segmented-button active" : "segmented-button", type: "button", onClick: () => { authMode.value = "login"; authStatus.value = ""; updateAuthSearchParams(); } }, copy("loginTab")),
                                h("button", { class: authMode.value === "register" ? "segmented-button active" : "segmented-button", type: "button", onClick: () => { authMode.value = "register"; authStatus.value = ""; updateAuthSearchParams(); } }, copy("registerTab"))
                              ]),
                          authMode.value === "login"
                            ? h("div", { class: "form-grid form-grid-2" }, [
                                inputField(copy("email"), auth.email, (value) => (auth.email = value), "email", "field-span-2"),
                                inputField(copy("password"), auth.password, (value) => (auth.password = value), "password", "field-span-2"),
                                h("div", { class: "field-span-2 auth-alt-row" }, [
                                  h(
                                    "button",
                                    {
                                      class: "text-button",
                                      type: "button",
                                      onClick: () => openPasswordReset(auth.email)
                                    },
                                    localeText({
                                      en: "Forgot your password?",
                                      "zh-CN": "忘记密码？",
                                      "ms-MY": "Lupa kata laluan?",
                                      "id-ID": "Lupa kata sandi?",
                                      "th-TH": "ลืมรหัสผ่าน?"
                                    })
                                  )
                                ]),
                                h("div", { class: "toolbar-row field-span-2" }, [
                                  h("button", { class: "action-button", type: "button", onClick: onLogin, disabled: auth.loading }, auth.loading ? copy("processing") : copy("loginAction")),
                                  h("button", { class: "action-button secondary-button", type: "button", onClick: () => { authMode.value = "register"; authStatus.value = ""; updateAuthSearchParams(); } }, copy("registerTab"))
                                ])
                              ])
                            : authMode.value === "register"
                              ? h("div", { class: "form-grid form-grid-2" }, [
                                inputField(copy("displayName"), auth.displayName, (value) => (auth.displayName = value), "text", "", "", DISPLAY_NAME_MAX_LENGTH),
                                inputField(copy("email"), auth.email, (value) => (auth.email = value), "email"),
                                inputField(copy("password"), auth.password, (value) => (auth.password = value), "password"),
                                selectField(
                                  copy("organizationCountry"),
                                  auth.organizationCountry,
                                  COUNTRY_CODES.map((item) => ({ value: item, label: `${item} · ${getCountryName(locale.value, item)}` })),
                                  (value) => (auth.organizationCountry = value)
                                ),
                                inputField(copy("organizationName"), auth.organizationName, (value) => (auth.organizationName = value), "text", "field-span-2", "", ORGANIZATION_NAME_MAX_LENGTH),
                                h("div", { class: "toolbar-row field-span-2" }, [
                                  h("button", { class: "action-button", type: "button", onClick: onRegister, disabled: auth.loading }, auth.loading ? copy("processing") : copy("registerAction")),
                                  h("button", { class: "action-button secondary-button", type: "button", onClick: () => { authMode.value = "login"; authStatus.value = ""; updateAuthSearchParams(); } }, copy("loginTab"))
                                ])
                              ])
                              : h("div", { class: "form-grid form-grid-2" }, [
                                  inputField(
                                    copy("email"),
                                    passwordReset.email,
                                    (value) => {
                                      passwordReset.email = value;
                                      updateAuthSearchParams("reset", value);
                                    },
                                    "email",
                                    "field-span-2"
                                  ),
                                  passwordReset.requested
                                    ? inputField(
                                        localeText({
                                          en: "Reset code",
                                          "zh-CN": "重置验证码",
                                          "ms-MY": "Kod tetapan semula",
                                          "id-ID": "Kode reset",
                                          "th-TH": "รหัสรีเซ็ต"
                                        }),
                                        passwordReset.code,
                                        (value) => (passwordReset.code = value),
                                        "text",
                                        "field-span-2"
                                      )
                                    : null,
                                  passwordReset.requested
                                    ? inputField(
                                        localeText({
                                          en: "New password",
                                          "zh-CN": "新密码",
                                          "ms-MY": "Kata laluan baharu",
                                          "id-ID": "Kata sandi baru",
                                          "th-TH": "รหัสผ่านใหม่"
                                        }),
                                        passwordReset.password,
                                        (value) => (passwordReset.password = value),
                                        "password"
                                      )
                                    : null,
                                  passwordReset.requested
                                    ? inputField(
                                        localeText({
                                          en: "Confirm new password",
                                          "zh-CN": "确认新密码",
                                          "ms-MY": "Sahkan kata laluan baharu",
                                          "id-ID": "Konfirmasi kata sandi baru",
                                          "th-TH": "ยืนยันรหัสผ่านใหม่"
                                        }),
                                        passwordReset.confirmPassword,
                                        (value) => (passwordReset.confirmPassword = value),
                                        "password"
                                      )
                                    : null,
                                  h("div", { class: "toolbar-row field-span-2" }, [
                                    h(
                                      "button",
                                      {
                                        class: "action-button",
                                        type: "button",
                                        onClick: passwordReset.requested ? onCompletePasswordReset : onRequestPasswordReset,
                                        disabled: auth.loading
                                      },
                                      auth.loading
                                        ? copy("processing")
                                        : passwordReset.requested
                                          ? localeText({
                                              en: "Reset password",
                                              "zh-CN": "重置密码",
                                              "ms-MY": "Tetapkan semula kata laluan",
                                              "id-ID": "Reset kata sandi",
                                              "th-TH": "รีเซ็ตรหัสผ่าน"
                                            })
                                          : localeText({
                                              en: "Send reset code",
                                              "zh-CN": "发送重置验证码",
                                              "ms-MY": "Hantar kod tetapan semula",
                                              "id-ID": "Kirim kode reset",
                                            "th-TH": "ส่งรหัสรีเซ็ต"
                                          })
                                    ),
                                    passwordReset.requested
                                      ? h(
                                          "button",
                                          {
                                            class: "action-button secondary-button",
                                            type: "button",
                                            onClick: onRequestPasswordReset,
                                            disabled: auth.loading || passwordResetCooldownRemaining.value > 0
                                          },
                                          passwordResetCooldownRemaining.value > 0
                                            ? localeText({
                                                en: `Resend in ${passwordResetCooldownRemaining.value}s`,
                                                "zh-CN": `${passwordResetCooldownRemaining.value} 秒后可重发`,
                                                "ms-MY": `Hantar semula dalam ${passwordResetCooldownRemaining.value}s`,
                                                "id-ID": `Kirim ulang dalam ${passwordResetCooldownRemaining.value} dtk`,
                                                "th-TH": `ส่งใหม่ได้ใน ${passwordResetCooldownRemaining.value} วินาที`
                                              })
                                            : localeText({
                                                en: "Resend code",
                                                "zh-CN": "重新发送验证码",
                                                "ms-MY": "Hantar semula kod",
                                                "id-ID": "Kirim ulang kode",
                                                "th-TH": "ส่งรหัสอีกครั้ง"
                                              })
                                        )
                                      : null
                                  ])
                                ])
                        ])
                  ])
                ])
              ])
            ]
          );

    const renderHeroPanel = () =>
      h("section", { class: "panel hero-banner" }, [
        h("div", { class: "hero-layout" }, [
          h("div", { class: "hero-copy-block" }, [
            h("p", { class: "eyebrow" }, copy("heroEyebrow")),
            h("h1", copy("heroTitle")),
            h("p", { class: "hero-copy" }, copy("heroSubtitle")),
            h("div", { class: "chip-row" }, [
              h("span", { class: "status-badge inverted-badge" }, copy("heroCountryBadge", { country: getCountryName(locale.value, country.value) }))
            ])
          ]),
          h("div", { class: "hero-visual-shell" }, [
            h("img", {
              class: "hero-visual",
              src: "/landing-hero-illustration.svg",
              alt: localeText({
                en: "Illustration of invoice drafting, compliance checks, and preview output.",
                "zh-CN": "展示发票草稿、合规检查与预览输出的产品插图。",
                "ms-MY": "Ilustrasi produk bagi draf invois, semakan pematuhan, dan output pratonton.",
                "id-ID": "Ilustrasi produk untuk draft invoice, pemeriksaan kepatuhan, dan keluaran pratinjau.",
                "th-TH": "ภาพประกอบของการร่างเอกสาร การตรวจตามกฎ และผลลัพธ์พรีวิว"
              })
            }),
            h(
              "div",
              { class: "hero-visual-caption" },
              localeText({
                en: "One draft, current rules, customer-ready preview.",
                "zh-CN": "一份草稿、当前规则、面向客户的预览。",
                "ms-MY": "Satu draf, peraturan semasa, pratonton sedia untuk pelanggan.",
                "id-ID": "Satu draft, aturan terkini, pratinjau siap untuk pelanggan.",
                "th-TH": "ร่างเดียว กฎปัจจุบัน และพรีวิวที่พร้อมให้ลูกค้า"
              })
            )
          ])
        ]),
        apiStatus.value ? h("div", { class: "notice-bar notice-bar-dark" }, apiStatus.value) : null
      ]);

    const renderIllustratedIcon = (icon: "draft" | "defaults" | "tasks" | "sg" | "my" | "id" | "th") => {
      const svg =
        icon === "draft"
          ? h("svg", { viewBox: "0 0 24 24", "aria-hidden": "true" }, [
              h("path", { d: "M7 4.5h7l4 4V19a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 19V6A1.5 1.5 0 0 1 7.5 4.5Z", fill: "none", stroke: "currentColor", "stroke-width": "1.7", "stroke-linejoin": "round" }),
              h("path", { d: "M14 4.5V9h4.5", fill: "none", stroke: "currentColor", "stroke-width": "1.7", "stroke-linejoin": "round" }),
              h("path", { d: "M9 15.5 15.8 8.7a1.4 1.4 0 0 1 2 2L11 17.5 8.5 18l.5-2.5Z", fill: "none", stroke: "currentColor", "stroke-width": "1.6", "stroke-linejoin": "round" })
            ])
          : icon === "defaults"
            ? h("svg", { viewBox: "0 0 24 24", "aria-hidden": "true" }, [
                h("circle", { cx: "12", cy: "12", r: "7.5", fill: "none", stroke: "currentColor", "stroke-width": "1.7" }),
                h("path", { d: "M12 4.5v15M4.5 12h15", fill: "none", stroke: "currentColor", "stroke-width": "1.5", "stroke-linecap": "round" }),
                h("path", { d: "M8 8.2c1.3 1 2.7 1.5 4 1.5s2.7-.5 4-1.5M8 15.8c1.3-1 2.7-1.5 4-1.5s2.7.5 4 1.5", fill: "none", stroke: "currentColor", "stroke-width": "1.4", "stroke-linecap": "round" })
              ])
            : icon === "tasks"
              ? h("svg", { viewBox: "0 0 24 24", "aria-hidden": "true" }, [
                  h("rect", { x: "5", y: "4.5", width: "14", height: "15", rx: "2.5", fill: "none", stroke: "currentColor", "stroke-width": "1.7" }),
                  h("path", { d: "M8.5 9.5h7M8.5 13h7", fill: "none", stroke: "currentColor", "stroke-width": "1.5", "stroke-linecap": "round" }),
                  h("path", { d: "M8 16.2 9.5 17.7 12.5 14.7", fill: "none", stroke: "currentColor", "stroke-width": "1.7", "stroke-linecap": "round", "stroke-linejoin": "round" })
                ])
              : icon === "sg"
                ? h("svg", { viewBox: "0 0 24 24", "aria-hidden": "true" }, [
                    h("path", { d: "M5 19.5h14M7 19.5V12h3v7.5M12 19.5V8.5h5v11", fill: "none", stroke: "currentColor", "stroke-width": "1.7", "stroke-linejoin": "round" }),
                    h("path", { d: "M13.5 8.5V5.8L17 4.5v4", fill: "none", stroke: "currentColor", "stroke-width": "1.6", "stroke-linejoin": "round" })
                  ])
                : icon === "my"
                  ? h("svg", { viewBox: "0 0 24 24", "aria-hidden": "true" }, [
                      h("path", { d: "M7 4.5h10A1.5 1.5 0 0 1 18.5 6v12A1.5 1.5 0 0 1 17 19.5H7A1.5 1.5 0 0 1 5.5 18V6A1.5 1.5 0 0 1 7 4.5Z", fill: "none", stroke: "currentColor", "stroke-width": "1.7" }),
                      h("path", { d: "M8.5 9h7M8.5 12h7M8.5 15h4.5", fill: "none", stroke: "currentColor", "stroke-width": "1.5", "stroke-linecap": "round" })
                    ])
                  : icon === "id"
                    ? h("svg", { viewBox: "0 0 24 24", "aria-hidden": "true" }, [
                        h("path", { d: "M12 4.5 18 7v4.4c0 4.2-2.6 6.8-6 8.1-3.4-1.3-6-3.9-6-8.1V7l6-2.5Z", fill: "none", stroke: "currentColor", "stroke-width": "1.7", "stroke-linejoin": "round" }),
                        h("path", { d: "M9.2 11.8 11 13.6l3.8-3.8", fill: "none", stroke: "currentColor", "stroke-width": "1.7", "stroke-linecap": "round", "stroke-linejoin": "round" })
                      ])
                    : h("svg", { viewBox: "0 0 24 24", "aria-hidden": "true" }, [
                        h("path", { d: "M12 4.5c4.4 0 8 2.2 8 5v5c0 2.8-3.6 5-8 5s-8-2.2-8-5v-5c0-2.8 3.6-5 8-5Z", fill: "none", stroke: "currentColor", "stroke-width": "1.7" }),
                        h("path", { d: "M8 9.5h8M8 14.5h8", fill: "none", stroke: "currentColor", "stroke-width": "1.5", "stroke-linecap": "round" })
                      ]);

      return h("span", { class: `feature-card-icon feature-card-icon-${icon}` }, [svg]);
    };

    const renderPageMasthead = (
      eyebrow: string,
      title: string,
      body: string,
      chips: string[],
      stats: Array<{ label: string; value: string; body: string }>
    ) =>
      h("section", { class: "panel page-masthead" }, [
        h("div", { class: "page-masthead-layout" }, [
          h("div", { class: "page-masthead-copy section-copy" }, [
            h("p", { class: "eyebrow" }, eyebrow),
            h("h2", title),
            h("p", { class: "muted-text" }, body),
            chips.length
              ? h("div", { class: "chip-row page-masthead-chips" }, chips.map((chip) => h("span", { class: "status-badge" }, chip)))
              : null
          ]),
          stats.length
            ? h("div", { class: "page-summary-grid" }, stats.map((card) =>
                h("article", { class: "page-summary-card" }, [
                  h("span", { class: "field-label" }, card.label),
                  h("strong", card.value),
                  h("small", card.body)
                ])
              ))
            : null
        ])
      ]);

    const renderWorkbenchMasthead = () =>
      h("section", { class: "panel page-masthead workspace-masthead" }, [
        h("div", { class: "workspace-masthead-row" }, [
          h("div", { class: "workspace-masthead-copy section-copy section-copy-compact" }, [
            h("p", { class: "eyebrow" }, localeText({ en: "Invoice workspace", "zh-CN": "智能开票", "ms-MY": "Ruang kerja invois", "id-ID": "Workspace invoice", "th-TH": "เวิร์กสเปซเอกสาร" })),
            h("h2", labels.value.draftTitle)
          ]),
          h("div", { class: "workspace-head-strip" }, [
            h("div", { class: "workspace-head-pill" }, [
              h("span", { class: "workspace-head-label" }, copy("countryLabel")),
              h("strong", getCountryName(locale.value, country.value))
            ]),
            h("div", { class: "workspace-head-pill" }, [
              h("span", { class: "workspace-head-label" }, localeText({ en: "Workspace", "zh-CN": "工作空间", "ms-MY": "Ruang kerja", "id-ID": "Workspace", "th-TH": "เวิร์กสเปซ" })),
              h("strong", activeOrganization.value?.name ?? copy("notAvailable"))
            ]),
            h("div", { class: "workspace-head-pill" }, [
              h("span", { class: "workspace-head-label" }, totalAmountLabel()),
              h("strong", `${draft.totals.grandTotal ?? 0} ${draft.totals.currency ?? pack.value.localCurrency}`)
            ]),
            h("div", { class: "workspace-head-pill" }, [
              h("span", { class: "workspace-head-label" }, localeText({ en: "Readiness", "zh-CN": "完成度", "ms-MY": "Kesiapan", "id-ID": "Kesiapan", "th-TH": "ความพร้อม" })),
              h("strong", localeText({
                en: `${draftProgressCompleteCount.value}/3 ready`,
                "zh-CN": `${draftProgressCompleteCount.value}/3 已就绪`,
                "ms-MY": `${draftProgressCompleteCount.value}/3 sedia`,
                "id-ID": `${draftProgressCompleteCount.value}/3 siap`,
                "th-TH": `${draftProgressCompleteCount.value}/3 พร้อม`
              }))
            ])
          ])
        ])
      ]);

    const renderFlowHeader = (page: "home" | "workspace") =>
      h("section", { class: page === "workspace" ? "panel workflow-header workspace-header" : "panel workflow-header" }, [
        h("div", { class: "workflow-header-top" }, [
          h("div", { class: "panel-copy" }, [
            h("p", { class: "eyebrow" }, page === "workspace"
              ? localeText({ en: "Invoice studio", "zh-CN": "智能开票", "ms-MY": "Studio invois", "id-ID": "Studio invoice" })
              : localeText({ en: "Product workflow", "zh-CN": "产品流程", "ms-MY": "Aliran produk", "id-ID": "Alur produk" })),
            h("h2", workflowTitle()),
            h("p", { class: "muted-text" }, workflowSubtitle())
          ]),
          h("div", { class: "chip-row" }, page === "workspace"
            ? [
                h("span", { class: "status-badge" }, getCountryName(locale.value, country.value)),
                h("span", { class: "status-badge" }, `${localeText({ en: "Rulebook", "zh-CN": "规则版本", "ms-MY": "Versi peraturan", "id-ID": "Versi aturan" })}: ${pack.value.rulebookVersion}`),
                h("span", { class: "status-badge" }, `${totalAmountLabel()}: ${draft.totals.grandTotal ?? 0} ${draft.totals.currency ?? pack.value.localCurrency}`)
              ]
            : [
                h("span", { class: "status-badge" }, getCountryName(locale.value, country.value)),
                h("span", { class: "status-badge" }, `${localeText({ en: "Rulebook", "zh-CN": "规则版本", "ms-MY": "Versi peraturan", "id-ID": "Versi aturan" })}: ${pack.value.rulebookVersion}`),
                h(
                  "span",
                  { class: "status-badge" },
                  localeText({
                    en: "Manual entry + OCR / AI + preview page",
                    "zh-CN": "手工录入 + OCR / AI + 预览页",
                    "ms-MY": "Input manual + OCR / AI + halaman pratonton",
                    "id-ID": "Input manual + OCR / AI + halaman pratinjau",
                    "th-TH": "กรอกเอง + OCR / AI + หน้าพรีวิว"
                  })
                )
              ])
        ]),
        h(
          "div",
          { class: "workflow-grid" },
          homeWorkflowSteps.value.map((step) =>
            h("article", { class: "workflow-card" }, [
              h("span", { class: "workflow-index" }, step.index),
              h("strong", step.title),
              h("p", step.body)
            ])
          )
        )
      ]);

    const renderEditorToolbar = () => {
      const readyLabel = localeText({ en: "Ready", "zh-CN": "已就绪", "ms-MY": "Sedia", "id-ID": "Siap", "th-TH": "พร้อม" });
      return h("section", { class: "editor-toolbar-shell" }, [
        h("section", { class: "editor-toolbar" }, [
          h("div", { class: "editor-toolbar-group editor-toolbar-group-mode" }, [
            h("div", { class: "entry-mode-pill-row" }, [
              h(
                "button",
                {
                  class: draftEntryMode.value === "manual" ? "entry-mode-pill active" : "entry-mode-pill",
                  type: "button",
                  onClick: () => setDraftEntryMode("manual")
                },
                localeText({ en: "Manual", "zh-CN": "手工录入", "ms-MY": "Manual", "id-ID": "Manual", "th-TH": "กรอกเอง" })
              ),
              h(
                "button",
                {
                  class: draftEntryMode.value === "import" ? "entry-mode-pill active" : "entry-mode-pill",
                  type: "button",
                  onClick: () => setDraftEntryMode("import")
                },
                localeText({ en: "Import", "zh-CN": "导入校对", "ms-MY": "Import", "id-ID": "Impor", "th-TH": "นำเข้า" })
              )
            ])
          ]),
          h(
            "button",
            {
              class: "icon-button editor-guide-button",
              type: "button",
              onClick: () => (editorGuideOpen.value = true)
            },
            localeText({
              en: "Guide",
              "zh-CN": "说明",
              "ms-MY": "Panduan",
              "id-ID": "Panduan",
              "th-TH": "คู่มือ"
            })
          )
        ]),
        h(
          "div",
          {
            class: "editor-step-strip",
            "aria-label": localeText({
              en: "Draft completion steps",
              "zh-CN": "草稿完成步骤",
              "ms-MY": "Langkah pelengkapan draf",
              "id-ID": "Langkah penyelesaian draft",
              "th-TH": "ขั้นตอนการทำร่างให้ครบ"
            })
          },
          draftProgressCards.value.map((card, index) => {
            const tabLabel = card.tab === "basics" ? labels.value.editorBasics : card.tab === "parties" ? labels.value.editorParties : labels.value.editorItems;
            const stepLabel = localeText({
              en: `Step ${index + 1}`,
              "zh-CN": `步骤 ${index + 1}`,
              "ms-MY": `Langkah ${index + 1}`,
              "id-ID": `Langkah ${index + 1}`,
              "th-TH": `ขั้นตอน ${index + 1}`
            });
            const stateLabel = card.complete
              ? readyLabel
              : localeText({
                  en: `${card.missingCount} left`,
                  "zh-CN": `还差 ${card.missingCount}`,
                  "ms-MY": `${card.missingCount} lagi`,
                  "id-ID": `Sisa ${card.missingCount}`,
                  "th-TH": `เหลือ ${card.missingCount}`
                });
            return h(
              "button",
              {
                class: [
                  "editor-step-item",
                  editorTab.value === card.tab ? "editor-step-item-active" : "",
                  card.complete ? "editor-step-item-complete" : "editor-step-item-open"
                ].filter(Boolean).join(" "),
                type: "button",
                title: `${stepLabel} · ${tabLabel}`,
                onClick: () => openEditorLocation(card.tab, card.fieldId)
              },
              [
                h("span", { class: "editor-step-index" }, stepLabel),
                h("span", { class: "editor-step-main" }, [
                  h("span", { class: "editor-step-title" }, tabLabel),
                  h("span", { class: card.complete ? "editor-step-state editor-step-state-ready" : "editor-step-state" }, stateLabel)
                ])
              ]
            );
          })
        )
      ]);
    };

    const renderDraftProgressPanel = () =>
      h("section", { class: "draft-progress-grid" }, draftProgressCards.value.map((card) =>
        h(
          "button",
          {
            class: [
              "draft-progress-card",
              editorTab.value === card.tab ? "draft-progress-card-active" : "",
              card.complete ? "draft-progress-card-complete" : "draft-progress-card-open"
            ].filter(Boolean).join(" "),
            type: "button",
            onClick: () => openEditorLocation(card.tab, card.fieldId)
          },
          [
            h("div", { class: "draft-progress-head" }, [
              h("span", { class: "draft-progress-label" }, card.tab === "basics" ? labels.value.editorBasics : card.tab === "parties" ? labels.value.editorParties : labels.value.editorItems),
              h(
                "span",
                { class: "draft-progress-pill" },
                card.complete
                  ? localeText({
                      en: "Ready",
                      "zh-CN": "已就绪",
                      "ms-MY": "Sedia",
                      "id-ID": "Siap",
                      "th-TH": "พร้อม"
                    })
                  : localeText({
                      en: `${card.missingCount} left`,
                      "zh-CN": `还差 ${card.missingCount} 项`,
                      "ms-MY": `${card.missingCount} lagi`,
                      "id-ID": `Sisa ${card.missingCount}`,
                      "th-TH": `เหลือ ${card.missingCount}`
                    })
              )
            ]),
            h("strong", card.title),
            h("p", card.body)
          ]
        )
      ));

    const renderBasicsView = () =>
      h("div", { class: "section-stack editor-section-shell" }, [
        h("div", { class: "editor-section-card editor-form-shell" }, [h("div", { class: "form-grid form-grid-2" }, [
          inputField(
            copy("invoiceNumber"),
            draft.invoiceNumber,
            (value) => (draft.invoiceNumber = value),
            "text",
            "field-span-2",
            "invoice-number",
            undefined,
            localeText({
              en: "Keep one stable invoice number per issued document or revision chain.",
              "zh-CN": "一张正式发出的发票应保持稳定编号，只有修订链才应延续同一主编号。",
              "ms-MY": "Kekalkan satu nombor invois yang stabil bagi setiap dokumen yang dikeluarkan atau rantaian pindaan.",
              "id-ID": "Pertahankan satu nomor invoice yang stabil untuk setiap dokumen yang diterbitkan atau rantai revisinya.",
              "th-TH": "ควรใช้เลขเอกสารที่ชัดเจนและคงที่ต่อหนึ่งเอกสารหรือชุดการแก้ไขเดียวกัน"
            }),
            { tone: keyFieldState.value.invoiceNumber ? "ready" : "missing" }
          ),
          country.value === "TH"
            ? selectField(
                localeText({
                  en: "Thailand invoice mode",
                  "zh-CN": "泰国发票模式",
                  "ms-MY": "Mod invois Thailand",
                  "id-ID": "Mode invoice Thailand",
                  "th-TH": "รูปแบบเอกสารของไทย"
                }),
                draft.invoiceType ?? "tax_invoice",
                thInvoiceModeOptions.value,
                (value) => {
                  draft.invoiceType = value;
                  if (value === "commercial_invoice") {
                    draft.taxRegistered = false;
                    draft.eInvoiceRequested = false;
                    setCountryExtension("etaxRegistered", false);
                  } else {
                    draft.taxRegistered = true;
                    draft.eInvoiceRequested = value === "etax_invoice";
                  }
                  applyCalculatedTotals();
                },
                "field-span-2",
                "th-invoice-type",
                thaiInvoiceModeHint.value
              )
            : null,
          inputField(
            copy("issueDate"),
            draft.issueDate,
            (value) => (draft.issueDate = value),
            "date",
            "",
            "issue-date",
            undefined,
            localeText({
              en:
                country.value === "SG"
                  ? "Use the actual date the tax invoice is issued."
                  : country.value === "MY"
                    ? "Use the issue date that will appear on the Malaysia document or e-Invoice submission."
                    : country.value === "ID"
                      ? "Keep the issue date aligned with the Indonesian tax invoice or supporting sales record."
                      : "Use the actual issue date for the Thai document mode you selected.",
              "zh-CN":
                country.value === "SG"
                  ? "填写这张税务发票实际出具的日期。"
                  : country.value === "MY"
                    ? "填写将出现在马来西亚单据或 e-Invoice 提交中的开票日期。"
                    : country.value === "ID"
                      ? "开票日期应与印尼税务发票或对应销售记录保持一致。"
                      : "填写与你选择的泰国发票模式一致的实际出具日期。",
              "ms-MY":
                country.value === "SG"
                  ? "Gunakan tarikh sebenar apabila invois cukai dikeluarkan."
                  : country.value === "MY"
                    ? "Gunakan tarikh terbit yang akan dipaparkan pada dokumen Malaysia atau penyerahan e-Invois."
                    : country.value === "ID"
                      ? "Pastikan tarikh terbit selaras dengan invois cukai Indonesia atau rekod jualan sokongan."
                      : "Gunakan tarikh sebenar bagi mod dokumen Thailand yang dipilih.",
              "id-ID":
                country.value === "SG"
                  ? "Gunakan tanggal sebenarnya saat invoice pajak diterbitkan."
                  : country.value === "MY"
                    ? "Gunakan tanggal terbit yang akan tampil pada dokumen Malaysia atau pengajuan e-Invoice."
                    : country.value === "ID"
                      ? "Pastikan tanggal terbit selaras dengan invoice pajak Indonesia atau catatan penjualan pendukung."
                      : "Gunakan tanggal terbit sebenarnya untuk mode dokumen Thailand yang dipilih.",
              "th-TH":
                country.value === "SG"
                  ? "ใช้วันที่ออกเอกสารจริงของใบกำกับภาษี"
                  : country.value === "MY"
                    ? "ใช้วันที่ที่จะปรากฏบนเอกสารของมาเลเซียหรือการส่ง e-Invoice"
                    : country.value === "ID"
                      ? "วันที่ออกเอกสารควรตรงกับเอกสารภาษีของอินโดนีเซียหรือบันทึกการขายที่เกี่ยวข้อง"
                      : "ใช้วันที่ออกเอกสารจริงให้ตรงกับรูปแบบเอกสารไทยที่เลือก"
            }),
            { tone: keyFieldState.value.issueDate ? "ready" : "missing" }
          ),
          inputField(
            copy("issueTime"),
            draft.issueTime,
            (value) => (draft.issueTime = value),
            "time",
            "",
            "issue-time",
            undefined,
            localeText({
              en:
                country.value === "MY"
                  ? "Malaysia e-Invoice flows usually expect an issue time."
                  : country.value === "TH" && (draft.invoiceType ?? "tax_invoice") === "etax_invoice"
                    ? "Keep the time aligned with the actual Thai e-Tax issuance event."
                    : "Use this when your document standard or internal process needs a precise issue time.",
              "zh-CN":
                country.value === "MY"
                  ? "马来西亚 e-Invoice 路径通常需要开票时间。"
                  : country.value === "TH" && (draft.invoiceType ?? "tax_invoice") === "etax_invoice"
                    ? "应与泰国 e-Tax 实际发行时间保持一致。"
                    : "当你的单据标准或内部流程需要精确到时间时再填写。",
              "ms-MY":
                country.value === "MY"
                  ? "Aliran e-Invois Malaysia biasanya memerlukan masa terbit."
                  : country.value === "TH" && (draft.invoiceType ?? "tax_invoice") === "etax_invoice"
                    ? "Selaraskan masa ini dengan acara terbitan e-Tax Thailand yang sebenar."
                    : "Gunakan apabila standard dokumen atau proses dalaman anda memerlukan masa yang tepat.",
              "id-ID":
                country.value === "MY"
                  ? "Jalur e-Invoice Malaysia biasanya memerlukan waktu terbit."
                  : country.value === "TH" && (draft.invoiceType ?? "tax_invoice") === "etax_invoice"
                    ? "Selaraskan waktu ini dengan momen penerbitan e-Tax Thailand yang sebenarnya."
                    : "Gunakan saat standar dokumen atau proses internal Anda memerlukan waktu yang presisi.",
              "th-TH":
                country.value === "MY"
                  ? "เส้นทาง e-Invoice ของมาเลเซียมักต้องระบุเวลาออกเอกสาร"
                  : country.value === "TH" && (draft.invoiceType ?? "tax_invoice") === "etax_invoice"
                    ? "ควรใช้เวลาเดียวกับเหตุการณ์ออก e-Tax จริง"
                    : "กรอกเมื่อมาตรฐานเอกสารหรือกระบวนการภายในของคุณต้องใช้เวลาออกเอกสารอย่างชัดเจน"
            }),
            country.value === "MY" ? { tone: keyFieldState.value.issueTime ? "ready" : "missing" } : {}
          ),
          inputField(
            copy("currency"),
            draft.totals.currency,
            (value) => (draft.totals.currency = value.toUpperCase()),
            "text",
            "",
            "invoice-currency",
            undefined,
            country.value === "TH"
              ? localeText({
                  en: "THB is the normal default for Thai tax invoices. Use another currency only if the approved format allows it.",
                  "zh-CN": "泰国税务发票通常默认使用 THB；只有在允许的格式下才应改用其他币种。",
                  "ms-MY": "THB ialah mata wang lalai biasa bagi invois cukai Thailand. Gunakan mata wang lain hanya jika format yang diluluskan membenarkannya.",
                  "id-ID": "THB adalah default normal untuk invoice pajak Thailand. Gunakan mata uang lain hanya jika format yang disetujui mengizinkannya.",
                  "th-TH": "ใบกำกับภาษีของไทยโดยทั่วไปควรใช้ THB เป็นหลัก เว้นแต่รูปแบบที่ได้รับอนุมัติจะอนุญาตอย่างอื่น"
                })
              : localeText({
                  en: `The default local currency for ${getCountryName(locale.value, country.value)} is ${pack.value.localCurrency}.`,
                  "zh-CN": `${getCountryName(locale.value, country.value)} 的默认本地币种是 ${pack.value.localCurrency}。`,
                  "ms-MY": `Mata wang tempatan lalai untuk ${getCountryName(locale.value, country.value)} ialah ${pack.value.localCurrency}.`,
                  "id-ID": `Mata uang lokal default untuk ${getCountryName(locale.value, country.value)} adalah ${pack.value.localCurrency}.`,
                  "th-TH": `สกุลเงินท้องถิ่นหลักของ ${getCountryName(locale.value, country.value)} คือ ${pack.value.localCurrency}`
                })
          ),
          inputField(
            copy("last12Turnover"),
            draft.turnoverLast12Months,
            (value) => (draft.turnoverLast12Months = Number(value || 0)),
            "number",
            "",
            "last-12-turnover",
            undefined,
            localeText({
              en:
                country.value === "SG"
                  ? "Used to monitor the S$1 million GST threshold."
                  : country.value === "MY"
                    ? "Used to judge SST thresholds and whether the e-Invoice rollout is likely to apply."
                    : country.value === "ID"
                      ? "Used to monitor the PKP threshold of IDR 4.8 billion."
                      : "Used to monitor the Thai VAT threshold of THB 1.8 million.",
              "zh-CN":
                country.value === "SG"
                  ? "用于监控 S$1 million 的 GST 注册阈值。"
                  : country.value === "MY"
                    ? "用于判断 SST 门槛，以及是否可能进入 e-Invoice 路径。"
                    : country.value === "ID"
                      ? "用于监控 IDR 4.8 billion 的 PKP 注册门槛。"
                      : "用于监控 THB 1.8 million 的泰国 VAT 注册门槛。",
              "ms-MY":
                country.value === "SG"
                  ? "Digunakan untuk memantau ambang GST S$1 juta."
                  : country.value === "MY"
                    ? "Digunakan untuk menilai ambang SST dan sama ada pelaksanaan e-Invois mungkin terpakai."
                    : country.value === "ID"
                      ? "Digunakan untuk memantau ambang PKP IDR 4.8 bilion."
                      : "Digunakan untuk memantau ambang VAT Thailand THB 1.8 juta.",
              "id-ID":
                country.value === "SG"
                  ? "Digunakan untuk memantau ambang GST S$1 juta."
                  : country.value === "MY"
                    ? "Digunakan untuk menilai ambang SST dan apakah rollout e-Invoice kemungkinan berlaku."
                    : country.value === "ID"
                      ? "Digunakan untuk memantau ambang PKP IDR 4,8 miliar."
                      : "Digunakan untuk memantau ambang VAT Thailand THB 1,8 juta.",
              "th-TH":
                country.value === "SG"
                  ? "ใช้เพื่อติดตามเกณฑ์ GST S$1 ล้าน"
                  : country.value === "MY"
                    ? "ใช้ประเมินเกณฑ์ SST และดูว่ามีโอกาสเข้าสู่เส้นทาง e-Invoice หรือไม่"
                    : country.value === "ID"
                      ? "ใช้ติดตามเกณฑ์ PKP ที่ IDR 4.8 พันล้าน"
                      : "ใช้ติดตามเกณฑ์จดทะเบียน VAT ของไทยที่ THB 1.8 ล้าน"
            })
          ),
          selectField(
            copy("buyerType"),
            draft.buyerType ?? "business",
            buyerTypeOptions.value,
            (value) => (draft.buyerType = value as InvoiceInput["buyerType"]),
            "",
            "buyer-type",
            buyerTypeHint.value
          ),
          selectField(
            copy("supplyNature"),
            draft.supplyNature ?? "domestic",
            supplyNatureOptions.value,
            (value) => {
              draft.supplyNature = value as InvoiceInput["supplyNature"];
              applyCalculatedTotals();
            },
            "",
            "supply-nature",
            supplyNatureHint.value
          ),
          selectField(
            copy("businessActivity"),
            draft.businessActivity ?? "general_services",
            businessActivityOptions.value,
            (value) => {
              draft.businessActivity = value as InvoiceInput["businessActivity"];
              applyCalculatedTotals();
            },
            "field-span-2",
            "business-activity",
            businessActivityHint.value
          ),
          country.value === "ID"
            ? inputField(
                localeText({
                  en: "Tax invoice transaction code",
                  "zh-CN": "税务发票交易代码",
                  "ms-MY": "Kod transaksi invois cukai",
                  "id-ID": "Kode transaksi faktur pajak",
                  "th-TH": "รหัสประเภทธุรกรรมภาษี"
                }),
                getCountryExtensionText("transactionCode"),
                (value) => setCountryExtension("transactionCode", value),
                "text",
                "field-span-2",
                "id-transaction-code",
                undefined,
                localeText({
                  en: "Use the transaction code that matches the Indonesian VAT treatment you are applying.",
                  "zh-CN": "请填写与当前印尼 VAT 处理路径一致的交易代码。",
                  "ms-MY": "Gunakan kod transaksi yang sepadan dengan rawatan VAT Indonesia yang anda gunakan.",
                  "id-ID": "Gunakan kode transaksi yang sesuai dengan perlakuan VAT Indonesia yang sedang Anda pakai.",
                  "th-TH": "ใช้รหัสธุรกรรมที่สอดคล้องกับแนวทาง VAT ของอินโดนีเซียที่กำลังใช้"
                })
              )
            : null
        ]),
          h(
            "div",
            { class: "checkbox-row" },
            [
              checkboxField(taxRegistrationLabel.value, draft.taxRegistered, (value) => {
                draft.taxRegistered = value;
                applyCalculatedTotals();
              }),
              country.value === "MY"
                ? checkboxField(copy("eInvoiceFlow"), draft.eInvoiceRequested, (value) => (draft.eInvoiceRequested = value))
                : null,
              country.value === "TH" && draft.invoiceType === "etax_invoice"
                ? checkboxField(
                    localeText({
                      en: "Approved e-Tax / e-Receipt issuer",
                      "zh-CN": "已获批准的 e-Tax / e-Receipt 发行资格",
                      "ms-MY": "Pengeluar e-Tax / e-Receipt yang diluluskan",
                      "id-ID": "Penerbit e-Tax / e-Receipt yang disetujui",
                      "th-TH": "ผู้มีสิทธิออก e-Tax / e-Receipt"
                    }),
                    getCountryExtensionFlag("etaxRegistered"),
                    (value) => setCountryExtension("etaxRegistered", value)
                  )
                : null,
              country.value === "TH" && draft.totals.currency?.toUpperCase() !== "THB"
                ? checkboxField(
                    localeText({
                      en: "Approved foreign language / currency use",
                      "zh-CN": "已获批准使用外文 / 外币",
                      "ms-MY": "Kelulusan penggunaan bahasa / mata wang asing",
                      "id-ID": "Persetujuan penggunaan bahasa / mata uang asing",
                      "th-TH": "ได้รับอนุมัติให้ใช้ภาษาหรือสกุลเงินต่างประเทศ"
                    }),
                    getCountryExtensionFlag("foreignCurrencyApproval"),
                    (value) => setCountryExtension("foreignCurrencyApproval", value)
                  )
                : null,
              draft.supplyNature === "reverse_charge"
                ? checkboxField(copy("reverseCharge"), draft.reverseCharge, (value) => (draft.reverseCharge = value))
                : null
            ].filter(Boolean)
          )
        ])
      ]);

    const renderPartyCardCompact = (title: string, party: InvoiceInput["seller"], isBuyer = false) =>
      h("article", { class: "subpanel editor-section-card editor-party-card" }, [
        h("div", { class: "section-copy section-copy-compact" }, [h("h3", title)]),
        h("div", { class: "form-grid form-grid-2" }, [
          inputField(
            copy("legalName"),
            party.legalName,
            (value) => (party.legalName = value),
            "text",
            "field-span-2",
            isBuyer ? "buyer-legal-name" : "seller-legal-name",
            undefined,
            "",
            {
              tone: isBuyer
                ? keyFieldState.value.buyerLegalName
                  ? "ready"
                  : buyerNeedsNamedProfile.value
                    ? "missing"
                    : undefined
                : keyFieldState.value.sellerLegalName
                  ? "ready"
                  : "missing"
            }
          ),
          isBuyer
            ? null
            : inputField(
                sellerRegistrationLabel(),
                party.registrationNumber,
                (value) => (party.registrationNumber = value),
                "text",
                "",
                "seller-registration-number",
                undefined,
                sellerRegistrationHelp.value,
                { tone: sellerNeedsRegistrationNumber.value ? (keyFieldState.value.sellerRegistrationNumber ? "ready" : "missing") : undefined }
              ),
          inputField(
            isBuyer ? buyerTaxLabel() : sellerTaxLabel(),
            party.taxNumber,
            (value) => (party.taxNumber = value),
            "text",
            "",
            isBuyer ? "buyer-tax-number" : "seller-tax-number",
            undefined,
            isBuyer ? buyerTaxHelp.value : sellerTaxHelp.value,
            !isBuyer
              ? { tone: sellerNeedsTaxProfile.value ? (keyFieldState.value.sellerTaxNumber ? "ready" : "missing") : undefined }
              : {}
          ),
          country.value === "TH"
            ? inputField(
                localeText({
                  en: "Head office / branch no.",
                  "zh-CN": "总机构 / 分支编号",
                  "ms-MY": "No. ibu pejabat / cawangan",
                  "id-ID": "No. kantor pusat / cabang",
                  "th-TH": "สำนักงานใหญ่ / เลขสาขา"
                }),
                getCountryExtensionText(isBuyer ? "buyerBranchCode" : "sellerBranchCode"),
                (value) => setCountryExtension(isBuyer ? "buyerBranchCode" : "sellerBranchCode", value),
                "text",
                "",
                isBuyer ? "buyer-branch-code" : "seller-branch-code",
                undefined,
                localeText({
                  en: "Thai documents often distinguish the head office from a branch by branch number or branch code.",
                  "zh-CN": "泰国单据里常常需要用分支编号区分总机构与门店 / 分支。",
                  "ms-MY": "Dokumen Thailand selalunya membezakan ibu pejabat dan cawangan melalui nombor atau kod cawangan.",
                  "id-ID": "Dokumen Thailand sering membedakan kantor pusat dan cabang dengan nomor atau kode cabang.",
                  "th-TH": "เอกสารไทยมักใช้เลขสาขาหรือรหัสสาขาเพื่อแยกสำนักงานใหญ่กับสาขา"
                })
              )
            : null,
          inputField(
            copy("phone"),
            party.phone,
            (value) => (party.phone = value),
            "text",
            "",
            isBuyer ? "buyer-phone" : "seller-phone",
            undefined,
            localeText({
              en:
                country.value === "MY"
                  ? "Malaysia e-Invoice reviews usually benefit from a reachable contact number."
                  : isBuyer
                    ? "Add a buyer contact number when the buyer profile needs to be explicit."
                    : "Add the seller contact number that customers or reviewers would reasonably expect.",
              "zh-CN":
                country.value === "MY"
                  ? "马来西亚 e-Invoice 场景通常更适合保留可联系的电话号码。"
                  : isBuyer
                    ? "当买方资料需要更完整展示时，建议补充联系电话。"
                    : "建议填写客户或复核人员可以合理联系到的卖方电话。",
              "ms-MY":
                country.value === "MY"
                  ? "Semakan e-Invois Malaysia biasanya lebih baik jika ada nombor telefon yang boleh dihubungi."
                  : isBuyer
                    ? "Tambahkan nombor telefon pembeli apabila profil pembeli perlu lebih jelas."
                    : "Tambahkan nombor telefon penjual yang munasabah untuk pelanggan atau penyemak.",
              "id-ID":
                country.value === "MY"
                  ? "Peninjauan e-Invoice Malaysia biasanya lebih baik jika ada nomor telepon yang dapat dihubungi."
                  : isBuyer
                    ? "Tambahkan nomor telepon pembeli bila profil pembeli perlu lebih jelas."
                    : "Tambahkan nomor telepon penjual yang wajar dihubungi pelanggan atau peninjau.",
              "th-TH":
                country.value === "MY"
                  ? "กรณี e-Invoice ของมาเลเซียมักควรมีหมายเลขโทรศัพท์ที่ติดต่อได้"
                  : isBuyer
                    ? "เพิ่มเบอร์โทรของผู้ซื้อเมื่อจำเป็นต้องแสดงข้อมูลผู้ซื้อให้ชัดขึ้น"
                    : "เพิ่มเบอร์โทรของผู้ขายที่ลูกค้าหรือผู้ตรวจทานควรติดต่อได้"
            })
          ),
          inputField(
            copy("email"),
            party.email,
            (value) => (party.email = value),
            "email",
            "",
            isBuyer ? "buyer-email" : "seller-email",
            undefined,
            localeText({
              en:
                country.value === "MY"
                  ? "Keep a buyer or seller email when the Malaysia document path needs fuller identity data."
                  : "Use a business email only when it genuinely belongs to this invoicing party.",
              "zh-CN":
                country.value === "MY"
                  ? "当马来西亚单据路径需要更完整身份资料时，建议保留买卖双方邮箱。"
                  : "只有当该邮箱确实属于当前开票主体时再填写。",
              "ms-MY":
                country.value === "MY"
                  ? "Simpan e-mel penjual atau pembeli apabila laluan dokumen Malaysia memerlukan identiti yang lebih lengkap."
                  : "Gunakan e-mel perniagaan hanya apabila ia benar-benar milik pihak pada invois ini.",
              "id-ID":
                country.value === "MY"
                  ? "Simpan email penjual atau pembeli saat jalur dokumen Malaysia memerlukan identitas yang lebih lengkap."
                  : "Gunakan email bisnis hanya bila benar-benar milik pihak pada invoice ini.",
              "th-TH":
                country.value === "MY"
                  ? "ควรเก็บอีเมลของผู้ขายหรือผู้ซื้อเมื่อเส้นทางเอกสารของมาเลเซียต้องใช้ข้อมูลตัวตนที่ครบขึ้น"
                  : "กรอกอีเมลธุรกิจเฉพาะเมื่อเป็นอีเมลของคู่สัญญาในเอกสารนี้จริง"
            })
          ),
          textareaField(
            copy("addressLine1"),
            party.address?.line1,
            (value) => (party.address!.line1 = value),
            4,
            "field-span-2",
            isBuyer ? "buyer-address-line1" : "seller-address-line1",
            localeText({
              en:
                country.value === "TH"
                  ? "Use the main business or branch address that matches the Thai issuing or receiving entity."
                  : country.value === "ID"
                    ? "Keep the operating or registered address consistent with the Indonesian tax profile used on the document."
                    : "Use the address that customers, auditors, or tax reviewers should associate with this party.",
              "zh-CN":
                country.value === "TH"
                  ? "填写与泰国开票或收票主体一致的总机构或分支地址。"
                  : country.value === "ID"
                    ? "地址应与这张单据所使用的印尼税务身份或经营地址保持一致。"
                    : "填写客户、审计人员或税务复核人员应当关联到该主体的地址。",
              "ms-MY":
                country.value === "TH"
                  ? "Gunakan alamat ibu pejabat atau cawangan yang sepadan dengan entiti Thailand yang mengeluarkan atau menerima dokumen."
                  : country.value === "ID"
                    ? "Pastikan alamat operasi atau berdaftar selaras dengan profil cukai Indonesia yang digunakan pada dokumen ini."
                    : "Gunakan alamat yang patut dikaitkan dengan pihak ini oleh pelanggan, auditor, atau penyemak cukai.",
              "id-ID":
                country.value === "TH"
                  ? "Gunakan alamat kantor pusat atau cabang yang sesuai dengan entitas Thailand yang menerbitkan atau menerima dokumen."
                  : country.value === "ID"
                    ? "Pastikan alamat operasional atau terdaftar selaras dengan profil pajak Indonesia yang dipakai pada dokumen ini."
                    : "Gunakan alamat yang seharusnya dikaitkan dengan pihak ini oleh pelanggan, auditor, atau peninjau pajak.",
              "th-TH":
                country.value === "TH"
                  ? "ใช้ที่อยู่สำนักงานใหญ่หรือสาขาที่ตรงกับนิติบุคคลไทยที่ออกหรือรับเอกสารนี้"
                  : country.value === "ID"
                    ? "ทำให้ที่อยู่ประกอบการหรือที่อยู่จดทะเบียนสอดคล้องกับโปรไฟล์ภาษีของอินโดนีเซียที่ใช้ในเอกสารนี้"
                    : "ใช้ที่อยู่ที่ลูกค้า ผู้ตรวจสอบ หรือผู้ทบทวนภาษีควรเชื่อมโยงกับคู่สัญญารายนี้"
            }),
            {
              tone: isBuyer
                ? keyFieldState.value.buyerAddress
                  ? "ready"
                  : buyerNeedsNamedProfile.value
                    ? "missing"
                    : undefined
                : keyFieldState.value.sellerAddress
                  ? "ready"
                  : "missing"
            }
          ),
          inputField(copy("city"), party.address?.city, (value) => (party.address!.city = value), "text", "", isBuyer ? "buyer-city" : "seller-city"),
          inputField(copy("postalCode"), party.address?.postalCode, (value) => (party.address!.postalCode = value), "text", "", isBuyer ? "buyer-postal-code" : "seller-postal-code"),
          isBuyer
            ? inputField(
                copy("projected12Turnover"),
                draft.projectedTurnoverNext12Months,
                (value) => (draft.projectedTurnoverNext12Months = Number(value || 0)),
                "number",
                "field-span-2",
                "projected-12-turnover",
                undefined,
                localeText({
                  en:
                    country.value === "SG"
                      ? "Important for Singapore's prospective GST test."
                      : country.value === "MY"
                        ? "Use this when growth may soon push the business into SST or e-Invoice scope."
                        : country.value === "ID"
                          ? "Helpful when growth planning may soon affect PKP registration decisions."
                          : "Helpful when future Thai revenue may push the business closer to VAT registration.",
                  "zh-CN":
                    country.value === "SG"
                      ? "这是新加坡预测性 GST 注册测试的重要字段。"
                      : country.value === "MY"
                        ? "当业务增长可能很快触发 SST 或 e-Invoice 时，建议填写。"
                        : country.value === "ID"
                          ? "当业务增长规划可能很快影响 PKP 注册判断时，建议填写。"
                          : "当未来泰国收入可能逼近 VAT 注册门槛时，这个字段很有帮助。",
                  "ms-MY":
                    country.value === "SG"
                      ? "Medan ini penting untuk ujian prospektif GST Singapura."
                      : country.value === "MY"
                        ? "Gunakan apabila pertumbuhan mungkin mendorong perniagaan ke dalam skop SST atau e-Invois."
                        : country.value === "ID"
                          ? "Membantu apabila perancangan pertumbuhan mungkin menjejaskan keputusan pendaftaran PKP."
                          : "Membantu apabila hasil masa depan di Thailand mungkin mendekatkan perniagaan kepada pendaftaran VAT.",
                  "id-ID":
                    country.value === "SG"
                      ? "Bidang ini penting untuk uji prospektif GST Singapura."
                      : country.value === "MY"
                        ? "Gunakan saat pertumbuhan dapat segera membawa bisnis ke ruang lingkup SST atau e-Invoice."
                        : country.value === "ID"
                          ? "Membantu saat rencana pertumbuhan dapat segera memengaruhi keputusan pendaftaran PKP."
                          : "Membantu saat pendapatan Thailand ke depan dapat mendekatkan bisnis ke ambang pendaftaran VAT.",
                  "th-TH":
                    country.value === "SG"
                      ? "ข้อมูลนี้สำคัญต่อการประเมินเกณฑ์ GST แบบคาดการณ์ของสิงคโปร์"
                      : country.value === "MY"
                        ? "ใช้เมื่อการเติบโตอาจทำให้ธุรกิจเข้าสู่ขอบเขต SST หรือ e-Invoice ในไม่ช้า"
                        : country.value === "ID"
                          ? "มีประโยชน์เมื่อแผนการเติบโตอาจส่งผลต่อการตัดสินใจจดทะเบียน PKP"
                          : "มีประโยชน์เมื่อรายได้ในไทยช่วงต่อไปอาจทำให้ธุรกิจเข้าใกล้เกณฑ์จดทะเบียน VAT"
                })
              )
            : null
        ])
      ]);

    const renderPartiesView = () =>
      h("div", { class: "section-stack editor-section-shell" }, [
        h("div", { class: "editor-section-body split-panel-grid" }, [renderPartyCardCompact(copy("seller"), draft.seller), renderPartyCardCompact(copy("buyer"), draft.buyer, true)])
      ]);

    const renderItemsView = () =>
      h("div", { class: "section-stack editor-section-shell" }, [
        h("div", { class: "panel-head editor-section-lead" }, [
          h("div", { class: "panel-copy section-copy-compact" }, [h("h3", copy("invoiceItems"))]),
          h("button", { class: "action-button", type: "button", onClick: onAddItem }, copy("addRow"))
        ]),
        h(
          "div",
          { class: "item-list editor-section-card editor-list-shell" },
          draft.items.map((item, index) =>
            h("article", { class: "item-card" }, [
              h("div", { class: "item-card-head" }, [
                h("strong", `${copy("invoiceItems")} ${index + 1}`),
                h("button", { class: "action-button secondary-button subtle-button", type: "button", onClick: () => onRemoveItem(index), disabled: draft.items.length === 1 }, copy("remove"))
              ]),
              h("div", { class: "form-grid form-grid-2" }, [
                inputField(copy("description"), item.description, (value) => {
                  item.description = value;
                }, "text", "field-span-2", `invoice-item-${index}-description`, undefined, localeText({
                  en:
                    country.value === "TH"
                      ? "Use a plain business description that still makes sense on a Thai customer document."
                      : "Use a customer-readable item description that still makes the tax treatment clear.",
                  "zh-CN":
                    country.value === "TH"
                      ? "描述应既适合泰国客户阅读，也能准确表达这笔商品或服务。"
                      : "描述既要让客户看得懂，也要让税务处理路径足够清晰。",
                  "ms-MY":
                    country.value === "TH"
                      ? "Gunakan deskripsi perniagaan yang jelas dan masih sesuai pada dokumen pelanggan Thailand."
                      : "Gunakan deskripsi item yang mudah difahami pelanggan dan masih jelas untuk rawatan cukai.",
                  "id-ID":
                    country.value === "TH"
                      ? "Gunakan deskripsi bisnis yang jelas dan tetap cocok tampil pada dokumen pelanggan Thailand."
                      : "Gunakan deskripsi item yang mudah dipahami pelanggan sekaligus jelas untuk perlakuan pajak.",
                  "th-TH":
                    country.value === "TH"
                      ? "ใช้คำอธิบายที่เหมาะกับเอกสารของลูกค้าไทยและยังบอกได้ชัดว่าคือสินค้า/บริการอะไร"
                      : "ใช้คำอธิบายที่ลูกค้าอ่านเข้าใจง่ายและยังสะท้อนแนวทางภาษีได้ชัดเจน"
                }), index === 0 ? { tone: keyFieldState.value.firstItemDescription ? "ready" : "missing" } : {}),
                inputField(classificationCodeLabel(), item.classificationCode, (value) => {
                  item.classificationCode = value;
                }, "text", "", `invoice-item-${index}-classificationCode`, undefined, classificationCodeHelp.value, index === 0 && (country.value === "MY" || country.value === "ID") ? { tone: keyFieldState.value.firstItemClassification ? "ready" : "missing" } : {}),
                inputField(copy("qty"), item.quantity, (value) => {
                  item.quantity = Number(value || 0);
                  applyCalculatedTotals();
                }, "number", "", `invoice-item-${index}-quantity`),
                inputField(copy("unitPrice"), item.unitPrice, (value) => {
                  item.unitPrice = Number(value || 0);
                  applyCalculatedTotals();
                }, "number", "", `invoice-item-${index}-unitPrice`, undefined, localeText({
                  en: "Enter the commercial unit price before tax unless your local format explicitly needs a different convention.",
                  "zh-CN": "除非当地格式另有明确要求，否则这里通常填写未税单价。",
                  "ms-MY": "Masukkan harga seunit komersial sebelum cukai kecuali format tempatan anda jelas memerlukan konvensyen lain.",
                  "id-ID": "Masukkan harga satuan komersial sebelum pajak kecuali format lokal Anda secara jelas memerlukan konvensi lain.",
                  "th-TH": "โดยทั่วไปควรกรอกราคาต่อหน่วยก่อนภาษี เว้นแต่รูปแบบท้องถิ่นที่คุณใช้จะกำหนดไว้ต่างออกไป"
                }), index === 0 ? { tone: keyFieldState.value.firstItemUnitPrice ? "ready" : "missing" } : {}),
                inputField(copy("taxRate"), item.taxRate, (value) => {
                  item.taxRate = Number(value || 0);
                  applyCalculatedTotals();
                }, "number", "", `invoice-item-${index}-taxRate`, undefined, localeText({
                  en:
                    country.value === "ID"
                      ? "Indonesia normally follows 11% for standard domestic cases and 12% only for the luxury path."
                      : country.value === "MY"
                        ? "Malaysia commonly stays on 6% or 8% service-tax paths depending on the activity."
                        : country.value === "TH"
                          ? "Thailand currently uses 7% for the normal VAT path through 30 September 2026."
                          : "Singapore domestic standard-rated supplies normally use 9%.",
                  "zh-CN":
                    country.value === "ID"
                      ? "印尼普通境内交易通常走 11%，12% 仅用于奢侈品路径。"
                      : country.value === "MY"
                        ? "马来西亚通常按业务活动走 6% 或 8% 的服务税路径。"
                        : country.value === "TH"
                          ? "泰国当前普通 VAT 路径在 2026-09-30 前通常按 7%。"
                          : "新加坡境内标准税率交易通常按 9%。",
                  "ms-MY":
                    country.value === "ID"
                      ? "Indonesia biasanya menggunakan 11% untuk kes domestik standard dan 12% hanya untuk laluan barang mewah."
                      : country.value === "MY"
                        ? "Malaysia lazimnya berada pada laluan cukai perkhidmatan 6% atau 8% mengikut aktiviti."
                        : country.value === "TH"
                          ? "Thailand kini menggunakan 7% bagi laluan VAT biasa sehingga 30 September 2026."
                          : "Bekalan domestik standard Singapura biasanya menggunakan 9%.",
                  "id-ID":
                    country.value === "ID"
                      ? "Indonesia biasanya memakai 11% untuk kasus domestik standar dan 12% hanya untuk jalur barang mewah."
                      : country.value === "MY"
                        ? "Malaysia umumnya berada pada jalur pajak layanan 6% atau 8% tergantung aktivitasnya."
                        : country.value === "TH"
                          ? "Thailand saat ini memakai 7% untuk jalur VAT biasa sampai 30 September 2026."
                          : "Penyerahan domestik standar Singapura biasanya memakai 9%.",
                  "th-TH":
                    country.value === "ID"
                      ? "อินโดนีเซียโดยทั่วไปใช้ 11% สำหรับกรณีปกติในประเทศ และใช้ 12% เฉพาะเส้นทางสินค้าฟุ่มเฟือย"
                      : country.value === "MY"
                        ? "มาเลเซียมักใช้เส้นทางภาษีบริการ 6% หรือ 8% ตามประเภทธุรกิจ"
                        : country.value === "TH"
                          ? "ปัจจุบันไทยใช้ 7% สำหรับเส้นทาง VAT ปกติไปจนถึง 30 กันยายน 2026"
                          : "ธุรกรรมในประเทศของสิงคโปร์ที่ใช้อัตรามาตรฐานมักอยู่ที่ 9%"
                }), index === 0 ? { tone: keyFieldState.value.firstItemTaxRate ? "ready" : "missing" } : {}),
                inputField(copy("netAmount"), item.amountExcludingTax, (value) => {
                  item.amountExcludingTax = Number(value || 0);
                  applyCalculatedTotals();
                }, "number", "", `invoice-item-${index}-amountExcludingTax`),
                inputField(copy("taxAmount"), item.taxAmount, (value) => {
                  item.taxAmount = Number(value || 0);
                  applyCalculatedTotals();
                }, "number", "", `invoice-item-${index}-taxAmount`),
                inputField(copy("lineTotal"), item.totalAmount, (value) => {
                  item.totalAmount = Number(value || 0);
                  applyCalculatedTotals();
                }, "number", "field-span-2", `invoice-item-${index}-totalAmount`)
              ])
            ])
          )
        ),
        h("div", { class: "totals-strip editor-summary-strip" }, [
          h("span", `${copy("subtotal")}: ${draft.totals.subtotal ?? 0}`),
          h("span", `${copy("tax")}: ${draft.totals.taxTotal ?? 0}`),
          h("strong", `${totalAmountLabel()}: ${draft.totals.grandTotal ?? 0} ${draft.totals.currency ?? pack.value.localCurrency}`)
        ])
      ]);

    const renderIntakeView = () =>
      h("div", { class: "section-stack editor-section-shell" }, [
        extractStatus.value ? h("div", { class: "notice-bar intake-status-bar" }, extractStatus.value) : null,
        h("div", { class: "editor-section-body split-panel-grid" }, [
          h("article", { class: "subpanel editor-section-card" }, [textareaField(copy("notesLabel"), notesInput.value, (value) => (notesInput.value = value), 8, "", "draft-notes")]),
          h("article", { class: "subpanel editor-section-card" }, [textareaField(copy("ocrTextLabel"), extractText.value, (value) => (extractText.value = value), 8, "", "ocr-text-input")])
        ]),
        h("article", { class: "subpanel upload-panel editor-section-card" }, [
          h("div", { class: "section-copy section-copy-compact" }, [h("h3", copy("uploadImageLabel"))]),
          h("input", {
            class: "field-input file-input",
            type: "file",
            accept: "image/jpeg,image/png,image/webp,application/pdf",
            onChange: async (event: Event) => {
              const file = (event.target as HTMLInputElement).files?.[0];
              if (!file) {
                uploadedFileData.value = "";
                uploadedFileName.value = "";
                uploadedMime.value = "";
                return;
              }
              if (!ALLOWED_EXTRACT_FILE_TYPES.has(file.type)) {
                uploadedFileData.value = "";
                uploadedFileName.value = "";
                uploadedMime.value = "";
                extractStatus.value = copy("uploadTypeError");
                return;
              }
              const byteLimit = file.type === "application/pdf" ? MAX_EXTRACT_PDF_BYTES : MAX_EXTRACT_IMAGE_BYTES;
              if (file.size > byteLimit) {
                uploadedFileData.value = "";
                uploadedFileName.value = "";
                uploadedMime.value = "";
                extractStatus.value = copy("uploadSizeError");
                return;
              }
              uploadedFileData.value = await readFileAsDataUrl(file);
              uploadedFileName.value = file.name;
              uploadedMime.value = file.type;
              extractStatus.value = copy("uploadSelected", {
                name: file.name,
                size: (file.size / 1024 / 1024).toFixed(2)
              });
            }
          }),
          uploadedFileName.value ? h("div", { class: "file-pill upload-file-pill" }, uploadedFileName.value) : null,
          h("div", { class: "toolbar-row upload-action-row" }, [
            h("button", { class: "action-button", type: "button", onClick: onExtract, disabled: !canUseServerActions.value || !bootstrap.aiAvailable || !hasExtractInput.value }, copy("extractDraft")),
            h("button", { class: "action-button secondary-button", type: "button", onClick: applyCalculatedTotals }, copy("recalculateTotals"))
          ])
        ])
      ]);

    const renderEditorView = () => {
      switch (editorTab.value) {
        case "parties":
          return renderPartiesView();
        case "items":
          return renderItemsView();
        case "intake":
          return renderIntakeView();
        default:
          return renderBasicsView();
      }
    };

      const renderResultView = () =>
        h("div", { class: "section-stack" }, [
          h("article", { class: "result-summary-card" }, [
            h("div", { class: "result-summary-toolbar" }, [
              h("div", { class: "chip-row result-summary-chips" }, [
              h("span", { class: "result-pill result-" + activeResult.value.status }, t(locale.value, "status_" + activeResult.value.status)),
                h("span", { class: "result-pill pill-muted" }, `${copy("riskLabel")}: ${localizeRisk(activeResult.value.overallRiskLevel)}`),
                h("span", { class: "result-pill pill-muted" }, `${openFindingCount.value} ${localeText({ en: "open", "zh-CN": "待处理", "ms-MY": "terbuka", "id-ID": "terbuka", "th-TH": "ค้างอยู่" })}`)
              ])
            ]),
            h("div", { class: "section-copy section-copy-compact result-summary-copy" }, [
              h("h3", activeResult.value.summary.title),
              h("p", { class: "muted-text" }, activeResult.value.summary.subtitle),
              h("small", { class: "result-summary-meta" }, `${copy("rulebookLabel")}: ${activeResult.value.rulebookVersion}${resolvedFindingCount.value > 0 ? ` · ${resolvedFindingCount.value} ${localeText({ en: "fixed in draft", "zh-CN": "项已在草稿修复", "ms-MY": "sudah dibaiki dalam draf", "id-ID": "sudah diperbaiki di draft", "th-TH": "รายการแก้แล้วในร่าง" })}` : ""}`)
            ]),
            h("div", { class: "quick-action-inline result-summary-actions" }, [
              h("button", { class: "action-button", type: "button", onClick: onServerCheck, disabled: !canUseServerActions.value }, copy("runServerCheck")),
              h("button", { class: "action-button secondary-button", type: "button", onClick: onGenerateInvoice, disabled: !canUseServerActions.value }, copy("generateHtml"))
            ])
          ]),
        h("div", { class: "tab-strip compact-tab-strip result-detail-strip" }, [
          tabButton(
            localeText({
              en: `Action list (${openFindingCount.value})`,
              "zh-CN": `处理清单 (${openFindingCount.value})`,
              "ms-MY": `Senarai tindakan (${openFindingCount.value})`,
              "id-ID": `Daftar tindakan (${openFindingCount.value})`,
              "th-TH": `รายการที่ต้องทำ (${openFindingCount.value})`
            }),
            resultPanelTab.value === "issues",
            () => (resultPanelTab.value = "issues")
          ),
          tabButton(
            localeText({
              en: `Watchlist (${watchlistThresholds.value.length})`,
              "zh-CN": `业务提醒 (${watchlistThresholds.value.length})`,
              "ms-MY": `Peringatan (${watchlistThresholds.value.length})`,
              "id-ID": `Pengingat (${watchlistThresholds.value.length})`,
              "th-TH": `รายการเฝ้าระวัง (${watchlistThresholds.value.length})`
            }),
            resultPanelTab.value === "watchlist",
            () => (resultPanelTab.value = "watchlist")
          )
        ]),
        resultPanelTab.value === "issues"
          ? renderFindings()
          : null,
        resultPanelTab.value === "watchlist" ? renderThresholds() : null
      ]);

    const renderPreviewView = () =>
      h("div", { class: "section-stack" }, [
        h(
          "div",
          { class: "section-copy section-copy-compact" },
          previewHtml.value
            ? [h("h3", copy("previewTitle"))]
            : [h("h3", copy("previewTitle")), h("p", { class: "muted-text" }, copy("previewEmpty"))]
        ),
        previewHtml.value
          ? h("div", { class: "preview-shell" }, [h("iframe", { class: "preview-frame", srcdoc: previewHtml.value })])
          : h("div", { class: "empty-state" }, copy("previewEmpty"))
      ]);

    const renderPreviewModal = () =>
      !previewDialogOpen.value
        ? null
        : h(
            "div",
            {
              class: "modal-backdrop",
              onClick: (event: Event) => {
                if (event.target === event.currentTarget) {
                  closePreviewDialog();
                }
              }
            },
            [
              h("section", { class: "modal-card preview-modal-card" }, [
                h("section", { class: "panel preview-modal-panel" }, [
                  h("div", { class: "panel-head" }, [
                    h("div", { class: "panel-copy" }, [
                      h("h2", copy("previewTitle")),
                      h(
                        "p",
                        { class: "muted-text" },
                        localeText({
                          en: "This is a customer-facing preview generated from the current draft. It is not a tax filing or an official issuance channel.",
                          "zh-CN": "这里显示的是根据当前草稿生成的客户预览页，不代表税局报送或官方开票通道。",
                          "ms-MY": "Ini ialah halaman pratonton pelanggan yang dijana daripada draf semasa, dan bukannya saluran pemfailan cukai atau penerbitan rasmi.",
                          "id-ID": "Ini adalah halaman pratinjau pelanggan yang dibuat dari draft saat ini, bukan saluran pelaporan pajak atau penerbitan resmi.",
                          "th-TH": "นี่คือหน้าพรีวิวสำหรับลูกค้าที่สร้างจากร่างปัจจุบัน ไม่ใช่การยื่นภาษีหรือช่องทางออกเอกสารทางการ"
                        })
                      )
                    ]),
                    h("button", { class: "icon-button", type: "button", onClick: closePreviewDialog }, copy("close"))
                  ]),
                  renderPreviewView()
                ])
              ])
            ]
          );

    const renderActivityView = () =>
      !signedIn.value
        ? h("div", { class: "empty-state" }, labels.value.activityEmpty)
        : h("div", { class: "section-stack" }, [
            h("div", { class: "section-copy section-copy-compact" }, [
              h("h3", localeText({ en: "Recent work", "zh-CN": "最近记录", "ms-MY": "Kerja terkini", "id-ID": "Pekerjaan terbaru", "th-TH": "งานล่าสุด" })),
              h("p", { class: "muted-text" }, localeText({
                en: "Only records that can be reopened stay here, so the workspace remains focused.",
                "zh-CN": "这里只保留可重新打开的记录，让工作台保持聚焦。",
                "ms-MY": "Hanya rekod yang boleh dibuka semula dipaparkan di sini supaya ruang kerja kekal fokus.",
                "id-ID": "Hanya catatan yang bisa dibuka kembali ditampilkan di sini agar workspace tetap fokus.",
                "th-TH": "ส่วนนี้จะแสดงเฉพาะรายการที่เปิดกลับมาได้ เพื่อให้เวิร์กสเปซยังคงกระชับและโฟกัส"
              }))
            ]),
            h("section", { class: "subpanel" }, [
              h("div", { class: "section-copy" }, [h("h3", copy("recentRuns"))]),
              sidebarRuns.value.length === 0
                ? h("div", { class: "empty-state empty-state-compact" }, copy("noRuns"))
                : h(
                    "div",
                    { class: "activity-list" },
                    sidebarRuns.value.map((run) =>
                      h("article", { class: "activity-card" }, [
                        h("div", { class: "activity-card-head" }, [
                          h("strong", `${localizeCountryLabel(run.country)} · ${run.invoiceNumber ?? run.id}`),
                          h("span", { class: "status-badge" }, localizeOpsStatus(run.status))
                        ]),
                        h("p", { class: "muted-text" }, `${localizeRisk(run.overallRiskLevel)} · ${run.rulebookVersion ?? copy("notAvailable")}`),
                        h("small", `${run.trigger ? localizeOpsStatus(run.trigger) : copy("triggerSync")} · ${formatTimestamp(run.createdAt, locale.value)}`),
                        run.invoiceRecordId
                          ? h("div", { class: "toolbar-row card-action-row" }, [
                              h("button", { class: "action-button secondary-button subtle-button", type: "button", onClick: () => loadSavedInvoiceIntoEditor(run.invoiceRecordId!) }, localeText({ en: "Continue editing", "zh-CN": "继续编辑", "ms-MY": "Sambung edit", "id-ID": "Lanjutkan edit", "th-TH": "แก้ไขต่อ" })),
                              run.previewAvailable
                                ? h("button", { class: "action-button secondary-button subtle-button", type: "button", onClick: () => openSavedInvoicePreview(run.invoiceRecordId!) }, localeText({ en: "Open customer preview", "zh-CN": "打开客户预览", "ms-MY": "Buka pratonton pelanggan", "id-ID": "Buka pratinjau pelanggan", "th-TH": "เปิดพรีวิวลูกค้า" }))
                                : null
                            ])
                          : null
                      ])
                    )
                  )
            ]),
            h("section", { class: "subpanel" }, [
              h("div", { class: "section-copy" }, [h("h3", labels.value.historyInvoices)]),
              sidebarInvoices.value.length === 0
                ? h("div", { class: "empty-state empty-state-compact" }, labels.value.historyNoInvoices)
                : h(
                    "div",
                    { class: "activity-list" },
                    sidebarInvoices.value.map((invoice) =>
                      h("article", { class: "activity-card" }, [
                        h("div", { class: "activity-card-head" }, [
                          h("strong", `${invoice.invoiceNumber} · v${invoice.revisionNumber}`),
                          h("span", { class: "status-badge" }, localizeOpsStatus(invoice.status))
                        ]),
                        h("p", { class: "muted-text" }, `${invoice.sellerName} · ${invoice.buyerName ?? copy("notAvailable")}`),
                        h("small", `${localizeRisk(invoice.overallRiskLevel)} · ${invoice.rulebookVersion} · ${formatTimestamp(invoice.updatedAt, locale.value)}`),
                        h("div", { class: "toolbar-row card-action-row" }, [
                          h("button", { class: "action-button secondary-button subtle-button", type: "button", onClick: () => loadSavedInvoiceIntoEditor(invoice.id) }, localeText({ en: "Continue editing", "zh-CN": "继续编辑", "ms-MY": "Sambung edit", "id-ID": "Lanjutkan edit", "th-TH": "แก้ไขต่อ" })),
                          invoice.generatedHtml
                            ? h("button", { class: "action-button secondary-button subtle-button", type: "button", onClick: () => openSavedInvoicePreview(invoice.id) }, localeText({ en: "Open customer preview", "zh-CN": "打开客户预览", "ms-MY": "Buka pratonton pelanggan", "id-ID": "Buka pratinjau pelanggan", "th-TH": "เปิดพรีวิวลูกค้า" }))
                            : null
                        ])
                      ])
                    )
                  )
            ]),
            h("div", { class: "toolbar-row" }, [
              h("button", { class: "action-button secondary-button", type: "button", onClick: () => navigateTo("history") }, labels.value.goHistory)
            ])
          ]);

    const renderSidebarView = () => {
      switch (sidebarTab.value) {
        case "activity":
          return renderActivityView();
        default:
          return renderResultView();
      }
    };

    const renderVerificationCard = () => {
      if (!signedIn.value || emailVerified.value || !bootstrap.emailVerificationEnabled) {
        return null;
      }

      return h("section", { class: "panel verification-card" }, [
        h("div", { class: "panel-copy" }, [
          h("h2", labels.value.verifyTitle),
          h("p", { class: "muted-text" }, labels.value.verifySubtitle)
        ]),
        !bootstrap.mailConfigured ? h("div", { class: "notice-bar" }, labels.value.verifyMailMissing) : null,
        h("div", { class: "verification-layout" }, [
          inputField(labels.value.verifyInput, verifyCode.value, (value) => (verifyCode.value = value), "text"),
          h("div", { class: "toolbar-row verification-actions" }, [
            h("button", { class: "action-button", type: "button", onClick: onVerifyEmail, disabled: auth.verifying }, auth.verifying ? copy("processing") : labels.value.verifyAction),
            h(
              "button",
              {
                class: "action-button secondary-button",
                type: "button",
                onClick: onResendVerification,
                disabled: auth.verifying || verifyCooldownRemaining.value > 0
              },
              verifyCooldownRemaining.value > 0
                ? localeText({
                    en: `Resend in ${verifyCooldownRemaining.value}s`,
                    "zh-CN": `${verifyCooldownRemaining.value} 秒后可重发`,
                    "ms-MY": `Hantar semula dalam ${verifyCooldownRemaining.value}s`,
                    "id-ID": `Kirim ulang dalam ${verifyCooldownRemaining.value} dtk`
                  })
                : labels.value.resendAction
            )
          ])
        ]),
        verifyStatus.value ? h("div", { class: "notice-bar" }, verifyStatus.value) : null
      ]);
    };

    const planToneClass = (code: string) => {
      switch (code) {
        case "scale":
          return "pricing-card-scale";
        case "growth":
          return "pricing-card-growth";
        default:
          return "pricing-card-starter";
      }
    };

    const planTagline = (code: string) => {
      switch (code) {
        case "scale":
          return localeText({
            en: "For regional operations and tighter team controls",
            "zh-CN": "适合跨区域协作和更稳定的正式运营",
            "ms-MY": "Untuk operasi serantau dan kawalan pasukan yang lebih ketat",
            "id-ID": "Untuk operasi lintas wilayah dan kontrol tim yang lebih kuat",
            "th-TH": "เหมาะกับการดำเนินงานหลายประเทศและการควบคุมทีมที่จริงจังขึ้น"
          });
        case "growth":
          return localeText({
            en: "For teams moving from trial to steady monthly volume",
            "zh-CN": "适合从试用走向稳定月度业务量的团队",
            "ms-MY": "Untuk pasukan yang beralih daripada percubaan kepada jumlah bulanan yang stabil",
            "id-ID": "Untuk tim yang beralih dari uji coba ke volume bulanan yang stabil",
            "th-TH": "เหมาะกับทีมที่กำลังก้าวจากการทดลองใช้ไปสู่ปริมาณงานประจำ"
          });
        default:
          return localeText({
            en: "A lean starting point for evaluation and low-volume work",
            "zh-CN": "适合试用、验证与低频业务的轻量起点",
            "ms-MY": "Titik permulaan yang ringan untuk penilaian dan jumlah rendah",
            "id-ID": "Titik awal ringan untuk evaluasi dan volume rendah",
            "th-TH": "จุดเริ่มต้นแบบกระชับสำหรับการทดลองใช้และงานปริมาณต่ำ"
          });
      }
    };

    const planHighlight = (code: string) => {
      switch (code) {
        case "scale":
          return localeText({
            en: "Premium",
            "zh-CN": "旗舰方案",
            "ms-MY": "Premium",
            "id-ID": "Premium",
            "th-TH": "พรีเมียม"
          });
        case "growth":
          return localeText({
            en: "Most popular",
            "zh-CN": "最常选择",
            "ms-MY": "Paling popular",
            "id-ID": "Paling populer",
            "th-TH": "ยอดนิยม"
          });
        default:
          return localeText({
            en: "Starter",
            "zh-CN": "试用起步",
            "ms-MY": "Permulaan",
            "id-ID": "Starter",
            "th-TH": "เริ่มต้น"
          });
      }
    };

    const renderPlanCard = (plan: PlanPreset, current = false) =>
      h("article", { class: [ "pricing-card", planToneClass(plan.code), current ? "pricing-card-current" : "" ].filter(Boolean).join(" ") }, [
        h("div", { class: "pricing-card-head" }, [
          h("div", { class: "pricing-title-block" }, [
            h("div", { class: "pricing-badge-row" }, [
              h("span", { class: "pricing-tier-label" }, localeText({ en: "Plan", "zh-CN": "方案", "ms-MY": "Pelan", "id-ID": "Paket", "th-TH": "แผน" })),
              h("span", { class: "pricing-highlight-pill" }, planHighlight(plan.code))
            ]),
            h("strong", localizePlan(plan.code)),
            h("span", { class: "pricing-price" }, localizePlanPrice(plan)),
            h("p", { class: "pricing-tagline" }, planTagline(plan.code))
          ]),
          current ? h("span", { class: "status-badge" }, localeText({ en: "Current", "zh-CN": "当前方案", "ms-MY": "Pelan semasa", "id-ID": "Paket saat ini", "th-TH": "แผนปัจจุบัน" })) : null
        ]),
        h("div", { class: "pricing-list" }, [
          h("div", { class: "pricing-line" }, [h("span", localeText({ en: "Checks / month", "zh-CN": "每月检查", "ms-MY": "Semakan / bulan", "id-ID": "Pemeriksaan / bulan" })), h("strong", String(plan.checksPerMonth))]),
          h("div", { class: "pricing-line" }, [h("span", localeText({ en: "Extractions / month", "zh-CN": "每月抽取", "ms-MY": "Ekstraksi / bulan", "id-ID": "Ekstraksi / bulan" })), h("strong", String(plan.extractionsPerMonth))]),
          h("div", { class: "pricing-line" }, [h("span", localeText({ en: "Invoices / month", "zh-CN": "每月发票生成", "ms-MY": "Invois / bulan", "id-ID": "Invoice / bulan" })), h("strong", String(plan.generatedInvoicesPerMonth))]),
          h("div", { class: "pricing-line" }, [h("span", localeText({ en: "Members", "zh-CN": "成员数", "ms-MY": "Ahli", "id-ID": "Anggota", "th-TH": "สมาชิก" })), h("strong", String(plan.members))]),
          h("div", { class: "pricing-line" }, [h("span", localeText({ en: "History window", "zh-CN": "历史保留", "ms-MY": "Tempoh sejarah", "id-ID": "Jangka riwayat", "th-TH": "ช่วงเวลาประวัติ" })), h("strong", localeText({ en: `${plan.historyDays} days`, "zh-CN": `${plan.historyDays} 天`, "ms-MY": `${plan.historyDays} hari`, "id-ID": `${plan.historyDays} hari`, "th-TH": `${plan.historyDays} วัน` }))])
        ]),
        h("div", { class: "toolbar-row pricing-action-row" }, [
          current
            ? h(
                "button",
                {
                  class: "action-button secondary-button",
                  type: "button",
                  disabled: !currentSubscription.value || subscriptionManagePending.value,
                  onClick: () => openSubscriptionManagement()
                },
                currentSubscription.value
                  ? subscriptionManagePending.value
                    ? copy("processing")
                    : localeText({ en: "Manage subscription", "zh-CN": "管理订阅", "ms-MY": "Urus langganan", "id-ID": "Kelola langganan", "th-TH": "จัดการการสมัคร" })
                  : localeText({ en: "Current plan", "zh-CN": "当前方案", "ms-MY": "Pelan semasa", "id-ID": "Paket saat ini", "th-TH": "แผนปัจจุบัน" })
              )
            : !signedIn.value
              ? h("button", { class: "action-button", type: "button", onClick: () => openAuthModal("register") }, copy("topbarRegister"))
              : plan.code === "starter"
                ? h("button", { class: "action-button secondary-button", type: "button", onClick: () => navigateTo("billing") }, localeText({ en: "Included", "zh-CN": "已包含", "ms-MY": "Disertakan", "id-ID": "Sudah termasuk", "th-TH": "รวมอยู่แล้ว" }))
                : activeOrganization.value?.planCode !== "starter"
                  ? h("button", { class: "action-button secondary-button", type: "button", onClick: () => openSubscriptionManagement(), disabled: subscriptionManagePending.value }, subscriptionManagePending.value ? copy("processing") : localeText({ en: "Manage plan", "zh-CN": "管理套餐", "ms-MY": "Urus pelan", "id-ID": "Kelola paket", "th-TH": "จัดการแพ็กเกจ" }))
                  : h("button", { class: "action-button", type: "button", onClick: () => openCheckoutForPlan(plan.code as PlanCode), disabled: checkoutPending.value || !canPurchasePaidPlan.value }, checkoutPending.value ? copy("processing") : localeText({ en: "Buy now", "zh-CN": "立即购买", "ms-MY": "Beli sekarang", "id-ID": "Beli sekarang", "th-TH": "ซื้อเลย" }))
        ])
      ]);

    const renderWorkbenchView = () =>
      h("section", { class: "page-stack" }, [
        renderWorkbenchMasthead(),
        h("section", { class: "workbench-layout" }, [
          h("section", { class: "panel editor-panel editor-stage-panel" }, [
            renderEditorToolbar(),
            h("div", { class: "editor-canvas-shell" }, [
              h("div", { class: "editor-canvas" }, [renderEditorView()])
            ])
          ]),
          h("aside", { class: "sidebar-column sidebar-stack" }, [
            renderVerificationCard(),
              h("section", { class: "panel sidebar-panel sticky-sidebar review-sidebar" }, [
                h("div", { class: "panel-head review-sidebar-head" }, [
                  h("div", { class: "section-copy section-copy-compact" }, [
                    h("h2", localeText({ en: "Review", "zh-CN": "检查与预览", "ms-MY": "Semakan", "id-ID": "Tinjauan", "th-TH": "ตรวจทาน" }))
                  ]),
                  signedIn.value ? h("div", { class: "chip-row sidebar-identity" }, [
                    h("span", { class: "status-badge" }, activeOrganization.value?.name ?? copy("notAvailable")),
                  h("span", { class: "status-badge" }, localizePlan(activeOrganization.value?.planCode))
                ]) : null
              ]),
              renderResultView(),
              signedIn.value ? h("div", { class: "toolbar-row review-sidebar-footer" }, [
                h("button", { class: "action-button secondary-button subtle-button", type: "button", onClick: () => navigateTo("history") }, labels.value.goHistory)
              ]) : null
            ])
          ])
        ])
      ]);

    const renderSourcesView = () =>
      h("section", { class: "page-stack" }, [
        renderPageMasthead(
          localeText({ en: "Rule library", "zh-CN": "规则中心", "ms-MY": "Pusat peraturan", "id-ID": "Pusat aturan", "th-TH": "ศูนย์กฎ" }),
          copy("sourcesTitle"),
          labels.value.sourceLead,
          [getCountryName(locale.value, country.value), pack.value.rulebookVersion],
          sourceOverviewCards.value
        ),
        h("section", { class: "panel source-panel" }, [
          h("div", { class: "section-copy" }, [
            h("h2", localeText({
              en: "Browse by topic",
              "zh-CN": "按主题查看",
              "ms-MY": "Semak mengikut topik",
              "id-ID": "Telusuri per topik",
              "th-TH": "เรียกดูตามหัวข้อ"
            })),
            h("p", { class: "muted-text" }, copy("sourcesSubtitle", { country: getCountryName(locale.value, country.value), version: pack.value.rulebookVersion }))
          ]),
          h("div", { class: "source-library-grid" }, sourceLibraryEntries.value.map((section) =>
            h("section", { class: "subpanel source-library-section" }, [
              h("div", { class: "section-copy source-library-head" }, [
                h("h3", section.title),
                h("p", { class: "muted-text" }, section.body)
              ]),
              h("div", { class: "source-grid source-section-grid" }, section.sources.map((source) =>
                h("article", { class: "source-card" }, [
                  h("div", { class: "chip-row" }, [
                    h("span", { class: "status-badge" }, source.authority),
                    source.effectiveFrom
                      ? h("span", { class: "status-badge" }, `${localeText({ en: "Effective", "zh-CN": "生效", "ms-MY": "Berkuat kuasa", "id-ID": "Berlaku", "th-TH": "มีผลใช้" })} ${source.effectiveFrom}`)
                      : null
                  ]),
                  h("strong", source.title),
                  h("p", source.summary),
                  h("div", { class: "source-meta" }, `${copy("updatedOn")} ${source.updatedOn ?? copy("notAvailable")} · ${copy("accessedOn")} ${source.accessedOn}`),
                  h("a", { class: "source-link source-cta", href: source.url, target: "_blank", rel: "noreferrer" }, localeText({
                    en: "Open official source",
                    "zh-CN": "打开官方来源",
                    "ms-MY": "Buka sumber rasmi",
                    "id-ID": "Buka sumber resmi",
                    "th-TH": "เปิดแหล่งข้อมูลทางการ"
                  }))
                ])
              ))
            ])
          ))
        ])
      ]);

    const renderHomeView = () =>
      h("div", { class: "page-stack" }, [
        renderHeroPanel(),
        h("section", { class: "panel home-showcase" }, [
          h("div", { class: "section-copy" }, [
            h("p", { class: "eyebrow" }, localeText({ en: "Why teams choose it", "zh-CN": "为什么团队会选它", "ms-MY": "Mengapa pasukan memilihnya", "id-ID": "Mengapa tim memilihnya", "th-TH": "เหตุผลที่ทีมเลือกใช้" })),
            h("h2", localeText({
              en: "Keep drafting, review, and customer previews in one place.",
              "zh-CN": "把起草、复核和客户预览都放在同一个产品里。",
              "ms-MY": "Simpan draf, semakan, dan pratonton pelanggan dalam satu tempat.",
              "id-ID": "Simpan drafting, review, dan pratinjau pelanggan di satu tempat.",
              "th-TH": "เก็บการร่าง การทบทวน และพรีวิวสำหรับลูกค้าไว้ในที่เดียว"
            })),
            h("p", { class: "muted-text" }, localeText({
              en: "The workflow stays structured even when teams switch countries, formats, or document sources.",
              "zh-CN": "即便团队切换国家、票据格式或导入来源，整体流程也保持清晰一致。",
              "ms-MY": "Aliran kerja kekal kemas walaupun pasukan bertukar negara, format, atau sumber dokumen.",
              "id-ID": "Alurnya tetap rapi walau tim berpindah negara, format, atau sumber dokumen.",
              "th-TH": "เวิร์กโฟลว์ยังคงเป็นระเบียบ แม้ทีมจะสลับประเทศ รูปแบบ หรือแหล่งเอกสาร"
            }))
          ]),
          h("div", { class: "feature-grid" }, landingFeatures.value.map((feature) =>
            h("article", { class: "feature-card" }, [renderIllustratedIcon(feature.icon), h("strong", feature.title), h("p", feature.body)])
          )),
          h("div", { class: "home-cta-row" }, [
            h("button", { class: "action-button", type: "button", onClick: () => navigateTo("workspace") }, labels.value.homePrimary),
            h("button", { class: "action-button tertiary-button", type: "button", onClick: () => navigateTo("rules") }, labels.value.homeSecondary)
          ])
        ]),
        h("section", { class: "panel home-panel" }, [
          h("div", { class: "panel-copy" }, [
            h("h2", localeText({
              en: "Coverage by market",
              "zh-CN": "市场覆盖",
              "ms-MY": "Liputan mengikut pasaran",
              "id-ID": "Cakupan per pasar",
              "th-TH": "ความครอบคลุมตามตลาด"
            })),
            h("p", { class: "muted-text" }, localeText({
              en: "Each market keeps its own rulebook version, tax assumptions, and editor guidance so teams do not have to relearn the workflow every time they switch countries.",
              "zh-CN": "每个市场都保留自己的规则版本、税务假设和字段引导，团队切换国家时不需要重新学习整套流程。",
              "ms-MY": "Setiap pasaran mengekalkan versi peraturan, andaian cukai, dan panduan editor tersendiri supaya pasukan tidak perlu belajar semula aliran kerja setiap kali bertukar negara.",
              "id-ID": "Setiap pasar memiliki versi aturan, asumsi pajak, dan panduan editor sendiri sehingga tim tidak perlu mempelajari ulang alur kerja saat berpindah negara.",
              "th-TH": "แต่ละตลาดมีเวอร์ชันกฎ สมมติฐานภาษี และคำแนะนำในตัวแก้ไขของตนเอง ทำให้ทีมไม่ต้องเรียนรู้เวิร์กโฟลว์ใหม่ทุกครั้งที่สลับประเทศ"
            }))
          ]),
          h("div", { class: "coverage-grid" }, landingMarketCards.value.map((market) =>
            h("article", { class: `feature-card coverage-card${market.code === country.value ? " coverage-card-active" : ""}` }, [
              h("div", { class: "coverage-card-head" }, [
                renderIllustratedIcon(market.icon),
                h("div", { class: "pricing-badge-row" }, [
                  h("span", { class: "pricing-tier-label" }, market.code),
                  h("span", { class: "pricing-highlight-pill" }, market.meta)
                ])
              ]),
              h("strong", market.title),
              h("p", market.body)
            ])
          ))
        ]),
        h("section", { class: "panel home-panel" }, [
          h("div", { class: "panel-copy" }, [
            h("h2", labels.value.pricingTitle),
            h("p", { class: "muted-text" }, labels.value.pricingSubtitle)
          ]),
          h("div", { class: "pricing-grid" }, availablePlans.value.map((plan) => renderPlanCard(plan, plan.code === activeOrganization.value?.planCode))),
          h("p", { class: "home-footnote muted-text" }, localeText({
            en: "Start with the free plan, then upgrade when invoice operations become part of daily work.",
            "zh-CN": "可以先用免费版试跑，等发票处理进入日常业务后再升级。",
            "ms-MY": "Mulakan dengan pelan percuma, kemudian naik taraf apabila operasi invois menjadi kerja harian.",
            "id-ID": "Mulai dari paket gratis, lalu upgrade saat operasi invoice menjadi pekerjaan harian.",
            "th-TH": "เริ่มต้นด้วยแผนฟรีก่อน แล้วค่อยอัปเกรดเมื่อการจัดการเอกสารกลายเป็นงานประจำวัน"
          })),
          signedIn.value && billingOverview.value
            ? h("div", { class: "chip-row" }, [
                h("span", { class: "status-badge" }, billingOverview.value.organization.name),
                h("span", { class: "status-badge" }, localizePlan(billingOverview.value.organization.planCode)),
                h("span", { class: "status-badge" }, localizeBilling(billingOverview.value.organization.billingStatus))
              ])
            : null
        ])
      ]);

    const renderStaticSections = (sections: StaticSection[]) =>
      h("div", { class: "policy-grid" }, sections.map((section) =>
        h("article", { class: "policy-card" }, [
          h("div", { class: "section-copy section-copy-compact" }, [
            h("h3", section.title),
            h("p", { class: "muted-text" }, section.body)
          ]),
          section.bullets?.length
            ? h("ul", { class: "bullet-list" }, section.bullets.map((item) => h("li", item)))
            : null
        ])
      ));

    const renderStaticPage = (
      eyebrow: string,
      title: string,
      body: string,
      chips: string[],
      stats: StaticStat[],
      sections: StaticSection[],
      footerNote?: string
    ) =>
      h("div", { class: "page-stack" }, [
        renderPageMasthead(eyebrow, title, body, chips, stats),
        h("section", { class: "panel static-page-panel" }, [
          renderStaticSections(sections),
          footerNote ? h("p", { class: "legal-footnote muted-text" }, footerNote) : null
        ])
      ]);

    const legalLocale = (
      en: string,
      zh: string,
      ms: string,
      id: string,
      th: string
    ) => localeText({
      en,
      "zh-CN": zh,
      "ms-MY": ms,
      "id-ID": id,
      "th-TH": th
    });

    const renderProductView = () =>
      renderStaticPage(
        legalLocale("Product", "产品说明", "Produk", "Produk", "ข้อมูลผลิตภัณฑ์"),
        legalLocale(
          "One workspace for invoice drafting, checks, and customer-ready previews.",
          "在一个工作空间里完成发票起草、规则检查和客户预览。",
          "Satu ruang kerja untuk draf invois, semakan, dan pratonton sedia pelanggan.",
          "Satu workspace untuk draft invoice, pemeriksaan, dan pratinjau siap pelanggan.",
          "เวิร์กสเปซเดียวสำหรับร่างเอกสาร ตรวจสอบกฎ และหน้าพรีวิวพร้อมส่งลูกค้า"
        ),
        legalLocale(
          "Use one draft flow across Singapore, Malaysia, Indonesia, and Thailand. Import OCR or PDF content when you need speed, then review the same draft before delivery.",
          "围绕同一份草稿完成新加坡、马来西亚、印度尼西亚和泰国市场的录入、识别、校验与预览。需要提速时可导入 OCR 或 PDF，之后仍回到同一份草稿中复核。",
          "Gunakan satu aliran draf untuk Singapura, Malaysia, Indonesia, dan Thailand. Import kandungan OCR atau PDF apabila anda perlukan kelajuan, kemudian semak semula draf yang sama sebelum dihantar.",
          "Gunakan satu alur draft untuk Singapura, Malaysia, Indonesia, dan Thailand. Impor konten OCR atau PDF saat perlu lebih cepat, lalu tinjau kembali draft yang sama sebelum dikirim.",
          "ใช้เวิร์กโฟลว์ร่างเดียวสำหรับสิงคโปร์ มาเลเซีย อินโดนีเซีย และไทย นำเข้า OCR หรือ PDF เมื่อต้องการความเร็ว แล้วกลับมาตรวจทานร่างเดิมก่อนส่งมอบ"
        ),
        [
          legalLocale(`Markets live: ${COUNTRY_CODES.length}`, `已上线市场：${COUNTRY_CODES.length}`, `Pasaran aktif: ${COUNTRY_CODES.length}`, `Pasar aktif: ${COUNTRY_CODES.length}`, `ตลาดที่เปิดใช้งาน: ${COUNTRY_CODES.length}`),
          legalLocale("Single draft workflow", "单一草稿流", "Satu aliran draf", "Satu alur draft", "เวิร์กโฟลว์ร่างเดียว"),
          legalLocale(`Updated ${LEGAL_LAST_UPDATED}`, `更新于 ${LEGAL_LAST_UPDATED}`, `Dikemas kini ${LEGAL_LAST_UPDATED}`, `Diperbarui ${LEGAL_LAST_UPDATED}`, `อัปเดต ${LEGAL_LAST_UPDATED}`)
        ],
        [
          {
            label: legalLocale("Input paths", "输入路径", "Laluan input", "Jalur input", "ช่องทางนำเข้า"),
            value: legalLocale("Manual + OCR", "手工 + OCR", "Manual + OCR", "Manual + OCR", "กรอกเอง + OCR"),
            body: legalLocale(
              "Manual entry, OCR text, image upload, and PDF import all lead back to one draft.",
              "手工录入、OCR 文本、图片上传和 PDF 导入都会回到同一份草稿。",
              "Input manual, teks OCR, muat naik imej, dan import PDF semuanya kembali ke satu draf yang sama.",
              "Input manual, teks OCR, unggah gambar, dan impor PDF semuanya kembali ke satu draft yang sama.",
              "การกรอกเอง ข้อความ OCR การอัปโหลดรูป และการนำเข้า PDF จะกลับมาที่ร่างเดียวกันทั้งหมด"
            )
          },
          {
            label: legalLocale("Core outputs", "核心输出", "Output teras", "Output inti", "ผลลัพธ์หลัก"),
            value: legalLocale("Checks + previews", "检查 + 预览", "Semakan + pratonton", "Pemeriksaan + pratinjau", "การตรวจสอบ + พรีวิว"),
            body: legalLocale(
              "The platform focuses on rule checks and customer-facing preview pages, not government filing.",
              "平台聚焦规则检查和客户预览页，不等同于向税局正式报送或申报。",
              "Platform ini memberi tumpuan pada semakan peraturan dan halaman pratonton untuk pelanggan, bukan pemfailan kerajaan.",
              "Platform ini berfokus pada pemeriksaan aturan dan halaman pratinjau untuk pelanggan, bukan pelaporan ke pemerintah.",
              "แพลตฟอร์มนี้เน้นการตรวจสอบกฎและหน้าพรีวิวสำหรับลูกค้า ไม่ใช่บริการยื่นต่อหน่วยงานรัฐ"
            )
          },
          {
            label: legalLocale("Workspace model", "工作空间模式", "Model ruang kerja", "Model workspace", "รูปแบบเวิร์กสเปซ"),
            value: legalLocale("Team-based", "按团队", "Berasaskan pasukan", "Berbasis tim", "ใช้ตามทีม"),
            body: legalLocale(
              "Drafts, history, orders, and plan limits are all isolated by workspace.",
              "草稿、记录、订单和套餐额度都按工作空间隔离。",
              "Draf, sejarah, pesanan, dan had pelan semuanya diasingkan mengikut ruang kerja.",
              "Draft, riwayat, pesanan, dan batas paket semuanya dipisahkan per workspace.",
              "ร่าง ประวัติ คำสั่งซื้อ และโควตาแพ็กเกจทั้งหมดแยกตามเวิร์กสเปซ"
            )
          }
        ],
        [
          {
            title: legalLocale("What the platform does", "这个产品具体做什么", "Apa yang platform ini lakukan", "Apa yang dilakukan platform ini", "แพลตฟอร์มนี้ทำอะไร"),
            body: legalLocale(
              "It helps finance, operations, and founders prepare invoice drafts, apply market-specific checks, and open clean preview pages for internal review or customer delivery.",
              "它帮助财务、运营和创始团队准备发票草稿，执行各市场规则检查，并生成可用于内部复核或客户查看的清晰预览页。",
              "Ia membantu pasukan kewangan, operasi, dan pengasas menyediakan draf invois, menjalankan semakan khusus pasaran, dan membuka halaman pratonton yang kemas untuk semakan dalaman atau penghantaran kepada pelanggan.",
              "Platform ini membantu tim keuangan, operasional, dan pendiri menyiapkan draft invoice, menjalankan pemeriksaan khusus pasar, dan membuka halaman pratinjau yang rapi untuk review internal atau pengiriman ke pelanggan.",
              "ช่วยให้ทีมการเงิน ฝ่ายปฏิบัติการ และผู้ก่อตั้งจัดทำร่างเอกสาร ใช้การตรวจสอบเฉพาะตลาด และเปิดหน้าพรีวิวที่พร้อมสำหรับการตรวจภายในหรือส่งให้ลูกค้า"
            ),
            bullets: [
              legalLocale("Market-aware defaults for SG, MY, ID, and TH", "针对 SG、MY、ID、TH 的国家感知默认值与字段引导", "Tetapan lalai dan panduan medan mengikut pasaran untuk SG, MY, ID, dan TH", "Default sadar pasar dan panduan kolom untuk SG, MY, ID, dan TH", "ค่าเริ่มต้นและคำแนะนำช่องกรอกตามตลาดสำหรับ SG, MY, ID และ TH"),
              legalLocale("Official-source-backed rule library and versioned checks", "带有官方来源支撑的规则库和版本化检查", "Pustaka peraturan bersumber rasmi dan semakan berasaskan versi", "Pustaka aturan berbasis sumber resmi dan pemeriksaan berversi", "คลังกฎที่อ้างอิงแหล่งทางการและการตรวจสอบแบบมีเวอร์ชัน"),
              legalLocale("Saved check snapshots and reopenable preview records", "可回看的检查快照与可重新打开的预览记录", "Syot semakan tersimpan dan rekod pratonton yang boleh dibuka semula", "Snapshot pemeriksaan tersimpan dan rekaman pratinjau yang bisa dibuka kembali", "สแนปช็อตการตรวจสอบที่บันทึกไว้และประวัติพรีวิวที่เปิดซ้ำได้")
            ]
          },
          {
            title: legalLocale("Who it is for", "适合谁使用", "Siapa yang sesuai menggunakannya", "Siapa yang cocok menggunakannya", "เหมาะกับใคร"),
            body: legalLocale(
              "Use it when your team needs a controlled drafting and review workflow across multiple Southeast Asian markets, but does not want to rebuild forms and rule assumptions country by country.",
              "适用于需要跨多个东南亚市场进行受控起草和复核，但又不想每切一个国家就重建一套表单和规则假设的团队。",
              "Gunakan apabila pasukan anda memerlukan aliran kerja draf dan semakan yang terkawal merentas beberapa pasaran Asia Tenggara, tetapi tidak mahu membina semula borang dan andaian peraturan mengikut negara.",
              "Gunakan saat tim Anda memerlukan alur draft dan review yang terkendali di beberapa pasar Asia Tenggara, tetapi tidak ingin membangun ulang formulir dan asumsi aturan per negara.",
              "เหมาะเมื่อทีมของคุณต้องการเวิร์กโฟลว์การร่างและตรวจทานที่ควบคุมได้ในหลายตลาดเอเชียตะวันออกเฉียงใต้ โดยไม่ต้องสร้างฟอร์มและสมมติฐานกฎใหม่ทุกประเทศ"
            ),
            bullets: [
              legalLocale("Cross-border SaaS and service businesses", "跨境 SaaS 与服务型业务", "Perniagaan SaaS dan perkhidmatan rentas sempadan", "Bisnis SaaS dan layanan lintas negara", "ธุรกิจ SaaS และบริการข้ามพรมแดน"),
              legalLocale("Regional finance and operations teams", "区域财务与运营团队", "Pasukan kewangan dan operasi serantau", "Tim keuangan dan operasional regional", "ทีมการเงินและปฏิบัติการระดับภูมิภาค"),
              legalLocale("Founders validating invoicing operations before filing automation", "在正式做申报自动化前先验证开票作业流程的创始团队", "Pengasas yang mahu mengesahkan operasi invois sebelum automasi pemfailan", "Pendiri yang ingin memvalidasi operasi invoice sebelum otomatisasi pelaporan", "ทีมผู้ก่อตั้งที่ต้องการยืนยันเวิร์กโฟลว์เอกสารก่อนทำระบบอัตโนมัติด้านการยื่น")
            ]
          },
          {
            title: legalLocale("What it does not do", "它不是什么", "Apa yang ia tidak lakukan", "Apa yang tidak dilakukan platform ini", "สิ่งที่แพลตฟอร์มนี้ไม่ได้ทำ"),
            body: legalLocale(
              "It is not a tax filing agent, a government submission service, or a substitute for legal, tax, or accounting advice. Teams should still review official rules and professional advice where needed.",
              "它不是税务申报代理，也不是政府报送服务，更不能替代法律、税务或会计专业意见。必要时仍应结合官方规则和专业意见使用。",
              "Ia bukan ejen pemfailan cukai, perkhidmatan penyerahan kerajaan, atau pengganti nasihat undang-undang, cukai, atau perakaunan. Pasukan masih perlu merujuk peraturan rasmi dan nasihat profesional apabila perlu.",
              "Platform ini bukan agen pelaporan pajak, layanan pengiriman ke pemerintah, atau pengganti nasihat hukum, pajak, maupun akuntansi. Tim tetap perlu meninjau aturan resmi dan nasihat profesional bila diperlukan.",
              "แพลตฟอร์มนี้ไม่ใช่ตัวแทนยื่นภาษี ไม่ใช่บริการส่งข้อมูลให้ภาครัฐ และไม่ใช่สิ่งทดแทนคำปรึกษาด้านกฎหมาย ภาษี หรือบัญชี ทีมยังต้องตรวจสอบกฎทางการและคำแนะนำจากผู้เชี่ยวชาญเมื่อจำเป็น"
            )
          }
        ],
        legalLocale(
          "Use this page as the plain-language overview of the product scope, supported markets, and paid workspace model before purchase.",
          "这页用于清楚说明产品范围、支持市场以及付费工作空间模式，便于团队在购买前完成内部评估。",
          "Gunakan halaman ini sebagai gambaran ringkas tentang skop produk, pasaran yang disokong, dan model ruang kerja berbayar sebelum membeli.",
          "Gunakan halaman ini sebagai gambaran ringkas mengenai cakupan produk, pasar yang didukung, dan model workspace berbayar sebelum membeli.",
          "ใช้หน้านี้เป็นภาพรวมแบบเข้าใจง่ายของขอบเขตผลิตภัณฑ์ ตลาดที่รองรับ และรูปแบบเวิร์กสเปซแบบชำระเงินก่อนตัดสินใจซื้อ"
        )
      );

    const renderPricingPage = () =>
      h("div", { class: "page-stack" }, [
        renderPageMasthead(
          legalLocale("Pricing", "定价页", "Harga", "Harga", "ราคา"),
          legalLocale("Simple monthly plans for workspace-based usage.", "按工作空间计费的简洁月付方案。", "Pelan bulanan ringkas untuk penggunaan berasaskan ruang kerja.", "Paket bulanan sederhana untuk penggunaan berbasis workspace.", "แพ็กเกจรายเดือนแบบเรียบง่ายสำหรับการใช้งานตามเวิร์กสเปซ"),
          legalLocale(
            "Choose a plan based on monthly check volume, extraction needs, team size, and history retention. Paid plans are provisioned after checkout confirmation and webhook sync.",
            "按每月检查量、抽取需求、团队人数和历史保留时长选择方案。付费套餐会在支付确认和 webhook 回写后生效。",
            "Pilih pelan berdasarkan jumlah semakan bulanan, keperluan ekstraksi, saiz pasukan, dan tempoh penyimpanan sejarah. Pelan berbayar diaktifkan selepas pengesahan pembayaran dan penyegerakan webhook.",
            "Pilih paket berdasarkan volume pemeriksaan bulanan, kebutuhan ekstraksi, ukuran tim, dan retensi riwayat. Paket berbayar aktif setelah konfirmasi checkout dan sinkronisasi webhook.",
            "เลือกแพ็กเกจตามจำนวนการตรวจสอบต่อเดือน ความต้องการด้านการดึงข้อมูล ขนาดทีม และการเก็บประวัติ แพ็กเกจแบบชำระเงินจะเปิดใช้งานหลังยืนยันการชำระเงินและซิงก์ webhook"
          ),
          [
            legalLocale("Monthly billing", "按月计费", "Bil bulanan", "Tagihan bulanan", "เรียกเก็บรายเดือน"),
            legalLocale("Workspace-level limits", "按工作空间限额", "Had mengikut ruang kerja", "Batas per workspace", "โควตาตามเวิร์กสเปซ"),
            legalLocale("Paddle checkout", "Paddle 收款", "Checkout Paddle", "Checkout Paddle", "ชำระเงินผ่าน Paddle")
          ],
          [
            {
              label: legalLocale("Free entry", "免费起步", "Permulaan percuma", "Mulai gratis", "เริ่มใช้ฟรี"),
              value: "$0",
              body: legalLocale("Starter is designed for evaluation and low-volume work.", "Starter 适合试用和低频业务验证。", "Starter direka untuk penilaian dan kerja bervolum rendah.", "Starter dirancang untuk evaluasi dan pekerjaan ber-volume rendah.", "Starter ออกแบบมาสำหรับการทดลองใช้งานและงานปริมาณต่ำ")
            },
            {
              label: legalLocale("Billing cycle", "计费周期", "Kitaran bil", "Siklus tagihan", "รอบการเรียกเก็บเงิน"),
              value: legalLocale("Monthly", "月付", "Bulanan", "Bulanan", "รายเดือน"),
              body: legalLocale("All paid plans renew monthly until canceled.", "所有付费方案按月自动续费，直到取消。", "Semua pelan berbayar diperbaharui setiap bulan sehingga dibatalkan.", "Semua paket berbayar diperpanjang tiap bulan sampai dibatalkan.", "แพ็กเกจแบบชำระเงินทั้งหมดจะต่ออายุรายเดือนจนกว่าจะยกเลิก")
            },
            {
              label: legalLocale("Refund path", "退款路径", "Laluan bayaran balik", "Alur refund", "ช่องทางการคืนเงิน"),
              value: legalLocale("Manual review", "人工审核", "Semakan manual", "Tinjauan manual", "ตรวจสอบด้วยเจ้าหน้าที่"),
              body: legalLocale("Refunds are reviewed against the refund policy and issued through Paddle.", "退款会按退款政策审核，并通过 Paddle 发起。", "Bayaran balik disemak mengikut polisi bayaran balik dan diproses melalui Paddle.", "Refund ditinjau berdasarkan kebijakan refund dan diproses melalui Paddle.", "การคืนเงินจะได้รับการตรวจสอบตามนโยบายคืนเงินและดำเนินการผ่าน Paddle")
            }
          ]
        ),
        h("section", { class: "panel home-panel" }, [
          h("div", { class: "panel-copy" }, [
            h("h2", labels.value.pricingTitle),
            h("p", { class: "muted-text" }, labels.value.pricingSubtitle)
          ]),
          h("div", { class: "pricing-grid" }, availablePlans.value.map((plan) => renderPlanCard(plan, plan.code === activeOrganization.value?.planCode)))
        ]),
        h("section", { class: "panel static-page-panel" }, [
          renderStaticSections([
            {
              title: legalLocale("How to read the plans", "如何理解这些方案", "Cara membaca pelan", "Cara membaca paket", "วิธีอ่านรายละเอียดแพ็กเกจ"),
              body: legalLocale(
                "All plans use the same product and rule engine. What changes is the monthly operating allowance: checks, extraction volume, preview generation, workspace member count, and how much reopenable history is retained.",
                "所有方案使用同一套产品与规则引擎。差异主要在于每月可用额度：检查次数、抽取量、预览生成次数、工作空间成员数，以及可重新打开的历史保留窗口。",
                "Semua pelan menggunakan produk dan enjin peraturan yang sama. Yang berubah ialah elaun operasi bulanan: semakan, jumlah ekstraksi, penjanaan pratonton, bilangan ahli ruang kerja, dan tempoh sejarah yang boleh dibuka semula.",
                "Semua paket memakai produk dan mesin aturan yang sama. Yang berbeda adalah jatah operasional bulanan: pemeriksaan, volume ekstraksi, pembuatan pratinjau, jumlah anggota workspace, dan berapa lama riwayat dapat dibuka kembali.",
                "ทุกแพ็กเกจใช้ผลิตภัณฑ์และเอนจินกฎเดียวกัน สิ่งที่ต่างกันคือโควตาการใช้งานรายเดือน เช่น จำนวนการตรวจสอบ ปริมาณการดึงข้อมูล การสร้างพรีวิว จำนวนสมาชิกในเวิร์กสเปซ และระยะเวลาที่เปิดดูประวัติย้อนหลังได้"
              ),
              bullets: [
                legalLocale("Starter is for evaluation, trial workflows, and very low monthly volume.", "Starter 用于试用、验证流程和非常低频的月度业务量。", "Starter sesuai untuk penilaian, percubaan aliran kerja, dan jumlah bulanan yang sangat rendah.", "Starter cocok untuk evaluasi, uji alur kerja, dan volume bulanan yang sangat rendah.", "Starter เหมาะสำหรับการทดลองใช้ ทดสอบเวิร์กโฟลว์ และปริมาณงานรายเดือนที่ต่ำมาก"),
                legalLocale("Growth is for teams that already run invoice review as a recurring operational task.", "Growth 适合已经把发票复核作为固定作业流程的团队。", "Growth sesuai untuk pasukan yang sudah menjalankan semakan invois sebagai operasi berulang.", "Growth cocok untuk tim yang sudah menjalankan review invoice sebagai proses operasional rutin.", "Growth เหมาะสำหรับทีมที่มีการตรวจทานเอกสารเป็นงานประจำ"),
                legalLocale("Scale is for regional teams that need more allowance, more seats, and a longer working memory.", "Scale 适合跨区域团队，需要更多额度、更多席位和更长的工作记忆。", "Scale sesuai untuk pasukan serantau yang memerlukan elaun lebih besar, lebih banyak tempat duduk, dan memori kerja yang lebih panjang.", "Scale cocok untuk tim regional yang membutuhkan kuota lebih besar, lebih banyak kursi, dan retensi kerja yang lebih panjang.", "Scale เหมาะสำหรับทีมระดับภูมิภาคที่ต้องการโควตาเพิ่ม ที่นั่งมากขึ้น และการเก็บประวัติที่ยาวขึ้น")
              ]
            },
            {
              title: legalLocale("What is included in every plan", "每个方案都包含什么", "Apa yang termasuk dalam setiap pelan", "Apa yang termasuk di setiap paket", "สิ่งที่รวมอยู่ในทุกแพ็กเกจ"),
              body: legalLocale(
                "Every plan includes the same country-aware editor, official rule-source library, workspace history model, email verification flow, and workspace billing records.",
                "每个方案都包含相同的国家感知编辑器、官方规则来源库、工作空间历史模型、邮箱验证流程和账单记录能力。",
                "Setiap pelan merangkumi editor yang peka pasaran, pustaka sumber peraturan rasmi, model sejarah ruang kerja, aliran pengesahan e-mel, dan rekod bil ruang kerja yang sama.",
                "Setiap paket mencakup editor sadar negara yang sama, pustaka sumber aturan resmi, model riwayat workspace, alur verifikasi email, dan catatan tagihan workspace.",
                "ทุกแพ็กเกจรวมตัวแก้ไขที่รับรู้ตามประเทศ คลังแหล่งอ้างอิงกฎทางการ ระบบประวัติเวอร์กสเปซ ขั้นตอนยืนยันอีเมล และบันทึกการเรียกเก็บเงินของเวิร์กสเปซเหมือนกัน"
              ),
              bullets: [
                legalLocale("Manual drafting and OCR / PDF import share the same draft workflow.", "手工起草与 OCR / PDF 导入共用同一份草稿流程。", "Penggubalan manual dan import OCR / PDF berkongsi aliran draf yang sama.", "Penyusunan manual dan impor OCR / PDF memakai alur draft yang sama.", "การร่างเองและการนำเข้า OCR / PDF ใช้เวิร์กโฟลว์ร่างเดียวกัน"),
                legalLocale("Rule checks remain explainable and tied back to versioned official sources.", "规则检查结果可解释，并能回溯到版本化的官方来源。", "Semakan peraturan boleh dijelaskan dan dihubungkan semula kepada sumber rasmi berversi.", "Hasil pemeriksaan aturan tetap dapat dijelaskan dan ditelusuri kembali ke sumber resmi berversi.", "ผลการตรวจสอบกฎสามารถอธิบายได้และย้อนกลับไปยังแหล่งทางการที่มีเวอร์ชันได้"),
                legalLocale("Saved preview pages and check snapshots stay attached to the workspace.", "已保存的预览页和检查快照都会绑定在工作空间下。", "Halaman pratonton tersimpan dan syot semakan kekal terikat pada ruang kerja.", "Halaman pratinjau tersimpan dan snapshot pemeriksaan tetap terhubung ke workspace.", "หน้าพรีวิวที่บันทึกไว้และสแนปช็อตการตรวจสอบจะผูกกับเวิร์กสเปซเสมอ")
              ]
            },
            {
              title: legalLocale("How billing and renewals work", "计费与续费方式", "Cara bil dan pembaharuan berfungsi", "Cara tagihan dan perpanjangan berjalan", "วิธีทำงานของการเรียกเก็บเงินและการต่ออายุ"),
              body: legalLocale(
                "Self-serve paid plans are billed monthly in advance. Checkout, payment authorization, buyer receipts, and refund execution are handled through Paddle. Workspace limits are enforced from a real usage ledger, not from front-end estimates.",
                "自助付费方案按月预付。结账、支付授权、买家收据和退款执行通过 Paddle 完成。工作空间限额基于真实用量账本执行，而不是前端估算。",
                "Pelan berbayar layan diri dicaj bulanan secara pendahuluan. Checkout, kebenaran pembayaran, resit pembeli, dan pelaksanaan bayaran balik diurus melalui Paddle. Had ruang kerja dikuatkuasakan berdasarkan lejar penggunaan sebenar, bukan anggaran bahagian hadapan.",
                "Paket berbayar self-serve ditagih bulanan di muka. Checkout, otorisasi pembayaran, kuitansi pembeli, dan pelaksanaan refund ditangani melalui Paddle. Batas workspace ditegakkan dari ledger penggunaan nyata, bukan perkiraan front-end.",
                "แพ็กเกจแบบชำระเงินด้วยตนเองจะเรียกเก็บรายเดือนล่วงหน้า ขั้นตอนชำระเงิน การอนุมัติการชำระ ใบเสร็จผู้ซื้อ และการคืนเงินจัดการผ่าน Paddle โควตาเวิร์กสเปซอิงตามบัญชีการใช้งานจริง ไม่ใช่การประมาณจากหน้าจอ"
              ),
              bullets: [
                legalLocale("A plan starts after checkout confirmation and provider webhook sync.", "套餐会在支付确认和支付服务商 webhook 回写后正式生效。", "Pelan bermula selepas pengesahan checkout dan penyegerakan webhook pembekal.", "Paket aktif setelah konfirmasi checkout dan sinkronisasi webhook penyedia.", "แพ็กเกจจะเริ่มหลังการยืนยันการชำระเงินและการซิงก์ webhook ของผู้ให้บริการ"),
                legalLocale("Renewals continue until you cancel before the next billing date.", "如果你没有在下一次账单日前取消，订阅会持续自动续费。", "Pembaharuan berterusan sehingga anda membatalkannya sebelum tarikh bil seterusnya.", "Perpanjangan berjalan terus sampai Anda membatalkan sebelum tanggal tagihan berikutnya.", "การต่ออายุจะดำเนินต่อไปจนกว่าคุณจะยกเลิกก่อนรอบบิลถัดไป"),
                legalLocale("If payment fails, paid access may be limited until the billing state is corrected.", "如果续费失败，付费权益可能会在账单状态恢复前受到限制。", "Jika pembayaran gagal, akses berbayar mungkin dihadkan sehingga status bil diperbetulkan.", "Jika pembayaran gagal, akses berbayar dapat dibatasi sampai status tagihan kembali normal.", "หากการชำระเงินล้มเหลว สิทธิ์แบบชำระเงินอาจถูกจำกัดจนกว่าสถานะการเรียกเก็บเงินจะถูกแก้ไข")
              ]
            },
            {
              title: legalLocale("What counts as usage", "什么会计入用量", "Apa yang dikira sebagai penggunaan", "Apa yang dihitung sebagai penggunaan", "สิ่งที่นับเป็นการใช้งาน"),
              body: legalLocale(
                "Usage is tracked per workspace. Compliance checks, OCR / AI extraction runs, generated preview pages, and member seats each have their own plan allowance and reset with the monthly billing period.",
                "用量按工作空间记录。合规检查、OCR / AI 抽取、预览页生成和成员席位都有独立的套餐额度，并随月度计费周期重置。",
                "Penggunaan dijejaki mengikut ruang kerja. Semakan pematuhan, ekstraksi OCR / AI, penjanaan halaman pratonton, dan tempat duduk ahli masing-masing mempunyai elaun pelan sendiri dan ditetapkan semula mengikut kitaran bil bulanan.",
                "Penggunaan dicatat per workspace. Pemeriksaan kepatuhan, proses ekstraksi OCR / AI, pembuatan halaman pratinjau, dan kursi anggota masing-masing memiliki kuota paket sendiri dan di-reset setiap siklus tagihan bulanan.",
                "การใช้งานถูกบันทึกตามเวิร์กสเปซ การตรวจสอบความสอดคล้อง การดึงข้อมูลด้วย OCR / AI การสร้างหน้าพรีวิว และจำนวนที่นั่งสมาชิกต่างมีโควตาแยกกันและรีเซ็ตตามรอบบิลรายเดือน"
              ),
              bullets: [
                legalLocale("A saved or realtime compliance check uses one check allowance.", "一次实时或保存的合规检查会消耗一次检查额度。", "Satu semakan pematuhan yang disimpan atau masa nyata menggunakan satu kuota semakan.", "Satu pemeriksaan kepatuhan realtime atau tersimpan memakai satu kuota pemeriksaan.", "การตรวจสอบความสอดคล้องแบบเรียลไทม์หรือที่บันทึกไว้หนึ่งครั้งจะใช้โควตาการตรวจสอบหนึ่งหน่วย"),
                legalLocale("OCR and AI extraction share the extraction allowance.", "OCR 与 AI 抽取共用同一份抽取额度。", "OCR dan ekstraksi AI berkongsi elaun ekstraksi yang sama.", "OCR dan ekstraksi AI berbagi kuota ekstraksi yang sama.", "OCR และการดึงข้อมูลด้วย AI ใช้โควตาการดึงข้อมูลร่วมกัน"),
                legalLocale("Opening a preview page only counts when a new preview is generated, not when an existing one is reopened.", "只有生成新的预览页时才会计入额度，重新打开旧预览不会重复扣减。", "Membuka halaman pratonton hanya dikira apabila pratonton baharu dijana, bukan apabila pratonton sedia ada dibuka semula.", "Membuka halaman pratinjau hanya dihitung saat pratinjau baru dibuat, bukan saat pratinjau lama dibuka kembali.", "การเปิดหน้าพรีวิวจะนับก็ต่อเมื่อมีการสร้างพรีวิวใหม่เท่านั้น ไม่ใช่เมื่อเปิดพรีวิวเดิมซ้ำ")
              ]
            },
            {
              title: legalLocale("Plan changes, cancellation, and refunds", "变更、取消与退款", "Perubahan pelan, pembatalan, dan bayaran balik", "Perubahan paket, pembatalan, dan refund", "การเปลี่ยนแพ็กเกจ การยกเลิก และการคืนเงิน"),
              body: legalLocale(
                "Existing paid subscriptions are managed through the subscription-management link. Cancellation stops future renewals. Refunds are reviewed under the published refund policy and issued through the original payment path when approved.",
                "已生效的付费订阅通过订阅管理链接管理。取消会阻止未来续费。退款会按公开退款政策审核，获批后通过原支付路径退回。",
                "Langganan berbayar sedia ada diurus melalui pautan pengurusan langganan. Pembatalan menghentikan pembaharuan masa depan. Bayaran balik disemak mengikut polisi yang diterbitkan dan dipulangkan ke saluran pembayaran asal apabila diluluskan.",
                "Langganan berbayar yang aktif dikelola melalui tautan manajemen langganan. Pembatalan menghentikan perpanjangan berikutnya. Refund ditinjau berdasarkan kebijakan refund dan dikembalikan ke metode pembayaran asli jika disetujui.",
                "การสมัครแบบชำระเงินที่มีอยู่จัดการผ่านลิงก์จัดการการสมัคร การยกเลิกจะหยุดการต่ออายุในอนาคต การคืนเงินจะได้รับการตรวจสอบตามนโยบายที่เผยแพร่และคืนไปยังช่องทางการชำระเดิมเมื่ออนุมัติ"
              ),
              bullets: [
                legalLocale("Use the billing page for current plan, order, and subscription visibility.", "你可以在账单页查看当前方案、订单与订阅状态。", "Gunakan halaman bil untuk melihat pelan semasa, pesanan, dan status langganan.", "Gunakan halaman tagihan untuk melihat paket aktif, pesanan, dan status langganan.", "ใช้หน้าบิลเพื่อตรวจสอบแพ็กเกจปัจจุบัน คำสั่งซื้อ และสถานะการสมัคร"),
                legalLocale("If a full refund is approved, the workspace may return to the free plan.", "如果全额退款获批，工作空间可能被降回免费方案。", "Jika bayaran balik penuh diluluskan, ruang kerja mungkin kembali ke pelan percuma.", "Jika refund penuh disetujui, workspace dapat kembali ke paket gratis.", "หากการคืนเงินเต็มจำนวนได้รับอนุมัติ เวิร์กสเปซอาจกลับไปใช้แพ็กเกจฟรี"),
                legalLocale("Read the refund page before purchase if your team needs internal approval or procurement review.", "如果你的团队采购流程需要内部审批，建议在购买前先阅读退款说明页。", "Baca halaman bayaran balik sebelum membeli jika pasukan anda memerlukan kelulusan dalaman atau semakan perolehan.", "Baca halaman refund sebelum membeli jika tim Anda memerlukan persetujuan internal atau peninjauan pengadaan.", "อ่านหน้านโยบายคืนเงินก่อนซื้อ หากทีมของคุณต้องผ่านการอนุมัติภายในหรือการตรวจสอบจัดซื้อ")
              ]
            },
            {
              title: legalLocale("Common pricing questions", "常见定价问题", "Soalan harga lazim", "Pertanyaan harga umum", "คำถามที่พบบ่อยเรื่องราคา"),
              body: legalLocale(
                "Starter is intentionally limited so teams can validate the workflow without replacing production operations. Growth and Scale are the plans intended for recurring operational use.",
                "Starter 被刻意控制在试用与验证级别，方便团队确认流程是否适合，而不会直接覆盖真实生产运营。Growth 和 Scale 才是面向持续作业的正式方案。",
                "Starter sengaja dihadkan supaya pasukan boleh mengesahkan aliran kerja tanpa menggantikan operasi pengeluaran. Growth dan Scale ialah pelan untuk penggunaan operasi berulang.",
                "Starter sengaja dibatasi agar tim bisa memvalidasi alur kerja tanpa menggantikan operasi produksi. Growth dan Scale adalah paket untuk penggunaan operasional berulang.",
                "Starter ถูกจำกัดไว้โดยตั้งใจเพื่อให้ทีมสามารถยืนยันเวิร์กโฟลว์ได้โดยไม่แทนที่การทำงานจริง ส่วน Growth และ Scale คือแพ็กเกจสำหรับการใช้งานปฏิบัติการแบบต่อเนื่อง"
              ),
              bullets: [
                legalLocale("Do you charge per user? No. Limits are enforced per workspace plan, with a member cap.", "按人收费吗？不是。当前是按工作空间方案收费，并附带成员上限。", "Adakah caj dikenakan setiap pengguna? Tidak. Had dikuatkuasakan mengikut pelan ruang kerja, bersama had ahli.", "Apakah dikenakan biaya per pengguna? Tidak. Batas diterapkan per paket workspace, dengan batas anggota.", "คิดค่าบริการต่อผู้ใช้หรือไม่? ไม่ใช่ ระบบคิดตามแพ็กเกจของเวิร์กสเปซและมีจำนวนสมาชิกสูงสุด"),
                legalLocale("Can I start free and upgrade later? Yes. Starter is the default entry point.", "可以先免费再升级吗？可以。Starter 就是默认入口方案。", "Bolehkah saya mula percuma dan naik taraf kemudian? Ya. Starter ialah pelan permulaan lalai.", "Bisakah saya mulai gratis lalu upgrade nanti? Ya. Starter adalah paket awal bawaan.", "เริ่มใช้ฟรีก่อนแล้วค่อยอัปเกรดได้ไหม? ได้ Starter คือจุดเริ่มต้นเริ่มต้น"),
                legalLocale("Can I buy without creating a workspace? No. Billing is always attached to a workspace.", "不创建工作空间能直接购买吗？不能。计费始终绑定在工作空间上。", "Bolehkah saya membeli tanpa mencipta ruang kerja? Tidak. Bil sentiasa terikat pada ruang kerja.", "Bisakah saya membeli tanpa membuat workspace? Tidak. Penagihan selalu terikat ke workspace.", "สามารถซื้อได้โดยไม่สร้างเวิร์กสเปซหรือไม่? ไม่ได้ การเรียกเก็บเงินจะผูกกับเวิร์กสเปซเสมอ")
              ]
            }
          ]),
          h("div", { class: "toolbar-row" }, [
            h("button", { class: "action-button secondary-button", type: "button", onClick: () => navigateTo("refunds") }, legalLocale("Read refund policy", "查看退款说明", "Lihat polisi bayaran balik", "Lihat kebijakan refund", "ดูนโยบายคืนเงิน")),
            signedIn.value ? h("button", { class: "action-button", type: "button", onClick: () => navigateTo("billing") }, legalLocale("Open billing", "打开账单页", "Buka bil", "Buka tagihan", "เปิดหน้าบิล")) : null
          ])
        ])
      ]);

    const renderPrivacyView = () =>
      renderStaticPage(
        legalLocale("Privacy", "隐私政策", "Privasi", "Privasi", "ความเป็นส่วนตัว"),
        legalLocale("Privacy Policy", "隐私政策", "Dasar Privasi", "Kebijakan Privasi", "นโยบายความเป็นส่วนตัว"),
        legalLocale(
          "This policy explains what information the service processes, why it is processed, how long it is retained, and how users can contact us about privacy requests.",
          "本政策说明本服务会处理哪些信息、出于什么目的处理、保留多久，以及用户如何就隐私事项联系我们。",
          "Dasar ini menerangkan maklumat apa yang diproses oleh perkhidmatan, sebab ia diproses, tempoh penyimpanannya, dan cara pengguna boleh menghubungi kami mengenai permintaan privasi.",
          "Kebijakan ini menjelaskan informasi apa yang diproses layanan, mengapa diproses, berapa lama disimpan, dan bagaimana pengguna dapat menghubungi kami mengenai permintaan privasi.",
          "นโยบายนี้อธิบายว่าบริการประมวลผลข้อมูลใด เหตุใดจึงประมวลผล เก็บไว้นานเท่าใด และผู้ใช้จะติดต่อเราเรื่องคำขอด้านความเป็นส่วนตัวได้อย่างไร"
        ),
        [
          legalLocale(`Privacy contact: ${PRIVACY_EMAIL}`, `隐私联系：${PRIVACY_EMAIL}`, `Hubungan privasi: ${PRIVACY_EMAIL}`, `Kontak privasi: ${PRIVACY_EMAIL}`, `ติดต่อด้านความเป็นส่วนตัว: ${PRIVACY_EMAIL}`),
          legalLocale(`Updated ${LEGAL_LAST_UPDATED}`, `更新于 ${LEGAL_LAST_UPDATED}`, `Dikemas kini ${LEGAL_LAST_UPDATED}`, `Diperbarui ${LEGAL_LAST_UPDATED}`, `อัปเดต ${LEGAL_LAST_UPDATED}`)
        ],
        [
          {
            label: legalLocale("Core data", "核心数据", "Data teras", "Data inti", "ข้อมูลหลัก"),
            value: legalLocale("Account + drafts", "账户 + 草稿", "Akaun + draf", "Akun + draft", "บัญชี + ร่าง"),
            body: legalLocale("The service stores account, workspace, draft, preview, order, and usage-ledger data.", "服务会存储账户、工作空间、草稿、预览、订单和用量账本数据。", "Perkhidmatan menyimpan data akaun, ruang kerja, draf, pratonton, pesanan, dan lejar penggunaan.", "Layanan menyimpan data akun, workspace, draft, pratinjau, pesanan, dan ledger penggunaan.", "บริการจัดเก็บข้อมูลบัญชี เวิร์กสเปซ ร่าง พรีวิว คำสั่งซื้อ และบันทึกการใช้งาน")
          },
          {
            label: legalLocale("Key processors", "主要处理方", "Pemproses utama", "Pemroses utama", "ผู้ประมวลผลหลัก"),
            value: legalLocale("Alibaba + Paddle", "阿里云 + Paddle", "Alibaba + Paddle", "Alibaba + Paddle", "Alibaba + Paddle"),
            body: legalLocale("Alibaba Cloud may process OCR / AI tasks, while Paddle handles card checkout and payment records.", "阿里云可能处理 OCR / AI 任务，Paddle 负责卡支付结账与支付记录。", "Alibaba Cloud mungkin memproses tugas OCR / AI, manakala Paddle mengendalikan checkout kad dan rekod pembayaran.", "Alibaba Cloud dapat memproses tugas OCR / AI, sementara Paddle menangani checkout kartu dan catatan pembayaran.", "Alibaba Cloud อาจประมวลผลงาน OCR / AI ส่วน Paddle จัดการการชำระเงินด้วยบัตรและบันทึกการชำระ")
          },
          {
            label: legalLocale("Retention", "保留", "Penyimpanan", "Retensi", "การเก็บรักษา"),
            value: legalLocale("Plan-aware", "按套餐", "Mengikut pelan", "Berdasarkan paket", "ตามแพ็กเกจ"),
            body: legalLocale("Operational records stay available according to plan limits and legal obligations.", "运营记录会按套餐能力和法定义务保留。", "Rekod operasi kekal tersedia mengikut had pelan dan kewajipan undang-undang.", "Catatan operasional tersedia sesuai batas paket dan kewajiban hukum.", "บันทึกการดำเนินงานจะพร้อมใช้งานตามโควตาแพ็กเกจและข้อผูกพันทางกฎหมาย")
          }
        ],
        [
          {
            title: legalLocale("Scope of this policy", "本政策适用范围", "Skop dasar ini", "Ruang lingkup kebijakan ini", "ขอบเขตของนโยบายนี้"),
            body: legalLocale("This policy applies to the website, workspace product, billing flows, and support channels associated with the service. It covers information we collect directly from users, from workspace activity, and from approved service providers that help us deliver the product.", "本政策适用于本网站、工作空间产品、计费流程以及与本服务相关的支持渠道。它覆盖我们直接从用户收集的信息、工作空间使用过程中产生的信息，以及帮助我们提供产品的获授权服务商提供的信息。", "Dasar ini terpakai pada laman web, produk ruang kerja, aliran bil, dan saluran sokongan yang berkaitan dengan perkhidmatan. Ia meliputi maklumat yang kami kumpul terus daripada pengguna, daripada aktiviti ruang kerja, dan daripada penyedia perkhidmatan yang diluluskan yang membantu kami menyampaikan produk.", "Kebijakan ini berlaku untuk situs web, produk workspace, alur tagihan, dan saluran dukungan yang terkait dengan layanan. Ini mencakup informasi yang kami kumpulkan langsung dari pengguna, dari aktivitas workspace, dan dari penyedia layanan yang disetujui yang membantu kami menjalankan produk.", "นโยบายนี้ใช้กับเว็บไซต์ ผลิตภัณฑ์เวิร์กสเปซ ขั้นตอนการเรียกเก็บเงิน และช่องทางสนับสนุนที่เกี่ยวข้องกับบริการ ครอบคลุมข้อมูลที่เราเก็บโดยตรงจากผู้ใช้ จากกิจกรรมในเวิร์กสเปซ และจากผู้ให้บริการที่ได้รับอนุมัติซึ่งช่วยให้เราส่งมอบผลิตภัณฑ์")
          },
          {
            title: legalLocale("Information we collect", "我们会收集哪些信息", "Maklumat yang kami kumpul", "Informasi yang kami kumpulkan", "ข้อมูลที่เราเก็บรวบรวม"),
            body: legalLocale("We collect information you provide when creating an account or workspace, including email, password-derived authentication data, display name, workspace name, and selected market. We also collect invoice draft content, preview records, verification events, order state, refund state, and usage-ledger records created when the service is used.", "我们会收集你在创建账户或工作空间时主动提供的信息，包括邮箱、密码派生的认证数据、显示名称、工作空间名称和所选市场。我们也会收集发票草稿内容、预览记录、验证事件、订单状态、退款状态，以及服务使用时产生的用量账本记录。", "Kami mengumpul maklumat yang anda berikan semasa mencipta akaun atau ruang kerja, termasuk e-mel, data pengesahan terbitan kata laluan, nama paparan, nama ruang kerja, dan pasaran yang dipilih. Kami juga mengumpul kandungan draf invois, rekod pratonton, peristiwa pengesahan, status pesanan, status bayaran balik, dan rekod lejar penggunaan yang terhasil apabila perkhidmatan digunakan.", "Kami mengumpulkan informasi yang Anda berikan saat membuat akun atau workspace, termasuk email, data autentikasi turunan kata sandi, nama tampilan, nama workspace, dan pasar yang dipilih. Kami juga mengumpulkan konten draft invoice, catatan pratinjau, peristiwa verifikasi, status pesanan, status refund, dan catatan ledger penggunaan yang dibuat saat layanan digunakan.", "เราเก็บข้อมูลที่คุณให้ไว้เมื่อสร้างบัญชีหรือเวิร์กสเปซ รวมถึงอีเมล ข้อมูลยืนยันตัวตนที่ได้จากรหัสผ่าน ชื่อที่แสดง ชื่อเวิร์กสเปซ และตลาดที่เลือก นอกจากนี้เรายังเก็บเนื้อหาร่างเอกสาร ประวัติพรีวิว เหตุการณ์การยืนยัน สถานะคำสั่งซื้อ สถานะการคืนเงิน และบันทึกการใช้งานที่เกิดขึ้นเมื่อมีการใช้บริการ"),
            bullets: [
              legalLocale("Uploaded files and OCR text may contain personal, business, invoice, or tax-related information that you choose to submit.", "你主动上传的文件和 OCR 文本可能包含个人、企业、发票或税务相关信息。", "Fail yang dimuat naik dan teks OCR mungkin mengandungi maklumat peribadi, perniagaan, invois, atau cukai yang anda pilih untuk serahkan.", "File yang diunggah dan teks OCR dapat berisi informasi pribadi, bisnis, invoice, atau perpajakan yang Anda pilih untuk kirimkan.", "ไฟล์ที่อัปโหลดและข้อความ OCR อาจมีข้อมูลส่วนบุคคล ข้อมูลธุรกิจ ข้อมูลเอกสาร หรือข้อมูลภาษีที่คุณเลือกส่งเข้ามา"),
              legalLocale("Operational metadata such as timestamps, workspace membership, and request traces may also be stored for reliability and auditability.", "为了可靠性和可追溯性，我们也可能记录时间戳、工作空间成员关系和请求追踪等运营元数据。", "Metadata operasi seperti cap masa, keahlian ruang kerja, dan jejak permintaan juga mungkin disimpan untuk kebolehpercayaan dan audit.", "Metadata operasional seperti cap waktu, keanggotaan workspace, dan jejak permintaan juga dapat disimpan untuk keandalan dan auditabilitas.", "เมทาดาต้าด้านการปฏิบัติการ เช่น เวลา สมาชิกในเวิร์กสเปซ และร่องรอยคำขอ อาจถูกเก็บไว้เพื่อความน่าเชื่อถือและการตรวจสอบย้อนกลับ")
            ]
          },
          {
            title: legalLocale("Why we process information", "我们为什么处理这些信息", "Mengapa kami memproses maklumat", "Mengapa kami memproses informasi", "เหตุใดเราจึงประมวลผลข้อมูล"),
            body: legalLocale("We process information to run the service, maintain workspace security, send verification emails, apply rule checks, generate preview pages, keep usage and billing records, detect abuse, answer support requests, and comply with applicable law or payment-provider requirements.", "我们处理这些信息，是为了运行服务、维护工作空间安全、发送验证邮件、执行规则检查、生成预览页、保留用量和账单记录、识别滥用行为、处理支持请求，并遵守适用法律或支付服务商要求。", "Kami memproses maklumat untuk menjalankan perkhidmatan, mengekalkan keselamatan ruang kerja, menghantar e-mel pengesahan, melaksanakan semakan peraturan, menjana halaman pratonton, menyimpan rekod penggunaan dan bil, mengesan penyalahgunaan, menjawab permintaan sokongan, dan mematuhi undang-undang yang berkenaan atau keperluan penyedia pembayaran.", "Kami memproses informasi untuk menjalankan layanan, menjaga keamanan workspace, mengirim email verifikasi, menerapkan pemeriksaan aturan, menghasilkan halaman pratinjau, menyimpan catatan penggunaan dan tagihan, mendeteksi penyalahgunaan, menjawab permintaan dukungan, dan mematuhi hukum yang berlaku atau persyaratan penyedia pembayaran.", "เราใช้ข้อมูลเพื่อให้บริการ รักษาความปลอดภัยของเวิร์กสเปซ ส่งอีเมลยืนยัน ใช้การตรวจสอบกฎ สร้างหน้าพรีวิว เก็บบันทึกการใช้งานและการเรียกเก็บเงิน ตรวจจับการใช้งานที่ไม่เหมาะสม ตอบคำขอด้านการสนับสนุน และปฏิบัติตามกฎหมายหรือข้อกำหนดของผู้ให้บริการชำระเงิน")
          },
          {
            title: legalLocale("Cookies, sessions, and technical data", "Cookie、会话与技术数据", "Kuki, sesi, dan data teknikal", "Cookie, sesi, dan data teknis", "คุกกี้ เซสชัน และข้อมูลทางเทคนิค"),
            body: legalLocale("We use session cookies and similar technical signals to keep users signed in, protect checkout and verification flows, remember selected language or market preferences, and understand whether core pages are working correctly. We do not sell this technical data to third parties.", "我们会使用会话 Cookie 和类似的技术信号来维持登录状态、保护结账与验证流程、记住语言或市场偏好，并确认核心页面是否正常运行。我们不会把这些技术数据出售给第三方。", "Kami menggunakan kuki sesi dan isyarat teknikal yang serupa untuk memastikan pengguna kekal log masuk, melindungi aliran checkout dan pengesahan, mengingati pilihan bahasa atau pasaran, serta memahami sama ada halaman teras berfungsi dengan betul. Kami tidak menjual data teknikal ini kepada pihak ketiga.", "Kami menggunakan cookie sesi dan sinyal teknis serupa untuk menjaga pengguna tetap masuk, melindungi alur checkout dan verifikasi, mengingat preferensi bahasa atau pasar, serta memahami apakah halaman inti berfungsi dengan benar. Kami tidak menjual data teknis ini kepada pihak ketiga.", "เราใช้คุกกี้เซสชันและสัญญาณทางเทคนิคที่คล้ายกันเพื่อคงสถานะการเข้าสู่ระบบ ปกป้องขั้นตอนการชำระเงินและการยืนยัน จดจำภาษาหรือความต้องการด้านตลาด และตรวจสอบว่าหน้าที่สำคัญทำงานถูกต้อง เราไม่ขายข้อมูลทางเทคนิคนี้ให้บุคคลที่สาม"),
            bullets: [
              legalLocale("Language and market preferences may be stored locally in the browser to improve repeat visits.", "语言和市场偏好可能会被保存在浏览器本地，以改善回访体验。", "Pilihan bahasa dan pasaran mungkin disimpan secara setempat dalam pelayar untuk meningkatkan pengalaman lawatan berulang.", "Preferensi bahasa dan pasar dapat disimpan secara lokal di browser untuk meningkatkan pengalaman kunjungan ulang.", "ค่าภาษาและตลาดอาจถูกเก็บไว้ในเบราว์เซอร์เพื่อช่วยให้การกลับมาใช้งานครั้งถัดไปราบรื่นขึ้น"),
              legalLocale("Session and anti-abuse controls may remain active even when a user has not purchased a paid plan.", "即使用户尚未购买付费方案，会话和防滥用控制也可能继续生效。", "Kawalan sesi dan anti-penyalahgunaan mungkin kekal aktif walaupun pengguna belum membeli pelan berbayar.", "Kontrol sesi dan anti-penyalahgunaan dapat tetap aktif meskipun pengguna belum membeli paket berbayar.", "ระบบควบคุมเซสชันและการป้องกันการใช้งานที่ไม่เหมาะสมอาจยังทำงานแม้ผู้ใช้ยังไม่ได้ซื้อแพ็กเกจแบบชำระเงิน")
            ]
          },
          {
            title: legalLocale("Third-party processors and infrastructure", "第三方处理方与基础设施", "Pemproses pihak ketiga dan infrastruktur", "Pemroses pihak ketiga dan infrastruktur", "ผู้ประมวลผลภายนอกและโครงสร้างพื้นฐาน"),
            body: legalLocale("We may use hosting, database, email, OCR, AI, and payment providers to run the service. In the current setup, this includes Alibaba Cloud services for infrastructure, email, OCR, or AI tasks where enabled, and Paddle for self-serve checkout, receipts, and refunds.", "我们可能会使用托管、数据库、邮件、OCR、AI 和支付服务商来运行产品。当前配置中，这包括在启用时用于基础设施、邮件、OCR 或 AI 的阿里云服务，以及用于自助结账、收据和退款的 Paddle。", "Kami mungkin menggunakan penyedia pengehosan, pangkalan data, e-mel, OCR, AI, dan pembayaran untuk menjalankan perkhidmatan. Dalam konfigurasi semasa, ini termasuk perkhidmatan Alibaba Cloud untuk infrastruktur, e-mel, OCR, atau tugas AI apabila diaktifkan, dan Paddle untuk checkout layan diri, resit, dan bayaran balik.", "Kami dapat menggunakan penyedia hosting, database, email, OCR, AI, dan pembayaran untuk menjalankan layanan. Pada pengaturan saat ini, ini mencakup layanan Alibaba Cloud untuk infrastruktur, email, OCR, atau tugas AI jika diaktifkan, dan Paddle untuk checkout mandiri, kuitansi, dan refund.", "เราอาจใช้ผู้ให้บริการด้านโฮสติ้ง ฐานข้อมูล อีเมล OCR AI และการชำระเงินเพื่อให้บริการ ในการตั้งค่าปัจจุบันนี้รวมถึง Alibaba Cloud สำหรับโครงสร้างพื้นฐาน อีเมล OCR หรือ AI เมื่อเปิดใช้ และ Paddle สำหรับการชำระเงินด้วยตนเอง ใบเสร็จ และการคืนเงิน"),
            bullets: [
              legalLocale("Payment-card data is handled by Paddle in the checkout flow and is not fully stored inside the workspace application.", "支付卡数据由 Paddle 在结账流程中处理，不会以完整形式存储在工作空间应用中。", "Data kad pembayaran dikendalikan oleh Paddle dalam aliran checkout dan tidak disimpan sepenuhnya dalam aplikasi ruang kerja.", "Data kartu pembayaran ditangani oleh Paddle dalam alur checkout dan tidak disimpan sepenuhnya di dalam aplikasi workspace.", "ข้อมูลบัตรชำระเงินถูกจัดการโดย Paddle ในขั้นตอน checkout และไม่ได้เก็บไว้ทั้งหมดภายในแอปเวิร์กสเปซ"),
              legalLocale("Uploaded content submitted for OCR or AI extraction may be processed by the corresponding cloud service used by your deployment.", "提交用于 OCR 或 AI 抽取的文件内容，可能会由你的部署所启用的对应云服务处理。", "Kandungan yang dimuat naik untuk ekstraksi OCR atau AI mungkin diproses oleh perkhidmatan awan yang sepadan dengan penggunaan anda.", "Konten yang diunggah untuk ekstraksi OCR atau AI dapat diproses oleh layanan cloud yang digunakan dalam deployment Anda.", "เนื้อหาที่อัปโหลดเพื่อใช้กับ OCR หรือ AI อาจถูกประมวลผลโดยบริการคลาวด์ที่สอดคล้องกับการติดตั้งของคุณ")
            ]
          },
          {
            title: legalLocale("Retention and deletion", "保留与删除", "Penyimpanan dan pemadaman", "Retensi dan penghapusan", "การเก็บรักษาและการลบ"),
            body: legalLocale("We keep data only as long as reasonably necessary for the service, billing, support, auditability, and legal obligations. Some reopenable workspace history is limited by plan. Certain accounting, billing, anti-fraud, and legal records may be retained longer where required.", "我们仅在提供服务、计费、支持、审计留痕和履行法定义务所合理需要的期间内保留数据。部分可重新打开的工作空间历史会受到套餐限制。某些会计、计费、反欺诈和法律相关记录可能会因为义务要求而保留更久。", "Kami menyimpan data hanya selama yang munasabah diperlukan untuk perkhidmatan, bil, sokongan, audit, dan kewajipan undang-undang. Sebahagian sejarah ruang kerja yang boleh dibuka semula dihadkan mengikut pelan. Rekod tertentu berkaitan perakaunan, bil, anti-penipuan, dan undang-undang mungkin disimpan lebih lama jika diperlukan.", "Kami menyimpan data hanya selama yang wajar dibutuhkan untuk layanan, penagihan, dukungan, auditabilitas, dan kewajiban hukum. Sebagian riwayat workspace yang dapat dibuka kembali dibatasi oleh paket. Catatan tertentu terkait akuntansi, tagihan, anti-fraud, dan hukum dapat disimpan lebih lama jika diwajibkan.", "เราเก็บข้อมูลไว้เพียงเท่าที่จำเป็นอย่างสมเหตุสมผลสำหรับบริการ การเรียกเก็บเงิน การสนับสนุน การตรวจสอบย้อนหลัง และข้อผูกพันทางกฎหมาย ประวัติเวอร์กสเปซบางส่วนที่เปิดซ้ำได้อาจถูกจำกัดตามแพ็กเกจ และบันทึกบางประเภทด้านบัญชี การเรียกเก็บเงิน การป้องกันการทุจริต และกฎหมายอาจต้องเก็บไว้นานกว่า")
          },
          {
            title: legalLocale("Security", "安全措施", "Keselamatan", "Keamanan", "ความปลอดภัย"),
            body: legalLocale("Access to service data is limited to authorized operators and approved processors that need it to run the product. We apply reasonable administrative, technical, and organizational measures to protect account, workspace, and billing information, but no internet service can promise absolute security.", "服务数据的访问会被限制在为运行产品所必须的获授权运营人员和获批准处理方范围内。我们会采取合理的管理、技术与组织措施来保护账户、工作空间和账单信息，但任何互联网服务都无法承诺绝对安全。", "Akses kepada data perkhidmatan dihadkan kepada operator yang dibenarkan dan pemproses yang diluluskan yang memerlukannya untuk menjalankan produk. Kami menggunakan langkah pentadbiran, teknikal, dan organisasi yang munasabah untuk melindungi maklumat akaun, ruang kerja, dan bil, tetapi tiada perkhidmatan internet boleh menjanjikan keselamatan mutlak.", "Akses ke data layanan dibatasi pada operator yang berwenang dan pemroses yang disetujui yang membutuhkannya untuk menjalankan produk. Kami menerapkan langkah administratif, teknis, dan organisasi yang wajar untuk melindungi informasi akun, workspace, dan tagihan, tetapi tidak ada layanan internet yang dapat menjanjikan keamanan mutlak.", "การเข้าถึงข้อมูลบริการจำกัดไว้เฉพาะผู้ปฏิบัติงานที่ได้รับอนุญาตและผู้ประมวลผลที่ได้รับอนุมัติซึ่งจำเป็นต่อการให้บริการ เราใช้มาตรการด้านการบริหาร เทคนิค และองค์กรอย่างเหมาะสมเพื่อปกป้องข้อมูลบัญชี เวิร์กสเปซ และการเรียกเก็บเงิน แต่ไม่มีบริการอินเทอร์เน็ตใดรับประกันความปลอดภัยได้อย่างสมบูรณ์")
          },
          {
            title: legalLocale("International use and transfers", "跨境使用与传输", "Penggunaan dan pemindahan antarabangsa", "Penggunaan dan transfer internasional", "การใช้งานและการโอนข้อมูลระหว่างประเทศ"),
            body: legalLocale("Because the service is designed for regional use across multiple Southeast Asian markets, information may be processed in jurisdictions different from where a user or buyer is located. We require service providers to process information only for approved purposes related to the service.", "由于本服务面向多个东南亚市场，信息可能会在与用户或买方所在地不同的司法辖区中处理。我们会要求服务商仅在与本服务相关且获批准的目的范围内处理这些信息。", "Oleh sebab perkhidmatan ini direka untuk kegunaan serantau merentasi beberapa pasaran Asia Tenggara, maklumat mungkin diproses dalam bidang kuasa yang berbeza daripada lokasi pengguna atau pembeli. Kami menghendaki penyedia perkhidmatan memproses maklumat hanya bagi tujuan yang diluluskan berkaitan dengan perkhidmatan.", "Karena layanan ini dirancang untuk penggunaan regional di beberapa pasar Asia Tenggara, informasi dapat diproses di yurisdiksi yang berbeda dari lokasi pengguna atau pembeli. Kami mewajibkan penyedia layanan memproses informasi hanya untuk tujuan yang disetujui terkait layanan.", "เนื่องจากบริการนี้ออกแบบมาสำหรับการใช้งานในหลายตลาดเอเชียตะวันออกเฉียงใต้ ข้อมูลอาจถูกประมวลผลในเขตอำนาจที่ต่างจากที่อยู่ของผู้ใช้หรือผู้ซื้อ เรากำหนดให้ผู้ให้บริการประมวลผลข้อมูลเฉพาะเพื่อวัตถุประสงค์ที่ได้รับอนุมัติซึ่งเกี่ยวข้องกับบริการเท่านั้น")
          },
          {
            title: legalLocale("Your rights and requests", "你的权利与请求", "Hak dan permintaan anda", "Hak dan permintaan Anda", "สิทธิและคำขอของคุณ"),
            body: legalLocale(`You can contact ${PRIVACY_EMAIL} to request access, correction, deletion, or other privacy-related help. We may need to verify your identity, confirm your workspace role, and retain limited information where the law, fraud prevention, or billing obligations require it.`, `你可以通过 ${PRIVACY_EMAIL} 联系我们，申请访问、更正、删除或其他隐私相关帮助。必要时我们会先核验身份、确认你的工作空间角色，并在法律、反欺诈或计费义务要求的范围内保留少量信息。`, `Anda boleh menghubungi ${PRIVACY_EMAIL} untuk meminta akses, pembetulan, pemadaman, atau bantuan privasi lain. Kami mungkin perlu mengesahkan identiti anda, mengesahkan peranan anda dalam ruang kerja, dan mengekalkan maklumat terhad apabila dikehendaki oleh undang-undang, pencegahan penipuan, atau kewajipan bil.`, `Anda dapat menghubungi ${PRIVACY_EMAIL} untuk meminta akses, koreksi, penghapusan, atau bantuan terkait privasi lainnya. Kami mungkin perlu memverifikasi identitas Anda, memastikan peran Anda di workspace, dan menyimpan informasi terbatas bila diwajibkan oleh hukum, pencegahan penipuan, atau kewajiban penagihan.`, `คุณสามารถติดต่อ ${PRIVACY_EMAIL} เพื่อขอเข้าถึง แก้ไข ลบ หรือขอความช่วยเหลือด้านความเป็นส่วนตัวอื่น ๆ ได้ เราอาจต้องยืนยันตัวตนของคุณ ยืนยันบทบาทของคุณในเวิร์กสเปซ และเก็บข้อมูลบางส่วนไว้เท่าที่กฎหมาย การป้องกันการทุจริต หรือข้อผูกพันด้านการเรียกเก็บเงินกำหนด`),
            bullets: [
              legalLocale("Please include your workspace name and registered email when making a request.", "发起请求时，请附上你的工作空间名称和注册邮箱。", "Sila sertakan nama ruang kerja dan e-mel berdaftar anda semasa membuat permintaan.", "Harap sertakan nama workspace dan email terdaftar Anda saat mengajukan permintaan.", "โปรดระบุชื่อเวิร์กสเปซและอีเมลที่ลงทะเบียนไว้เมื่อส่งคำขอ"),
              legalLocale("We may refuse or limit a request where required to protect other users, security, or legal compliance.", "如果为保护其他用户、安全或法律合规所必需，我们可能会拒绝或限制某些请求。", "Kami mungkin menolak atau mengehadkan permintaan jika perlu untuk melindungi pengguna lain, keselamatan, atau pematuhan undang-undang.", "Kami dapat menolak atau membatasi permintaan jika diperlukan untuk melindungi pengguna lain, keamanan, atau kepatuhan hukum.", "เราอาจปฏิเสธหรือจำกัดคำขอบางอย่างหากจำเป็นเพื่อคุ้มครองผู้ใช้อื่น ความปลอดภัย หรือการปฏิบัติตามกฎหมาย")
            ]
          },
          {
            title: legalLocale("Children and sensitive information", "未成年人和敏感信息", "Kanak-kanak dan maklumat sensitif", "Anak-anak dan informasi sensitif", "เด็กและข้อมูลอ่อนไหว"),
            body: legalLocale("The service is designed for business users, not for children. Please avoid uploading unnecessary sensitive personal information, especially where a drafting or compliance review can be completed without it.", "本服务面向业务用户，而非未成年人。若某项起草或合规复核并不需要敏感个人信息，请避免额外上传这些内容。", "Perkhidmatan ini direka untuk pengguna perniagaan, bukan untuk kanak-kanak. Elakkan memuat naik maklumat peribadi sensitif yang tidak perlu, terutamanya jika proses penggubalan atau semakan pematuhan boleh diselesaikan tanpanya.", "Layanan ini dirancang untuk pengguna bisnis, bukan untuk anak-anak. Hindari mengunggah informasi pribadi sensitif yang tidak diperlukan, terutama bila penyusunan atau review kepatuhan dapat dilakukan tanpanya.", "บริการนี้ออกแบบมาสำหรับผู้ใช้เชิงธุรกิจ ไม่ได้ออกแบบมาสำหรับเด็ก โปรดหลีกเลี่ยงการอัปโหลดข้อมูลส่วนบุคคลที่อ่อนไหวโดยไม่จำเป็น โดยเฉพาะเมื่อการร่างหรือการตรวจสอบความสอดคล้องสามารถทำได้โดยไม่ต้องใช้ข้อมูลดังกล่าว")
          },
          {
            title: legalLocale("Changes to this policy", "本政策的更新", "Perubahan pada dasar ini", "Perubahan pada kebijakan ini", "การเปลี่ยนแปลงนโยบายนี้"),
            body: legalLocale("We may update this policy as the product, providers, or legal requirements evolve. Material updates will be published on this page with a new effective date.", "随着产品、服务商或法律要求的变化，我们可能更新本政策。重大更新会在本页发布，并附带新的生效日期。", "Kami mungkin mengemas kini dasar ini apabila produk, penyedia, atau keperluan undang-undang berubah. Kemas kini penting akan diterbitkan pada halaman ini bersama tarikh kuat kuasa baharu.", "Kami dapat memperbarui kebijakan ini seiring perubahan produk, penyedia, atau persyaratan hukum. Pembaruan material akan dipublikasikan di halaman ini dengan tanggal berlaku yang baru.", "เราอาจปรับปรุงนโยบายนี้เมื่อผลิตภัณฑ์ ผู้ให้บริการ หรือข้อกำหนดทางกฎหมายเปลี่ยนแปลง การเปลี่ยนแปลงสำคัญจะเผยแพร่ในหน้านี้พร้อมวันที่มีผลฉบับใหม่")
          }
        ],
        legalLocale(`Privacy requests and data-rights questions can be sent to ${PRIVACY_EMAIL}. Material updates to this policy will be posted here with a new effective date.`, `如有隐私请求或数据权利问题，请发送至 ${PRIVACY_EMAIL}。本政策如有重大更新，会在此页发布新的生效日期。`, `Permintaan privasi dan soalan hak data boleh dihantar ke ${PRIVACY_EMAIL}. Kemas kini penting pada dasar ini akan diterbitkan di sini bersama tarikh kuat kuasa baharu.`, `Permintaan privasi dan pertanyaan hak data dapat dikirim ke ${PRIVACY_EMAIL}. Perubahan material pada kebijakan ini akan dipublikasikan di halaman ini dengan tanggal berlaku yang baru.`, `คำขอด้านความเป็นส่วนตัวและคำถามเรื่องสิทธิข้อมูลสามารถส่งมาที่ ${PRIVACY_EMAIL} ได้ การเปลี่ยนแปลงสำคัญของนโยบายนี้จะเผยแพร่ในหน้านี้พร้อมวันที่มีผลฉบับใหม่`)
      );

    const renderTermsView = () =>
      renderStaticPage(
        legalLocale("Legal", "服务条款", "Terma", "Ketentuan", "ข้อกำหนด"),
        legalLocale("Terms of Service", "服务条款", "Terma Perkhidmatan", "Ketentuan Layanan", "ข้อกำหนดการใช้บริการ"),
        legalLocale(
          "These terms govern access to the service, workspace ownership, permitted use, and how paid plans, refunds, and service limitations are handled.",
          "本条款约束你对本服务的访问、工作空间归属、允许用途，以及付费方案、退款和服务限制的处理方式。",
          "Terma ini mengawal akses kepada perkhidmatan, pemilikan ruang kerja, penggunaan yang dibenarkan, serta cara pelan berbayar, bayaran balik, dan had perkhidmatan dikendalikan.",
          "Ketentuan ini mengatur akses ke layanan, kepemilikan workspace, penggunaan yang diizinkan, serta bagaimana paket berbayar, refund, dan batasan layanan ditangani.",
          "ข้อกำหนดนี้ควบคุมการเข้าถึงบริการ ความเป็นเจ้าของเวิร์กสเปซ การใช้งานที่อนุญาต และวิธีจัดการแพ็กเกจแบบชำระเงิน การคืนเงิน และข้อจำกัดของบริการ"
        ),
        [
          legalLocale("Self-serve SaaS terms", "自助 SaaS 条款", "Terma SaaS layan diri", "Ketentuan SaaS self-serve", "ข้อกำหนด SaaS แบบบริการตนเอง"),
          legalLocale(`Support contact: ${SUPPORT_EMAIL}`, `支持联系：${SUPPORT_EMAIL}`, `Hubungan sokongan: ${SUPPORT_EMAIL}`, `Kontak dukungan: ${SUPPORT_EMAIL}`, `ติดต่อฝ่ายสนับสนุน: ${SUPPORT_EMAIL}`)
        ],
        [
          {
            label: legalLocale("Service scope", "服务范围", "Skop perkhidmatan", "Cakupan layanan", "ขอบเขตบริการ"),
            value: legalLocale("Drafting + review", "起草 + 复核", "Draf + semakan", "Penyusunan + review", "ร่าง + ตรวจทาน"),
            body: legalLocale("The service supports invoice drafting, rule checks, previews, billing, and records. It does not file taxes for you.", "服务支持发票起草、规则检查、预览、计费和记录留存，但不代你向税局申报。", "Perkhidmatan ini menyokong penggubalan invois, semakan peraturan, pratonton, bil, dan rekod. Ia tidak memfailkan cukai bagi pihak anda.", "Layanan ini mendukung penyusunan invoice, pemeriksaan aturan, pratinjau, tagihan, dan pencatatan. Layanan ini tidak melaporkan pajak untuk Anda.", "บริการรองรับการร่างเอกสาร การตรวจสอบกฎ พรีวิว การเรียกเก็บเงิน และบันทึกต่าง ๆ แต่ไม่ได้ยื่นภาษีแทนคุณ")
          },
          {
            label: legalLocale("Paid plans", "付费方案", "Pelan berbayar", "Paket berbayar", "แพ็กเกจแบบชำระเงิน"),
            value: legalLocale("Monthly", "月付", "Bulanan", "Bulanan", "รายเดือน"),
            body: legalLocale("Paid plans renew monthly until canceled and remain subject to plan quotas.", "付费方案按月续费，直到取消，并持续受套餐配额限制。", "Pelan berbayar diperbaharui setiap bulan sehingga dibatalkan dan kekal tertakluk kepada kuota pelan.", "Paket berbayar diperpanjang tiap bulan sampai dibatalkan dan tetap tunduk pada kuota paket.", "แพ็กเกจแบบชำระเงินจะต่ออายุรายเดือนจนกว่าจะยกเลิก และยังคงอยู่ภายใต้โควตาของแพ็กเกจ"),
          },
          {
            label: legalLocale("Checkout provider", "结账提供方", "Penyedia checkout", "Penyedia checkout", "ผู้ให้บริการชำระเงิน"),
            value: "Paddle",
            body: legalLocale("Card checkout and refunds for self-serve plans are handled through Paddle.", "自助方案的卡支付与退款通过 Paddle 完成。", "Checkout kad dan bayaran balik untuk pelan layan diri dikendalikan melalui Paddle.", "Checkout kartu dan refund untuk paket self-serve ditangani melalui Paddle.", "การชำระด้วยบัตรและการคืนเงินสำหรับแพ็กเกจแบบบริการตนเองดำเนินการผ่าน Paddle")
          }
        ],
        [
          {
            title: legalLocale("Acceptance of the service", "对服务的接受", "Penerimaan perkhidmatan", "Penerimaan layanan", "การยอมรับบริการ"),
            body: legalLocale("By accessing the website, creating an account, opening a workspace, or purchasing a paid plan, you agree to these Terms of Service. If you use the service on behalf of a company or team, you confirm that you have authority to bind that organization to these terms.", "只要你访问网站、创建账户、开通工作空间或购买付费方案，就表示你同意本服务条款。如果你代表公司或团队使用本服务，即表示你确认自己有权让该组织受这些条款约束。", "Dengan mengakses laman web, mencipta akaun, membuka ruang kerja, atau membeli pelan berbayar, anda bersetuju dengan Terma Perkhidmatan ini. Jika anda menggunakan perkhidmatan bagi pihak syarikat atau pasukan, anda mengesahkan bahawa anda mempunyai kuasa untuk mengikat organisasi tersebut kepada terma ini.", "Dengan mengakses situs web, membuat akun, membuka workspace, atau membeli paket berbayar, Anda menyetujui Ketentuan Layanan ini. Jika Anda menggunakan layanan atas nama perusahaan atau tim, Anda menegaskan bahwa Anda berwenang untuk mengikat organisasi tersebut pada ketentuan ini.", "เมื่อคุณเข้าถึงเว็บไซต์ สร้างบัญชี เปิดเวิร์กสเปซ หรือซื้อแพ็กเกจแบบชำระเงิน ถือว่าคุณยอมรับข้อกำหนดการใช้บริการนี้ หากคุณใช้บริการในนามบริษัทหรือทีม คุณยืนยันว่าคุณมีอำนาจผูกพันองค์กรนั้นตามข้อกำหนดนี้")
          },
          {
            title: legalLocale("Accounts and workspaces", "账户与工作空间", "Akaun dan ruang kerja", "Akun dan workspace", "บัญชีและเวิร์กสเปซ"),
            body: legalLocale("Users are responsible for protecting their credentials and for activity under their accounts. Workspace owners control the workspace, its members, its data, and its billing choices. You are responsible for the legality and accuracy of the content you enter, upload, or share through the service.", "用户应自行保护账户凭证并对账户下的活动负责。工作空间所有者控制该工作空间及其成员、数据和计费选择。你应对自己在服务中录入、上传或分享内容的合法性和准确性负责。", "Pengguna bertanggungjawab melindungi kelayakan mereka dan aktiviti di bawah akaun mereka. Pemilik ruang kerja mengawal ruang kerja, ahlinya, datanya, dan pilihan bilnya. Anda bertanggungjawab atas kesahan dan ketepatan kandungan yang anda masukkan, muat naik, atau kongsi melalui perkhidmatan.", "Pengguna bertanggung jawab menjaga kredensial mereka dan seluruh aktivitas di bawah akun mereka. Pemilik workspace mengendalikan workspace, anggota, data, dan pilihan penagihannya. Anda bertanggung jawab atas legalitas dan keakuratan konten yang Anda masukkan, unggah, atau bagikan melalui layanan.", "ผู้ใช้มีหน้าที่ดูแลข้อมูลรับรองของตนและรับผิดชอบต่อกิจกรรมภายใต้บัญชีของตน เจ้าของเวิร์กสเปซเป็นผู้ควบคุมเวิร์กสเปซ สมาชิก ข้อมูล และทางเลือกด้านการเรียกเก็บเงิน คุณต้องรับผิดชอบต่อความถูกต้องและความชอบด้วยกฎหมายของเนื้อหาที่คุณกรอก อัปโหลด หรือแชร์ผ่านบริการ"),
            bullets: [
              legalLocale("Do not share credentials with unauthorized people.", "请勿把账户凭证分享给未获授权的人。", "Jangan kongsi kelayakan dengan orang yang tidak dibenarkan.", "Jangan membagikan kredensial kepada orang yang tidak berwenang.", "อย่าแบ่งปันข้อมูลรับรองให้กับผู้ที่ไม่ได้รับอนุญาต"),
              legalLocale("Keep workspace membership current and remove access when people no longer need it.", "请及时维护工作空间成员关系，在不再需要时移除访问权限。", "Pastikan keahlian ruang kerja sentiasa dikemas kini dan alih keluar akses apabila seseorang tidak lagi memerlukannya.", "Pastikan keanggotaan workspace selalu mutakhir dan cabut akses ketika seseorang tidak lagi membutuhkannya.", "ดูแลสมาชิกในเวิร์กสเปซให้เป็นปัจจุบัน และยกเลิกสิทธิ์เมื่อไม่จำเป็นต้องใช้งานแล้ว")
            ]
          },
          {
            title: legalLocale("Permitted and prohibited use", "允许用途与禁止行为", "Penggunaan dibenarkan dan dilarang", "Penggunaan yang diizinkan dan dilarang", "การใช้งานที่อนุญาตและต้องห้าม"),
            body: legalLocale("You may use the service only for lawful business operations related to invoice drafting, review, and associated commercial workflows. You must not use it to process prohibited content, abuse payment systems, interfere with the service, misrepresent your business, or violate applicable laws.", "你只能将本服务用于与发票起草、复核及相关商业流程有关的合法业务运营。不得利用本服务处理被禁止的内容、滥用支付系统、干扰服务运行、虚假陈述你的业务，或违反适用法律。", "Anda hanya boleh menggunakan perkhidmatan ini untuk operasi perniagaan yang sah berkaitan dengan penggubalan invois, semakan, dan aliran kerja komersial yang berkaitan. Anda tidak boleh menggunakannya untuk memproses kandungan terlarang, menyalahgunakan sistem pembayaran, mengganggu perkhidmatan, memalsukan perniagaan anda, atau melanggar undang-undang yang berkenaan.", "Anda hanya boleh menggunakan layanan ini untuk operasi bisnis yang sah terkait penyusunan invoice, review, dan alur kerja komersial terkait. Anda tidak boleh menggunakannya untuk memproses konten terlarang, menyalahgunakan sistem pembayaran, mengganggu layanan, memalsukan bisnis Anda, atau melanggar hukum yang berlaku.", "คุณอาจใช้บริการนี้ได้เฉพาะเพื่อการดำเนินธุรกิจที่ชอบด้วยกฎหมายซึ่งเกี่ยวข้องกับการร่างเอกสาร การตรวจทาน และเวิร์กโฟลว์เชิงพาณิชย์ที่เกี่ยวข้องเท่านั้น ห้ามใช้เพื่อประมวลผลเนื้อหาต้องห้าม ใช้ระบบชำระเงินในทางที่ผิด รบกวนบริการ แสดงข้อมูลธุรกิจอันเป็นเท็จ หรือฝ่าฝืนกฎหมายที่ใช้บังคับ")
          },
          {
            title: legalLocale("Service scope and limitations", "服务范围与限制", "Skop dan had perkhidmatan", "Cakupan dan batasan layanan", "ขอบเขตและข้อจำกัดของบริการ"),
            body: legalLocale("The service is an operational product for invoice drafting, checks, previews, workspace records, and billing administration. It is not a tax filing service, a government reporting agent, or a substitute for legal, tax, or accounting advice. You remain responsible for the decisions you make using the product outputs.", "本服务是用于发票起草、检查、预览、工作空间记录和账单管理的业务工具。它不是税务申报服务，也不是政府报送代理，更不能替代法律、税务或会计专业意见。你仍需对基于本产品输出所做出的决策负责。", "Perkhidmatan ini ialah produk operasi untuk penggubalan invois, semakan, pratonton, rekod ruang kerja, dan pentadbiran bil. Ia bukan perkhidmatan pemfailan cukai, ejen pelaporan kerajaan, atau pengganti nasihat undang-undang, cukai, atau perakaunan. Anda kekal bertanggungjawab terhadap keputusan yang anda buat menggunakan output produk.", "Layanan ini adalah produk operasional untuk penyusunan invoice, pemeriksaan, pratinjau, catatan workspace, dan administrasi tagihan. Layanan ini bukan layanan pelaporan pajak, agen pelaporan pemerintah, atau pengganti nasihat hukum, pajak, maupun akuntansi. Anda tetap bertanggung jawab atas keputusan yang dibuat berdasarkan output produk.", "บริการนี้เป็นผลิตภัณฑ์เชิงปฏิบัติการสำหรับการร่างเอกสาร การตรวจสอบ พรีวิว บันทึกของเวิร์กสเปซ และการบริหารการเรียกเก็บเงิน ไม่ใช่บริการยื่นภาษี ตัวแทนรายงานต่อรัฐ หรือสิ่งทดแทนคำปรึกษาด้านกฎหมาย ภาษี หรือบัญชี คุณยังคงต้องรับผิดชอบต่อการตัดสินใจที่ทำโดยอาศัยผลลัพธ์จากผลิตภัณฑ์")
          },
          {
            title: legalLocale("Fees, renewals, and refunds", "费用、续费与退款", "Yuran, pembaharuan, dan bayaran balik", "Biaya, perpanjangan, dan refund", "ค่าธรรมเนียม การต่ออายุ และการคืนเงิน"),
            body: legalLocale("Paid plans are charged in advance for each billing period. Renewals continue until canceled. Self-serve checkout, buyer receipts, and refunds may be handled through Paddle as the checkout provider and merchant of record. Refund handling follows the published refund policy.", "付费方案按计费周期预先收费，直到取消前会持续续费。自助结账、买家收据和退款可能由 Paddle 作为结账提供方和 merchant of record 处理。退款处理遵循公开的退款说明。", "Pelan berbayar dicaj lebih awal untuk setiap tempoh bil. Pembaharuan berterusan sehingga dibatalkan. Checkout layan diri, resit pembeli, dan bayaran balik mungkin dikendalikan melalui Paddle sebagai penyedia checkout dan merchant of record. Pengendalian bayaran balik tertakluk kepada polisi bayaran balik yang diterbitkan.", "Paket berbayar ditagih di muka untuk setiap periode tagihan. Perpanjangan berlanjut sampai dibatalkan. Checkout mandiri, kuitansi pembeli, dan refund dapat ditangani melalui Paddle sebagai penyedia checkout dan merchant of record. Penanganan refund mengikuti kebijakan refund yang dipublikasikan.", "แพ็กเกจแบบชำระเงินจะถูกเรียกเก็บล่วงหน้าสำหรับแต่ละรอบบิล การต่ออายุจะดำเนินต่อไปจนกว่าจะยกเลิก การชำระเงินด้วยตนเอง ใบเสร็จผู้ซื้อ และการคืนเงินอาจจัดการผ่าน Paddle ในฐานะผู้ให้บริการ checkout และ merchant of record การคืนเงินเป็นไปตามนโยบายคืนเงินที่เผยแพร่ไว้"),
            bullets: [
              legalLocale("Workspace limits are enforced based on plan entitlements and real usage records.", "工作空间限额会根据套餐权益和真实用量记录执行。", "Had ruang kerja dikuatkuasakan berdasarkan hak pelan dan rekod penggunaan sebenar.", "Batas workspace ditegakkan berdasarkan hak paket dan catatan penggunaan nyata.", "โควตาเวิร์กสเปซจะบังคับใช้ตามสิทธิ์ของแพ็กเกจและบันทึกการใช้งานจริง"),
              legalLocale("If payment fails or a refund is issued, paid access may be suspended, canceled, or downgraded.", "如果续费失败或发生退款，付费权益可能被暂停、取消或降级。", "Jika pembayaran gagal atau bayaran balik dikeluarkan, akses berbayar mungkin digantung, dibatalkan, atau diturunkan taraf.", "Jika pembayaran gagal atau refund diterbitkan, akses berbayar dapat ditangguhkan, dibatalkan, atau diturunkan.", "หากการชำระเงินล้มเหลวหรือมีการคืนเงิน สิทธิ์แบบชำระเงินอาจถูกระงับ ยกเลิก หรือปรับลดระดับ")
            ]
          },
          {
            title: legalLocale("Third-party services", "第三方服务", "Perkhidmatan pihak ketiga", "Layanan pihak ketiga", "บริการของบุคคลที่สาม"),
            body: legalLocale("The service may depend on third-party infrastructure and providers, including hosting, OCR, AI, email, and payment services. We are not responsible for third-party outages, policy decisions, or failures outside our reasonable control, though we will try to communicate major issues transparently.", "本服务可能依赖第三方基础设施与服务商，包括托管、OCR、AI、邮件和支付服务。对于超出我们合理控制范围之外的第三方故障、政策变化或中断，我们不承担责任，但会尽量透明沟通重大影响。", "Perkhidmatan ini mungkin bergantung pada infrastruktur dan penyedia pihak ketiga, termasuk pengehosan, OCR, AI, e-mel, dan perkhidmatan pembayaran. Kami tidak bertanggungjawab atas gangguan pihak ketiga, keputusan polisi, atau kegagalan di luar kawalan munasabah kami, walaupun kami akan cuba memaklumkan isu besar secara telus.", "Layanan ini dapat bergantung pada infrastruktur dan penyedia pihak ketiga, termasuk hosting, OCR, AI, email, dan layanan pembayaran. Kami tidak bertanggung jawab atas gangguan pihak ketiga, keputusan kebijakan, atau kegagalan di luar kendali wajar kami, meskipun kami akan berupaya mengomunikasikan isu besar secara transparan.", "บริการอาจพึ่งพาโครงสร้างพื้นฐานและผู้ให้บริการภายนอก รวมถึงโฮสติ้ง OCR AI อีเมล และบริการชำระเงิน เราไม่รับผิดชอบต่อการหยุดชะงัก การตัดสินใจเชิงนโยบาย หรือความล้มเหลวของบุคคลที่สามที่อยู่นอกเหนือการควบคุมตามสมควรของเรา แม้ว่าเราจะพยายามสื่อสารประเด็นสำคัญอย่างโปร่งใส")
          },
          {
            title: legalLocale("Intellectual property and content", "知识产权与内容", "Harta intelek dan kandungan", "Kekayaan intelektual dan konten", "ทรัพย์สินทางปัญญาและเนื้อหา"),
            body: legalLocale("We keep our rights in the service, software, website design, and product materials. You keep your rights in the data and business content you submit, but you grant us the rights needed to host, process, display, and operate that content as part of the service.", "我们保留对本服务、软件、网站设计和产品材料的权利。你保留对自己提交的数据和业务内容的权利，但需要授予我们为托管、处理、展示和运行服务所必需的使用权。", "Kami mengekalkan hak kami terhadap perkhidmatan, perisian, reka bentuk laman web, dan bahan produk. Anda mengekalkan hak anda terhadap data dan kandungan perniagaan yang anda serahkan, tetapi anda memberi kami hak yang diperlukan untuk mengehos, memproses, memaparkan, dan mengendalikan kandungan tersebut sebagai sebahagian daripada perkhidmatan.", "Kami mempertahankan hak kami atas layanan, perangkat lunak, desain situs web, dan materi produk. Anda tetap memiliki hak atas data dan konten bisnis yang Anda kirimkan, tetapi Anda memberi kami hak yang diperlukan untuk meng-host, memproses, menampilkan, dan mengoperasikan konten tersebut sebagai bagian dari layanan.", "เรายังคงสิทธิ์ของเราในบริการ ซอฟต์แวร์ การออกแบบเว็บไซต์ และสื่อของผลิตภัณฑ์ คุณยังคงสิทธิ์ในข้อมูลและเนื้อหาทางธุรกิจที่คุณส่งเข้ามา แต่คุณมอบสิทธิ์ที่จำเป็นให้เราในการโฮสต์ ประมวลผล แสดงผล และดำเนินงานเนื้อหานั้นในฐานะส่วนหนึ่งของบริการ")
          },
          {
            title: legalLocale("Availability, suspension, and termination", "可用性、暂停与终止", "Ketersediaan, penggantungan, dan penamatan", "Ketersediaan, penangguhan, dan penghentian", "ความพร้อมใช้งาน การระงับ และการยุติ"),
            body: legalLocale("We may update, suspend, or restrict the service to protect users, comply with law, prevent abuse, or address operational risks. We may suspend or terminate access for material breach, non-payment, fraud concerns, or misuse that creates legal, security, or chargeback risk.", "为了保护用户、遵守法律、防止滥用或处理运营风险，我们可能更新、暂停或限制服务。若发生重大违约、未付款、欺诈风险，或造成法律、安全或拒付风险的滥用，我们可能暂停或终止访问。", "Kami mungkin mengemas kini, menggantung, atau mengehadkan perkhidmatan untuk melindungi pengguna, mematuhi undang-undang, mencegah penyalahgunaan, atau menangani risiko operasi. Kami mungkin menggantung atau menamatkan akses atas pelanggaran material, kegagalan pembayaran, kebimbangan penipuan, atau penyalahgunaan yang mewujudkan risiko undang-undang, keselamatan, atau chargeback.", "Kami dapat memperbarui, menangguhkan, atau membatasi layanan untuk melindungi pengguna, mematuhi hukum, mencegah penyalahgunaan, atau menangani risiko operasional. Kami dapat menangguhkan atau menghentikan akses karena pelanggaran material, gagal bayar, kekhawatiran fraud, atau penyalahgunaan yang menimbulkan risiko hukum, keamanan, atau chargeback.", "เราอาจอัปเดต ระงับ หรือจำกัดบริการเพื่อคุ้มครองผู้ใช้ ปฏิบัติตามกฎหมาย ป้องกันการใช้งานที่ไม่เหมาะสม หรือจัดการความเสี่ยงด้านปฏิบัติการ เราอาจระงับหรือยุติการเข้าถึงหากมีการละเมิดอย่างมีนัยสำคัญ ไม่ชำระเงิน มีความเสี่ยงด้านการทุจริต หรือมีการใช้งานที่ก่อให้เกิดความเสี่ยงทางกฎหมาย ความปลอดภัย หรือการเรียกเงินคืน")
          },
          {
            title: legalLocale("Disclaimers and liability", "免责声明与责任限制", "Penafian dan liabiliti", "Penafian dan tanggung jawab", "ข้อสงวนสิทธิ์และความรับผิด"),
            body: legalLocale("The service is provided on an as-available basis. To the maximum extent permitted by law, we disclaim warranties not expressly stated in these terms. Our total liability relating to the service is limited to the amount you paid for the affected paid plan period, except where the law does not allow such limitation.", "本服务按“现状可用”提供。在法律允许的最大范围内，我们不承担本条款中未明确写出的保证责任。除法律不允许限制的情形外，我们与本服务有关的总责任，以你为受影响付费周期实际支付的金额为限。", "Perkhidmatan disediakan atas dasar tersedia-seadanya. Setakat maksimum yang dibenarkan oleh undang-undang, kami menafikan jaminan yang tidak dinyatakan secara jelas dalam terma ini. Jumlah liabiliti kami berkaitan dengan perkhidmatan adalah terhad kepada amaun yang anda bayar untuk tempoh pelan berbayar yang terjejas, kecuali apabila undang-undang tidak membenarkan had sedemikian.", "Layanan disediakan berdasarkan ketersediaan. Sejauh diizinkan oleh hukum, kami menolak jaminan yang tidak dinyatakan secara tegas dalam ketentuan ini. Total tanggung jawab kami terkait layanan dibatasi pada jumlah yang Anda bayarkan untuk periode paket berbayar yang terdampak, kecuali jika hukum tidak mengizinkan pembatasan tersebut.", "บริการนี้ให้ตามสภาพที่พร้อมใช้งาน เท่าที่กฎหมายอนุญาตสูงสุด เราขอปฏิเสธการรับประกันใด ๆ ที่ไม่ได้ระบุไว้อย่างชัดแจ้งในข้อกำหนดนี้ ความรับผิดรวมของเราที่เกี่ยวกับบริการจำกัดอยู่ที่จำนวนเงินที่คุณชำระสำหรับรอบแพ็กเกจแบบชำระเงินที่ได้รับผลกระทบ เว้นแต่กฎหมายจะไม่อนุญาตให้จำกัดเช่นนั้น")
          },
          {
            title: legalLocale("Changes to these terms", "条款更新", "Perubahan kepada terma ini", "Perubahan pada ketentuan ini", "การเปลี่ยนแปลงข้อกำหนดนี้"),
            body: legalLocale("We may update these terms as the product, providers, or legal requirements change. Continued use after an update becomes effective means you accept the revised terms.", "随着产品、服务商或法律要求的变化，我们可能更新本条款。更新生效后继续使用本服务，即表示你接受修订后的条款。", "Kami mungkin mengemas kini terma ini apabila produk, penyedia, atau keperluan undang-undang berubah. Penggunaan berterusan selepas kemas kini berkuat kuasa bermakna anda menerima terma yang disemak.", "Kami dapat memperbarui ketentuan ini seiring perubahan produk, penyedia, atau persyaratan hukum. Penggunaan berkelanjutan setelah pembaruan berlaku berarti Anda menerima ketentuan yang direvisi.", "เราอาจปรับปรุงข้อกำหนดนี้เมื่อผลิตภัณฑ์ ผู้ให้บริการ หรือข้อกำหนดทางกฎหมายเปลี่ยนแปลง การใช้งานต่อหลังจากข้อกำหนดใหม่มีผลถือว่าคุณยอมรับข้อกำหนดฉบับแก้ไข")
          }
        ],
        legalLocale(`Questions about these terms, account access, or workspace obligations can be sent to ${SUPPORT_EMAIL}. For buyer-facing payment receipts and checkout records, the applicable Paddle checkout records also apply.`, `如对本条款、账户访问或工作空间责任有疑问，请联系 ${SUPPORT_EMAIL}。涉及买家付款收据和结账记录时，也应同时参考对应的 Paddle 结账记录。`, `Soalan tentang terma ini, akses akaun, atau tanggungjawab ruang kerja boleh dihantar ke ${SUPPORT_EMAIL}. Untuk resit pembayaran pembeli dan rekod checkout, rekod checkout Paddle yang berkaitan juga terpakai.`, `Pertanyaan tentang ketentuan ini, akses akun, atau kewajiban workspace dapat dikirim ke ${SUPPORT_EMAIL}. Untuk kuitansi pembayaran pembeli dan catatan checkout, catatan checkout Paddle yang relevan juga berlaku.`, `หากมีคำถามเกี่ยวกับข้อกำหนดนี้ การเข้าถึงบัญชี หรือหน้าที่ของเวิร์กสเปซ โปรดติดต่อ ${SUPPORT_EMAIL} ส่วนใบเสร็จสำหรับผู้ซื้อและบันทึกการชำระเงิน ให้ยึดตามบันทึก checkout ของ Paddle ที่เกี่ยวข้องด้วย`)
      );

    const renderRefundsView = () =>
      renderStaticPage(
        legalLocale("Refunds", "退款说明", "Bayaran balik", "Refund", "การคืนเงิน"),
        legalLocale("Refund Policy", "退款说明", "Polisi Bayaran Balik", "Kebijakan Refund", "นโยบายการคืนเงิน"),
        legalLocale(
          "This page explains how self-serve plan refunds are reviewed and processed. Approved refunds are returned to the original payment method through Paddle.",
          "本页说明自助方案的退款审核与处理方式。获批退款会通过 Paddle 退回到原支付方式。",
          "Halaman ini menerangkan cara bayaran balik untuk pelan layan diri disemak dan diproses. Bayaran balik yang diluluskan akan dikembalikan ke kaedah pembayaran asal melalui Paddle.",
          "Halaman ini menjelaskan bagaimana refund paket self-serve ditinjau dan diproses. Refund yang disetujui dikembalikan ke metode pembayaran asal melalui Paddle.",
          "หน้านี้อธิบายวิธีตรวจสอบและดำเนินการคืนเงินสำหรับแพ็กเกจแบบบริการตนเอง การคืนเงินที่ได้รับอนุมัติจะถูกส่งกลับไปยังวิธีชำระเงินเดิมผ่าน Paddle"
        ),
        [
          legalLocale(`Billing contact: ${BILLING_EMAIL}`, `计费联系：${BILLING_EMAIL}`, `Hubungan bil: ${BILLING_EMAIL}`, `Kontak tagihan: ${BILLING_EMAIL}`, `ติดต่อฝ่ายบิล: ${BILLING_EMAIL}`),
          legalLocale("Manual review before refund", "退款前人工审核", "Semakan manual sebelum bayaran balik", "Tinjauan manual sebelum refund", "ตรวจสอบโดยเจ้าหน้าที่ก่อนคืนเงิน")
        ],
        [
          {
            label: "Starter",
            value: legalLocale("No charge", "免费", "Tiada caj", "Gratis", "ไม่มีค่าใช้จ่าย"),
            body: legalLocale("The starter plan does not create a paid order, so there is no subscription fee to refund.", "Starter 不产生付费订单，因此没有订阅费用需要退款。", "Pelan starter tidak mewujudkan pesanan berbayar, jadi tiada yuran langganan untuk dipulangkan.", "Paket starter tidak membuat pesanan berbayar, jadi tidak ada biaya langganan untuk direfund.", "แผน Starter ไม่ก่อให้เกิดคำสั่งซื้อแบบชำระเงิน จึงไม่มีค่าบริการสมัครสมาชิกที่ต้องคืน")
          },
          {
            label: legalLocale("Paid plans", "付费方案", "Pelan berbayar", "Paket berbayar", "แพ็กเกจแบบชำระเงิน"),
            value: legalLocale("Monthly in advance", "按月预付", "Bulanan dibayar awal", "Bulanan dibayar di muka", "ชำระล่วงหน้ารายเดือน"),
            body: legalLocale("Growth and Scale are billed in advance for each monthly period.", "Growth 和 Scale 会按每个自然计费周期预先收取费用。", "Growth dan Scale dicaj lebih awal bagi setiap tempoh bulanan.", "Growth dan Scale ditagih di muka untuk setiap periode bulanan.", "Growth และ Scale จะเรียกเก็บล่วงหน้าสำหรับแต่ละรอบรายเดือน")
          },
          {
            label: legalLocale("Where refunds are sent", "退款去向", "Ke mana bayaran balik dihantar", "Refund dikirim ke mana", "คืนเงินไปที่ใด"),
            value: legalLocale("Original method", "原支付方式", "Kaedah asal", "Metode asal", "วิธีชำระเงินเดิม"),
            body: legalLocale("Approved refunds are returned to the original payment method. Timing depends on the payment provider and card issuer.", "获批退款会退回原支付方式，到账时间取决于支付服务商和发卡机构。", "Bayaran balik yang diluluskan akan dipulangkan ke kaedah pembayaran asal. Tempoh bergantung pada penyedia pembayaran dan pengeluar kad.", "Refund yang disetujui dikembalikan ke metode pembayaran asal. Waktunya bergantung pada penyedia pembayaran dan penerbit kartu.", "การคืนเงินที่อนุมัติจะส่งกลับไปยังวิธีชำระเงินเดิม ระยะเวลาขึ้นอยู่กับผู้ให้บริการชำระเงินและผู้ออกบัตร")
          }
        ],
        [
          {
            title: legalLocale("How this policy works", "这份退款政策如何运作", "Cara polisi ini berfungsi", "Cara kerja kebijakan ini", "นโยบายนี้ทำงานอย่างไร"),
            body: legalLocale("This policy applies to self-serve paid workspace plans purchased through the website checkout. Refunds are reviewed manually and, if approved, are returned through the original payment path handled by Paddle.", "本政策适用于通过网站自助结账购买的付费工作空间方案。退款会先由人工审核，获批后再通过 Paddle 沿原支付路径退回。", "Polisi ini terpakai kepada pelan ruang kerja berbayar layan diri yang dibeli melalui checkout laman web. Bayaran balik disemak secara manual dan, jika diluluskan, dipulangkan melalui laluan pembayaran asal yang dikendalikan oleh Paddle.", "Kebijakan ini berlaku untuk paket workspace berbayar self-serve yang dibeli melalui checkout situs web. Refund ditinjau secara manual dan, jika disetujui, dikembalikan melalui jalur pembayaran asal yang ditangani oleh Paddle.", "นโยบายนี้ใช้กับแพ็กเกจเวิร์กสเปซแบบชำระเงินที่ซื้อผ่านการชำระเงินบนเว็บไซต์ การคืนเงินจะได้รับการตรวจสอบโดยเจ้าหน้าที่ และหากอนุมัติ จะถูกส่งคืนผ่านช่องทางการชำระเงินเดิมที่ Paddle ดูแล")
          },
          {
            title: legalLocale("When a refund may be approved", "哪些情况可能获批退款", "Bila bayaran balik mungkin diluluskan", "Kapan refund dapat disetujui", "เมื่อใดที่อาจอนุมัติคืนเงิน"),
            body: legalLocale("We may approve a full refund for duplicate charges, accidental purchases, material service unavailability, obvious billing errors, or other situations where a refund is appropriate under applicable law or reasonable commercial judgment.", "对于重复扣费、误购、重大服务不可用、明显计费错误，或适用法律或合理商业判断认为适合退款的情况，我们可能批准全额退款。", "Kami mungkin meluluskan bayaran balik penuh untuk caj berganda, pembelian tidak sengaja, ketiadaan perkhidmatan yang material, kesilapan bil yang jelas, atau keadaan lain di mana bayaran balik wajar menurut undang-undang yang terpakai atau pertimbangan komersial yang munasabah.", "Kami dapat menyetujui refund penuh untuk tagihan ganda, pembelian tidak sengaja, layanan tidak tersedia secara material, kesalahan penagihan yang jelas, atau situasi lain di mana refund dianggap tepat menurut hukum yang berlaku atau pertimbangan komersial yang wajar.", "เราอาจอนุมัติการคืนเงินเต็มจำนวนในกรณีเรียกเก็บเงินซ้ำ ซื้อโดยไม่ได้ตั้งใจ บริการไม่พร้อมใช้งานอย่างมีนัยสำคัญ ความผิดพลาดในการเรียกเก็บเงินอย่างชัดเจน หรือสถานการณ์อื่นที่เหมาะสมต่อการคืนเงินภายใต้กฎหมายที่ใช้บังคับหรือดุลยพินิจทางการค้าที่สมเหตุสมผล"),
            bullets: [
              legalLocale("Requests made within 14 days of a first purchase or renewal are more likely to be reviewed positively, especially where the service was not materially used.", "在首次购买或续费后 14 天内提出的请求，若服务尚未被实质性使用，通常更容易获得积极审核。", "Permintaan yang dibuat dalam tempoh 14 hari selepas pembelian pertama atau pembaharuan lebih berkemungkinan disemak secara positif, terutamanya jika perkhidmatan belum digunakan secara material.", "Permintaan yang diajukan dalam 14 hari setelah pembelian pertama atau perpanjangan lebih mungkin ditinjau secara positif, terutama jika layanan belum digunakan secara material.", "คำขอที่ส่งภายใน 14 วันหลังการซื้อครั้งแรกหรือการต่ออายุมีแนวโน้มได้รับการพิจารณาในทางบวกมากกว่า โดยเฉพาะเมื่อบริการยังไม่ได้ถูกใช้งานอย่างมีนัยสำคัญ"),
              legalLocale("A refund can also be considered if your card was charged twice for the same plan period.", "如果同一个计费周期发生重复扣款，也可以申请退款。", "Bayaran balik juga boleh dipertimbangkan jika kad anda dicaj dua kali untuk tempoh pelan yang sama.", "Refund juga dapat dipertimbangkan jika kartu Anda ditagih dua kali untuk periode paket yang sama.", "อาจพิจารณาคืนเงินได้เช่นกัน หากบัตรของคุณถูกเรียกเก็บเงินซ้ำสองครั้งสำหรับรอบแพ็กเกจเดียวกัน")
            ]
          },
          {
            title: legalLocale("When a refund may not be approved", "哪些情况可能不予退款", "Bila bayaran balik mungkin tidak diluluskan", "Kapan refund mungkin tidak disetujui", "เมื่อใดที่อาจไม่อนุมัติคืนเงิน"),
            body: legalLocale("We may decline refunds for already-used service periods, abusive or fraudulent behavior, policy breaches, procurement-only change of mind after meaningful use, or requests submitted long after the charge date unless the law requires otherwise.", "对于已经实际使用过的服务周期、存在滥用或欺诈行为、违反政策、在已实际使用后仅因内部采购决定改变主意，或距离扣费时间较久的请求，我们可能不予退款，法律另有要求的除外。", "Kami mungkin menolak bayaran balik untuk tempoh perkhidmatan yang telah digunakan, tingkah laku menyalahguna atau menipu, pelanggaran polisi, perubahan fikiran atas sebab perolehan selepas penggunaan yang bermakna, atau permintaan yang dikemukakan lama selepas tarikh caj melainkan undang-undang menghendaki sebaliknya.", "Kami dapat menolak refund untuk periode layanan yang sudah digunakan, perilaku abusif atau curang, pelanggaran kebijakan, perubahan keputusan pengadaan setelah penggunaan yang bermakna, atau permintaan yang diajukan jauh setelah tanggal penagihan kecuali hukum mengharuskan sebaliknya.", "เราอาจปฏิเสธการคืนเงินสำหรับรอบบริการที่ถูกใช้งานไปแล้ว พฤติกรรมที่เป็นการใช้งานในทางที่ผิดหรือฉ้อโกง การละเมิดนโยบาย การเปลี่ยนใจด้านจัดซื้อหลังมีการใช้งานอย่างมีนัยสำคัญ หรือคำขอที่ยื่นหลังวันที่เรียกเก็บเงินเป็นเวลานาน เว้นแต่กฎหมายจะกำหนดไว้เป็นอย่างอื่น")
          },
          {
            title: legalLocale("How to request a refund", "如何申请退款", "Cara memohon bayaran balik", "Cara meminta refund", "วิธีขอคืนเงิน"),
            body: legalLocale(`Email ${BILLING_EMAIL} with the workspace name, order date, payment email, and the reason for your request. We may ask for additional details before issuing a decision.`, `请发送邮件至 ${BILLING_EMAIL}，并附上工作空间名称、订单日期、支付邮箱和退款原因。作出决定前，我们可能会要求补充信息。`, `E-melkan kepada ${BILLING_EMAIL} bersama nama ruang kerja, tarikh pesanan, e-mel pembayaran, dan sebab permintaan anda. Kami mungkin meminta butiran tambahan sebelum membuat keputusan.`, `Kirim email ke ${BILLING_EMAIL} dengan nama workspace, tanggal pesanan, email pembayaran, dan alasan permintaan Anda. Kami dapat meminta detail tambahan sebelum memberikan keputusan.`, `ส่งอีเมลไปที่ ${BILLING_EMAIL} พร้อมชื่อเวิร์กสเปซ วันที่สั่งซื้อ อีเมลที่ใช้ชำระเงิน และเหตุผลในการขอคืนเงิน เราอาจขอข้อมูลเพิ่มเติมก่อนตัดสินใจ`),
            bullets: [
              legalLocale("Include any transaction ID, invoice copy, or screenshot that helps us identify the charge.", "请尽量附上交易号、订单截图或其他有助于识别付款的材料。", "Sertakan sebarang ID transaksi, salinan invois, atau tangkap layar yang membantu kami mengenal pasti caj tersebut.", "Sertakan ID transaksi, salinan invoice, atau tangkapan layar yang membantu kami mengidentifikasi tagihan tersebut.", "โปรดแนบรหัสธุรกรรม สำเนาใบแจ้งหนี้ หรือภาพหน้าจอใด ๆ ที่ช่วยให้เราระบุรายการเรียกเก็บเงินได้"),
              legalLocale("Refund requests are normally answered within 2 business days.", "退款申请通常会在 2 个工作日内得到初步回复。", "Permintaan bayaran balik biasanya dijawab dalam tempoh 2 hari bekerja.", "Permintaan refund biasanya ditanggapi dalam 2 hari kerja.", "คำขอคืนเงินโดยปกติจะได้รับการตอบกลับภายใน 2 วันทำการ")
            ]
          },
          {
            title: legalLocale("Cancellation versus refund", "取消与退款的区别", "Pembatalan berbanding bayaran balik", "Pembatalan versus refund", "การยกเลิกเทียบกับการคืนเงิน"),
            body: legalLocale("Canceling a subscription stops future renewals but does not automatically reverse a charge that has already happened. A refund request is a separate review process.", "取消订阅只会阻止未来续费，并不会自动撤销已经发生的扣费。退款申请是另一条独立的审核流程。", "Membatalkan langganan menghentikan pembaharuan masa depan tetapi tidak membalikkan caj yang telah berlaku secara automatik. Permintaan bayaran balik ialah proses semakan yang berasingan.", "Membatalkan langganan menghentikan perpanjangan berikutnya tetapi tidak otomatis membalikkan tagihan yang sudah terjadi. Permintaan refund adalah proses peninjauan yang terpisah.", "การยกเลิกการสมัครจะหยุดการต่ออายุในอนาคต แต่จะไม่ย้อนการเรียกเก็บเงินที่เกิดขึ้นแล้วโดยอัตโนมัติ การขอคืนเงินเป็นกระบวนการตรวจสอบแยกต่างหาก")
          },
          {
            title: legalLocale("Effect of a refund", "退款后的影响", "Kesan bayaran balik", "Dampak refund", "ผลของการคืนเงิน"),
            body: legalLocale("If a paid order is fully refunded, the related paid entitlement may be canceled or downgraded to the free plan for that workspace.", "若付费订单被全额退款，与之关联的付费权益可能被取消，或该工作空间会被降回免费方案。", "Jika pesanan berbayar dipulangkan sepenuhnya, hak berbayar yang berkaitan mungkin dibatalkan atau diturunkan ke pelan percuma bagi ruang kerja tersebut.", "Jika pesanan berbayar direfund penuh, hak akses berbayar terkait dapat dibatalkan atau diturunkan ke paket gratis untuk workspace tersebut.", "หากคำสั่งซื้อแบบชำระเงินได้รับการคืนเงินเต็มจำนวน สิทธิ์แบบชำระเงินที่เกี่ยวข้องอาจถูกยกเลิกหรือปรับกลับเป็นแพ็กเกจฟรีสำหรับเวิร์กสเปซนั้น"),
            bullets: [
              legalLocale("Access to paid allowance may be removed immediately after the refund is finalized.", "退款完成后，付费额度可能会被立即移除。", "Akses kepada elaun berbayar mungkin dialih keluar sebaik sahaja bayaran balik dimuktamadkan.", "Akses ke kuota berbayar dapat dicabut segera setelah refund diselesaikan.", "สิทธิ์การใช้โควตาแบบชำระเงินอาจถูกนำออกทันทีหลังการคืนเงินเสร็จสิ้น"),
              legalLocale("Previously created records remain subject to retention and legal obligations.", "已生成的历史记录仍会受保留策略和法定义务约束。", "Rekod yang telah diwujudkan sebelum ini kekal tertakluk kepada dasar penyimpanan dan kewajipan undang-undang.", "Catatan yang telah dibuat sebelumnya tetap tunduk pada kebijakan retensi dan kewajiban hukum.", "บันทึกที่สร้างไว้ก่อนหน้านี้ยังคงอยู่ภายใต้นโยบายการเก็บรักษาและข้อผูกพันทางกฎหมาย")
            ]
          },
          {
            title: legalLocale("Chargebacks and misuse", "拒付与滥用", "Chargeback dan penyalahgunaan", "Chargeback dan penyalahgunaan", "การดึงเงินคืนและการใช้งานที่ไม่เหมาะสม"),
            body: legalLocale("If a payment is disputed through a chargeback instead of our refund process, we may pause the workspace while the dispute is investigated and may decline future self-serve purchases where abuse risk is high.", "如果付款不是通过我们的退款流程，而是直接走拒付争议，我们可能会在调查期间暂停该工作空间，并在滥用风险较高时拒绝未来的自助购买。", "Jika pembayaran dipertikaikan melalui chargeback dan bukannya proses bayaran balik kami, kami mungkin menjeda ruang kerja semasa pertikaian disiasat dan mungkin menolak pembelian layan diri pada masa hadapan apabila risiko penyalahgunaan tinggi.", "Jika pembayaran disengketakan melalui chargeback alih-alih proses refund kami, kami dapat menjeda workspace selama sengketa diselidiki dan dapat menolak pembelian self-serve di masa depan jika risiko penyalahgunaan tinggi.", "หากมีการโต้แย้งการชำระเงินผ่านกระบวนการดึงเงินคืนแทนขั้นตอนคืนเงินของเรา เราอาจหยุดเวิร์กสเปซชั่วคราวระหว่างการตรวจสอบข้อพิพาท และอาจปฏิเสธการซื้อแบบบริการตนเองในอนาคตหากมีความเสี่ยงด้านการใช้งานที่ไม่เหมาะสมสูง")
          }
        ],
        legalLocale(`For workspace-specific billing questions or refund requests, contact ${BILLING_EMAIL} before opening a chargeback so we can review the order and payment record with you.`, `如有与工作空间相关的账单问题或退款申请，请先联系 ${BILLING_EMAIL}，让我们先与你一起核对订单和支付记录，再决定是否进入拒付流程。`, `Untuk soalan bil khusus ruang kerja atau permintaan bayaran balik, hubungi ${BILLING_EMAIL} sebelum membuka chargeback supaya kami boleh menyemak pesanan dan rekod pembayaran bersama anda.`, `Untuk pertanyaan tagihan workspace atau permintaan refund, hubungi ${BILLING_EMAIL} sebelum membuka chargeback agar kami dapat meninjau pesanan dan catatan pembayaran bersama Anda.`, `หากมีคำถามเรื่องบิลของเวิร์กสเปซหรือคำขอคืนเงิน โปรดติดต่อ ${BILLING_EMAIL} ก่อนเปิดกระบวนการดึงเงินคืน เพื่อให้เราตรวจสอบคำสั่งซื้อและบันทึกการชำระเงินร่วมกับคุณได้ก่อน`)
      );

    const renderContactView = () =>
      h("div", { class: "page-stack" }, [
        renderPageMasthead(
          legalLocale("Contact", "联系方式", "Hubungi", "Kontak", "ติดต่อ"),
          legalLocale("Reach the team behind the workspace.", "联系这个产品背后的团队。", "Hubungi pasukan di sebalik ruang kerja ini.", "Hubungi tim di balik workspace ini.", "ติดต่อทีมที่อยู่เบื้องหลังเวิร์กสเปซนี้"),
          legalLocale(
            `Use ${SUPPORT_EMAIL} for product help, billing follow-up, refund requests, privacy requests, and legal notices. Include workspace, market, and order details where relevant.`,
            `请使用 ${SUPPORT_EMAIL} 处理产品问题、账单跟进、退款申请、隐私请求和法律通知；如有需要，请附上工作空间、市场和订单信息。`,
            `Gunakan ${SUPPORT_EMAIL} untuk bantuan produk, susulan bil, permintaan bayaran balik, permintaan privasi, dan notis undang-undang. Sertakan butiran ruang kerja, pasaran, dan pesanan apabila berkaitan.`,
            `Gunakan ${SUPPORT_EMAIL} untuk bantuan produk, tindak lanjut tagihan, permintaan refund, permintaan privasi, dan pemberitahuan hukum. Sertakan detail workspace, pasar, dan pesanan bila relevan.`,
            `ใช้ ${SUPPORT_EMAIL} สำหรับความช่วยเหลือด้านผลิตภัณฑ์ การติดตามเรื่องบิล คำขอคืนเงิน คำขอด้านความเป็นส่วนตัว และหนังสือแจ้งทางกฎหมาย พร้อมระบุข้อมูลเวิร์กสเปซ ตลาด และคำสั่งซื้อเมื่อเกี่ยวข้อง`
          ),
          [legalLocale(`Contact: ${SUPPORT_EMAIL}`, `联系邮箱：${SUPPORT_EMAIL}`, `Hubungi: ${SUPPORT_EMAIL}`, `Kontak: ${SUPPORT_EMAIL}`, `ติดต่อ: ${SUPPORT_EMAIL}`)],
          [
            {
              label: legalLocale("Response target", "响应目标", "Sasaran respons", "Target respons", "เป้าหมายการตอบกลับ"),
              value: legalLocale("2 business days", "2 个工作日", "2 hari bekerja", "2 hari kerja", "2 วันทำการ"),
              body: legalLocale("Urgent billing or account-security issues are handled first.", "涉及计费或账户安全的紧急问题会被优先处理。", "Isu bil atau keselamatan akaun yang mendesak akan diutamakan.", "Masalah tagihan atau keamanan akun yang mendesak akan diprioritaskan.", "ประเด็นเร่งด่วนด้านการเรียกเก็บเงินหรือความปลอดภัยของบัญชีจะได้รับการจัดการก่อน")
            },
            {
              label: legalLocale("Coverage", "覆盖范围", "Liputan", "Cakupan", "ขอบเขต"),
              value: "SG / MY / ID / TH",
              body: legalLocale("Please include the target market and workspace name when contacting us.", "联系时请附上目标市场和工作空间名称。", "Sila sertakan pasaran sasaran dan nama ruang kerja semasa menghubungi kami.", "Harap sertakan pasar tujuan dan nama workspace saat menghubungi kami.", "โปรดระบุตลาดเป้าหมายและชื่อเวิร์กสเปซเมื่อคุณติดต่อเรา")
            },
            {
              label: legalLocale("Checkout seller", "结账卖家", "Penjual checkout", "Penjual checkout", "ผู้ขายในขั้นตอนชำระเงิน"),
              value: "Paddle",
              body: legalLocale("Self-serve checkout receipts may be issued by Paddle as the merchant of record.", "自助结账交易的收据可能由 Paddle 作为 merchant of record 开具。", "Resit checkout layan diri mungkin dikeluarkan oleh Paddle sebagai merchant of record.", "Kuitansi checkout self-serve dapat diterbitkan oleh Paddle sebagai merchant of record.", "ใบเสร็จจากการชำระเงินแบบบริการตนเองอาจออกโดย Paddle ในฐานะ merchant of record")
            }
          ]
        ),
        h("section", { class: "panel static-page-panel" }, [
          h("div", { class: "contact-grid" }, [
            h("article", { class: "policy-card contact-card" }, [
              h("span", { class: "field-label" }, legalLocale("Product support", "产品支持", "Sokongan produk", "Dukungan produk", "ฝ่ายสนับสนุนผลิตภัณฑ์")),
              h("strong", SUPPORT_EMAIL),
              h("p", { class: "muted-text" }, legalLocale("Questions about drafting, checks, previews, and workspace access.", "处理与草稿、检查、预览和工作空间访问有关的问题。", "Pertanyaan tentang penggubalan, semakan, pratonton, dan akses ruang kerja.", "Pertanyaan tentang penyusunan, pemeriksaan, pratinjau, dan akses workspace.", "คำถามเกี่ยวกับการร่าง การตรวจสอบ พรีวิว และการเข้าถึงเวิร์กสเปซ"))
            ]),
            h("article", { class: "policy-card contact-card" }, [
              h("span", { class: "field-label" }, legalLocale("Billing and refunds", "计费与退款", "Bil dan bayaran balik", "Tagihan dan refund", "การเรียกเก็บเงินและการคืนเงิน")),
              h("strong", BILLING_EMAIL),
              h("p", { class: "muted-text" }, legalLocale("Plan changes, checkout issues, invoices, and refund requests.", "处理套餐变更、结账问题、订单发票和退款申请。", "Perubahan pelan, isu checkout, invois, dan permintaan bayaran balik.", "Perubahan paket, masalah checkout, invoice, dan permintaan refund.", "การเปลี่ยนแพ็กเกจ ปัญหา checkout ใบแจ้งหนี้ และคำขอคืนเงิน"))
            ]),
            h("article", { class: "policy-card contact-card" }, [
              h("span", { class: "field-label" }, legalLocale("Privacy and legal", "隐私与法务", "Privasi dan undang-undang", "Privasi dan hukum", "ความเป็นส่วนตัวและกฎหมาย")),
              h("strong", PRIVACY_EMAIL),
              h("p", { class: "muted-text" }, legalLocale("Personal-data requests, retention questions, and legal notices.", "处理个人数据请求、保留问题和法律通知。", "Permintaan data peribadi, soalan penyimpanan, dan notis undang-undang.", "Permintaan data pribadi, pertanyaan retensi, dan pemberitahuan hukum.", "คำขอข้อมูลส่วนบุคคล คำถามเรื่องการเก็บรักษา และหนังสือแจ้งทางกฎหมาย"))
            ])
          ]),
          renderStaticSections([
            {
              title: legalLocale("How to reach us", "如何联系我们", "Cara menghubungi kami", "Cara menghubungi kami", "วิธีติดต่อเรา"),
              body: legalLocale(`Email is the main contact channel for this product. Use ${SUPPORT_EMAIL} for product questions, billing follow-up, refund requests, privacy requests, and legal notices.`, `邮箱是本产品的主要联系渠道。产品问题、账单跟进、退款申请、隐私请求和法律通知都可以发送至 ${SUPPORT_EMAIL}。`, `E-mel ialah saluran hubungan utama untuk produk ini. Gunakan ${SUPPORT_EMAIL} untuk soalan produk, susulan bil, permintaan bayaran balik, permintaan privasi, dan notis undang-undang.`, `Email adalah jalur kontak utama untuk produk ini. Gunakan ${SUPPORT_EMAIL} untuk pertanyaan produk, tindak lanjut tagihan, permintaan refund, permintaan privasi, dan pemberitahuan hukum.`, `อีเมลคือช่องทางติดต่อหลักของผลิตภัณฑ์นี้ ใช้ ${SUPPORT_EMAIL} สำหรับคำถามเกี่ยวกับผลิตภัณฑ์ การติดตามเรื่องบิล คำขอคืนเงิน คำขอด้านความเป็นส่วนตัว และหนังสือแจ้งทางกฎหมาย`),
              bullets: [
                legalLocale("For account or workspace issues, include the workspace name and the email used to sign in.", "涉及账户或工作空间问题时，请附上工作空间名称和登录邮箱。", "Untuk isu akaun atau ruang kerja, sertakan nama ruang kerja dan e-mel log masuk.", "Untuk masalah akun atau workspace, sertakan nama workspace dan email yang digunakan untuk masuk.", "หากเป็นปัญหาเกี่ยวกับบัญชีหรือเวิร์กสเปซ โปรดระบุชื่อเวิร์กสเปซและอีเมลที่ใช้เข้าสู่ระบบ"),
                legalLocale("For billing or refund requests, include the order date, payment email, and any transaction reference.", "涉及计费或退款时，请附上订单日期、支付邮箱和交易参考号。", "Untuk bil atau bayaran balik, sertakan tarikh pesanan, e-mel pembayaran, dan sebarang rujukan transaksi.", "Untuk tagihan atau refund, sertakan tanggal pesanan, email pembayaran, dan referensi transaksi yang tersedia.", "หากเป็นเรื่องบิลหรือการคืนเงิน โปรดระบุวันที่สั่งซื้อ อีเมลที่ใช้ชำระเงิน และเลขอ้างอิงธุรกรรม"),
                legalLocale("For privacy or legal requests, include enough identity or authority detail for us to verify the request safely.", "涉及隐私或法务请求时，请附上足以安全核验身份或授权的信息。", "Untuk permintaan privasi atau undang-undang, sertakan maklumat identiti atau kuasa yang mencukupi untuk pengesahan yang selamat.", "Untuk permintaan privasi atau hukum, sertakan detail identitas atau otorisasi yang cukup agar kami dapat memverifikasi permintaan dengan aman.", "หากเป็นคำขอด้านความเป็นส่วนตัวหรือกฎหมาย โปรดให้ข้อมูลยืนยันตัวตนหรืออำนาจที่เพียงพอเพื่อให้เราตรวจสอบได้อย่างปลอดภัย")
              ]
            },
            {
              title: legalLocale("What to include in your message", "来信建议附带的信息", "Apa yang perlu disertakan dalam mesej anda", "Apa yang perlu disertakan dalam pesan Anda", "สิ่งที่ควรใส่ไว้ในข้อความของคุณ"),
              body: legalLocale("To help us respond quickly, include your workspace name, registered email, target market, and any order, transaction, or invoice identifiers involved.", "为了更快处理你的请求，建议在邮件中附上工作空间名称、注册邮箱、目标市场，以及涉及的订单、交易或发票编号。", "Untuk membantu kami membalas dengan cepat, sertakan nama ruang kerja anda, e-mel berdaftar, pasaran sasaran, dan sebarang pengecam pesanan, transaksi, atau invois yang berkaitan.", "Agar kami dapat merespons lebih cepat, sertakan nama workspace, email terdaftar, pasar tujuan, serta pengenal pesanan, transaksi, atau invoice yang terkait.", "เพื่อให้เราตอบกลับได้เร็วขึ้น โปรดระบุชื่อเวิร์กสเปซ อีเมลที่ลงทะเบียน ตลาดเป้าหมาย และหมายเลขคำสั่งซื้อ ธุรกรรม หรือเอกสารที่เกี่ยวข้อง")
            },
            {
              title: legalLocale("Expected response times", "预期响应时间", "Jangkaan masa respons", "Perkiraan waktu respons", "ระยะเวลาตอบกลับที่คาดหวัง"),
              body: legalLocale("We aim to acknowledge urgent billing, access, and security issues within 2 business days. More complex product, legal, or compliance questions may take longer if we need to review records or coordinate with providers.", "对于紧急的计费、访问和安全问题，我们通常会在 2 个工作日内给出初步回应。若涉及复杂的产品、法务或合规问题，可能需要更多时间来复核记录或与服务商协同处理。", "Kami menyasarkan untuk memberikan pengesahan awal terhadap isu bil, akses, dan keselamatan yang mendesak dalam tempoh 2 hari bekerja. Soalan produk, undang-undang, atau pematuhan yang lebih kompleks mungkin mengambil masa lebih lama jika kami perlu menyemak rekod atau berkoordinasi dengan penyedia.", "Kami menargetkan untuk memberikan tanggapan awal atas masalah tagihan, akses, dan keamanan yang mendesak dalam 2 hari kerja. Pertanyaan produk, hukum, atau kepatuhan yang lebih kompleks dapat memerlukan waktu lebih lama jika kami perlu meninjau catatan atau berkoordinasi dengan penyedia.", "เราตั้งเป้าจะตอบรับปัญหาเร่งด่วนด้านการเรียกเก็บเงิน การเข้าถึง และความปลอดภัยภายใน 2 วันทำการ สำหรับคำถามด้านผลิตภัณฑ์ กฎหมาย หรือการปฏิบัติตามข้อกำหนดที่ซับซ้อนมากขึ้น อาจใช้เวลานานขึ้นหากเราต้องตรวจสอบบันทึกหรือประสานงานกับผู้ให้บริการ")
            }
          ])
        ])
      ]);

    const renderBillingView = () =>
      !signedIn.value
        ? h("div", { class: "page-stack" }, [
            renderPageMasthead(
              localeText({ en: "Billing", "zh-CN": "账单与套餐", "ms-MY": "Bil dan pelan", "id-ID": "Tagihan & paket", "th-TH": "การเรียกเก็บเงิน" }),
              localeText({ en: "Billing and plans", "zh-CN": "账单与套餐", "ms-MY": "Bil dan pelan", "id-ID": "Tagihan & paket", "th-TH": "การเรียกเก็บเงินและแพ็กเกจ" }),
              localeText({ en: "Sign in to purchase a paid plan, review usage, and manage orders for your workspace.", "zh-CN": "登录后可购买付费套餐、查看用量，并管理当前工作空间的订单。", "ms-MY": "Log masuk untuk membeli pelan berbayar, melihat penggunaan, dan mengurus pesanan ruang kerja anda.", "id-ID": "Masuk untuk membeli paket berbayar, melihat penggunaan, dan mengelola pesanan workspace Anda.", "th-TH": "เข้าสู่ระบบเพื่อซื้อแพ็กเกจ ดูการใช้งาน และจัดการคำสั่งซื้อของเวิร์กสเปซ" }),
              [],
              []
            ),
            h("section", { class: "panel history-panel" }, [
              h("div", { class: "empty-state" }, localeText({ en: "Sign in to open workspace billing.", "zh-CN": "请先登录后再查看工作空间账单。", "ms-MY": "Log masuk dahulu untuk membuka bil ruang kerja.", "id-ID": "Silakan masuk terlebih dahulu untuk membuka tagihan workspace.", "th-TH": "กรุณาเข้าสู่ระบบก่อนเพื่อดูบิลของเวิร์กสเปซ" })),
              h("div", { class: "toolbar-row" }, [h("button", { class: "action-button", type: "button", onClick: () => openAuthModal("login") }, copy("topbarLogin"))])
            ])
          ])
        : h("div", { class: "page-stack" }, [
            renderPageMasthead(
              localeText({ en: "Billing", "zh-CN": "账单与套餐", "ms-MY": "Bil dan pelan", "id-ID": "Tagihan & paket", "th-TH": "การเรียกเก็บเงิน" }),
              localeText({ en: "Workspace billing", "zh-CN": "工作空间账单", "ms-MY": "Bil ruang kerja", "id-ID": "Tagihan workspace", "th-TH": "บิลของเวิร์กสเปซ" }),
              localeText({ en: "Buy a plan, check remaining allowance, and review paid orders in one place.", "zh-CN": "在一个页面中完成套餐购买、剩余额度查看和已支付订单管理。", "ms-MY": "Beli pelan, semak baki kuota, dan semak pesanan berbayar di satu tempat.", "id-ID": "Beli paket, cek sisa kuota, dan tinjau pesanan berbayar di satu tempat.", "th-TH": "ซื้อแพ็กเกจ ตรวจสอบโควตาที่เหลือ และดูคำสั่งซื้อที่ชำระแล้วได้ในหน้าเดียว" }),
              activeOrganization.value ? [activeOrganization.value.name, localizePlan(activeOrganization.value.planCode), localizeBilling(activeOrganization.value.billingStatus)] : [],
              billingOverview.value
                ? [
                    {
                      label: localeText({ en: "Checks used", "zh-CN": "已用检查", "ms-MY": "Semakan digunakan", "id-ID": "Pemeriksaan terpakai", "th-TH": "ใช้การตรวจไปแล้ว" }),
                      value: `${billingOverview.value.usage.checks}/${billingOverview.value.limits.checksPerMonth}`,
                      body: localeText({ en: "Counts refresh monthly from the usage ledger.", "zh-CN": "使用次数按月结算，并从真实用量账本读取。", "ms-MY": "Kiraan disegar semula setiap bulan daripada lejar penggunaan.", "id-ID": "Hitungan direset tiap bulan dari usage ledger.", "th-TH": "จำนวนจะรีเฟรชรายเดือนจากบัญชีการใช้งานจริง" })
                    },
                    {
                      label: localeText({ en: "Extractions used", "zh-CN": "已用抽取", "ms-MY": "Ekstraksi digunakan", "id-ID": "Ekstraksi terpakai", "th-TH": "ใช้การดึงข้อมูลไปแล้ว" }),
                      value: `${billingOverview.value.usage.extractions}/${billingOverview.value.limits.extractionsPerMonth}`,
                      body: localeText({ en: "OCR and AI extraction share this monthly allowance.", "zh-CN": "OCR 与 AI 抽取共用这一份月度额度。", "ms-MY": "OCR dan ekstraksi AI berkongsi kuota bulanan ini.", "id-ID": "OCR dan ekstraksi AI berbagi kuota bulanan ini.", "th-TH": "OCR และการดึงข้อมูลด้วย AI ใช้โควตารายเดือนเดียวกัน" })
                    },
                    {
                      label: localeText({ en: "Previews used", "zh-CN": "已用预览生成", "ms-MY": "Pratonton digunakan", "id-ID": "Pratinjau terpakai", "th-TH": "ใช้การสร้างพรีวิวไปแล้ว" }),
                      value: `${billingOverview.value.usage.generatedInvoices}/${billingOverview.value.limits.generatedInvoicesPerMonth}`,
                      body: localeText({ en: "Preview-page generation is enforced by plan in real time.", "zh-CN": "预览页生成次数会按套餐实时拦截。", "ms-MY": "Penjanaan halaman pratonton dikuatkuasakan mengikut pelan secara masa nyata.", "id-ID": "Pembuatan halaman pratinjau dibatasi paket secara real time.", "th-TH": "การสร้างหน้าพรีวิวถูกจำกัดตามแพ็กเกจแบบเรียลไทม์" })
                    }
                  ]
                : []
            ),
            billingStatus.value ? h("div", { class: "notice-bar" }, billingStatus.value) : null,
            h("section", { class: "panel home-panel" }, [
              h("div", { class: "panel-copy" }, [
                h("h2", localeText({ en: "Choose a plan", "zh-CN": "选择套餐", "ms-MY": "Pilih pelan", "id-ID": "Pilih paket", "th-TH": "เลือกแพ็กเกจ" })),
                h("p", { class: "muted-text" }, localeText({ en: "Paid plans are activated after checkout confirmation and webhook sync. If a subscription already exists, use the management link to change it.", "zh-CN": "付费套餐会在支付完成并完成回写后生效。如果工作空间已存在订阅，请使用订阅管理链接进行变更。", "ms-MY": "Pelan berbayar diaktifkan selepas checkout disahkan dan webhook disegerakkan. Jika langganan sudah wujud, gunakan pautan pengurusan untuk mengubahnya.", "id-ID": "Paket berbayar aktif setelah checkout dikonfirmasi dan webhook tersinkron. Jika subscription sudah ada, gunakan tautan pengelolaan untuk mengubahnya.", "th-TH": "แพ็กเกจแบบชำระเงินจะมีผลหลังยืนยันการชำระและซิงก์ webhook หากมีการสมัครอยู่แล้ว ให้ใช้ลิงก์จัดการเพื่อเปลี่ยนแพ็กเกจ" }))
              ]),
              h("div", { class: "pricing-grid" }, availablePlans.value.map((plan) => renderPlanCard(plan, plan.code === activeOrganization.value?.planCode)))
            ]),
            h("section", { class: "panel history-panel" }, [
              h("div", { class: "panel-copy" }, [
                h("h2", localeText({ en: "Orders", "zh-CN": "订单", "ms-MY": "Pesanan", "id-ID": "Pesanan", "th-TH": "คำสั่งซื้อ" })),
                h("p", { class: "muted-text" }, localeText({ en: "These records are kept locally so your team can confirm payment status, renewal timing, and refund state without leaving the workspace.", "zh-CN": "这些订单会在本地保留，便于团队直接确认支付状态、续费节奏和退款状态。", "ms-MY": "Rekod ini disimpan secara tempatan supaya pasukan anda boleh mengesahkan status bayaran, masa pembaharuan, dan status refund tanpa keluar dari ruang kerja.", "id-ID": "Catatan ini disimpan secara lokal agar tim Anda dapat mengecek status pembayaran, waktu perpanjangan, dan status refund tanpa keluar dari workspace.", "th-TH": "คำสั่งซื้อเหล่านี้จะถูกเก็บในระบบ เพื่อให้ทีมของคุณตรวจสถานะการชำระ การต่ออายุ และการคืนเงินได้โดยไม่ต้องออกจากเวิร์กสเปซ" }))
              ]),
              billingOverview.value?.orders?.length
                ? h("div", { class: "activity-list" }, billingOverview.value.orders.map((order) =>
                    h("article", { class: "activity-card" }, [
                      h("div", { class: "activity-card-head" }, [
                        h("strong", `${localizePlan(order.planCode)} · ${order.providerTransactionId}`),
                        h("span", { class: "status-badge" }, localizeBilling(order.status))
                      ]),
                      h("p", { class: "muted-text" }, `${order.currency ?? "USD"} ${order.totalAmount ?? 0}${order.refundedAmount ? ` · ${localeText({ en: "Refunded", "zh-CN": "已退款", "ms-MY": "Dipulangkan", "id-ID": "Direfund", "th-TH": "คืนเงินแล้ว" })}: ${order.refundedAmount}` : ""}`),
                      h("small", `${formatTimestamp(order.createdAt, locale.value)}${order.completedAt ? ` · ${localeText({ en: "Paid", "zh-CN": "已支付", "ms-MY": "Dibayar", "id-ID": "Dibayar", "th-TH": "ชำระแล้ว" })}: ${formatTimestamp(order.completedAt, locale.value)}` : ""}`)
                    ])
                  ))
                : h("div", { class: "empty-state empty-state-compact" }, localeText({ en: "No paid orders yet.", "zh-CN": "还没有已支付订单。", "ms-MY": "Belum ada pesanan berbayar.", "id-ID": "Belum ada pesanan berbayar.", "th-TH": "ยังไม่มีคำสั่งซื้อที่ชำระแล้ว" }))
            ])
          ]);

    const renderAdminView = () =>
      !isBillingAdmin.value
        ? h("div", { class: "page-stack" }, [
            renderPageMasthead(
              localeText({ en: "Billing admin", "zh-CN": "订单后台", "ms-MY": "Pentadbir bil", "id-ID": "Admin tagihan", "th-TH": "หลังบ้านคำสั่งซื้อ" }),
              localeText({ en: "Billing admin", "zh-CN": "订单后台", "ms-MY": "Pentadbir bil", "id-ID": "Admin tagihan", "th-TH": "หลังบ้านคำสั่งซื้อ" }),
              localeText({ en: "This page is only available to emails listed in the billing admin allowlist.", "zh-CN": "只有出现在计费管理员白名单里的邮箱才能进入这个页面。", "ms-MY": "Halaman ini hanya tersedia untuk e-mel yang disenaraikan dalam senarai admin bil.", "id-ID": "Halaman ini hanya tersedia untuk email yang ada di allowlist admin billing.", "th-TH": "หน้านี้ใช้ได้เฉพาะอีเมลที่อยู่ใน allowlist ของผู้ดูแลการเรียกเก็บเงิน" }),
              [],
              []
            ),
            h("section", { class: "panel history-panel" }, [h("div", { class: "empty-state" }, localeText({ en: "No admin access.", "zh-CN": "当前没有后台权限。", "ms-MY": "Tiada akses pentadbir.", "id-ID": "Tidak memiliki akses admin.", "th-TH": "ไม่มีสิทธิ์เข้าถึงหลังบ้าน" }))])
          ])
        : h("div", { class: "page-stack" }, [
            renderPageMasthead(
              localeText({ en: "Billing admin", "zh-CN": "订单后台", "ms-MY": "Pentadbir bil", "id-ID": "Admin tagihan", "th-TH": "หลังบ้านคำสั่งซื้อ" }),
              localeText({ en: "Orders and refunds", "zh-CN": "订单与退款管理", "ms-MY": "Pesanan dan refund", "id-ID": "Pesanan dan refund", "th-TH": "คำสั่งซื้อและการคืนเงิน" }),
              localeText({ en: "Review paid orders across all workspaces, inspect refund state, and issue full refunds when needed.", "zh-CN": "查看所有工作空间的付费订单、退款状态，并在需要时发起全额退款。", "ms-MY": "Semak pesanan berbayar merentas semua ruang kerja, status refund, dan keluarkan refund penuh apabila perlu.", "id-ID": "Tinjau pesanan berbayar di semua workspace, status refund, dan lakukan refund penuh bila diperlukan.", "th-TH": "ตรวจคำสั่งซื้อแบบชำระเงินของทุกเวิร์กสเปซ ดูสถานะการคืนเงิน และออกคำสั่งคืนเงินเต็มจำนวนเมื่อจำเป็น" }),
              [],
              adminOverview.value
                ? [
                    { label: localeText({ en: "Orders", "zh-CN": "订单数", "ms-MY": "Pesanan", "id-ID": "Pesanan", "th-TH": "จำนวนคำสั่งซื้อ" }), value: String(adminOverview.value.counts.orders), body: localeText({ en: "All tracked billing orders.", "zh-CN": "系统内已追踪的全部账单订单。", "ms-MY": "Semua pesanan bil yang dijejaki.", "id-ID": "Semua pesanan billing yang dilacak.", "th-TH": "คำสั่งซื้อด้านบิลทั้งหมดที่ติดตามในระบบ" }) },
                    { label: localeText({ en: "Active subscriptions", "zh-CN": "有效订阅", "ms-MY": "Langganan aktif", "id-ID": "Subscription aktif", "th-TH": "การสมัครที่ใช้งานอยู่" }), value: String(adminOverview.value.counts.activeSubscriptions), body: localeText({ en: "Workspaces currently on an active paid subscription.", "zh-CN": "当前处于有效付费订阅中的工作空间数。", "ms-MY": "Ruang kerja yang kini berada dalam langganan berbayar aktif.", "id-ID": "Workspace yang saat ini berada pada subscription berbayar aktif.", "th-TH": "จำนวนเวิร์กสเปซที่อยู่บนการสมัครแบบชำระเงินที่ใช้งานอยู่" }) },
                    { label: localeText({ en: "Refunds pending", "zh-CN": "退款处理中", "ms-MY": "Refund diproses", "id-ID": "Refund diproses", "th-TH": "กำลังดำเนินการคืนเงิน" }), value: String(adminOverview.value.counts.refundPending), body: localeText({ en: "Orders with a refund still being processed by Paddle.", "zh-CN": "仍在由 Paddle 处理中的退款订单。", "ms-MY": "Pesanan yang refundnya masih diproses oleh Paddle.", "id-ID": "Pesanan yang refund-nya masih diproses oleh Paddle.", "th-TH": "คำสั่งซื้อที่การคืนเงินยังอยู่ระหว่างดำเนินการโดย Paddle" }) }
                  ]
                : []
            ),
            adminStatus.value ? h("div", { class: "notice-bar" }, adminStatus.value) : null,
            h("section", { class: "panel history-panel" }, [
              h("div", { class: "panel-copy" }, [h("h2", localeText({ en: "Order queue", "zh-CN": "订单队列", "ms-MY": "Senarai pesanan", "id-ID": "Antrian pesanan", "th-TH": "คิวคำสั่งซื้อ" }))]),
              adminOrders.value.length
                ? h("div", { class: "activity-list" }, adminOrders.value.map((order) =>
                    h("article", { class: "activity-card" }, [
                      h("div", { class: "activity-card-head" }, [
                        h("strong", `${order.organizationName} · ${localizePlan(order.planCode)}`),
                        h("span", { class: "status-badge" }, localizeBilling(order.status))
                      ]),
                      h("p", { class: "muted-text" }, `${order.providerTransactionId} · ${order.currency ?? "USD"} ${order.totalAmount ?? 0}`),
                      h("small", `${formatTimestamp(order.createdAt, locale.value)}${order.completedAt ? ` · ${formatTimestamp(order.completedAt, locale.value)}` : ""}`),
                      h("div", { class: "toolbar-row card-action-row" }, [
                        h("button", { class: "action-button secondary-button subtle-button", type: "button", disabled: adminRefundPendingOrderId.value === order.id || order.status === "refunded" || order.status === "refund_pending", onClick: () => refundAdminOrder(order.id) }, adminRefundPendingOrderId.value === order.id ? copy("processing") : localeText({ en: "Refund order", "zh-CN": "退款", "ms-MY": "Refund pesanan", "id-ID": "Refund pesanan", "th-TH": "คืนเงินคำสั่งซื้อ" }))
                      ])
                    ])
                  ))
                : h("div", { class: "empty-state empty-state-compact" }, localeText({ en: "No tracked orders yet.", "zh-CN": "还没有已追踪订单。", "ms-MY": "Belum ada pesanan yang dijejaki.", "id-ID": "Belum ada pesanan yang dilacak.", "th-TH": "ยังไม่มีคำสั่งซื้อที่ถูกติดตาม" }))
            ])
          ]);

    const renderHistoryView = () => {
      const historyLimit = 8;
      const historyRetentionDays =
        billingOverview.value?.limits.historyDays ??
        availablePlans.value.find((plan) => plan.code === activeOrganization.value?.planCode)?.historyDays ??
        DEFAULT_PLAN_CATALOG[0].historyDays;
      const historyTabLabel = (tab: HistoryTab) => {
        switch (tab) {
          case "invoices":
            return `${labels.value.historyInvoices} (${latestInvoices.value.length})`;
          default:
            return `${copy("recentRuns")} (${latestRuns.value.length})`;
        }
      };
      const renderHistoryMoreButton = (total: number) =>
        total > historyLimit
          ? h(
              "button",
              {
                class: "action-button secondary-button subtle-button result-more-button",
                type: "button",
                onClick: () => (historyExpanded.value = !historyExpanded.value)
              },
              historyExpanded.value
                ? localeText({ en: "Show fewer", "zh-CN": "收起", "ms-MY": "Tunjuk kurang", "id-ID": "Tampilkan lebih sedikit", "th-TH": "แสดงน้อยลง" })
                : localeText({ en: `Show all ${total}`, "zh-CN": `查看全部 ${total} 条`, "ms-MY": `Lihat semua ${total}`, "id-ID": `Lihat semua ${total}`, "th-TH": `ดูทั้งหมด ${total}` })
            )
          : null;

      const renderHistoryCurrentPanel = () => {
        if (historyTab.value === "invoices") {
          const items = historyExpanded.value ? latestInvoices.value : latestInvoices.value.slice(0, historyLimit);
          return h("section", { class: "subpanel history-section" }, [
            h("div", { class: "section-copy" }, [h("h3", labels.value.historyInvoices), h("p", { class: "muted-text" }, localeText({ en: "These are saved customer preview versions for this workspace. Reopen them to continue editing or to review what your team prepared earlier.", "zh-CN": "这里保存的是当前工作空间的客户预览版本。你可以重新打开继续编辑，或回看团队此前准备过的交付内容。", "ms-MY": "Ini ialah versi pratonton pelanggan yang disimpan untuk ruang kerja ini. Buka semula untuk sambung mengedit atau menyemak hasil yang pasukan anda sediakan sebelum ini.", "id-ID": "Ini adalah versi pratinjau pelanggan yang disimpan untuk workspace ini. Buka kembali untuk melanjutkan edit atau meninjau hasil yang pernah disiapkan tim Anda.", "th-TH": "นี่คือเวอร์ชันพรีวิวสำหรับลูกค้าที่บันทึกไว้ของเวิร์กสเปซนี้ คุณสามารถเปิดกลับมาเพื่อแก้ไขต่อหรือทบทวนสิ่งที่ทีมเคยเตรียมไว้ได้" }))]),
            latestInvoices.value.length === 0
              ? h("div", { class: "empty-state empty-state-compact" }, labels.value.historyNoInvoices)
              : h("div", { class: "activity-list" }, items.map((invoice) =>
                  h("article", { class: "activity-card" }, [
                    h("div", { class: "activity-card-head" }, [
                      h("strong", `${invoice.invoiceNumber} · v${invoice.revisionNumber}`),
                      h("span", { class: "status-badge" }, localizeOpsStatus(invoice.status))
                    ]),
                    h("p", { class: "muted-text" }, `${invoice.sellerName} · ${invoice.buyerName ?? copy("notAvailable")}`),
                    h("small", `${localizeRisk(invoice.overallRiskLevel)} · ${invoice.rulebookVersion} · ${invoice.generatedHtml ? localeText({ en: "Preview ready", "zh-CN": "预览已生成", "ms-MY": "Pratonton tersedia", "id-ID": "Pratinjau siap", "th-TH": "พรีวิวพร้อมแล้ว" }) : localeText({ en: "Preview pending", "zh-CN": "预览尚未生成", "ms-MY": "Pratonton belum dijana", "id-ID": "Pratinjau belum dibuat", "th-TH": "พรีวิวยังไม่พร้อม" })} · ${formatTimestamp(invoice.updatedAt, locale.value)}`),
                    h("div", { class: "toolbar-row card-action-row" }, [
                      h("button", { class: "action-button secondary-button subtle-button", type: "button", onClick: () => loadSavedInvoiceIntoEditor(invoice.id) }, localeText({ en: "Continue editing", "zh-CN": "继续编辑", "ms-MY": "Sambung edit", "id-ID": "Lanjutkan edit", "th-TH": "แก้ไขต่อ" })),
                      invoice.generatedHtml
                        ? h("button", { class: "action-button secondary-button subtle-button", type: "button", onClick: () => openSavedInvoicePreview(invoice.id) }, localeText({ en: "Open customer preview", "zh-CN": "打开客户预览", "ms-MY": "Buka pratonton pelanggan", "id-ID": "Buka pratinjau pelanggan", "th-TH": "เปิดพรีวิวลูกค้า" }))
                        : null
                    ])
                  ])
                )),
            renderHistoryMoreButton(latestInvoices.value.length)
          ]);
        }

        const items = historyExpanded.value ? latestRuns.value : latestRuns.value.slice(0, historyLimit);
        return h("section", { class: "subpanel history-section" }, [
          h("div", { class: "section-copy" }, [h("h3", copy("recentRuns")), h("p", { class: "muted-text" }, localeText({ en: "Each saved check is a point-in-time snapshot of one invoice draft. Use it to trace what changed, reopen the draft, or compare rule outcomes across edits.", "zh-CN": "每一条检查记录都代表某一时刻的一份发票草稿快照。你可以用它追踪改动、重新打开草稿，或对比不同版本下的规则结果。", "ms-MY": "Setiap semakan tersimpan ialah gambaran pada satu masa bagi satu draf invois. Gunakannya untuk menjejak perubahan, membuka semula draf, atau membandingkan hasil peraturan antara suntingan.", "id-ID": "Setiap pemeriksaan tersimpan adalah snapshot pada satu waktu untuk satu draft invoice. Gunakan untuk melacak perubahan, membuka kembali draft, atau membandingkan hasil aturan antarversi.", "th-TH": "ผลการตรวจแต่ละรายการคือสแนปชอตของร่างเอกสารในช่วงเวลาหนึ่ง คุณสามารถใช้เพื่อติดตามการเปลี่ยนแปลง เปิดร่างกลับมาอีกครั้ง หรือเปรียบเทียบผลตามกฎระหว่างเวอร์ชันได้" }))]),
          latestRuns.value.length === 0
            ? h("div", { class: "empty-state empty-state-compact" }, copy("noRuns"))
            : h("div", { class: "activity-list" }, items.map((run) =>
                h("article", { class: "activity-card" }, [
                  h("div", { class: "activity-card-head" }, [
                    h("strong", `${localizeCountryLabel(run.country)} · ${run.invoiceNumber ?? run.id}`),
                    h("span", { class: "status-badge" }, localizeOpsStatus(run.status))
                  ]),
                  h("p", { class: "muted-text" }, `${localizeRisk(run.overallRiskLevel)} · ${run.rulebookVersion ?? copy("notAvailable")}`),
                  h("small", `${run.trigger ? localizeOpsStatus(run.trigger) : copy("triggerSync")} · ${formatTimestamp(run.createdAt, locale.value)}`),
                  run.invoiceRecordId
                    ? h("div", { class: "toolbar-row card-action-row" }, [
                        h("button", { class: "action-button secondary-button subtle-button", type: "button", onClick: () => loadSavedInvoiceIntoEditor(run.invoiceRecordId!) }, localeText({ en: "Continue editing", "zh-CN": "继续编辑", "ms-MY": "Sambung edit", "id-ID": "Lanjutkan edit", "th-TH": "แก้ไขต่อ" })),
                        run.previewAvailable
                          ? h("button", { class: "action-button secondary-button subtle-button", type: "button", onClick: () => openSavedInvoicePreview(run.invoiceRecordId!) }, localeText({ en: "Open customer preview", "zh-CN": "打开客户预览", "ms-MY": "Buka pratonton pelanggan", "id-ID": "Buka pratinjau pelanggan", "th-TH": "เปิดพรีวิวลูกค้า" }))
                          : null
                      ])
                    : null
                ])
              )),
          renderHistoryMoreButton(latestRuns.value.length)
        ]);
      };

      return !signedIn.value
        ? h("div", { class: "page-stack" }, [
            renderPageMasthead(
              localeText({ en: "Workspace activity", "zh-CN": "工作空间记录", "ms-MY": "Aktiviti ruang kerja", "id-ID": "Aktivitas workspace", "th-TH": "บันทึกเวิร์กสเปซ" }),
              labels.value.historyTitle,
              labels.value.historySubtitle,
              [],
              []
            ),
            h("section", { class: "panel history-panel" }, [
              h("div", { class: "empty-state" }, labels.value.activityEmpty),
              h("div", { class: "toolbar-row" }, [h("button", { class: "action-button", type: "button", onClick: () => openAuthModal("login") }, copy("topbarLogin"))])
            ])
          ])
        : h("div", { class: "page-stack" }, [
            renderPageMasthead(
              localeText({ en: "Workspace activity", "zh-CN": "工作空间记录", "ms-MY": "Aktiviti ruang kerja", "id-ID": "Aktivitas workspace", "th-TH": "บันทึกเวิร์กสเปซ" }),
              labels.value.historyTitle,
              labels.value.historySubtitle,
              [
                activeOrganization.value?.name ?? copy("notAvailable"),
                localizePlan(activeOrganization.value?.planCode),
                localizeBilling(activeOrganization.value?.billingStatus)
              ],
              [
                {
                  label: labels.value.historyInvoices,
                  value: String(latestInvoices.value.length),
                  body: localeText({
                    en: "Saved preview versions that can be reopened exactly as prepared.",
                    "zh-CN": "按版本保存的预览页，可按当时状态重新打开。",
                    "ms-MY": "Versi pratonton tersimpan yang boleh dibuka semula seperti asal.",
                    "id-ID": "Versi pratinjau tersimpan yang bisa dibuka kembali seperti semula.",
                    "th-TH": "เวอร์ชันพรีวิวที่บันทึกไว้ และสามารถเปิดกลับมาได้ตามเดิม"
                  })
                },
                {
                  label: copy("recentRuns"),
                  value: String(latestRuns.value.length),
                  body: localeText({
                    en: "Check snapshots for comparing rule outcomes across draft changes.",
                    "zh-CN": "保存草稿检查快照，便于对比不同版本的规则结果。",
                    "ms-MY": "Snapshot semakan untuk membandingkan hasil peraturan merentas perubahan draf.",
                    "id-ID": "Snapshot pemeriksaan untuk membandingkan hasil aturan antarperubahan draft.",
                    "th-TH": "สแนปชอตผลการตรวจสำหรับเปรียบเทียบผลตามกฎระหว่างการแก้ไขร่าง"
                  })
                },
                {
                  label: localeText({ en: "History window", "zh-CN": "历史保留", "ms-MY": "Tempoh sejarah", "id-ID": "Jangka riwayat", "th-TH": "ช่วงเวลาประวัติ" }),
                  value: localeText({ en: `${historyRetentionDays} days`, "zh-CN": `${historyRetentionDays} 天`, "ms-MY": `${historyRetentionDays} hari`, "id-ID": `${historyRetentionDays} hari`, "th-TH": `${historyRetentionDays} วัน` }),
                  body: localeText({
                    en: "Only reopenable work records stay here. Background operations remain out of the way.",
                    "zh-CN": "这里只保留可重新打开的工作记录，后台运行细节不会打扰用户。",
                    "ms-MY": "Hanya rekod kerja yang boleh dibuka semula dipaparkan di sini. Operasi latar kekal tersembunyi.",
                    "id-ID": "Hanya catatan kerja yang bisa dibuka kembali ditampilkan di sini. Operasi latar tetap tersembunyi.",
                    "th-TH": "ที่นี่จะแสดงเฉพาะบันทึกงานที่กลับมาเปิดต่อได้ ส่วนการทำงานเบื้องหลังจะไม่รบกวนผู้ใช้"
                  })
                }
              ]
            ),
            h("section", { class: "panel history-panel" }, [
              h("div", { class: "tab-strip history-tab-strip" }, [
                tabButton(historyTabLabel("runs"), historyTab.value === "runs", () => (historyTab.value = "runs")),
                tabButton(historyTabLabel("invoices"), historyTab.value === "invoices", () => (historyTab.value = "invoices"))
              ]),
              renderHistoryCurrentPanel()
            ])
          ]);
    };

    const renderFooterLink = (label: string, page: AppPage) =>
      h(
        "button",
        {
          class: currentPage.value === page ? "footer-link active" : "footer-link",
          type: "button",
          onClick: () => navigateTo(page)
        },
        label
      );

    const renderSiteFooter = () =>
      h("footer", { class: "site-footer" }, [
        h("div", { class: "panel footer-panel" }, [
          h("div", { class: "footer-layout" }, [
            h("div", { class: "footer-brand" }, [
              h("strong", t(locale.value, "appTitle")),
              h("p", { class: "muted-text" }, legalLocale(
                "Smart invoicing and compliance platform for Southeast Asia.",
                "面向东南亚市场的智能开票与合规平台。",
                "Platform invois pintar dan pematuhan untuk Asia Tenggara.",
                "Platform invoice cerdas dan kepatuhan untuk Asia Tenggara.",
                "แพลตฟอร์มเอกสารและความสอดคล้องอัจฉริยะสำหรับเอเชียตะวันออกเฉียงใต้"
              ))
            ]),
            h("div", { class: "footer-grid" }, [
              h("div", { class: "footer-group" }, [
                h("span", { class: "field-label" }, legalLocale("Company", "公司", "Syarikat", "Perusahaan", "บริษัท")),
                renderFooterLink(legalLocale("Product", "产品说明", "Produk", "Produk", "ผลิตภัณฑ์"), "product"),
                renderFooterLink(legalLocale("Pricing", "定价页", "Harga", "Harga", "ราคา"), "pricing"),
                renderFooterLink(legalLocale("Contact", "联系方式", "Hubungi", "Kontak", "ติดต่อ"), "contact")
              ]),
              h("div", { class: "footer-group" }, [
                h("span", { class: "field-label" }, legalLocale("Legal", "合规", "Undang-undang", "Hukum", "กฎหมาย")),
                renderFooterLink(legalLocale("Privacy Policy", "隐私政策", "Dasar Privasi", "Kebijakan Privasi", "นโยบายความเป็นส่วนตัว"), "privacy"),
                renderFooterLink(legalLocale("Terms of Service", "服务条款", "Terma Perkhidmatan", "Ketentuan Layanan", "ข้อกำหนดการใช้บริการ"), "terms"),
                renderFooterLink(legalLocale("Refund Policy", "退款说明", "Polisi Bayaran Balik", "Kebijakan Refund", "นโยบายการคืนเงิน"), "refunds")
              ]),
              h("div", { class: "footer-group" }, [
                h("span", { class: "field-label" }, legalLocale("Workspace", "产品入口", "Ruang kerja", "Workspace", "เวิร์กสเปซ")),
                renderFooterLink(labels.value.home, "home"),
                renderFooterLink(labels.value.workbench, "workspace"),
                renderFooterLink(labels.value.sources, "rules")
              ])
            ])
          ]),
          h("div", { class: "footer-meta" }, [
            h("span", legalLocale(`Contact: ${SUPPORT_EMAIL}`, `联系邮箱：${SUPPORT_EMAIL}`, `Hubungi: ${SUPPORT_EMAIL}`, `Kontak: ${SUPPORT_EMAIL}`, `ติดต่อ: ${SUPPORT_EMAIL}`)),
            h("span", legalLocale(`Updated ${LEGAL_LAST_UPDATED}`, `更新于 ${LEGAL_LAST_UPDATED}`, `Dikemas kini ${LEGAL_LAST_UPDATED}`, `Diperbarui ${LEGAL_LAST_UPDATED}`, `อัปเดต ${LEGAL_LAST_UPDATED}`))
          ])
        ])
      ]);

    const renderPageContent = () => {
      switch (currentPage.value) {
        case "product":
          return renderProductView();
        case "pricing":
          return renderPricingPage();
        case "privacy":
          return renderPrivacyView();
        case "terms":
          return renderTermsView();
        case "refunds":
          return renderRefundsView();
        case "contact":
          return renderContactView();
        case "billing":
          return renderBillingView();
        case "admin":
          return renderAdminView();
        case "workspace":
          return renderWorkbenchView();
        case "history":
          return renderHistoryView();
        case "rules":
          return renderSourcesView();
        default:
          return renderHomeView();
      }
    };

    return () =>
      h("div", { class: "app-shell" }, [
        h("header", { class: "topbar" }, [
          h("div", { class: "brand-copy" }, [h("strong", t(locale.value, "appTitle")), h("p", { class: "muted-text" }, copy("tagline"))]),
          h("nav", { class: "topbar-nav" }, [
            navButton(labels.value.home, "home"),
            navButton(labels.value.workbench, "workspace"),
            navButton(labels.value.sources, "rules"),
            navButton(labels.value.history, "history"),
            signedIn.value ? navButton(labels.value.billing, "billing") : null,
            isBillingAdmin.value ? navButton(labels.value.admin, "admin") : null
          ]),
          h("div", { class: "topbar-actions" }, [
            compactSelect(copy("countryLabel"), country.value, COUNTRY_CODES.map((item) => ({ value: item, label: getCountryName(locale.value, item) })), (value) => {
              country.value = value as CountryCode;
              draft.country = value as CountryCode;
            }),
            compactSelect(copy("languageLabel"), locale.value, localeOptions.value, (value) => {
              locale.value = value as LocaleCode;
            }),
            auth.user
              ? h(
                  "button",
                  {
                    class: "topbar-user",
                    type: "button",
                    title: auth.user.displayName || auth.user.email,
                    onClick: () => (authDialogOpen.value = true)
                  },
                  auth.user.displayName || auth.user.email
                )
              : [
                  h("button", { class: "action-button secondary-button", type: "button", onClick: () => openAuthModal("register") }, copy("topbarRegister")),
                  h("button", { class: "action-button", type: "button", onClick: () => openAuthModal("login") }, copy("topbarLogin"))
                ]
          ])
        ]),
        h("main", { class: "app-main" }, [renderPageContent()]),
        renderSiteFooter(),
        h("div", { id: CAPTCHA_ELEMENT_ID, class: "captcha-anchor", "aria-hidden": "true" }),
        h("button", { id: CAPTCHA_TRIGGER_ID, type: "button", class: "captcha-trigger-hidden", tabindex: -1, "aria-hidden": "true" }),
        renderAuthModal(),
        renderEditorGuideDrawer(),
        renderPreviewModal()
      ]);
  }
});









